import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type MouseEvent } from 'react';
import { useRegisterHelpContext } from '../../contexts/ToastContext';
import { createPortal } from 'react-dom';
import {
  ArrowDownToLine,
  Banknote,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Copy,
  Table2,
  LayoutGrid,
  Eye,
  FileDown,
  FileText,
  Filter,
  Paperclip,
  GraduationCap,
  MoreVertical,
  Languages,
  Mail,
  MapPin,
  MessageCircle,
  RotateCcw,
  Search,
  Plus,
  Pencil,
  Trash2,
  Upload,
  UserPlus,
  Users,
  Wallet,
  X,
  History,
  ExternalLink,
  CheckCircle2,
  Check,
  type LucideIcon,
} from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { AddEditCustomerModal } from './AddEditCustomerModal';
import { AttendeeContactModal, CompanyCohortEmailContext } from './AttendeeContactModal';
import { Customer, CourseSession, CourseSessionAttendee, CourseSessionDateBlock, GeneratedCertificate } from '../../types/crm';
import { ProcessedCertificate } from '../../types';
import { COURSE_CONFIG, CERTIFICATE_FIELD_CONFIG, INBOUND_CHANNEL_OPTIONS, type InboundChannel } from '../../types';
import { fetchCourseFeeOverrideMap, searchActiveCourseNamesFromDb } from '../../services/coursesService';
import {
  AttendeePaymentMethod,
  AttendeePaymentStepType,
  PAYMENT_METHOD_OPTIONS,
  SessionDocumentAttachment,
  SessionHistoryEvent,
  assertNotAlreadyBooked,
  courseSessionsService,
  findDuplicateSessionGroups,
  formatSessionTargetLabel,
  resolveExternalBookedByLabel,
  sessionReferenceCodesForGroup,
  sessionsAreLikelyDuplicates,
} from '../../services/courseSessionsService';
import { interpretersService, InterpreterRow } from '../../services/interpretersService';
import { supabaseCustomerStorage, supabaseInstructorStorage } from '../../services/supabaseStorage';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import { calculateExpiryDate, generateCertificatePDF } from '../../utils/pdfGenerator';
import {
  revokeCertificatesForSessionSeat,
  upsertIssuedCertificateFromGenerated,
} from '../../services/issuedCertificatesService';
import {
  findExistingCertificateNumber,
  findMatchingGeneratedCertificateIndex,
  provisionalSessionCertificateId,
} from '../../utils/certificateNumber';
import { formatDateUk, formatDateTimeUk, formatDateUkForFileName, parseEuroInput, parseUkDateToIso } from '../../utils/paymentFormUtils';
import {
  EMPTY_ROSTER_EMAIL_INDICATORS,
  buildRosterEmailTitle,
  classifyRosterEmailKind,
  mapOutboxStatus,
  mergeKindStatus,
  pickInitiatorName,
  recipientEmailKey,
  type RosterEmailIndicators,
  type RosterEmailKind,
  type RosterEmailKindStatus,
} from '../../utils/rosterEmailIndicators';
import { PaymentMethodSelect } from '../payments/PaymentMethodSelect';
import {
  individualCustomerDisplayName,
  individualCustomerSearchSubtitle,
  persistNewCustomer,
} from '../../utils/customerRecordPersistence';
import {
  individualCustomerMatchLabel,
  individualIdentityDraftFromCustomer,
  searchIndividualIdentityMatches,
} from '../../utils/individualCustomerMatch';
import { normalizeParticipantNameParts } from '../../utils/textFormatting';
import { useAuth } from '../../hooks/useAuth';
import { ClientNotesService } from '../../services/clientNotesService';
import {
  getCourseCatalogDefaultFee,
  getCourseCatalogDefaultFeeForSession,
  getFeeDiscountPercentVsCatalog,
  hasLanguageSpecificCatalogFee,
  resolveCatalogFeeLanguage,
} from '../../utils/courseCatalogFee';
import { buildDefaultCertificateFileName } from '../../utils/generatedCertificateRegeneration';
import { sessionReferenceCode, formatDeliveryModeLabel, buildSessionReferenceIndex } from '../dashboard/trainingCalendarUtils';
import { SessionRosterSpreadsheet, type RosterSpreadsheetRow } from './SessionRosterSpreadsheet';
import { SessionsTableView, type SessionListTableRow } from '../bookings/TableView';
import {
  capPaidToFee,
  computeEditPaymentLineAmount,
  computePaidInFullFeeCorrectionDelta,
  derivePaymentStatusFromPaid,
  getAttendeeDisplayPaid,
  getExistingAttendeePaid,
  splitPaymentAcrossSeats,
  sumAttendeeLedgerPaid,
  sumAttendeeLedgerRaw,
  type AttendeePaymentLedgerRow
} from '../../utils/sessionAttendeePayments';
import type { SafePassCardTrackingStatus } from '../../types/crm';
import {
  CARD_STATUS_OPTIONS,
  getCardStatusLabel,
  getProcessStatusLabel,
  inferProcessStatus,
  isUrgentProcessStatus,
  todayIsoDate,
} from '../../utils/safePassTracking';
import {
  buildRosterDisplayGroups,
  flattenRosterDisplayOrder,
  rosterGroupKeyForAttendee,
} from '../../utils/rosterDisplayOrder';

const ROSTER_NOTE_MAX_LEN = 150;
/** ~3� a short roster note; keep full width so it does not shrink in the meta row. */
const ROSTER_NOTE_INPUT_CLASS =
  'h-7 w-[36rem] max-w-[85vw] shrink-0 rounded border border-slate-300 bg-white px-2 text-xs font-normal normal-case text-slate-800 placeholder:text-slate-400 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100';
/** Alternating seat cards ? same height, clear edge so neighbouring seats do not blur. */
function rosterSeatTone(rowIndex: number): { shell: string; edge: string } {
  if (rowIndex % 2 === 1) {
    return {
      shell: 'bg-sky-50 dark:bg-slate-700/70',
      edge: 'border-sky-400 dark:border-sky-500',
    };
  }
  return {
    shell: 'bg-white dark:bg-slate-800',
    edge: 'border-slate-400 dark:border-slate-500',
  };
}
import {
  attendeeBalanceEuro,
  computeSessionFinanceOverview,
  rosterPaidEuro,
  sumLedgerRawPaid
} from '../../utils/sessionFinanceOverview';
import {
  conflictMessageForEnrollment,
  findConflictingEnrollment,
  getCertificateIneligibilityReasons,
  isAttendeeCertificateEligible,
  isAttendeeCountedInSessionFinancials,
  isAttendeeFailed,
  isAttendeeNoShow,
  isAttendeeOnActiveRoster,
  isAttendeeRemoved,
  isAttendeeWaitingList,
  attendeeStatusLabel,
} from '../../utils/sessionAttendeeStatus';
import {
  type FinancialSummaryData,
  type FinancialSummaryParticipantGroup,
  type FinancialSummaryParticipantRow,
  type FinancialSummaryPaymentRow,
} from '../../utils/sessionFinancialSummaryExport';
import { CourseFinancialSummaryModal } from './CourseFinancialSummaryModal';
import { useAdmin } from '../../hooks/useAdmin';
import { useOwner } from '../../hooks/useOwner';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';

// Scheduled offerings (dates, capacity, venue, rosters): operational calendar/session UI.
// Per-person enrollments also surface on the client profile. See src/domain/saasDataOwnership.ts
const SAFE_PASS_COURSE = 'Safepass Training';
const DEFAULT_LOCATION_MATCH = '11c parkmore industrial estate';

type SessionLocationMaps = {
  labels: Record<string, string>;
  details: Record<string, any>;
  mapsUrls: Record<string, string>;
};

function locationLabelFromRow(row: any): string {
  const eircode = String(row?.eircode_postcode || row?.postcode || '').trim();
  const addressParts = [
    row?.address_line_1 || row?.address,
    row?.city,
    row?.county_state || row?.county,
    eircode,
    row?.country,
  ]
    .map((v: unknown) => String(v || '').trim())
    .filter(Boolean);
  const addressLabel = addressParts.join(', ');
  const name = String(row?.name || '').trim();
  return name ? (addressLabel ? `${name} - ${addressLabel}` : name) : (addressLabel || 'Location');
}

function locationOptionLabelFromRow(row: any): string {
  const eircode = String(row?.eircode_postcode || row?.postcode || '').trim();
  const addressParts = [row?.address_line_1 || row?.address, row?.city, eircode]
    .map((v: unknown) => String(v || '').trim())
    .filter(Boolean);
  const addressLabel = addressParts.join(', ');
  const name = String(row?.name || '').trim();
  return name ? (addressLabel ? `${name} - ${addressLabel}` : name) : (addressLabel || 'Location');
}

/** Venue name only ? keeps the Advanced filters location popup a normal width. */
function locationFilterShortLabel(fullOrName: string | null | undefined): string {
  const raw = String(fullOrName || '').trim();
  if (!raw) return 'Location';
  const beforeDash = raw.split(' - ')[0]?.trim();
  return beforeDash || raw;
}

/** Upsert venue rows into Bookings lookup maps (labels / details / maps links). */
function mergeLocationRowsIntoMaps(rows: any[], into: SessionLocationMaps = { labels: {}, details: {}, mapsUrls: {} }): SessionLocationMaps {
  for (const row of rows) {
    if (!row?.id) continue;
    const eircode = String(row.eircode_postcode || row.postcode || '').trim();
    const addressParts = [
      row.address_line_1 || row.address,
      row.city,
      row.county_state || row.county,
      eircode,
      row.country,
    ]
      .map((v: unknown) => String(v || '').trim())
      .filter(Boolean);
    const addressLabel = addressParts.join(', ');
    into.labels[row.id] = locationLabelFromRow(row);
    into.details[row.id] = {
      id: row.id,
      name: String(row.name || '').trim() || undefined,
      addressLine1: String(row.address_line_1 || row.address || '').trim(),
      addressLine2: String(row.address_line_2 || '').trim(),
      addressLine3: String(row.address_line_3 || '').trim(),
      city: String(row.city || '').trim(),
      countyState: String(row.county_state || row.county || '').trim(),
      eircodePostcode: eircode,
      country: String(row.country || '').trim() || 'Ireland',
      locationContactName: String(row.location_contact_name || row.contact_name || '').trim(),
      locationContactEmail: row.location_contact_email ?? row.contact_email ?? null,
      locationContactPhone: String(row.location_contact_phone || row.contact_phone || '').trim(),
      locationNotes: String(row.location_notes || row.notes || '').trim(),
      postcode: eircode,
      notes: String(row.location_notes || row.notes || '').trim(),
      address: String(row.address || row.address_line_1 || '').trim(),
    };
    const mapsQuery = eircode ? `${eircode}, Ireland` : addressLabel;
    if (mapsQuery) {
      into.mapsUrls[row.id] = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;
    }
  }
  return into;
}

async function fetchLocationsByIds(ids: string[]): Promise<any[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];
  const rows: any[] = [];
  const CHUNK = 80;
  for (let i = 0; i < unique.length; i += CHUNK) {
    const slice = unique.slice(i, i + CHUNK);
    const { data, error } = await supabase.from('locations').select('*').in('id', slice);
    if (error) {
      console.warn('Session location resolve failed:', error);
      continue;
    }
    rows.push(...((data || []) as any[]));
  }
  return rows;
}

function isSafePassCourse(courseName: string | undefined | null): boolean {
  const normalized = String(courseName || '').toLowerCase();
  return normalized.includes('safe pass') || normalized.includes('safepass');
}

function sortDateBlocks(blocks: CourseSessionDateBlock[]): CourseSessionDateBlock[] {
  return [...blocks].sort((a, b) => a.date.localeCompare(b.date));
}

function mergeSessionPayments(
  prev: Record<string, AttendeePaymentLedgerRow[]>,
  sessionAttendeeIds: string[],
  fresh: Record<string, AttendeePaymentLedgerRow[]>
): Record<string, AttendeePaymentLedgerRow[]> {
  const next = { ...prev };
  for (const id of sessionAttendeeIds) {
    next[id] = fresh[id] ?? [];
  }
  return next;
}

/** Valid ISO dates only; first occurrence wins per calendar day */
function normalizeDateBlocks(blocks: CourseSessionDateBlock[]): CourseSessionDateBlock[] {
  const seen = new Set<string>();
  const out: CourseSessionDateBlock[] = [];
  for (const b of sortDateBlocks(blocks)) {
    if (!b.date || !/^\d{4}-\d{2}-\d{2}/.test(b.date)) continue;
    if (seen.has(b.date)) continue;
    seen.add(b.date);
    out.push({
      date: b.date,
      startTime: b.startTime ?? null,
      endTime: b.endTime ?? null,
    });
  }
  return out;
}

function formatDateBlocksUk(blocks: { date?: string }[] | undefined): string {
  if (!blocks?.length) return 'No dates set';
  const norm = normalizeDateBlocks((blocks || []) as CourseSessionDateBlock[]);
  return norm.map((b) => formatDateUk(b.date)).join(' � ');
}

/** Client notes from attendee comms use "| Ref: TTxxxxx"; same course+dates on two sessions must not cross-match. */
function communicationNoteBelongsToSession(
  noteLower: string,
  sessionRefLower: string,
  sessionCtxLower: string,
): boolean {
  const pipeRefs = [...noteLower.matchAll(/\|\s*ref:\s*(tt\d{5})\b/g)].map((m) => m[1]);
  if (pipeRefs.length > 0) {
    return pipeRefs.includes(sessionRefLower);
  }
  if (noteLower.includes(`ref: ${sessionRefLower}`)) {
    return true;
  }
  return noteLower.includes(sessionCtxLower);
}

function timeInputValue(t: string | null | undefined): string {
  if (!t) return '';
  const s = String(t);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function dateBlocksSignature(blocks: CourseSessionDateBlock[]): string {
  return JSON.stringify(
    normalizeDateBlocks(blocks).map((b) => ({
      date: b.date,
      startTime: b.startTime ?? null,
      endTime: b.endTime ?? null,
    }))
  );
}

const POST_SESSION_META_PREFIX = '[POST_SESSION_META]';

type PostSessionChecklist = {
  trainerSubmissionReturned: boolean;
  cardsReceivedFromTrainer: boolean;
  scansArchived: boolean;
  batchReadyToSend: boolean;
};
type PostSessionMeta = {
  checklist: PostSessionChecklist;
  createdByName?: string;
  createdById?: string;
};

const DEFAULT_POST_SESSION_CHECKLIST: PostSessionChecklist = {
  trainerSubmissionReturned: false,
  cardsReceivedFromTrainer: false,
  scansArchived: false,
  batchReadyToSend: false
};

function parsePostSessionMeta(sessionOtherInfo: string | null | undefined): PostSessionMeta {
  const text = String(sessionOtherInfo || '');
  const markerIdx = text.indexOf(POST_SESSION_META_PREFIX);
  if (markerIdx < 0) return { checklist: { ...DEFAULT_POST_SESSION_CHECKLIST } };
  const rawJson = text.slice(markerIdx + POST_SESSION_META_PREFIX.length).trim();
  if (!rawJson) return { checklist: { ...DEFAULT_POST_SESSION_CHECKLIST } };
  try {
    const parsed = JSON.parse(rawJson) as Partial<PostSessionChecklist> & { checklist?: Partial<PostSessionChecklist>; createdByName?: string; createdById?: string };
    const src = parsed.checklist || parsed;
    return {
      checklist: {
        trainerSubmissionReturned: !!src.trainerSubmissionReturned,
        cardsReceivedFromTrainer: !!src.cardsReceivedFromTrainer,
        scansArchived: !!src.scansArchived,
        batchReadyToSend: !!src.batchReadyToSend
      },
      createdByName: typeof parsed.createdByName === 'string' ? parsed.createdByName : undefined,
      createdById: typeof parsed.createdById === 'string' ? parsed.createdById : undefined
    };
  } catch {
    return { checklist: { ...DEFAULT_POST_SESSION_CHECKLIST } };
  }
}

function composeSessionOtherInfoWithMeta(
  baseText: string | null | undefined,
  checklist: PostSessionChecklist,
  creator?: { createdByName?: string; createdById?: string }
): string | null {
  const cleanBase = String(baseText || '')
    .split(POST_SESSION_META_PREFIX)[0]
    .trim();
  const suffix = `${POST_SESSION_META_PREFIX}${JSON.stringify({
    checklist,
    createdByName: creator?.createdByName || undefined,
    createdById: creator?.createdById || undefined
  })}`;
  return `${cleanBase}\n${suffix}`.trim() || null;
}

function parseBookedByFromAttendeeNotes(attendeeNotes: string | null | undefined): string {
  const text = String(attendeeNotes || '');
  const match = text.match(/(?:^|\n)booked by:\s*(.+)$/im);
  return match?.[1]?.trim() || '';
}

function resolveBookedByLabel(attendee: {
  bookedByName?: string | null;
  attendeeNotes?: string | null;
}): string {
  return resolveExternalBookedByLabel(attendee) || parseBookedByFromAttendeeNotes(attendee.attendeeNotes);
}

function parseWaitingListAddedDate(attendeeNotes: string | null | undefined): string {
  const match = String(attendeeNotes || '').match(/waiting list\s*\(([^)]+)\)/i);
  return match?.[1]?.trim() || '';
}

/** Notes shown in UI ? excludes lines already rendered as Booked by / Waiting list. */
function attendeeNotesForDisplay(attendeeNotes: string | null | undefined): string[] {
  return String(attendeeNotes || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^booked by:/i.test(line) && !/^waiting list\s*\(/i.test(line));
}

function formatHistoryAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

function formatHistoryActionLabel(action: string): string {
  const labels: Record<string, string> = {
    attendee_waitlist: 'Waiting list',
    attendee_waitlist_promoted: 'Promoted from waitlist',
    attendee_no_show: 'No-show',
    attendee_attended: 'Attended',
    attendee_removed: 'Removed',
    attendee_restored: 'Restored',
    attendee_deleted: 'Deleted',
    attendee_added: 'Added',
    attendee_status_changed: 'Status change',
    payment_recorded: 'Payment',
    payment_updated: 'Payment updated',
  };
  if (labels[action]) return labels[action];
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function historyCategoryStyles(category: SessionHistoryEvent['category']): string {
  switch (category) {
    case 'session':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'attendee':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'payment':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    case 'expenses':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    default:
      return 'bg-violet-50 text-violet-800 border-violet-200';
  }
}

const HISTORY_HIDDEN_DETAIL_KEYS = new Set([
  'attendeeid',
  'attendee_id',
  'individualcustomerid',
  'individual_customer_id',
  'companycustomerid',
  'company_customer_id',
  'sponsoringcompanycustomerid',
  'sponsoring_company_customer_id',
  'payercustomerid',
  'payer_customer_id',
  'createdbyid',
  'created_by_id',
  'paymentid',
  'payment_id',
  'recordedbyuserid',
  'recorded_by_user_id',
  'sessionid',
  'session_id',
  'actoruserid',
  'actor_user_id'
]);

function isHistoryDetailHiddenKey(key: string): boolean {
  const normalized = key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
  return HISTORY_HIDDEN_DETAIL_KEYS.has(normalized) || normalized.endsWith('_id');
}

function visibleHistoryDetailEntries(
  details: SessionHistoryEvent['details']
): Array<[string, string | number | boolean]> {
  return Object.entries(details).filter(
    ([key, value]) =>
      value !== null &&
      value !== '' &&
      value !== undefined &&
      !isHistoryDetailHiddenKey(key)
  ) as Array<[string, string | number | boolean]>;
}

/** Distinct valid calendar days in the session */
function sessionDayCount(blocks: { date?: string }[] | undefined): number {
  return normalizeDateBlocks((blocks || []) as CourseSessionDateBlock[]).length;
}

function calendarLabelsFromIso(iso: string | undefined): { monthLabel: string; dayLabel: string } {
  let monthLabel = '?';
  let dayLabel = '?';
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y!, m! - 1, d!);
    monthLabel = dt.toLocaleString('en', { month: 'short' });
    dayLabel = String(d!);
  }
  return { monthLabel, dayLabel };
}

function getFirstDateMs(s: { dateBlocks?: { date?: string }[] }): number | null {
  const norm = normalizeDateBlocks((s.dateBlocks || []) as CourseSessionDateBlock[]);
  const first = norm[0]?.date;
  if (!first || !/^\d{4}-\d{2}-\d{2}/.test(first)) return null;
  const [y, m, d] = first.split('-').map(Number);
  return new Date(y!, m! - 1, d!).getTime();
}

/** Modal pickers: upcoming first (soonest), then past (most recent). */
function compareSessionsByNextDate(a: { dateBlocks?: { date?: string }[] }, b: { dateBlocks?: { date?: string }[] }): number {
  const ta = getFirstDateMs(a);
  const tb = getFirstDateMs(b);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const now = startOfToday.getTime();
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  const aFuture = ta >= now;
  const bFuture = tb >= now;
  if (aFuture && !bFuture) return -1;
  if (!aFuture && bFuture) return 1;
  if (aFuture && bFuture) return ta - tb;
  return tb - ta;
}

type SessionTimeRange = 'upcoming' | 'past' | 'all';

function compareSessionsForTimeRange(
  a: { dateBlocks?: { date?: string }[]; id?: string; courseName?: string },
  b: { dateBlocks?: { date?: string }[]; id?: string; courseName?: string },
  _range: SessionTimeRange
): number {
  const ta = getFirstDateMs(a);
  const tb = getFirstDateMs(b);
  if (ta === null && tb === null) {
    return String(a.courseName || '').localeCompare(String(b.courseName || '')) ||
      String(a.id || '').localeCompare(String(b.id || ''));
  }
  if (ta === null) return 1;
  if (tb === null) return -1;
  const dateCmp = ta - tb;
  if (dateCmp !== 0) return dateCmp;
  const nameCmp = String(a.courseName || '').localeCompare(String(b.courseName || ''));
  if (nameCmp !== 0) return nameCmp;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

function sessionMatchesTimeRange(firstMs: number | null, range: SessionTimeRange, todayMs: number): boolean {
  if (range === 'all') return true;
  if (firstMs === null) return false;
  if (range === 'upcoming') return firstMs >= todayMs;
  return firstMs < todayMs;
}

type SessionListFilters = {
  query: string;
  courseName: string;
  locationId: string;
  language: string;
  deliveryMode: string;
  instructorId: string;
  interpreterId: string;
  providerId: string;
  status: '' | 'scheduled' | 'cancelled';
  dateFrom: string;
  dateTo: string;
  timeRange: SessionTimeRange;
};

const EMPTY_SESSION_FILTERS: SessionListFilters = {
  query: '',
  courseName: '',
  locationId: '',
  language: '',
  deliveryMode: '',
  instructorId: '',
  interpreterId: '',
  providerId: '',
  status: '',
  dateFrom: '',
  dateTo: '',
  timeRange: 'upcoming',
};

function isoDateToStartMs(iso: string): number | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!).getTime();
}

function sessionMatchesListFilters(
  s: CourseSession,
  filters: SessionListFilters,
  ctx: {
    locationById: Record<string, string>;
    instructorLabelById: Record<string, string>;
    interpreterLabelById: Record<string, string>;
    providerLabelById: Record<string, string>;
  }
): boolean {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayMs = startOfToday.getTime();
  const firstMs = getFirstDateMs(s);

  if (!sessionMatchesTimeRange(firstMs, filters.timeRange, todayMs)) {
    return false;
  }

  if (filters.courseName && s.courseName !== filters.courseName) return false;
  if (filters.locationId && (s.locationId || '') !== filters.locationId) return false;
  if (filters.language && (s.language || '') !== filters.language) return false;
  if (filters.deliveryMode && (s.deliveryMode || '') !== filters.deliveryMode) return false;
  if (filters.instructorId === '__unassigned__') {
    if (s.instructorId) return false;
  } else if (filters.instructorId && (s.instructorId || '') !== filters.instructorId) {
    return false;
  }
  if (filters.interpreterId === '__unassigned__') {
    if (s.interpreterId) return false;
  } else if (filters.interpreterId && (s.interpreterId || '') !== filters.interpreterId) {
    return false;
  }
  if (filters.providerId === '__none__') {
    if (s.providerId) return false;
  } else if (filters.providerId && (s.providerId || '') !== filters.providerId) {
    return false;
  }
  if (filters.status && (s.status || 'scheduled') !== filters.status) return false;

  const fromMs = filters.dateFrom ? isoDateToStartMs(filters.dateFrom) : null;
  if (fromMs !== null && (firstMs === null || firstMs < fromMs)) return false;
  const toMs = filters.dateTo ? isoDateToStartMs(filters.dateTo) : null;
  if (toMs !== null && (firstMs === null || firstMs > toMs)) return false;

  const q = filters.query.trim().toLowerCase();
  if (q) {
    const locName = (ctx.locationById[s.locationId || ''] || '').toLowerCase();
    const instructor = (ctx.instructorLabelById[s.instructorId || ''] || '').toLowerCase();
    const interpreter = (ctx.interpreterLabelById[s.interpreterId || ''] || '').toLowerCase();
    const provider = (ctx.providerLabelById[s.providerId || ''] || '').toLowerCase();
    const ref = sessionReferenceCode(s.id).toLowerCase();
    const haystack = [
      s.courseName,
      s.language,
      locName,
      instructor,
      interpreter,
      provider,
      s.notes,
      ref,
      s.deliveryMode,
    ].map((v) => String(v || '').toLowerCase());
    if (!haystack.some((v) => v.includes(q))) return false;
  }

  return true;
}

function countActiveSessionFilters(filters: SessionListFilters, opsMode: boolean, opsFilter: string): number {
  let n = 0;
  if (filters.query.trim()) n += 1;
  if (filters.courseName) n += 1;
  if (filters.locationId) n += 1;
  if (filters.language) n += 1;
  if (filters.deliveryMode) n += 1;
  if (filters.instructorId) n += 1;
  if (filters.interpreterId) n += 1;
  if (filters.providerId) n += 1;
  if (filters.status) n += 1;
  if (filters.dateFrom) n += 1;
  if (filters.dateTo) n += 1;
  if (opsMode && opsFilter !== 'all') n += 1;
  return n;
}

function BookingsCollapsibleSection({
  title,
  open,
  onToggle,
  badge,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
        aria-expanded={open}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
          )}
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{title}</span>
          {badge}
        </div>
      </button>
      {open ? <div className="border-t border-gray-100 p-3">{children}</div> : null}
    </div>
  );
}

function mapCustomerRow(c: any): Customer {
  return {
    ...c,
    customerType: c.customerType || 'company',
    courseFee: typeof c.courseFee === 'number' ? c.courseFee : Number(c.courseFee || 0)
  };
}

function collectLinkedCustomerIds(attendees: CourseSessionAttendee[]): string[] {
  const needed = new Set<string>();
  for (const a of attendees || []) {
    if (a.individualCustomerId) needed.add(a.individualCustomerId);
    if (a.companyCustomerId) needed.add(a.companyCustomerId);
    if (a.payerCustomerId) needed.add(a.payerCustomerId);
    if (a.sponsoringCompanyCustomerId) needed.add(a.sponsoringCompanyCustomerId);
  }
  return [...needed];
}

function mergeCustomerLists(existing: Customer[], extras: Customer[]): Customer[] {
  const byId = new Map(existing.map((c) => [c.id, c]));
  for (const c of extras) {
    if (c?.id) byId.set(c.id, mapCustomerRow(c as any));
  }
  return Array.from(byId.values());
}

function getAttendeeBillingCustomerId(attendee: CourseSessionAttendee): string | null {
  return attendee.individualCustomerId || attendee.companyCustomerId || null;
}

function getAttendeeCustomerId(attendee: CourseSessionAttendee): string | null {
  return attendee.individualCustomerId || null;
}

function getEffectiveFee(
  attendee: CourseSessionAttendee,
  c: Customer | undefined,
  sessionCourseName: string | undefined,
  courseDbFees: Record<string, number>,
  sessionLanguage?: string | null
): number {
  const e = attendee.enrollmentCourseFee;
  if (e != null && !Number.isNaN(Number(e))) return Number(e);
  const catalog = sessionCourseName
    ? getCourseCatalogDefaultFeeForSession(sessionCourseName, courseDbFees, sessionLanguage)
    : 0;
  if (catalog > 0) return catalog;
  return Number(c?.courseFee || 0);
}

function getAttendeePaidForRoster(
  attendee: CourseSessionAttendee,
  _c: Customer | undefined,
  _sessionCourseName: string | undefined,
  _courseDbFees: Record<string, number>,
  ledgerRows: Array<{ amount: number }>
): number {
  return rosterPaidEuro(attendee, ledgerRows);
}

function companyCohortAttendees(
  anchor: CourseSessionAttendee,
  sessionAttendees: CourseSessionAttendee[],
  companyCustomers: Customer[],
  getCustomer: (attendee: CourseSessionAttendee) => Customer | null | undefined
): CourseSessionAttendee[] {
  const groupKey = rosterGroupKeyForAttendee(anchor, getCustomer(anchor), companyCustomers);
  if (groupKey === 'Individuals / Unassigned' || groupKey === 'Reserved / Unassigned') {
    return [anchor];
  }
  return sessionAttendees.filter(
    (a) => rosterGroupKeyForAttendee(a, getCustomer(a), companyCustomers) === groupKey
  );
}

function cohortInvoicePaid(cohort: CourseSessionAttendee[]): boolean {
  return cohort.some((a) => a.invoicePaid);
}

function resolveAttendeeInvoice(
  attendee: CourseSessionAttendee,
  customer?: Customer | null,
  companyCustomers?: Customer[],
  cohortAttendees?: CourseSessionAttendee[]
): { number: string; paid: boolean } {
  let number = '';
  const enrollmentInv = String(attendee.invoiceNumber || '').trim();
  if (enrollmentInv) {
    number = enrollmentInv;
  } else {
    const crmInv = String(customer?.invoiceNumber || '').trim();
    if (crmInv) {
      number = crmInv;
    } else {
      const companyId = attendee.sponsoringCompanyCustomerId || attendee.companyCustomerId;
      if (companyId && companyCustomers?.length) {
        const company = companyCustomers.find((co) => co.id === companyId);
        number = String(company?.invoiceNumber || '').trim();
      }
    }
  }
  if (!number) return { number: '', paid: false };

  const cohort = cohortAttendees?.length ? cohortAttendees : [attendee];
  const sharedCompanyInvoice =
    cohort.length > 1 ||
    Boolean(attendee.companyCustomerId || attendee.sponsoringCompanyCustomerId || attendee.isVirtual);
  const paid = sharedCompanyInvoice ? cohortInvoicePaid(cohort) : Boolean(attendee.invoicePaid);
  return { number, paid };
}

function resolveGroupInvoice(
  groupLabel: string,
  attendees: CourseSessionAttendee[],
  companyCustomers: Customer[],
  getCustomer: (attendee: CourseSessionAttendee) => Customer | null | undefined
): { number: string; paid: boolean } {
  let number = '';
  for (const attendee of attendees) {
    const resolved = resolveAttendeeInvoice(attendee, getCustomer(attendee), companyCustomers, attendees);
    if (resolved.number) {
      number = resolved.number;
      break;
    }
  }
  if (!number) {
    const companyId = attendees
      .map((x) => x.companyCustomerId || x.sponsoringCompanyCustomerId)
      .find((id): id is string => Boolean(id));
    const company =
      (companyId ? companyCustomers.find((co) => co.id === companyId) : undefined) ||
      companyCustomers.find((co) => String(co.companyName || '').trim() === groupLabel.trim());
    number = String(company?.invoiceNumber || '').trim();
  }
  if (!number) return { number: '', paid: false };
  return { number, paid: cohortInvoicePaid(attendees) };
}

function isCompanyInvoiceSettlementNote(notes: string | null | undefined): boolean {
  const text = String(notes || '').trim();
  if (!text) return false;
  return /^Company invoice(\s+\([^)]+\))?\s+settlement$/i.test(text);
}

async function applyCompanyInvoiceToCohort(
  sessionAttendees: CourseSessionAttendee[],
  anchor: CourseSessionAttendee,
  companyCustomers: Customer[],
  getCustomer: (attendee: CourseSessionAttendee) => Customer | null | undefined,
  patch: { invoiceNumber?: string | null; invoicePaid?: boolean },
  settlement?: {
    bookingId: string;
    courseName?: string;
    courseLanguage?: string;
    courseDbFees: Record<string, number>;
    paymentTimelineByAttendee: Record<string, AttendeePaymentLedgerRow[]>;
    paymentMethod?: AttendeePaymentMethod;
    paymentDate?: string;
    paymentReference?: string | null;
    getAttendeeLabel: (attendee: CourseSessionAttendee) => string;
    refreshLedgers?: boolean;
  } | null
): Promise<{ seatsSettled: number; totalEuro: number }> {
  const cohort = companyCohortAttendees(anchor, sessionAttendees, companyCustomers, getCustomer);
  const cohortIds = cohort.map((a) => a.id);
  let seatsSettled = 0;
  let totalEuro = 0;

  if (patch.invoicePaid === true && settlement) {
    const groupLabel = rosterGroupKeyForAttendee(anchor, getCustomer(anchor), companyCustomers);
    const invLabel = String(
      patch.invoiceNumber?.trim() ||
        resolveGroupInvoice(groupLabel, cohort, companyCustomers, getCustomer).number ||
        ''
    ).trim();
    const paymentDate = settlement.paymentDate || new Date().toISOString().slice(0, 10);
    const method = settlement.paymentMethod || 'bank';
    const noteSuffix = invLabel ? ` (${invLabel})` : '';
    const paymentReference = String(settlement.paymentReference || '').trim();
    const payerNotes = paymentReference || `Company invoice${noteSuffix} settlement`;

    for (const attendee of cohort) {
      if (!isAttendeeCountedInSessionFinancials(attendee)) continue;
      const customer = getCustomer(attendee);
      const fee = getEffectiveFee(
        attendee,
        customer,
        settlement.courseName,
        settlement.courseDbFees,
        settlement.courseLanguage
      );
      const ledger = settlement.refreshLedgers
        ? await courseSessionsService.listAttendeePayments(attendee.id)
        : settlement.paymentTimelineByAttendee[attendee.id] || [];
      const paid = getAttendeePaidForRoster(
        attendee,
        customer,
        settlement.courseName,
        settlement.courseDbFees,
        ledger
      );
      const balance = attendeeBalanceEuro(fee, paid);
      if (balance <= 0.005) continue;

      await courseSessionsService.addAttendeePayment({
        bookingId: settlement.bookingId,
        attendeeId: attendee.id,
        amount: balance,
        method,
        paymentDate,
        stepType: 'other',
        payerNotes,
        attendeeLabel: settlement.getAttendeeLabel(attendee),
      });
      seatsSettled += 1;
      totalEuro += balance;
    }

    if (totalEuro > 0) {
      await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: settlement.bookingId });
    }
    await courseSessionsService.updateAttendeesInvoice(cohortIds, {
      invoicePaid: true,
      ...(patch.invoiceNumber !== undefined ? { invoiceNumber: patch.invoiceNumber } : {}),
    });
    return { seatsSettled, totalEuro };
  }

  if (patch.invoicePaid === false) {
    const bookingId = settlement?.bookingId || anchor.bookingId || cohort[0]?.bookingId || '';
    let paymentsRemoved = 0;
    let totalRemoved = 0;

    for (const attendee of cohort) {
      const payments = await courseSessionsService.listAttendeePayments(attendee.id);
      for (const payment of payments) {
        if (!isCompanyInvoiceSettlementNote(payment.payerNotes)) continue;
        await courseSessionsService.deleteAttendeePayment(payment.id, {
          bookingId: bookingId || payment.bookingId,
          attendeeLabel: settlement?.getAttendeeLabel
            ? settlement.getAttendeeLabel(attendee)
            : null,
        });
        paymentsRemoved += 1;
        totalRemoved += Number(payment.amount || 0);
      }
    }

    if (bookingId && totalRemoved > 0) {
      await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId });
    }

    const dbPatch: { invoiceNumber?: string | null; invoicePaid: boolean } = { invoicePaid: false };
    if (patch.invoiceNumber !== undefined) dbPatch.invoiceNumber = patch.invoiceNumber;
    await courseSessionsService.updateAttendeesInvoice(cohortIds, dbPatch);
    return { seatsSettled: paymentsRemoved, totalEuro: totalRemoved };
  }

  const dbPatch: { invoiceNumber?: string | null; invoicePaid?: boolean } = {};
  if (patch.invoiceNumber !== undefined) dbPatch.invoiceNumber = patch.invoiceNumber;
  if (Object.keys(dbPatch).length > 0) {
    await courseSessionsService.updateAttendeesInvoice(cohortIds, dbPatch);
  }
  return { seatsSettled, totalEuro };
}

function invoiceSettlementSuccessSuffix(result: { seatsSettled: number; totalEuro: number }): string {
  if (result.seatsSettled <= 0) return '';
  return ` Recorded ?${result.totalEuro.toFixed(2)} paid across ${result.seatsSettled} seat${
    result.seatsSettled === 1 ? '' : 's'
  }.`;
}

function invoiceUnsettlementSuccessSuffix(result: { seatsSettled: number; totalEuro: number }): string {
  if (result.seatsSettled <= 0) return '';
  return ` Removed ?${result.totalEuro.toFixed(2)} from ${result.seatsSettled} seat${
    result.seatsSettled === 1 ? '' : 's'
  }.`;
}

function cohortSeatsOutstandingEuro(
  attendees: CourseSessionAttendee[],
  courseName: string | undefined,
  courseLanguage: string | undefined,
  courseDbFees: Record<string, number>,
  paymentTimelineByAttendee: Record<string, AttendeePaymentLedgerRow[]>,
  getCustomer: (attendee: CourseSessionAttendee) => Customer | null | undefined
): number {
  let total = 0;
  for (const attendee of attendees) {
    if (!isAttendeeCountedInSessionFinancials(attendee)) continue;
    const customer = getCustomer(attendee);
    const fee = getEffectiveFee(attendee, customer, courseName, courseDbFees, courseLanguage);
    const paid = getAttendeePaidForRoster(
      attendee,
      customer,
      courseName,
      courseDbFees,
      paymentTimelineByAttendee[attendee.id] || []
    );
    total += attendeeBalanceEuro(fee, paid);
  }
  return total;
}

const fieldIn =
  'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500';
const fieldLabel = 'mb-1 block text-xs font-medium text-gray-600 dark:text-slate-400';
const createRowLabel = fieldLabel;
/** Native selects/inputs ? same as Clients-style grays + emerald focus */
const nativeControl =
  'rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const btnPrimary = 'rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700';
/** Plain text ? no spinner arrows (use instead of type="number" for money) */
const moneyIn =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500';
/** Date pill on session list rows ? elevated surface for contrast in dark mode */
const sessionDatePill =
  'flex h-14 w-14 flex-col items-center justify-center rounded-xl border-2 border-slate-200 bg-white shadow-sm dark:border-emerald-500/70 dark:bg-slate-600 dark:shadow-lg dark:shadow-black/40';
const sessionDatePillMonth = 'text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-emerald-200';
const sessionDatePillDay = 'text-2xl font-bold leading-none text-slate-900 dark:text-white';

/** A few calm course colours so neighbouring sessions are not one gray slab. */
function sessionCourseAccent(courseName: string | null | undefined): { bar: string; name: string } {
  const n = String(courseName || '').toLowerCase();
  if (n.includes('safe pass') || n.includes('safepass')) {
    return { bar: 'bg-amber-500', name: 'text-amber-950 dark:text-amber-100' };
  }
  if (n.includes('manual handling')) {
    return { bar: 'bg-sky-600', name: 'text-sky-950 dark:text-sky-100' };
  }
  if (n.includes('cpc') || n.includes('driver')) {
    return { bar: 'bg-violet-600', name: 'text-violet-950 dark:text-violet-100' };
  }
  return { bar: 'bg-emerald-600', name: 'text-slate-950 dark:text-slate-50' };
}

type AddPayKind = 'unpaid' | 'prepaid' | 'part' | 'full' | 'keep';
type PaymentMethod = 'stripe' | 'revolut' | 'sumup' | 'paypal' | 'bank' | 'cash';
type CompanyBookingParticipantDraft = {
  id: string;
  rawInput: string;
  firstName: string;
  surname: string;
  emailAddress: string;
  contactPhoneNumber: string;
  preferredLanguage: string;
  safePassProcessNumber: string;
  matchCustomerId: string | null;
  action: 'match_existing' | 'create_new' | 'reserve_seat';
  parseError: string | null;
};

function initialsFromLabel(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return `${p[0][0]!}${p[1][0]!}`.toUpperCase();
}

function formatFileSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return '?';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function isIndividualCustomerRecord(customer: Customer): boolean {
  if (customer.customerType === 'individual') return true;
  const hasName = `${customer.firstName || ''} ${customer.surname || ''}`.trim().length > 0;
  return hasName;
}

function splitParticipantName(fullName: string): { firstName: string; surname: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', surname: '' };
  if (parts.length === 1) return normalizeParticipantNameParts(parts[0], '');
  return normalizeParticipantNameParts(parts[0], parts.slice(1).join(' '));
}

function participantLinesFromPaste(input: string): string[] {
  const trimmed = String(input || '').trim();
  if (!trimmed) return [];
  const byNewline = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (byNewline.length > 1) return byNewline;
  const single = byNewline[0] || '';
  const atCount = (single.match(/@/g) || []).length;
  if (atCount > 1 && single.includes(';')) {
    return single
      .split(/\s*;\s*/)
      .map((l) => l.trim())
      .filter(Boolean);
  }
  return byNewline;
}

function paymentMethodLabel(method: string): string {
  return PAYMENT_METHOD_OPTIONS.find((o) => o.value === method)?.label || method;
}

function paymentStepTypeLabel(stepType: string): string {
  switch (stepType) {
    case 'deposit':
      return 'Deposit';
    case 'on_day':
      return 'On day';
    case 'other':
      return 'Other';
    default:
      return stepType;
  }
}

function participantDraftKey(
  row: Pick<CompanyBookingParticipantDraft, 'firstName' | 'surname' | 'emailAddress' | 'contactPhoneNumber' | 'rawInput'>
): string {
  const fromFields = `${row.firstName.trim().toLowerCase()}|${row.surname.trim().toLowerCase()}|${row.emailAddress.trim().toLowerCase()}|${phoneDigitsOnly(row.contactPhoneNumber)}`;
  if (fromFields !== '|||') return fromFields;
  return row.rawInput.trim().toLowerCase();
}

function participantRowParseError(row: Pick<CompanyBookingParticipantDraft, 'firstName' | 'surname' | 'emailAddress' | 'contactPhoneNumber' | 'action'>): string | null {
  const hasFullName = !!row.firstName.trim() && !!row.surname.trim();
  if (!hasFullName && row.action !== 'reserve_seat') {
    return 'Needs at least first name and surname to create a participant.';
  }
  if (row.action === 'create_new' && !row.emailAddress.trim() && !row.contactPhoneNumber.trim()) {
    return 'Enter an email or mobile number.';
  }
  return null;
}

function parseParticipantLines(input: string, preferredLanguage: string): CompanyBookingParticipantDraft[] {
  return participantLinesFromPaste(input)
    .map((line) => {
      const tokens = line.split(/[\t,]+/).map((token) => token.trim()).filter(Boolean);
      const nameToken = tokens[0] || '';
      const { firstName, surname } = splitParticipantName(nameToken);
      let emailAddress = '';
      let contactPhoneNumber = '';
      for (const token of tokens.slice(1)) {
        if (!emailAddress && token.includes('@')) {
          emailAddress = token;
          continue;
        }
        if (!contactPhoneNumber) contactPhoneNumber = token;
      }
      return {
        id: crypto.randomUUID(),
        rawInput: line,
        firstName,
        surname,
        emailAddress,
        contactPhoneNumber,
        preferredLanguage,
        safePassProcessNumber: '',
        matchCustomerId: null,
        action: firstName && surname ? 'create_new' : 'reserve_seat',
        parseError: participantRowParseError({
          firstName,
          surname,
          emailAddress,
          contactPhoneNumber,
          action: firstName && surname ? 'create_new' : 'reserve_seat',
        }),
      };
    });
}

function phoneDigitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

function companySeatBadgeLabel(attendee: CourseSessionAttendee, cohort: CourseSessionAttendee[]): string | null {
  if (cohort.length === 0) return null;
  const sorted = [...cohort].sort((a, b) =>
    String(a.reservedCreatedAt || a.createdAt || '').localeCompare(String(b.reservedCreatedAt || b.createdAt || ''))
  );
  const idx = sorted.findIndex((x) => x.id === attendee.id);
  if (idx < 0) return null;
  return `${idx + 1}/${sorted.length}`;
}

function linkedCompanyDisplayForSeat(
  attendee: CourseSessionAttendee,
  customer: Customer | undefined,
  companyCustomers: Customer[]
): string {
  if (attendee.companyCustomerId) {
    const co = companyCustomers.find((x) => x.id === attendee.companyCustomerId);
    if (co?.companyName?.trim()) return co.companyName.trim();
  }
  if (attendee.sponsoringCompanyCustomerId) {
    const co = companyCustomers.find((x) => x.id === attendee.sponsoringCompanyCustomerId);
    if (co?.companyName?.trim()) return co.companyName.trim();
  }
  const billing = String(customer?.companyName || '').trim();
  const fullName = `${customer?.firstName || ''} ${customer?.surname || ''}`.trim();
  if (billing && fullName && billing.toLowerCase() !== fullName.toLowerCase()) return billing;
  return '';
}

/** Company booking context for an individual seat (null = solo / not company-linked). */
function resolveCompanyBookingContext(
  attendee: CourseSessionAttendee,
  customer: Customer | undefined,
  companyCustomers: Customer[]
): { companyId: string | null; companyName: string } | null {
  if (attendee.isVirtual) return null;
  const inferredCompany =
    (attendee.companyCustomerId
      ? companyCustomers.find((co) => co.id === attendee.companyCustomerId)
      : undefined) ||
    (attendee.sponsoringCompanyCustomerId
      ? companyCustomers.find((co) => co.id === attendee.sponsoringCompanyCustomerId)
      : undefined) ||
    companyCustomers.find(
      (co) =>
        String(co.companyName || '').trim().toLowerCase() ===
        String(customer?.companyName || '').trim().toLowerCase()
    );
  const linkedDisplay = linkedCompanyDisplayForSeat(attendee, customer, companyCustomers);
  const companyName = String(
    inferredCompany?.companyName || linkedDisplay || customer?.companyName || ''
  ).trim();
  const companyId =
    inferredCompany?.id || attendee.companyCustomerId || attendee.sponsoringCompanyCustomerId || null;
  if (!companyName || (!companyId && !linkedDisplay)) return null;
  return { companyId, companyName };
}

const EMPTY_ATTENDEES: CourseSessionAttendee[] = [];

type SessionMainTab = 'roster' | 'postSession' | 'logistics' | 'expenses' | 'interpreters' | 'history' | 'spreadsheet';

type SessionTabGroup = 'people' | 'wrapup' | 'setup' | 'other';

const SESSION_MAIN_TABS: Array<{
  id: SessionMainTab;
  label: string;
  hint: string;
  icon: LucideIcon;
  group: SessionTabGroup;
}> = [
  { id: 'roster', label: 'Roster', hint: 'Who is booked, payments, and session documents', icon: Users, group: 'people' },
  { id: 'spreadsheet', label: 'Spreadsheet', hint: 'Table view with CSV / Excel export', icon: Table2, group: 'people' },
  { id: 'postSession', label: 'Post-session', hint: 'After the course ? attendance, cards, trainer paperwork', icon: ClipboardCheck, group: 'wrapup' },
  { id: 'logistics', label: 'Logistics', hint: 'Dates, venue, instructor, and capacity', icon: MapPin, group: 'setup' },
  { id: 'interpreters', label: 'Interpreters', hint: 'Briefing notes and on-site access for interpreters', icon: Languages, group: 'setup' },
  { id: 'expenses', label: 'Expenses', hint: 'Tutor, venue, cards, and referral costs', icon: Wallet, group: 'setup' },
  { id: 'history', label: 'History', hint: 'Who changed what ? full audit trail', icon: History, group: 'other' },
];

const SESSION_TAB_GROUP_LABELS: Record<Exclude<SessionTabGroup, 'other'>, string> = {
  people: 'People',
  wrapup: 'After course',
  setup: 'Session setup',
};

function LabeledActionButton({
  icon: Icon,
  label,
  onClick,
  className = 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

type OverflowMenuItem = {
  icon?: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
};

function ActionsOverflowMenu({
  items,
  align = 'right',
  label,
  menuTitle,
  buttonClassName = 'inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
}: {
  items: OverflowMenuItem[];
  align?: 'left' | 'right';
  /** Visible button text (e.g. "Manage") ? omit for icon-only kebab menu */
  label?: string;
  menuTitle?: string;
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number } | null>(null);
  const visibleItems = items.filter(Boolean);

  const updateMenuPosition = useCallback(() => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth ?? 184;
    const menuHeight = menu?.offsetHeight ?? visibleItems.length * 36 + 8;
    const left =
      align === 'right'
        ? Math.max(8, rect.right - menuWidth)
        : Math.min(window.innerWidth - menuWidth - 8, rect.left);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    let top: number;
    if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
      top = Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
    } else {
      top = Math.max(8, rect.top - menuHeight - 6);
    }
    setMenuStyle({ top, left });
  }, [align, visibleItems.length]);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    requestAnimationFrame(() => updateMenuPosition());
    const onDoc = (e: Event) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => updateMenuPosition();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  if (visibleItems.length === 0) return null;

  const resolvedTitle = menuTitle || label || 'More actions';

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label={resolvedTitle}
        aria-expanded={open}
        title={resolvedTitle}
        onClick={(e) => {
          e.stopPropagation();
          if (!open && anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const menuWidth = 184;
            const estHeight = visibleItems.length * 36 + 8;
            const left =
              align === 'right'
                ? Math.max(8, rect.right - menuWidth)
                : Math.min(window.innerWidth - menuWidth - 8, rect.left);
            const spaceBelow = window.innerHeight - rect.bottom - 8;
            const spaceAbove = rect.top - 8;
            const top =
              spaceBelow >= estHeight || spaceBelow >= spaceAbove
                ? Math.min(window.innerHeight - estHeight - 8, rect.bottom + 6)
                : Math.max(8, rect.top - estHeight - 6);
            setMenuStyle({ top, left });
          }
          setOpen((v) => !v);
        }}
        className={
          label
            ? 'inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            : buttonClassName
        }
      >
        {label ? <span>{label}</span> : null}
        <MoreVertical className={label ? 'h-3.5 w-3.5 shrink-0 opacity-70' : 'h-4 w-4'} aria-hidden />
      </button>
      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: menuStyle.top, left: menuStyle.left, zIndex: 9999 }}
              className="max-h-[min(20rem,calc(100vh-2rem))] min-w-[11.5rem] overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-2xl dark:border-slate-600 dark:bg-slate-800"
            >
              {visibleItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  disabled={item.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    item.onClick();
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-700 ${
                    item.destructive ? 'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/50' : 'text-gray-700 dark:text-slate-200'
                  }`}
                >
                  {item.icon ? <item.icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden /> : null}
                  {item.label}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function PaymentStatusPill({ status }: { status: 'paid' | 'half_paid' | 'unpaid' }) {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-100 dark:ring-1 dark:ring-emerald-500/60',
    half_paid: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-100 dark:ring-1 dark:ring-amber-500/60',
    unpaid: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-100 dark:ring-1 dark:ring-red-500/60',
  };
  const labels = { paid: 'Paid', half_paid: 'Part paid', unpaid: 'Unpaid' };
  return (
    <span className={`shrink-0 rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}


function RosterEmailMark({
  status,
  title,
}: {
  status: 'sent' | 'failed' | 'not_sent';
  title: string;
}) {
  if (status === 'sent') {
    return (
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 dark:bg-emerald-950 dark:text-emerald-100 dark:ring-emerald-500/60"
        title={title}
        onClick={(e) => e.stopPropagation()}
        aria-label="Sent"
      >
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded bg-red-100 text-red-700 ring-1 ring-red-300 dark:bg-red-950 dark:text-red-100 dark:ring-red-500/60"
        title={title}
        onClick={(e) => e.stopPropagation()}
        aria-label="Failed"
      >
        <X className="h-3 w-3" strokeWidth={3} aria-hidden />
      </span>
    );
  }
  return (
    <span
      className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-100 text-slate-400 ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:ring-slate-500"
      title={title}
      onClick={(e) => e.stopPropagation()}
      aria-label="Not sent"
    >
      <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
    </span>
  );
}

function RosterGroupInvoiceLabel({
  number,
  invoiceFlagPaid,
  seatsOutstanding,
  onRecordPayments,
  onManageInvoice,
}: {
  number: string;
  invoiceFlagPaid: boolean;
  seatsOutstanding: number;
  onRecordPayments?: () => void;
  onManageInvoice?: () => void;
}) {
  const inv = String(number || '').trim();
  if (!inv && !onManageInvoice) return null;
  const due = Math.max(0, seatsOutstanding);
  const hasSeatDue = due > 0.005;
  const fullySettled = invoiceFlagPaid && !hasSeatDue;
  const paidFlagSeatsStillDue = invoiceFlagPaid && hasSeatDue;

  let className: string;
  let label: string;
  let title: string;
  if (!inv) {
    className = '';
    label = '';
    title = '';
  } else if (fullySettled) {
    className =
      'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-300 dark:bg-emerald-950 dark:text-emerald-100 dark:ring-emerald-600';
    label = `INV � ${inv} � Paid`;
    title = 'Company invoice paid ? all seats settled';
  } else if (paidFlagSeatsStillDue) {
    className =
      'bg-orange-50 text-orange-900 ring-1 ring-inset ring-orange-300 dark:bg-orange-950 dark:text-orange-100 dark:ring-orange-600';
    label = `INV � ${inv} � Paid � ?${due.toFixed(0)} due on seats`;
    title = 'Invoice marked paid but seat balances were not recorded yet';
  } else {
    className =
      'bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-300 dark:bg-amber-950 dark:text-amber-100 dark:ring-amber-600';
    label = `INV � ${inv} � Unpaid`;
    title = 'Company invoice unpaid';
  }

  return (
    <span className="inline-flex shrink-0 flex-wrap items-center justify-end gap-1.5">
      {inv ? (
        <span
          className={`inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide ${className}`}
          title={title}
        >
          {label}
        </span>
      ) : null}
      {onManageInvoice ? (
        <button
          type="button"
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          onClick={onManageInvoice}
          title="Set invoice number and paid status for every seat in this company group"
        >
          Company invoice
        </button>
      ) : null}
      {paidFlagSeatsStillDue && onRecordPayments ? (
        <button
          type="button"
          className="rounded border border-orange-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-orange-800 hover:bg-orange-50 dark:border-orange-600 dark:bg-slate-800 dark:text-orange-100 dark:hover:bg-orange-950/40"
          onClick={onRecordPayments}
        >
          Record seat payments
        </button>
      ) : null}
    </span>
  );
}

interface CourseSessionsPanelProps {
  currentCustomer?: Customer | null;
  mode?: 'manage' | 'assign-only';
  initialExpandedSessionId?: string | null;
  initialSessionTab?: SessionMainTab | null;
  initialOpsMode?: boolean;
  initialOpsFilter?: 'all' | 'unpaid' | 'compliance' | 'docs' | 'post' | 'noshow' | 'waitlist';
  /** Open CRM client profile (Bookings route passes App handler) */
  onNavigateToClient?: (customerId: string) => void;
  onViewChange?: (
    view: 'process-numbers',
    context?: { activeTab?: 'process-numbers' | 'safe-pass-cards'; sessionId?: string }
  ) => void;
}

export function CourseSessionsPanel({
  currentCustomer = null,
  mode = 'manage',
  initialExpandedSessionId = null,
  initialSessionTab = null,
  initialOpsMode = false,
  initialOpsFilter = 'all',
  onNavigateToClient,
  onViewChange
}: CourseSessionsPanelProps) {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const { isOwner } = useOwner();
  const { isSuperAdmin } = useSuperAdmin();
  const canDownloadFinancialSummary = isAdmin || isOwner || isSuperAdmin;
  const actorName =
    user?.user_metadata?.first_name && user?.user_metadata?.last_name
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`.trim()
      : user?.email?.split('@')[0] || 'Staff';
  /** Active course names from `courses` table (not hardcoded COURSE_CONFIG). */
  const [coursePickerHits, setCoursePickerHits] = useState<string[]>([]);
  const [coursePickerSearching, setCoursePickerSearching] = useState(false);
  const [coursePickerChosen, setCoursePickerChosen] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  /** courses.base_price by course name ? overrides static COURSE_CONFIG defaultPrice */
  const [courseDbFees, setCourseDbFees] = useState<Record<string, number>>({});
  const [attendeesBySession, setAttendeesBySession] = useState<Record<string, CourseSessionAttendee[]>>({});
  const [bookedCountBySessionId, setBookedCountBySessionId] = useState<Record<string, number>>({});
  const [rosterLoadingBySession, setRosterLoadingBySession] = useState<Record<string, boolean>>({});
  const [rosterHydratedBySession, setRosterHydratedBySession] = useState<Record<string, boolean>>({});
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [crmSearchHits, setCrmSearchHits] = useState<Customer[]>([]);
  const [instructors, setInstructors] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [locationById, setLocationById] = useState<Record<string, string>>({});
  const [locationDetailsById, setLocationDetailsById] = useState<Record<string, any>>({});
  const [locationMapsUrl, setLocationMapsUrl] = useState<Record<string, string>>({});
  const [locationOptions, setLocationOptions] = useState<Array<{ id: string; label: string; shortLabel: string }>>([]);
  const [preferredDefaultLocationId, setPreferredDefaultLocationId] = useState<string>('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(initialExpandedSessionId);
  const [searchBySession, setSearchBySession] = useState<Record<string, string>>({});
  const [newSession, setNewSession] = useState({
    courseName: '',
    language: '',
    deliveryMode: 'in_class',
    capacity: 12,
    locationId: '',
    instructorId: '',
    interpreterId: '',
    providerId: '',
    firstDate: '',
    startTime: '',
    endTime: '',
  });
  const [extraSessionDays, setExtraSessionDays] = useState<Array<{ date: string; startTime: string; endTime: string }>>([]);
  const [sessionDateBlocksDraft, setSessionDateBlocksDraft] = useState<CourseSessionDateBlock[]>([]);
  const [sessionDraft, setSessionDraft] = useState<{
    capacity: number;
    locationId: string;
    instructorId: string;
    interpreterId: string;
    providerId: string;
    notes: string;
    deliveryMode: string;
    documentsPrepDone: boolean;
    certificatesPrepDone: boolean;
    sessionOtherInfo: string;
    truckType: string;
    truckMake: string;
    truckCapacity: string;
    truckAttachments: string;
    interpreterBriefing: string;
    arrivalInstructions: string;
    parkingAccess: string;
    siteHazardsPpe: string;
    onSiteContactOverrideName: string;
    onSiteContactOverridePhone: string;
    onSiteContactOverrideEmail: string;
    interpreterReminders: string;
  }>({
    capacity: 1,
    locationId: '',
    instructorId: '',
    interpreterId: '',
    providerId: '',
    notes: '',
    deliveryMode: 'in_class',
    documentsPrepDone: false,
    certificatesPrepDone: false,
    sessionOtherInfo: '',
    truckType: '',
    truckMake: '',
    truckCapacity: '',
    truckAttachments: '',
    interpreterBriefing: '',
    arrivalInstructions: '',
    parkingAccess: '',
    siteHazardsPpe: '',
    onSiteContactOverrideName: '',
    onSiteContactOverridePhone: '',
    onSiteContactOverrideEmail: '',
    interpreterReminders: ''
  });
  const [interpreterOptions, setInterpreterOptions] = useState<InterpreterRow[]>([]);
  const [providerOptions, setProviderOptions] = useState<Array<{ id: string; name: string }>>([]);
  /** Active rows from `course_languages` (Admin ? Languages); merged into new-session language dropdown. */
  const [globalCourseLanguageNames, setGlobalCourseLanguageNames] = useState<string[]>([]);
  const [newSessionPanelOpen, setNewSessionPanelOpen] = useState(false);
  const [sessionFiltersPanelOpen, setSessionFiltersPanelOpen] = useState(false);
  const bookingsStickyRef = useRef<HTMLDivElement>(null);
  const [bookingsStickyHeight, setBookingsStickyHeight] = useState(120);
  const [sessionsListLayout, setSessionsListLayout] = useState<'cards' | 'table'>('cards');
  const [sessionFilters, setSessionFilters] = useState<SessionListFilters>(EMPTY_SESSION_FILTERS);
  const [visibleSessionLimit, setVisibleSessionLimit] = useState(25);
  const [opsMode, setOpsMode] = useState(initialOpsMode);
  const [opsFilter, setOpsFilter] = useState<'all' | 'unpaid' | 'compliance' | 'docs' | 'post' | 'noshow' | 'waitlist'>(initialOpsFilter);
  const [selectedOpsSessionIds, setSelectedOpsSessionIds] = useState<Record<string, boolean>>({});
  const [sessionMainTab, setSessionMainTab] = useState<SessionMainTab>(initialSessionTab || 'roster');
  const [seatOpenedModal, setSeatOpenedModal] = useState<{
    sessionId: string;
    waitlistCount: number;
    freedLabel?: string;
  } | null>(null);
  const [sessionHistoryEvents, setSessionHistoryEvents] = useState<SessionHistoryEvent[]>([]);
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [sessionHistoryError, setSessionHistoryError] = useState<string | null>(null);
  const [postSessionChecklist, setPostSessionChecklist] = useState<PostSessionChecklist>({ ...DEFAULT_POST_SESSION_CHECKLIST });
  const [showHeaderExistingSearch, setShowHeaderExistingSearch] = useState(false);
  const [rosterSnapshotOpen, setRosterSnapshotOpen] = useState(false);
  const [isGeneratingSessionCerts, setIsGeneratingSessionCerts] = useState(false);
  const lastAutoExpenseSignatureRef = useRef<string>('');
  const lastAutoDetailsSignatureRef = useRef<string>('');
  const detailsDraftSignatureRef = useRef<string>('');
  const selectedSessionIdRef = useRef<string | null>(selectedSessionId);
  selectedSessionIdRef.current = selectedSessionId;
  const rosterHydratedRef = useRef<Set<string>>(new Set());
  const rosterInFlightRef = useRef<Map<string, Promise<void>>>(new Map());
  const [companyPickerQuery, setCompanyPickerQuery] = useState('');
  const [companyPickerHits, setCompanyPickerHits] = useState<Customer[]>([]);
  const [companyPickerSearching, setCompanyPickerSearching] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSavedAtLabel, setAutoSavedAtLabel] = useState('');
  const [addAttendeeModalSource, setAddAttendeeModalSource] = useState<InboundChannel | ''>('');
  const [addAttendeeModal, setAddAttendeeModal] = useState<{
    sessionId: string;
    customer: Customer;
    editingAttendee?: CourseSessionAttendee;
  } | null>(null);
  const [createCrmForSessionOpen, setCreateCrmForSessionOpen] = useState(false);
  const [createCrmForVirtualSeatOpen, setCreateCrmForVirtualSeatOpen] = useState(false);
  const [duplicateCreatePrompt, setDuplicateCreatePrompt] = useState<{
    draft: Customer;
    matches: Customer[];
    selectedMatchId: string;
    mode: 'session' | 'virtual';
  } | null>(null);
  const [companyBookingModal, setCompanyBookingModal] = useState<{
    sessionId: string;
    companyCustomerId: string;
    totalSeats: string;
    pastedParticipants: string;
    parsedRows: CompanyBookingParticipantDraft[];
    payKind: AddPayKind;
    amountStr: string;
    paymentMethod: PaymentMethod;
    enrollmentCourseFee: string;
    feeLocked: boolean;
    source: InboundChannel;
    invoiceNumber: string;
    invoicePaid: boolean;
    paymentReference: string;
    /** External booker (person/company who placed the booking). */
    bookedByName: string;
  } | null>(null);
  const [addAttendeeSaving, setAddAttendeeSaving] = useState(false);
  const addAttendeeSavingRef = useRef(false);
  const [companyBookingSaving, setCompanyBookingSaving] = useState(false);
  const companyBookingSavingRef = useRef(false);
  const [bulkVirtualFillSaving, setBulkVirtualFillSaving] = useState(false);
  const bulkVirtualFillSavingRef = useRef(false);
  const [quickRescheduleModal, setQuickRescheduleModal] = useState<{
    sessionId: string;
    courseName: string;
    dateBlocks: CourseSessionDateBlock[];
  } | null>(null);
  const [cancelSessionModal, setCancelSessionModal] = useState<{
    session: CourseSession;
    reason: string;
  } | null>(null);
  const [rescheduleAttendeeModal, setRescheduleAttendeeModal] = useState<{
    attendee: CourseSessionAttendee;
    targetSessionId: string;
  } | null>(null);
  const [reserveVirtualModal, setReserveVirtualModal] = useState<{
    countStr: string;
    companyCustomerId: string;
    enrollmentCourseFee: string;
    payKind: AddPayKind;
    amountStr: string;
    paymentMethod: PaymentMethod;
    source: InboundChannel | '';
  } | null>(null);
  const [virtualFeeLocked, setVirtualFeeLocked] = useState(false);
  const [sessionFeeLocked, setSessionFeeLocked] = useState(false);
  const [contactModal, setContactModal] = useState<{
    attendee: CourseSessionAttendee;
    customer: Customer | undefined;
    initialTab: 'email' | 'whatsapp';
    companyCohort?: CompanyCohortEmailContext;
  } | null>(null);
  const [addSessionFee, setAddSessionFee] = useState('');
  const [addPayKind, setAddPayKind] = useState<AddPayKind>('unpaid');
  const [addAmountStr, setAddAmountStr] = useState('');
  const [addPaymentMethod, setAddPaymentMethod] = useState<PaymentMethod>('cash');
  const [addPaymentStepType, setAddPaymentStepType] = useState<AttendeePaymentStepType>('other');
  const [addPaymentDateUk, setAddPaymentDateUk] = useState(formatDateUk(new Date().toISOString().slice(0, 10)));
  const [addPaymentNote, setAddPaymentNote] = useState('');
  const [addSafePassProcessNumber, setAddSafePassProcessNumber] = useState('');
  const [addBookedByName, setAddBookedByName] = useState('');
  const [addAssignedCompanyCustomerId, setAddAssignedCompanyCustomerId] = useState('');
  const [replaceVirtualModal, setReplaceVirtualModal] = useState<{ attendee: CourseSessionAttendee; query: string } | null>(null);
  const [bulkVirtualFillModal, setBulkVirtualFillModal] = useState<{
    groupLabel: string;
    attendeeIds: string[];
    pasted: string;
  } | null>(null);
  const [transferAttendeeModal, setTransferAttendeeModal] = useState<{
    attendee: CourseSessionAttendee;
    note: string;
  } | null>(null);
  const [paymentTimelineByAttendee, setPaymentTimelineByAttendee] = useState<Record<string, AttendeePaymentLedgerRow[]>>({});
  const [copyAttendeeModal, setCopyAttendeeModal] = useState<{
    attendee: CourseSessionAttendee;
    customer: Customer;
    targetSessionId: string;
  } | null>(null);
  const [addInvoiceNumber, setAddInvoiceNumber] = useState('');
  const [addInvoicePaid, setAddInvoicePaid] = useState(false);
  const [editPaymentModal, setEditPaymentModal] = useState<{
    paymentId: string;
    attendeeId: string;
    bookingId: string;
    amountStr: string;
    paymentDateUk: string;
    method: AttendeePaymentMethod;
    stepType: AttendeePaymentStepType;
    payerNotes: string;
  } | null>(null);
  const [companyGroupInvoiceModal, setCompanyGroupInvoiceModal] = useState<{
    sessionId: string;
    anchorAttendeeId: string;
    companyLabel: string;
    seatCount: number;
    invoiceNumber: string;
    invoicePaid: boolean;
    seatsOutstandingEuro: number;
    paymentMethod: AttendeePaymentMethod;
  } | null>(null);
  const [companyInvoicePaidConfirm, setCompanyInvoicePaidConfirm] = useState<{
    companyLabel: string;
    seatCount: number;
    sessionLabel: string;
    target: 'addAttendee' | 'editPayment' | 'companyBooking' | 'companyGroupInvoice';
    sessionId: string;
    anchorAttendeeId?: string;
    invoiceNumber?: string;
    seatsOutstandingEuro?: number;
  } | null>(null);
  const [invoiceSettlementMethod, setInvoiceSettlementMethod] = useState<AttendeePaymentMethod>('bank');
  const [invoiceSettlementDateUk, setInvoiceSettlementDateUk] = useState(() =>
    formatDateUk(new Date().toISOString().slice(0, 10))
  );
  const [invoiceSettlementReference, setInvoiceSettlementReference] = useState('');
  const [communicationPreviewByAttendee, setCommunicationPreviewByAttendee] = useState<Record<string, { channel: 'email' | 'whatsapp' | 'other'; text: string; createdAt: string }>>({});
  const [rosterEmailByAttendee, setRosterEmailByAttendee] = useState<Record<string, RosterEmailIndicators>>({});
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocumentAttachment[]>([]);
  const [sessionDocumentCounts, setSessionDocumentCounts] = useState<Record<string, number>>({});
  const sessionCardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const sessionDocCountsRequestedRef = useRef<Set<string>>(new Set());
  const resolvedLocationIdsRef = useRef<Set<string>>(new Set());
  const sessionCardObserverRef = useRef<IntersectionObserver | null>(null);
  const registerSessionCardRef = useCallback((sessionId: string, el: HTMLElement | null) => {
    const map = sessionCardRefs.current;
    const observer = sessionCardObserverRef.current;
    const existing = map.get(sessionId);
    if (existing && existing !== el) {
      observer?.unobserve(existing);
      map.delete(sessionId);
    }
    if (el) {
      map.set(sessionId, el);
      el.dataset.sessionId = sessionId;
      observer?.observe(el);
    }
  }, []);
  useEffect(() => {
    if (!companyInvoicePaidConfirm) return;
    setInvoiceSettlementMethod('bank');
    setInvoiceSettlementDateUk(formatDateUk(new Date().toISOString().slice(0, 10)));
    setInvoiceSettlementReference('');
  }, [companyInvoicePaidConfirm]);
  useEffect(() => {
    const el = bookingsStickyRef.current;
    if (!el) return;
    const syncHeight = () => setBookingsStickyHeight(el.offsetHeight);
    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [newSessionPanelOpen, sessionFiltersPanelOpen, opsMode, sessionFilters.timeRange]);
  const [sessionDocumentsLoading, setSessionDocumentsLoading] = useState(false);
  const [sessionDocumentUploading, setSessionDocumentUploading] = useState(false);
  const [sessionDocumentViewer, setSessionDocumentViewer] = useState<SessionDocumentAttachment | null>(null);
  const [sessionDocumentPreviewHtml, setSessionDocumentPreviewHtml] = useState('');
  const [sessionDocumentPreviewLoading, setSessionDocumentPreviewLoading] = useState(false);
  const [sessionDocumentPreviewError, setSessionDocumentPreviewError] = useState('');
  const [sessionDocumentsModal, setSessionDocumentsModal] = useState<{ sessionId: string; courseName: string } | null>(null);
  const uploadDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const [financialSnapshot, setFinancialSnapshot] = useState<{
    totalCollected: number;
    totalOutstanding: number;
    totalsByMethod: Record<string, number>;
    tutorExpense: number;
    interpreterExpense: number;
    venueExpense: number;
    numberCards: number;
    cardFee: number;
    referralFee1: number;
    referralFee2: number;
    referralFee3: number;
    otherExpenses: number;
  } | null>(null);
  const [expenseDraft, setExpenseDraft] = useState({
    tutorExpense: '',
    interpreterExpense: '',
    venueExpense: '',
    numberCards: '',
    cardFee: '',
    referralFee1: '',
    referralFee2: '',
    referralFee3: '',
    otherExpenses: ''
  });
  const [financialSummaryModal, setFinancialSummaryModal] = useState<FinancialSummaryData | null>(null);

  const customerById = useMemo(() => {
    const map: Record<string, Customer> = {};
    allCustomers.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [allCustomers]);
  const companyCustomers = useMemo(
    () => allCustomers.filter((c) => c.customerType === 'company' && String(c.companyName || '').trim().length > 0),
    [allCustomers]
  );
  const individualCustomers = useMemo(
    () => allCustomers.filter((c) => isIndividualCustomerRecord(c)),
    [allCustomers]
  );

  const sessionLanguageOptions = useMemo(() => {
    const set = new Set<string>();
    globalCourseLanguageNames.forEach((n) => {
      const trimmed = n.trim();
      if (trimmed) set.add(trimmed);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [globalCourseLanguageNames]);

  useEffect(() => {
    setNewSession((prev) => {
      if (sessionLanguageOptions.length === 0) {
        return prev.language ? { ...prev, language: '' } : prev;
      }
      if (prev.language && sessionLanguageOptions.includes(prev.language)) {
        return prev;
      }
      return { ...prev, language: sessionLanguageOptions[0] };
    });
  }, [sessionLanguageOptions]);

  const getAttendeeCustomer = (attendee: CourseSessionAttendee) => {
    if (attendee.individualCustomerId) {
      return customerById[attendee.individualCustomerId];
    }
    if (attendee.isVirtual) return undefined;
    return attendee.companyCustomerId ? customerById[attendee.companyCustomerId] : undefined;
  };
  const getAttendeeLabel = (attendee: CourseSessionAttendee) => {
    if (attendee.isVirtual) {
      return attendee.placeholderLabel?.trim() || 'Reserved spot';
    }
    const enrollmentName = `${attendee.enrollmentFirstName || ''} ${attendee.enrollmentSurname || ''}`.trim();
    if (enrollmentName) return enrollmentName;
    if (attendee.individualCustomerId) {
      const c = customerById[attendee.individualCustomerId];
      if (c) {
        return `${c.firstName || ''} ${c.surname || ''}`.trim() || 'Unknown attendee';
      }
    }
    return 'Unknown attendee';
  };

  const applyCompanyInvoicePaidFlag = (
    target: 'addAttendee' | 'editPayment' | 'companyBooking' | 'companyGroupInvoice',
    paid: boolean
  ) => {
    if (target === 'addAttendee') {
      setAddInvoicePaid(paid);
      return;
    }
    if (target === 'companyGroupInvoice') {
      setCompanyGroupInvoiceModal((prev) => (prev ? { ...prev, invoicePaid: paid } : prev));
      return;
    }
    setCompanyBookingModal((prev) => (prev ? { ...prev, invoicePaid: paid } : prev));
  };

  const requestCompanyInvoicePaidToggle = (
    checked: boolean,
    args: {
      target: 'addAttendee' | 'editPayment' | 'companyBooking' | 'companyGroupInvoice';
      sessionId: string;
      anchorAttendee?: CourseSessionAttendee | null;
      companyLabel?: string;
      seatCount?: number;
      invoiceNumber?: string;
    }
  ) => {
    if (!checked) {
      applyCompanyInvoicePaidFlag(args.target, false);
      if (args.anchorAttendee && args.sessionId) {
        void (async () => {
          try {
            const sessionAttendees = attendeesBySession[args.sessionId] || [];
            const settlement = makeInvoiceSettlement(args.sessionId);
            const result = await applyCompanyInvoiceToCohort(
              sessionAttendees,
              args.anchorAttendee!,
              companyCustomers,
              getAttendeeCustomer,
              { invoicePaid: false },
              settlement
            );
            await load();
            if (result.seatsSettled > 0) {
              showSuccess(
                `Company invoice marked unpaid.${invoiceUnsettlementSuccessSuffix(result)}`
              );
            }
            if (args.target === 'companyGroupInvoice') {
              setCompanyGroupInvoiceModal(null);
            }
          } catch (e: unknown) {
            showError((e as Error)?.message || 'Could not revert company invoice payments.');
          }
        })();
      }
      return;
    }
    const session = sessions.find((s) => s.id === args.sessionId);
    const sessionLabel = session
      ? `${session.courseName}${session.language ? ` (${session.language})` : ''} ? ${formatDateBlocksUk(session.dateBlocks)}`
      : 'this session';
    let companyLabel = String(args.companyLabel || '').trim();
    let seatCount = args.seatCount ?? 0;
    if (args.anchorAttendee) {
      const sessionAttendees = attendeesBySession[args.sessionId] || [];
      const cohort = companyCohortAttendees(
        args.anchorAttendee,
        sessionAttendees,
        companyCustomers,
        getAttendeeCustomer
      );
      companyLabel = rosterGroupKeyForAttendee(
        args.anchorAttendee,
        getAttendeeCustomer(args.anchorAttendee),
        companyCustomers
      );
      seatCount = cohort.length;
    }
    if (!companyLabel || companyLabel === 'Individuals / Unassigned' || companyLabel === 'Reserved / Unassigned') {
      companyLabel = 'this company group';
    }
    if (seatCount < 1) seatCount = 1;
    const sessionAttendees = attendeesBySession[args.sessionId] || [];
    const cohortForConfirm = args.anchorAttendee
      ? companyCohortAttendees(args.anchorAttendee, sessionAttendees, companyCustomers, getAttendeeCustomer)
      : [];
    const sessionRow = sessions.find((s) => s.id === args.sessionId);
    const seatsOutstandingEuro = cohortForConfirm.length
      ? cohortSeatsOutstandingEuro(
          cohortForConfirm,
          sessionRow?.courseName,
          sessionRow?.language,
          courseDbFees,
          paymentTimelineByAttendee,
          getAttendeeCustomer
        )
      : 0;
    const invoiceNumber =
      args.invoiceNumber !== undefined
        ? String(args.invoiceNumber).trim()
        : args.anchorAttendee
        ? resolveGroupInvoice(
            companyLabel,
            cohortForConfirm.length ? cohortForConfirm : [args.anchorAttendee],
            companyCustomers,
            getAttendeeCustomer
          ).number
        : '';
    setCompanyInvoicePaidConfirm({
      companyLabel,
      seatCount,
      sessionLabel,
      target: args.target,
      sessionId: args.sessionId,
      anchorAttendeeId: args.anchorAttendee?.id,
      invoiceNumber,
      seatsOutstandingEuro,
    });
  };

  const settleCompanyGroupSeatPayments = async (
    sessionId: string,
    anchor: CourseSessionAttendee,
    invoiceNumber?: string,
    opts?: { paymentMethod?: AttendeePaymentMethod; paymentDate?: string }
  ) => {
    try {
      const sessionAttendees = attendeesBySession[sessionId] || [];
      const settlement = makeInvoiceSettlement(sessionId, {
        paymentMethod: opts?.paymentMethod,
        paymentDate: opts?.paymentDate,
      });
      if (!settlement) {
        showError('Could not resolve session for payment settlement.');
        return;
      }
      const result = await applyCompanyInvoiceToCohort(
        sessionAttendees,
        anchor,
        companyCustomers,
        getAttendeeCustomer,
        { invoicePaid: true, invoiceNumber: invoiceNumber || null },
        settlement
      );
      await load();
      showSuccess(`Seat payments recorded.${invoiceSettlementSuccessSuffix(result)}`);
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not record seat payments.');
    }
  };

  const openCompanyGroupInvoiceModal = (
    sessionId: string,
    anchor: CourseSessionAttendee,
    groupLabel: string,
    seatCount: number,
    invoice: { number: string; paid: boolean },
    seatsOutstandingEuro: number
  ) => {
    setCompanyGroupInvoiceModal({
      sessionId,
      anchorAttendeeId: anchor.id,
      companyLabel: groupLabel,
      seatCount,
      invoiceNumber: invoice.number,
      invoicePaid: invoice.paid,
      seatsOutstandingEuro,
      paymentMethod: 'bank',
    });
  };

  const saveCompanyGroupInvoice = async () => {
    if (!companyGroupInvoiceModal) return;
    const sessionAttendees = attendeesBySession[companyGroupInvoiceModal.sessionId] || [];
    const anchor = sessionAttendees.find((a) => a.id === companyGroupInvoiceModal.anchorAttendeeId);
    if (!anchor) {
      showError('Could not find company group on this session.');
      return;
    }
    try {
      await applyCompanyInvoiceToCohort(
        sessionAttendees,
        anchor,
        companyCustomers,
        getAttendeeCustomer,
        { invoiceNumber: companyGroupInvoiceModal.invoiceNumber.trim() || null },
        null
      );
      await load();
      setCompanyGroupInvoiceModal(null);
      showSuccess('Company invoice number saved.');
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not save company invoice.');
    }
  };

  const confirmCompanyInvoicePaidApply = async () => {
    const confirm = companyInvoicePaidConfirm;
    if (!confirm) return;
    try {
      applyCompanyInvoicePaidFlag(confirm.target, true);
      if (confirm.sessionId && confirm.anchorAttendeeId) {
        const sessionAttendees = attendeesBySession[confirm.sessionId] || [];
        const anchor = sessionAttendees.find((a) => a.id === confirm.anchorAttendeeId);
        if (anchor) {
          const paymentDate = parseUkDateToIso(invoiceSettlementDateUk) || new Date().toISOString().slice(0, 10);
          const settlement = makeInvoiceSettlement(confirm.sessionId, {
            paymentMethod: invoiceSettlementMethod,
            paymentDate,
            paymentReference: invoiceSettlementReference.trim() || null,
          });
          const result = await applyCompanyInvoiceToCohort(
            sessionAttendees,
            anchor,
            companyCustomers,
            getAttendeeCustomer,
            { invoicePaid: true, invoiceNumber: confirm.invoiceNumber || null },
            settlement
          );
          await load();
          showSuccess(`Company invoice marked paid.${invoiceSettlementSuccessSuffix(result)}`);
          setCompanyInvoicePaidConfirm(null);
          if (confirm.target === 'companyGroupInvoice') {
            setCompanyGroupInvoiceModal(null);
          }
          return;
        }
      }
      setCompanyInvoicePaidConfirm(null);
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not mark company invoice paid.');
    }
  };

  const makeInvoiceSettlement = (
    sessionId: string,
    opts?: { paymentMethod?: PaymentMethod; paymentDate?: string; paymentReference?: string | null; refreshLedgers?: boolean }
  ) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return null;
    return {
      bookingId: sessionId,
      courseName: session.courseName,
      courseLanguage: session.language,
      courseDbFees,
      paymentTimelineByAttendee,
      paymentMethod: (opts?.paymentMethod || 'bank') as AttendeePaymentMethod,
      paymentDate: opts?.paymentDate || new Date().toISOString().slice(0, 10),
      paymentReference: opts?.paymentReference || null,
      getAttendeeLabel,
      refreshLedgers: opts?.refreshLedgers ?? true,
    };
  };

  const resolveAttendeeLabelForCertificate = async (attendee: CourseSessionAttendee): Promise<string> => {
    const fromRoster = getAttendeeLabel(attendee);
    if (fromRoster !== 'Unknown attendee') return fromRoster;
    const customerId = attendee.individualCustomerId || attendee.companyCustomerId;
    if (!customerId) return fromRoster;
    const customer =
      getAttendeeCustomer(attendee) || (await supabaseCustomerStorage.getCustomerById(customerId));
    if (!customer) return fromRoster;
    if (customer.customerType === 'individual') {
      return `${customer.firstName || ''} ${customer.surname || ''}`.trim() || customer.companyName || fromRoster;
    }
    return customer.companyName || fromRoster;
  };

  const getSessionStats = (sessionId: string) => {
    const sessionRow = sessions.find((s) => s.id === sessionId);
    const courseName = sessionRow?.courseName;
    const sessionLanguage = sessionRow?.language;
    const attendees = attendeesBySession[sessionId] || [];
    const booked = attendees.filter((a) => isAttendeeOnActiveRoster(a));
    let revenueTarget = 0;
    let revenueCollected = 0;
    let revenueCollectedBooked = 0;
    let revenueCollectedRemoved = 0;
    let revenueOutstanding = 0;
    let paidCount = 0;
    let halfPaidCount = 0;
    booked.forEach((a) => {
      const c = getAttendeeCustomer(a);
      const fee = getEffectiveFee(a, c, courseName, courseDbFees, sessionLanguage);
      const ledger = paymentTimelineByAttendee[a.id];
      const rawPaid = ledger?.length
        ? sumLedgerRawPaid(ledger)
        : Math.max(0, Number(a.amountPaid || 0));
      revenueTarget += fee;
      revenueCollectedBooked += rawPaid;
      revenueOutstanding += fee > 0 ? Math.max(0, fee - rawPaid) : 0;
      if (fee > 0 && rawPaid >= fee - 0.005) paidCount += 1;
      else if (rawPaid > 0) halfPaidCount += 1;
    });
    attendees.forEach((a) => {
      const ledger = paymentTimelineByAttendee[a.id] || [];
      const paid = ledger.length
        ? sumLedgerRawPaid(ledger)
        : Math.max(0, Number(a.amountPaid || 0));
      revenueCollected += paid;
      if (a.status === 'cancelled') revenueCollectedRemoved += paid;
    });
    const unpaidCount = booked.length - paidCount - halfPaidCount;
    return {
      booked,
      revenueTarget,
      revenueCollected,
      revenueCollectedBooked,
      revenueCollectedRemoved,
      revenueOutstanding,
      paidCount,
      halfPaidCount,
      unpaidCount,
    };
  };

  const upsertCustomersInMemory = (extras: Customer[]) => {
    if (!extras.length) return;
    setAllCustomers((prev) => mergeCustomerLists(prev, extras));
  };

  /** Before Create new client: reuse existing identity matches or prompt when duplicates exist. */
  const beginCreateIndividualWithDedup = async (
    draft: Customer,
    mode: 'session' | 'virtual'
  ): Promise<'prompted' | 'created'> => {
    if (draft.customerType === 'company') {
      const saved = await persistNewCustomer(draft);
      upsertCustomersInMemory([saved]);
      if (mode === 'session' && selectedSessionId) {
        setCreateCrmForSessionOpen(false);
        openAddAttendeeModal(selectedSessionId, saved);
      } else if (mode === 'virtual') {
        setCreateCrmForVirtualSeatOpen(false);
        await confirmReplaceVirtualWithCustomer(saved);
      }
      return 'created';
    }
    const identity = individualIdentityDraftFromCustomer(draft);
    const matches = await searchIndividualIdentityMatches(identity, allCustomers);
    if (matches.length > 0) {
      upsertCustomersInMemory(matches);
      setDuplicateCreatePrompt({
        draft,
        matches,
        selectedMatchId: matches[0]!.id,
        mode,
      });
      return 'prompted';
    }
    const saved = await persistNewCustomer(draft);
    upsertCustomersInMemory([saved]);
    if (mode === 'session' && selectedSessionId) {
      setCreateCrmForSessionOpen(false);
      openAddAttendeeModal(selectedSessionId, saved);
    } else if (mode === 'virtual') {
      setCreateCrmForVirtualSeatOpen(false);
      await confirmReplaceVirtualWithCustomer(saved);
    }
    return 'created';
  };

  const resolveDuplicateCreatePrompt = async (action: 'use_existing' | 'create_anyway' | 'cancel') => {
    const prompt = duplicateCreatePrompt;
    if (!prompt) return;
    setDuplicateCreatePrompt(null);
    if (action === 'cancel') return;
    try {
      if (action === 'use_existing') {
        const existing =
          prompt.matches.find((m) => m.id === prompt.selectedMatchId) || prompt.matches[0];
        if (!existing) return;
        upsertCustomersInMemory([existing]);
        if (prompt.mode === 'session' && selectedSessionId) {
          setCreateCrmForSessionOpen(false);
          openAddAttendeeModal(selectedSessionId, existing);
        } else if (prompt.mode === 'virtual') {
          setCreateCrmForVirtualSeatOpen(false);
          await confirmReplaceVirtualWithCustomer(existing);
        }
        return;
      }
      const saved = await persistNewCustomer(prompt.draft);
      upsertCustomersInMemory([saved]);
      if (prompt.mode === 'session' && selectedSessionId) {
        setCreateCrmForSessionOpen(false);
        openAddAttendeeModal(selectedSessionId, saved);
      } else if (prompt.mode === 'virtual') {
        setCreateCrmForVirtualSeatOpen(false);
        await confirmReplaceVirtualWithCustomer(saved);
      }
    } catch (e: any) {
      showError(e?.message || 'Could not create record.');
    }
  };

  const resetCompanyPicker = (prefillQuery = '') => {
    setCompanyPickerQuery(prefillQuery);
    setCompanyPickerHits([]);
    setCompanyPickerSearching(false);
  };

  const hydrateCustomersForAttendees = async (
    attendeeMaps: Record<string, CourseSessionAttendee[]>,
    alreadyLoaded?: Customer[]
  ) => {
    const known = new Set((alreadyLoaded || []).map((c) => c.id));
    const needed = new Set<string>();
    for (const list of Object.values(attendeeMaps)) {
      for (const id of collectLinkedCustomerIds(list || [])) {
        if (!known.has(id)) needed.add(id);
      }
    }
    if (needed.size === 0) return alreadyLoaded || [];
    const extras = await supabaseCustomerStorage.getCustomersByIds([...needed]);
    if (!extras.length) return alreadyLoaded || [];
    const mapped = extras.map((c) => mapCustomerRow(c as any));
    upsertCustomersInMemory(mapped);
    return mergeCustomerLists(alreadyLoaded || [], mapped);
  };

  const loadSessionRoster = async (sessionId: string, opts?: { force?: boolean }) => {
    if (!sessionId) return;
    if (!opts?.force && rosterHydratedRef.current.has(sessionId)) return;
    if (!opts?.force) {
      const inFlight = rosterInFlightRef.current.get(sessionId);
      if (inFlight) return inFlight;
    }

    const run = (async () => {
      setRosterLoadingBySession((prev) => ({ ...prev, [sessionId]: true }));
      try {
        const attendeeMaps = await courseSessionsService.listAttendeesBySessionIds([sessionId]);
        const list = attendeeMaps[sessionId] || [];
        setAttendeesBySession((prev) => ({ ...prev, [sessionId]: list }));
        setBookedCountBySessionId((prev) => ({
          ...prev,
          [sessionId]: list.filter((a) => isAttendeeOnActiveRoster(a)).length,
        }));
        const payments = await courseSessionsService.listPaymentsBySessionIds([sessionId]).catch(() => ({}));
        const sessionAttendeeIds = list.map((a) => a.id);
        setPaymentTimelineByAttendee((prev) => mergeSessionPayments(prev, sessionAttendeeIds, payments));
        await hydrateCustomersForAttendees({ [sessionId]: list });
        rosterHydratedRef.current.add(sessionId);
        setRosterHydratedBySession((prev) => ({ ...prev, [sessionId]: true }));
      } catch (e) {
        console.error('Failed to load session roster', e);
        rosterHydratedRef.current.add(sessionId);
        setRosterHydratedBySession((prev) => ({ ...prev, [sessionId]: true }));
      } finally {
        setRosterLoadingBySession((prev) => ({ ...prev, [sessionId]: false }));
        rosterInFlightRef.current.delete(sessionId);
      }
    })();

    rosterInFlightRef.current.set(sessionId, run);
    return run;
  };

  const load = async () => {
    const list = await courseSessionsService.listSessions();
    setSessions(list);
    const sessionIds = list.map((s) => s.id);
    void courseSessionsService.reconcileFinancialIntegrity().catch((e) => {
      console.warn('Financial reconcile skipped:', e);
    });
    try {
      const counts = await courseSessionsService.listBookedAttendeeCountsBySessionIds(sessionIds);
      setBookedCountBySessionId(counts);
    } catch (e) {
      console.error('Failed to load booked counts', e);
    }

    const openId = selectedSessionIdRef.current;
    rosterHydratedRef.current = new Set();
    setRosterHydratedBySession({});
    setAttendeesBySession((prev) => {
      if (!openId || !prev[openId]) return {};
      return { [openId]: prev[openId] };
    });
    if (openId) {
      await loadSessionRoster(openId, { force: true });
    }

    void (async () => {
      try {
        const { data: langRows } = await supabase
          .from('course_languages')
          .select('name')
          .eq('is_active', true)
          .order('name', { ascending: true });
        if (langRows) {
          setGlobalCourseLanguageNames(
            langRows.map((r: { name?: string }) => String(r.name || '').trim()).filter(Boolean)
          );
        }
      } catch {
        /* keep existing list on failure */
      }
    })();

    void (async () => {
      try {
        const map = await fetchCourseFeeOverrideMap();
        setCourseDbFees(map);
      } catch {
        setCourseDbFees({});
      }
    })();
  };

  /** Keep in-memory session rows in sync after auto-save (silent saves skip full load()). */
  const patchSessionInState = (sessionId: string, patch: Partial<CourseSession>) => {
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, ...patch } : s)));
  };

  // Ops mode needs accurate doc counts across every session (it drives "missing
  // docs" health), so when it's enabled we eagerly load any counts that the
  // lazy observer hasn't seen yet. Done with a small concurrency to avoid
  // hammering storage.
  useEffect(() => {
    if (!opsMode) return;
    const pending = sessions
      .map((s) => s.id)
      .filter((id) => sessionDocumentCounts[id] === undefined && !sessionDocCountsRequestedRef.current.has(id));
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      const CONC = 4;
      for (let i = 0; i < pending.length; i += CONC) {
        if (cancelled) return;
        const slice = pending.slice(i, i + CONC);
        await Promise.all(
          slice.map(async (id) => {
            if (sessionDocCountsRequestedRef.current.has(id)) return;
            sessionDocCountsRequestedRef.current.add(id);
            try {
              const count = await courseSessionsService.countSessionDocuments(id);
              if (cancelled) return;
              setSessionDocumentCounts((prev) =>
                prev[id] === count ? prev : { ...prev, [id]: count }
              );
            } catch {
              /* ignored */
            }
          })
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opsMode, sessions]);

  useEffect(() => {
    setVisibleSessionLimit(25);
  }, [
    sessionFilters.timeRange,
    sessionFilters.query,
    sessionFilters.courseName,
    sessionFilters.locationId,
    sessionFilters.language,
    sessionFilters.deliveryMode,
    sessionFilters.instructorId,
    sessionFilters.interpreterId,
    sessionFilters.providerId,
    sessionFilters.status,
    sessionFilters.dateFrom,
    sessionFilters.dateTo,
    opsMode,
    opsFilter,
  ]);

  // Lazy-load per-session document counts only for cards that scroll into view.
  // Replaces the eager N storage.list() calls that used to fire on every page load.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const sid = (entry.target as HTMLElement).dataset.sessionId;
          if (!sid) return;
          if (sessionDocCountsRequestedRef.current.has(sid)) return;
          sessionDocCountsRequestedRef.current.add(sid);
          courseSessionsService
            .countSessionDocuments(sid)
            .then((count) => {
              setSessionDocumentCounts((prev) =>
                prev[sid] === count ? prev : { ...prev, [sid]: count }
              );
            })
            .catch(() => {
              /* leave unknown ? UI defaults to "No docs uploaded" */
            });
        });
      },
      { rootMargin: '200px 0px' }
    );
    sessionCardObserverRef.current = observer;
    sessionCardRefs.current.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
      sessionCardObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (initialExpandedSessionId) setSelectedSessionId(initialExpandedSessionId);
  }, [initialExpandedSessionId]);
  useEffect(() => {
    if (!selectedSessionId) return;
    void loadSessionRoster(selectedSessionId);
  }, [selectedSessionId]);
  useEffect(() => {
    if (initialSessionTab) setSessionMainTab(initialSessionTab);
  }, [initialSessionTab, initialExpandedSessionId]);
  useEffect(() => {
    if (initialOpsMode) setOpsMode(true);
    if (initialOpsFilter && initialOpsFilter !== 'all') setOpsFilter(initialOpsFilter);
  }, [initialOpsMode, initialOpsFilter]);
  useEffect(() => {
    load().catch(console.error);
    resolvedLocationIdsRef.current = new Set();
    (async () => {
      const [instructorList, locationsRes, interpList, providersRes, languagesRes] = await Promise.all([
        supabaseInstructorStorage.getInstructors(500, 0),
        // Picker options + seed map: training venues only (not the full customer-address table).
        supabase.from('locations').select('*').eq('is_training_location', true).eq('is_active', true),
        interpretersService.listActive().catch(() => [] as InterpreterRow[]),
        supabase.from('course_providers').select('id,name').eq('is_active', true).order('name', { ascending: true }),
        supabase.from('course_languages').select('name').eq('is_active', true).order('name', { ascending: true })
      ]);
      setInterpreterOptions(interpList);
      setProviderOptions(((providersRes.data || []) as Array<{ id: string; name: string }>).filter((p) => p.id && p.name));
      if (!languagesRes.error && languagesRes.data) {
        setGlobalCourseLanguageNames(
          (languagesRes.data as Array<{ name?: string }>).map((r) => String(r.name || '').trim()).filter(Boolean)
        );
      }
      setInstructors((instructorList || []).map((i) => ({ id: i.id, firstName: i.firstName, lastName: i.lastName, email: i.email })));
      const data = (locationsRes.data || []) as any[];
      if (locationsRes.error) {
        console.warn('Training locations query failed:', locationsRes.error);
      }
      const maps = mergeLocationRowsIntoMaps(data);
      const opts = data.map((row: any) => {
        const label = locationOptionLabelFromRow(row);
        const shortLabel = locationFilterShortLabel(String(row?.name || '').trim() || label);
        return { id: row.id, label, shortLabel };
      });
      data.forEach((row: any) => {
        if (row?.id) resolvedLocationIdsRef.current.add(row.id);
      });
      setLocationById((prev) => ({ ...prev, ...maps.labels }));
      setLocationDetailsById((prev) => ({ ...prev, ...maps.details }));
      setLocationMapsUrl((prev) => ({ ...prev, ...maps.mapsUrls }));
      setLocationOptions(opts);
      if (data.length === 0) {
        console.warn('No active training locations found; venue picker will stay empty until session venues resolve.');
      }
      const preferred =
        opts.find((o) => o.label.toLowerCase().includes(DEFAULT_LOCATION_MATCH)) ||
        opts.find((o) => /ireland\s*t\s*&\s*t/i.test(o.label)) ||
        opts[0];
      setPreferredDefaultLocationId(preferred?.id || '');
    })().catch(console.error);
  }, [mode]);

  // Resolve only location IDs that appear on loaded sessions (same idea as roster/instructor labels).
  useEffect(() => {
    const needed = [
      ...new Set(
        sessions
          .map((s) => s.locationId)
          .filter((id): id is string => Boolean(id) && !resolvedLocationIdsRef.current.has(id as string))
      ),
    ] as string[];
    if (needed.length === 0) return;
    let cancelled = false;
    needed.forEach((id) => resolvedLocationIdsRef.current.add(id));
    (async () => {
      const rows = await fetchLocationsByIds(needed);
      if (cancelled || rows.length === 0) return;
      const maps = mergeLocationRowsIntoMaps(rows);
      setLocationById((prev) => ({ ...prev, ...maps.labels }));
      setLocationDetailsById((prev) => ({ ...prev, ...maps.details }));
      setLocationMapsUrl((prev) => ({ ...prev, ...maps.mapsUrls }));
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [sessions]);

  const createSession = async () => {
    if (!newSession.courseName.trim() || !newSession.firstDate) return showError('Course and first date are required.');
    if (!newSession.language.trim()) return showError('Please add a language in Admin before creating a session.');
    const firstBlock: CourseSessionDateBlock = {
      date: newSession.firstDate,
      startTime: newSession.startTime || null,
      endTime: newSession.endTime || null,
    };
    const extraBlocks: CourseSessionDateBlock[] = extraSessionDays
      .filter((d) => d.date.trim())
      .map((d) => ({
        date: d.date,
        startTime: d.startTime || newSession.startTime || null,
        endTime: d.endTime || newSession.endTime || null,
      }));
    const dateBlocks = normalizeDateBlocks([firstBlock, ...extraBlocks]);
    if (dateBlocks.length === 0) return showError('At least one valid course date is required.');
    const ruleCheck = await courseSessionsService.checkSchedulingRules({
      dateBlocks,
      instructorId: newSession.instructorId || null,
      interpreterId: newSession.interpreterId || null
    });
    if (ruleCheck.blockedDay?.severity === 'warn') {
      const proceed = window.confirm(ruleCheck.blockedDay.reason || 'This date is marked as blocked (warning). Continue anyway?');
      if (!proceed) return;
    }
    try {
      const created = await courseSessionsService.createSession({
        courseName: newSession.courseName.trim(),
        language: newSession.language,
        deliveryMode: newSession.deliveryMode,
        capacity: Number(newSession.capacity) || 1,
        locationId: newSession.locationId || preferredDefaultLocationId || null,
        instructorId: newSession.instructorId || null,
        interpreterId: newSession.interpreterId || null,
        providerId: newSession.providerId?.trim() ? newSession.providerId.trim() : null,
        dateBlocks,
      });
      if (actorName || user?.id) {
        const metaOtherInfo = composeSessionOtherInfoWithMeta(
          String(created.sessionOtherInfo || '').split(POST_SESSION_META_PREFIX)[0].trim(),
          { ...DEFAULT_POST_SESSION_CHECKLIST },
          {
            createdByName: actorName || undefined,
            createdById: user?.id || undefined
          }
        );
        await courseSessionsService.updateSession(created.id, { sessionOtherInfo: metaOtherInfo });
        void courseSessionsService.logSessionActivity({
          sessionId: created.id,
          actorUserId: user?.id || null,
          actorName: actorName || null,
          eventType: 'session_created',
          summary: `${created.courseName} session created`,
          details: { courseName: created.courseName }
        });
      }
      setNewSession({
        courseName: '',
        language: sessionLanguageOptions[0] || '',
        deliveryMode: 'in_class',
        capacity: 12,
        locationId: '',
        instructorId: '',
        interpreterId: '',
        providerId: '',
        firstDate: '',
        startTime: '',
        endTime: '',
      });
      setCoursePickerChosen(false);
      setCoursePickerHits([]);
      setExtraSessionDays([]);
      await load();
      showSuccess('Course session created.');
    } catch (e: any) {
      showError(e?.message || 'Could not create course session.');
    }
  };

  const getListBookedCount = (sessionId: string) => {
    if (rosterHydratedRef.current.has(sessionId)) {
      return (attendeesBySession[sessionId] || []).filter((a) => isAttendeeOnActiveRoster(a)).length;
    }
    return Number(bookedCountBySessionId[sessionId] || 0);
  };

  const getSessionOccupancy = (sessionId: string) => {
    const sessionRow = sessions.find((s) => s.id === sessionId);
    const booked = getListBookedCount(sessionId);
    const capacity = Math.max(
      1,
      Number(
        selectedSession?.id === sessionId
          ? sessionDraft.capacity || selectedSession?.capacity || 1
          : sessionRow?.capacity || 1
      )
    );
    return { booked, capacity, isFull: booked >= capacity };
  };

  const rejectIfCourseFullyBooked = (sessionId: string): boolean => {
    if (!getSessionOccupancy(sessionId).isFull) return false;
    showError('Course fully booked.');
    return true;
  };

  const isSessionCancelled = (sessionId: string): boolean => {
    const sessionRow = sessions.find((s) => s.id === sessionId);
    return (sessionRow?.status || 'scheduled') === 'cancelled';
  };

  const rejectIfCourseCancelled = (sessionId: string): boolean => {
    if (!isSessionCancelled(sessionId)) return false;
    showError('This course session has been cancelled.');
    return true;
  };

  const findActiveBookingForCustomer = (
    sessionId: string,
    customer: Customer
  ): CourseSessionAttendee | undefined => {
    if (!isIndividualCustomerRecord(customer)) {
      return (attendeesBySession[sessionId] || []).find((row) => {
        if (!isAttendeeOnActiveRoster(row)) return false;
        return row.companyCustomerId === customer.id;
      });
    }
    return findConflictingEnrollment(
      attendeesBySession[sessionId] || [],
      customer.id
    ) as CourseSessionAttendee | undefined;
  };

  const existingEnrollmentMessage = (sessionId: string, customer: Customer): string | null => {
    const conflict = findActiveBookingForCustomer(sessionId, customer);
    if (!conflict) return null;
    return conflictMessageForEnrollment(conflict);
  };

  const openAddAttendeeModal = (sessionId: string, customer: Customer) => {
    if (rejectIfCourseCancelled(sessionId)) return;
    if (rejectIfCourseFullyBooked(sessionId)) return;
    if (isIndividualCustomerRecord(customer) && findActiveBookingForCustomer(sessionId, customer)) {
      showError(existingEnrollmentMessage(sessionId, customer) || 'This person is already booked on this session.');
      return;
    }
    const sessionRow = sessions.find((s) => s.id === sessionId);
    const courseDefault = getCourseCatalogDefaultFeeForSession(
      sessionRow?.courseName,
      courseDbFees,
      sessionRow?.language
    );
    const fromCrm = customer.courseFee != null && Number(customer.courseFee) > 0 ? Number(customer.courseFee) : 0;
    const def = courseDefault > 0 ? courseDefault : fromCrm;
    setAddSessionFee(def > 0 ? def.toFixed(2) : '');
    setAddPayKind('unpaid');
    setAddAmountStr('');
    setAddPaymentMethod('cash');
    setAddPaymentStepType('other');
    setAddPaymentDateUk(formatDateUk(new Date().toISOString().slice(0, 10)));
    setAddSafePassProcessNumber(String((customer as any)?.safePassProcessNumber || ''));
    setAddBookedByName('');
    // Individuals: never auto-assign to a company by name. An individual's `companyName` field
    // is filled with their own first+surname when they don't have an employer (see AddEditCustomerModal),
    // so name-based matching produces unstable results ? sometimes Unassigned, sometimes self-assigned
    // to a stale/unrelated company. Default to Unassigned for individuals; user can pick explicitly.
    if (customer.customerType === 'individual') {
      setAddAssignedCompanyCustomerId('');
      resetCompanyPicker();
    } else {
      const matchedCompany =
        customer.customerType === 'company'
          ? customer
          : companyCustomers.find(
              (co) =>
                String(co.companyName || '').trim().toLowerCase() ===
                String(customer.companyName || '').trim().toLowerCase()
            );
      if (matchedCompany) upsertCustomersInMemory([matchedCompany]);
      setAddAssignedCompanyCustomerId(matchedCompany?.id || '');
      resetCompanyPicker(matchedCompany ? String(matchedCompany.companyName || '') : '');
    }
    setSessionFeeLocked(courseDefault > 0);
    setAddAttendeeModalSource('');
    setAddInvoiceNumber('');
    setAddInvoicePaid(false);
    setAddPaymentNote('');
    setAddAttendeeModal({ sessionId, customer });
  };

  const openEditAttendeeModal = (
    attendee: CourseSessionAttendee,
    sessionId: string,
    opts?: { recordPayment?: boolean }
  ) => {
    const c = getAttendeeCustomer(attendee);
    if (!c) {
      showError('No CRM profile linked for this attendee.');
      return;
    }
    const sessionRow = sessions.find((s) => s.id === sessionId);
    const fee = getEffectiveFee(attendee, c, sessionRow?.courseName, courseDbFees, sessionRow?.language);
    setAddSessionFee(fee > 0 ? fee.toFixed(2) : '');
    const ledger = paymentTimelineByAttendee[attendee.id] || [];
    const paid = getExistingAttendeePaid(attendee, ledger, fee);
    setAddSafePassProcessNumber(String(attendee.safePassProcessNumber || (c as any)?.safePassProcessNumber || ''));
    setAddBookedByName(resolveBookedByLabel(attendee));
    const invoiceResolved = resolveAttendeeInvoice(
      attendee,
      c,
      companyCustomers,
      companyCohortAttendees(attendee, attendeesBySession[sessionId] || [], companyCustomers, getAttendeeCustomer)
    );
    setAddInvoiceNumber(invoiceResolved.number);
    setAddInvoicePaid(invoiceResolved.paid);
    const lastPayment = (paymentTimelineByAttendee[attendee.id] || []).slice(-1)[0];
    if (opts?.recordPayment) {
      setAddPayKind('part');
      setAddPaymentStepType(lastPayment?.stepType || 'other');
    } else {
      setAddPayKind('keep');
      setAddPaymentStepType('other');
    }
    setAddAmountStr('');
    setAddPaymentMethod(((lastPayment?.method || 'cash') as PaymentMethod));
    setAddPaymentDateUk(lastPayment?.paymentDate ? formatDateUk(lastPayment.paymentDate) : formatDateUk(new Date().toISOString().slice(0, 10)));
    setAddAssignedCompanyCustomerId(attendee.companyCustomerId || '');
    // Keep fee editable when correcting enrollments (e.g. mistaken paid-in-full amount).
    setSessionFeeLocked(false);
    setAddPaymentNote('');
    setAddAttendeeModalSource('');
    setAddAttendeeModal({ sessionId, customer: c, editingAttendee: attendee });
  };

  const assignCurrent = (sessionId: string) => {
    if (!currentCustomer) return;
    if (!isIndividualCustomerRecord(currentCustomer)) {
      showError('Session attendees must be individuals. Add individual names/people later to this session.');
      return;
    }
    openAddAttendeeModal(sessionId, currentCustomer);
  };

  const confirmAddAttendee = async () => {
    if (!addAttendeeModal) return;
    if (addAttendeeSavingRef.current) return;
    addAttendeeSavingRef.current = true;
    setAddAttendeeSaving(true);
    try {
    const { sessionId, customer, editingAttendee } = addAttendeeModal;
    if (!editingAttendee && rejectIfCourseCancelled(sessionId)) return;
    if (
      !editingAttendee &&
      isIndividualCustomerRecord(customer) &&
      findActiveBookingForCustomer(sessionId, customer)
    ) {
      showError(existingEnrollmentMessage(sessionId, customer) || 'This person is already booked on this session.');
      return;
    }

    if (editingAttendee?.isVirtual) {
      const fee = parseEuroInput(addSessionFee);
      try {
        const sessionAttendees = attendeesBySession[sessionId] || [];
        await courseSessionsService.updateAttendee(editingAttendee.id, {
          enrollmentCourseFee: fee > 0 ? fee : null,
        });
        const invoicePatch = {
          invoiceNumber: addInvoiceNumber.trim() || null,
          invoicePaid: addInvoicePaid,
        };
        const settlement =
          invoicePatch.invoicePaid === true || invoicePatch.invoicePaid === false
            ? makeInvoiceSettlement(sessionId)
            : null;
        const invoiceResult = await applyCompanyInvoiceToCohort(
          sessionAttendees,
          editingAttendee,
          companyCustomers,
          getAttendeeCustomer,
          invoicePatch,
          settlement
        );
        setAddAttendeeModal(null);
        await load();
        showSuccess(
          `Virtual seat updated.${
            invoicePatch.invoicePaid
              ? invoiceSettlementSuccessSuffix(invoiceResult)
              : invoiceUnsettlementSuccessSuffix(invoiceResult)
          }`
        );
      } catch (e: unknown) {
        showError((e as Error)?.message || 'Failed to update virtual seat.');
      }
      return;
    }

    if (!isIndividualCustomerRecord(customer)) {
      showError('Companies are not direct attendees. Add individual participant names to the session roster.');
      return;
    }
    if (!editingAttendee && !addAttendeeModalSource) {
      showError('Pick how this booking reached us (Source).');
      return;
    }
    const fee = parseEuroInput(addSessionFee);
    const amt = parseEuroInput(addAmountStr);
    const parsedPaymentDateIso = parseUkDateToIso(addPaymentDateUk);
    const isEditFeeOnly = Boolean(editingAttendee) && addPayKind === 'keep';
    // Prepaid/part hide behind an amount field; "full" uses the fee so amt is often 0 by design.
    const isEditWithoutNewPayment =
      Boolean(editingAttendee) && addPayKind !== 'keep' && addPayKind !== 'full' && amt <= 0;
    let paymentStatus: 'unpaid' | 'half_paid' | 'paid' = 'unpaid';
    let amountPaid = 0;

    if (isEditFeeOnly) {
      paymentStatus = 'unpaid';
      amountPaid = 0;
    } else if (addPayKind === 'unpaid') {
      paymentStatus = 'unpaid';
      amountPaid = 0;
    } else if (addPayKind === 'full') {
      paymentStatus = 'paid';
      amountPaid = fee > 0 ? fee : amt;
      if (amountPaid <= 0) {
        showError('Set a course fee for this session, or enter an amount paid.');
        return;
      }
    } else if (addPayKind === 'part') {
      if (amt <= 0 && !isEditWithoutNewPayment) {
        showError('Enter how much has been paid so far.');
        return;
      }
      paymentStatus = 'half_paid';
      const cap = fee > 0 ? fee : amt;
      amountPaid = Math.min(amt, cap);
    } else if (addPayKind === 'prepaid') {
      if (amt <= 0 && !isEditWithoutNewPayment) {
        showError('Enter the prepaid amount.');
        return;
      }
      const cap = fee > 0 ? fee : amt;
      amountPaid = Math.min(amt, cap);
      if (fee > 0 && amountPaid >= fee - 0.005) paymentStatus = 'paid';
      else if (amountPaid > 0) paymentStatus = 'half_paid';
      else paymentStatus = 'unpaid';
    }
    if (amountPaid > 0 && !parsedPaymentDateIso) {
      showError('Payment date must be in DD/MM/YYYY format.');
      return;
    }

    const safePassProcessValue = String(addSafePassProcessNumber ?? '').trim() || null;
    const assignedCompanyCustomerId = customer.customerType === 'individual'
      ? String(addAssignedCompanyCustomerId || '').trim() || null
      : null;

    const syncSafePassToCustomer = async (sessionIdForNote: string, value: string | null) => {
      const prev = String((customer as any)?.safePassProcessNumber || '').trim() || null;
      if (prev === value) return;
      try {
        await supabaseCustomerStorage.updateCustomer({ ...(customer as any), safePassProcessNumber: value });
        const sessionRow = sessions.find((s) => s.id === sessionIdForNote);
        const datesSummary = sessionRow ? formatDateBlocksUk(sessionRow.dateBlocks) : '';
        await ClientNotesService.logClientTrainingUpdate(
          customer.id,
          { courseName: sessionRow?.courseName || 'Course session', datesSummary },
          value
            ? `Safe Pass process number updated from session: ${value}`
            : 'Safe Pass process number cleared from session',
          user?.id,
          actorName
        );
      } catch (err) {
        console.error('Failed to sync Safe Pass process number to CRM profile:', err);
      }
    };

    if (editingAttendee) {
      try {
        const ledger = paymentTimelineByAttendee[editingAttendee.id] || [];
        const enrollmentFee =
          editingAttendee.enrollmentCourseFee != null && Number(editingAttendee.enrollmentCourseFee) > 0
            ? Number(editingAttendee.enrollmentCourseFee)
            : 0;
        const ledgerRaw = sumAttendeeLedgerRaw(ledger);
        const paidTowardOldFee =
          ledger.length > 0 ? ledgerRaw : Math.max(0, Number(editingAttendee.amountPaid || 0));
        // Prefer stored enrollment fee; if missing but already paid in full, treat collected amount as the old fee.
        const oldFee =
          enrollmentFee > 0
            ? enrollmentFee
            : editingAttendee.paymentStatus === 'paid' && paidTowardOldFee > 0
              ? paidTowardOldFee
              : 0;
        const existingPaid = getExistingAttendeePaid(editingAttendee, ledger, fee);
        const paymentLineAmount = computeEditPaymentLineAmount(
          addPayKind,
          amt,
          existingPaid,
          fee,
          ledger.length > 0
        );
        const isCompanySeat =
          customer.customerType !== 'individual' ||
          Boolean(
            editingAttendee.companyCustomerId ||
              editingAttendee.sponsoringCompanyCustomerId ||
              assignedCompanyCustomerId
          );

        if (addPayKind === 'unpaid' && (ledger.length > 0 || paidTowardOldFee > 0.005)) {
          if (
            !window.confirm(
              'Clear all recorded payments for this attendee and mark as not paid? This undoes a mistaken paid-in-full (or any other payments).'
            )
          ) {
            return;
          }
        }

        await courseSessionsService.updateAttendee(editingAttendee.id, {
          enrollmentCourseFee: fee > 0 ? fee : null,
          safePassProcessNumber: safePassProcessValue,
          bookedByName: addBookedByName.trim() || null,
          companyCustomerId: assignedCompanyCustomerId,
          sponsoringCompanyCustomerId: assignedCompanyCustomerId,
          ...derivePayerForCompanyContext(assignedCompanyCustomerId, customer.id),
          ...(addPayKind === 'unpaid' && ledger.length === 0
            ? { paymentStatus: 'unpaid' as const, amountPaid: 0 }
            : {}),
        });

        let paymentLedgerTouched = false;

        const paymentDateForLedger =
          parsedPaymentDateIso || new Date().toISOString().slice(0, 10);

        const recordLedgerPayment = async (
          amount: number,
          payerNotes: string | null,
          stepType = addPaymentStepType
        ) => {
          if (!(amount > 0.005)) return;
          await courseSessionsService.addAttendeePayment({
            bookingId: sessionId,
            attendeeId: editingAttendee.id,
            amount,
            method: addPaymentMethod,
            paymentDate: paymentDateForLedger,
            stepType,
            payerNotes,
            attendeeLabel: getAttendeeLabel(editingAttendee),
          });
          paymentLedgerTouched = true;
        };

        const clearLedgerPayments = async () => {
          for (const p of ledger) {
            await courseSessionsService.deleteAttendeePayment(p.id, {
              bookingId: sessionId,
              attendeeLabel: getAttendeeLabel(editingAttendee),
            });
          }
          if (ledger.length > 0) paymentLedgerTouched = true;
        };

        if (addPayKind === 'unpaid') {
          await clearLedgerPayments();
          await courseSessionsService.syncAttendeePaidFromLedger(editingAttendee.id);
          paymentLedgerTouched = paymentLedgerTouched || paidTowardOldFee > 0.005;
        } else if (addPayKind === 'keep') {
          const feeCorrectionDelta = computePaidInFullFeeCorrectionDelta(oldFee, fee, paidTowardOldFee);
          if (Math.abs(feeCorrectionDelta) > 0.005 && fee > 0) {
            // Always leave a fresh Payment history line for the corrected paid-in-full fee.
            await clearLedgerPayments();
            await recordLedgerPayment(
              fee,
              addPaymentNote.trim() ||
                `Fee correction (paid in full): ?${oldFee.toFixed(2)} ? ?${fee.toFixed(2)}`
            );
          } else {
            await courseSessionsService.syncAttendeePaidFromLedger(editingAttendee.id);
          }
        } else if (addPayKind === 'full' && fee > 0) {
          // Remainder if ledger exists; otherwise write the full fee so history is never empty
          // when status is paid-in-full (covers snapshot-only / mistaken prior saves).
          const amountToRecord =
            ledger.length > 0 ? paymentLineAmount : fee;
          if (amountToRecord > 0.005) {
            if (ledger.length === 0 && paidTowardOldFee > 0.005) {
              // Replace snapshot-only paid state with a real history line.
              await courseSessionsService.updateAttendee(editingAttendee.id, {
                amountPaid: 0,
                paymentStatus: 'unpaid',
              });
            }
            await recordLedgerPayment(
              amountToRecord,
              addPaymentNote.trim() ||
                (ledger.length === 0 ? `Paid in full (?${fee.toFixed(2)})` : null)
            );
          } else {
            await courseSessionsService.syncAttendeePaidFromLedger(editingAttendee.id);
          }
        } else if (paymentLineAmount > 0 && parsedPaymentDateIso) {
          await recordLedgerPayment(paymentLineAmount, addPaymentNote.trim() || null);
        } else {
          await courseSessionsService.syncAttendeePaidFromLedger(editingAttendee.id);
        }

        let invoiceResult = { seatsSettled: 0, totalEuro: 0 };
        if (isCompanySeat) {
          invoiceResult = await applyCompanyInvoiceToCohort(
            attendeesBySession[sessionId] || [],
            editingAttendee,
            companyCustomers,
            getAttendeeCustomer,
            {
              invoiceNumber: addInvoiceNumber.trim() || null,
              invoicePaid: addInvoicePaid,
            },
            addInvoicePaid === true || addInvoicePaid === false
              ? makeInvoiceSettlement(sessionId, {
                  paymentMethod: addPaymentMethod,
                  paymentDate: parsedPaymentDateIso || undefined,
                })
              : null
          );
        }
        if (
          paymentLedgerTouched ||
          (isCompanySeat && (invoiceResult.seatsSettled > 0 || invoiceResult.totalEuro > 0))
        ) {
          await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: sessionId });
        }
        const sessionRow = sessions.find((s) => s.id === sessionId);
        const datesSummary = sessionRow ? formatDateBlocksUk(sessionRow.dateBlocks) : '';
        const attendeeDisplayName =
          customer.customerType === 'individual'
            ? `${customer.firstName || ''} ${customer.surname || ''}`.trim() || customer.companyName
            : customer.companyName;
        try {
          await ClientNotesService.logClientTrainingUpdate(
            customer.id,
            { courseName: sessionRow?.courseName || 'Course session', datesSummary },
            `Session enrollment updated: ${attendeeDisplayName} | fee ?${fee.toFixed(2)}`,
            user?.id,
            actorName
          );
        } catch (noteErr) {
          console.error('Failed to log client note:', noteErr);
        }
        await syncSafePassToCustomer(sessionId, safePassProcessValue);
        setAddAttendeeModal(null);
        await load();
        showSuccess(
          `Enrollment updated.${
            addInvoicePaid
              ? invoiceSettlementSuccessSuffix(invoiceResult)
              : invoiceUnsettlementSuccessSuffix(invoiceResult)
          }`
        );
      } catch (e: any) {
        showError(e?.message || 'Failed to update enrollment.');
      }
      return;
    }

    try {
      const linkedCompanyId = customer.customerType !== 'individual' ? customer.id : assignedCompanyCustomerId || undefined;
      await courseSessionsService.addAttendee(sessionId, {
        individualCustomerId: customer.customerType === 'individual' ? customer.id : undefined,
        companyCustomerId: linkedCompanyId,
        sponsoringCompanyCustomerId: linkedCompanyId,
        ...derivePayerForCompanyContext(linkedCompanyId, customer.customerType === 'individual' ? customer.id : null),
        paymentStatus,
        amountPaid,
        enrollmentCourseFee: fee > 0 ? fee : null,
        safePassProcessNumber: safePassProcessValue,
        bookedByName: addBookedByName.trim() || null,
        source: addAttendeeModalSource || undefined,
      });
      const refreshed = await courseSessionsService.listAttendees(sessionId);
      const linked = refreshed
        .filter((a) => a.individualCustomerId === customer.id)
        .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
        .slice(-1)[0];
      if (linked?.id) {
        if (amountPaid > 0 && parsedPaymentDateIso) {
          await courseSessionsService.addAttendeePayment({
            bookingId: sessionId,
            attendeeId: linked.id,
            amount: amountPaid,
            method: addPaymentMethod,
            paymentDate: parsedPaymentDateIso,
            stepType: addPaymentStepType,
            payerNotes: addPaymentNote.trim() || null
          });
        }
        if (addInvoiceNumber.trim() || addInvoicePaid || assignedCompanyCustomerId) {
          await applyCompanyInvoiceToCohort(
            refreshed,
            linked,
            companyCustomers,
            getAttendeeCustomer,
            {
              invoiceNumber: addInvoiceNumber.trim() || null,
              invoicePaid: addInvoicePaid,
            },
            addInvoicePaid === true || addInvoicePaid === false
              ? makeInvoiceSettlement(sessionId, {
                  paymentMethod: addPaymentMethod,
                  paymentDate: parsedPaymentDateIso || undefined,
                })
              : null
          );
        }
        if (amountPaid > 0) {
          await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: sessionId });
        }
      }
      const sessionRow = sessions.find((s) => s.id === sessionId);
      const datesSummary = sessionRow ? formatDateBlocksUk(sessionRow.dateBlocks) : '';
      let paymentLabel = 'Not paid';
      if (paymentStatus === 'paid') {
        paymentLabel = `Paid in full (?${amountPaid.toFixed(2)})`;
      } else if (paymentStatus === 'half_paid') {
        paymentLabel = `Part paid (?${amountPaid.toFixed(2)})`;
      }
      const attendeeDisplayName =
        customer.customerType === 'individual'
          ? `${customer.firstName || ''} ${customer.surname || ''}`.trim() || customer.companyName
          : customer.companyName;
      const sessionPaymentLine = (() => {
        if (addPayKind === 'unpaid') return 'Not paid yet';
        if (addPayKind === 'full') return `Paid in full (?${amountPaid.toFixed(2)})`;
        if (addPayKind === 'part') return `Partial payment (?${amountPaid.toFixed(2)})`;
        if (addPayKind === 'prepaid') {
          if (paymentStatus === 'paid') return `Prepaid ? full amount (?${amountPaid.toFixed(2)})`;
          return `Deposit / prepaid (?${amountPaid.toFixed(2)})`;
        }
        return paymentLabel;
      })();
      try {
        await ClientNotesService.logClientTrainingUpdate(
          customer.id,
          { courseName: sessionRow?.courseName || 'Course session', datesSummary },
          `Attendee added: ${attendeeDisplayName} | ${sessionPaymentLine}`,
          user?.id,
          actorName
        );
      } catch (noteErr: any) {
        console.error('Failed to log client note:', noteErr);
        showError(
          noteErr?.message
            ? `Attendee added, but activity note was not saved: ${noteErr.message}`
            : 'Attendee added, but activity note could not be saved. Check the browser console or database policies for client_notes.'
        );
      }
      await syncSafePassToCustomer(sessionId, safePassProcessValue);
      setAddAttendeeModal(null);
      setSearchBySession((p) => ({ ...p, [sessionId]: '' }));
      await load();
      showSuccess('Attendee added.');
    } catch (e: any) {
      const msg = String(e?.message || '');
      const duplicate = msg.toLowerCase().includes('already booked') || msg.toLowerCase().includes('no-show') || msg.toLowerCase().includes('duplicate');
      if (duplicate) {
        setAddAttendeeModal(null);
        showError(msg || 'This person is already booked on this session.');
        return;
      }
      showError(msg || 'Failed to add attendee.');
    }
    } finally {
      addAttendeeSavingRef.current = false;
      setAddAttendeeSaving(false);
    }
  };

  const addSelected = (sessionId: string) => {
    const search = (searchBySession[sessionId] || '').trim().toLowerCase();
    if (!search) {
      showError('Enter a name, email, or phone to search before using Add match.');
      return;
    }
    const match = allCustomers.find((c) => {
      if (!isIndividualCustomerRecord(c)) return false;
      const name = `${c.firstName || ''} ${c.surname || ''}`.trim();
      return [name, c.companyName, c.emailAddress, c.contactPhoneNumber].some((v) => (v || '').toLowerCase().includes(search));
    });
    if (!match) return showError('No attendee match found.');
    openAddAttendeeModal(sessionId, match);
  };


  const softRevokeSeatCertificates = async (
    attendee: CourseSessionAttendee,
    reason: string
  ): Promise<void> => {
    if (!selectedSession || attendee.isVirtual) return;
    const customerId = getAttendeeCustomerId(attendee);
    if (!customerId) return;
    try {
      await revokeCertificatesForSessionSeat({
        customerId,
        sessionId: selectedSession.id,
        attendeeId: attendee.id,
        reason,
      });
    } catch (e) {
      console.error('soft-revoke seat certificates failed', e);
    }
  };

  const removeAttendeeFromBooking = async (attendeeId: string) => {
    if (!selectedSession) return;
    const attendee = (attendeesBySession[selectedSession.id] || []).find((x) => x.id === attendeeId);
    if (!attendee) return;
    const customer = getAttendeeCustomer(attendee);
    const companyCtx = resolveCompanyBookingContext(attendee, customer, companyCustomers);
    if (companyCtx) {
      await unassignAttendeeToCompanyVirtualSeat(attendee);
      return;
    }
    if (!window.confirm('Remove this person from this booking?')) return;
    const label = getAttendeeLabel(attendee);
    try {
      await softRevokeSeatCertificates(attendee, 'Removed from main roster');
      await courseSessionsService.removeAttendee(attendeeId);
      const removedCustomerId = attendee ? getAttendeeCustomerId(attendee) : null;
      if (removedCustomerId) {
        const datesSummary = formatDateBlocksUk(selectedSession.dateBlocks);
        try {
          await ClientNotesService.logClientTrainingUpdate(
            removedCustomerId,
            { courseName: selectedSession.courseName, datesSummary },
            `Attendee removed from session: ${label}`,
            user?.id,
            actorName
          );
        } catch (noteErr) {
          console.error('Failed to log client note:', noteErr);
        }
      }
      await load();
      promptSeatOpenedIfApplicable(selectedSession.id, label);
      showSuccess('Removed from booking.');
    } catch (e: any) {
      showError(e?.message || 'Could not remove attendee.');
    }
  };

  const unassignAttendeeToCompanyVirtualSeat = async (attendee: CourseSessionAttendee) => {
    if (!selectedSession || attendee.isVirtual) return;
    const attendeeCustomer = getAttendeeCustomer(attendee);
    const companyCtx = resolveCompanyBookingContext(attendee, attendeeCustomer, companyCustomers);
    if (!companyCtx) {
      showError('This attendee is not linked to a company booking.');
      return;
    }
    const { companyId: linkedCompanyId, companyName } = companyCtx;
    if (!window.confirm(`Remove this attendee and keep the seat reserved under "${companyName}"?`)) return;
    try {
      await softRevokeSeatCertificates(attendee, 'Unassigned to company virtual seat');
      await courseSessionsService.updateAttendee(attendee.id, {
        isVirtual: true,
        placeholderLabel: companyName,
        individualCustomerId: null,
        companyCustomerId: linkedCompanyId,
        sponsoringCompanyCustomerId: linkedCompanyId,
        ...derivePayerForCompanyContext(linkedCompanyId, null)
      });
      await load();
      promptSeatOpenedIfApplicable(selectedSession.id, getAttendeeLabel(attendee));
      showSuccess('Attendee removed; seat is now reserved for the company.');
    } catch (e: any) {
      showError(e?.message || 'Could not remove attendee from company booking.');
    }
  };

  const restoreOrphanedAsVirtual = async (attendee: CourseSessionAttendee) => {
    if (!selectedSession) return;
    if (!window.confirm('The linked CRM record no longer exists. Restore this seat as a virtual (reserved) seat?')) return;
    try {
      const companyId = attendee.companyCustomerId || attendee.sponsoringCompanyCustomerId || null;
      const companyRecord = companyId ? companyCustomers.find((co) => co.id === companyId) : null;
      const placeholderLabel = companyRecord?.companyName || 'Restored seat';
      await courseSessionsService.updateAttendee(attendee.id, {
        isVirtual: true,
        placeholderLabel,
        individualCustomerId: null
      });
      await load();
      showSuccess('Seat restored as a virtual reservation.');
    } catch (e: any) {
      showError(e?.message || 'Failed to restore virtual seat.');
    }
  };

  const reserveVirtualSpots = async () => {
    if (!selectedSession) return;
    const matchedCompany =
      currentCustomer?.customerType === 'company'
        ? currentCustomer
        : companyCustomers.find(
            (co) =>
              String(co.companyName || '').trim().toLowerCase() ===
              String(currentCustomer?.companyName || '').trim().toLowerCase()
          );
    if (matchedCompany) upsertCustomersInMemory([matchedCompany]);
    resetCompanyPicker(matchedCompany ? String(matchedCompany.companyName || '') : '');
    const courseDefault = getCourseCatalogDefaultFee(selectedSession.courseName, courseDbFees, selectedSession.language);
    setVirtualFeeLocked(courseDefault > 0);
    setReserveVirtualModal({
      countStr: '1',
      companyCustomerId: matchedCompany?.id || '',
      enrollmentCourseFee: courseDefault > 0 ? courseDefault.toFixed(2) : '',
      payKind: 'unpaid',
      amountStr: '',
      paymentMethod: 'cash',
      source: '',
    });
  };

  const normalizeStrictPhone = (value: string): string => value.trim().replace(/\s+/g, '');

  const matchParticipantDraftToCustomer = (row: CompanyBookingParticipantDraft): Customer | null => {
    const firstName = row.firstName.trim().toLowerCase();
    const surname = row.surname.trim().toLowerCase();
    const emailLower = row.emailAddress.trim().toLowerCase();
    const phoneDigits = phoneDigitsOnly(row.contactPhoneNumber);
    if (!firstName || !surname) return null;
    if (emailLower) {
      const byEmail = individualCustomers.filter((c) => {
        const candidateFirst = String(c.firstName || '').trim().toLowerCase();
        const candidateSurname = String(c.surname || '').trim().toLowerCase();
        return (
          String(c.emailAddress || '').trim().toLowerCase() === emailLower &&
          candidateFirst === firstName &&
          candidateSurname === surname
        );
      });
      if (byEmail.length === 1) return byEmail[0]!;
    }
    if (phoneDigits.length >= 8) {
      const hits = individualCustomers.filter((c) => {
        const candidateFirst = String(c.firstName || '').trim().toLowerCase();
        const candidateSurname = String(c.surname || '').trim().toLowerCase();
        const candidateDigits = phoneDigitsOnly(String(c.contactPhoneNumber || ''));
        return candidateFirst === firstName && candidateSurname === surname && candidateDigits === phoneDigits;
      });
      if (hits.length === 1) return hits[0]!;
    }
    return null;
  };

  const crmCandidateMatchesParticipantRow = (
    row: CompanyBookingParticipantDraft,
    candidate?: Customer
  ): boolean => {
    if (!candidate) return false;
    const firstName = row.firstName.trim().toLowerCase();
    const surname = row.surname.trim().toLowerCase();
    const candidateFirst = String(candidate.firstName || '').trim().toLowerCase();
    const candidateSurname = String(candidate.surname || '').trim().toLowerCase();
    if (!firstName || !surname || candidateFirst !== firstName || candidateSurname !== surname) {
      return false;
    }
    const emailLower = row.emailAddress.trim().toLowerCase();
    const phoneDigits = phoneDigitsOnly(row.contactPhoneNumber);
    const candidateEmail = String(candidate.emailAddress || '').trim().toLowerCase();
    const candidatePhone = phoneDigitsOnly(String(candidate.contactPhoneNumber || ''));
    if (emailLower && candidateEmail === emailLower) return true;
    if (phoneDigits.length >= 8 && candidatePhone === phoneDigits) return true;
    return !emailLower && phoneDigits.length < 8;
  };

  const companyBookingMatchCandidates = (row: CompanyBookingParticipantDraft): Customer[] => {
    const firstName = row.firstName.trim().toLowerCase();
    const surname = row.surname.trim().toLowerCase();
    if (!firstName || !surname) return [];
    return individualCustomers.filter((candidate) => crmCandidateMatchesParticipantRow(row, candidate));
  };

  const matchIndividualByNameOnly = (firstName: string, surname: string): Customer | null => {
    const fn = firstName.trim().toLowerCase();
    const sn = surname.trim().toLowerCase();
    if (!fn || !sn) return null;
    const hits = individualCustomers.filter((c) => {
      const candidateFirst = String(c.firstName || '').trim().toLowerCase();
      const candidateSurname = String(c.surname || '').trim().toLowerCase();
      return candidateFirst === fn && candidateSurname === sn;
    });
    if (hits.length === 1) return hits[0]!;
    if (hits.length > 1) {
      throw new Error(
        `Multiple CRM matches for ${firstName} ${surname} ? use single-seat fill to pick the right person.`
      );
    }
    return null;
  };

  const openCompanyBookingModal = (sessionId: string) => {
    if (rejectIfCourseCancelled(sessionId)) return;
    if (rejectIfCourseFullyBooked(sessionId)) return;
    const sessionRow = sessions.find((s) => s.id === sessionId);
    if (!sessionRow) return;
    const matchedCompany =
      currentCustomer?.customerType === 'company'
        ? currentCustomer
        : companyCustomers.find(
            (co) =>
              String(co.companyName || '').trim().toLowerCase() ===
              String(currentCustomer?.companyName || '').trim().toLowerCase()
          );
    if (matchedCompany) upsertCustomersInMemory([matchedCompany]);
    resetCompanyPicker(matchedCompany ? String(matchedCompany.companyName || '') : '');
    const courseDefault = getCourseCatalogDefaultFee(sessionRow.courseName, courseDbFees, sessionRow.language);
    setCompanyBookingModal({
      sessionId,
      companyCustomerId: matchedCompany?.id || '',
      totalSeats: '1',
      pastedParticipants: '',
      parsedRows: [],
      payKind: 'unpaid',
      amountStr: '',
      paymentMethod: 'cash',
      enrollmentCourseFee: courseDefault > 0 ? courseDefault.toFixed(2) : '',
      feeLocked: courseDefault > 0,
      source: 'bulk_import',
      invoiceNumber: '',
      invoicePaid: false,
      paymentReference: '',
      bookedByName: matchedCompany?.companyName || '',
    });
  };

  const buildCompanyBookingParsedRows = (modal: NonNullable<typeof companyBookingModal>) => {
    const company = companyCustomers.find((co) => co.id === modal.companyCustomerId);
    const preferredLanguage = company?.preferredLanguage || 'English';
    const previousByKey = new Map((modal.parsedRows || []).map((row) => [participantDraftKey(row), row]));
    return parseParticipantLines(modal.pastedParticipants, preferredLanguage).map((row) => {
      const match = matchParticipantDraftToCustomer(row);
      const hasFullName = !!row.firstName.trim() && !!row.surname.trim();
      const suggestedAction: CompanyBookingParticipantDraft['action'] = !hasFullName
        ? 'reserve_seat'
        : match
        ? 'match_existing'
        : 'create_new';
      const prev = previousByKey.get(participantDraftKey(row));
      let action = prev?.action ?? suggestedAction;
      let matchCustomerId: string | null = null;
      if (action === 'match_existing') {
        const manualStillValid =
          prev?.matchCustomerId &&
          crmCandidateMatchesParticipantRow(row, individualCustomers.find((c) => c.id === prev.matchCustomerId));
        if (manualStillValid) {
          matchCustomerId = prev!.matchCustomerId;
        } else if (match?.id) {
          matchCustomerId = match.id;
        } else {
          action = 'create_new';
          matchCustomerId = null;
        }
      }
      return {
        ...row,
        id: prev?.id || row.id,
        preferredLanguage: prev?.preferredLanguage || row.preferredLanguage,
        safePassProcessNumber: prev?.safePassProcessNumber || row.safePassProcessNumber,
        matchCustomerId,
        action,
        parseError: participantRowParseError({ ...row, action }),
      };
    });
  };

  const selectCompanyForBooking = (company: Customer) => {
    upsertCustomersInMemory([company]);
    setCompanyBookingModal((prev) =>
      prev
        ? {
            ...prev,
            companyCustomerId: company.id,
            bookedByName: prev.bookedByName.trim() || String(company.companyName || ''),
            parsedRows: buildCompanyBookingParsedRows({ ...prev, companyCustomerId: company.id }),
          }
        : prev
    );
    setCompanyPickerQuery(String(company.companyName || ''));
    setCompanyPickerHits([]);
  };

  const clearCompanyBookingSelection = () => {
    setCompanyBookingModal((prev) =>
      prev
        ? {
            ...prev,
            companyCustomerId: '',
            parsedRows: buildCompanyBookingParsedRows({ ...prev, companyCustomerId: '' }),
          }
        : prev
    );
    resetCompanyPicker();
  };

  const parseCompanyBookingParticipants = () => {
    setCompanyBookingModal((prev) => (prev ? { ...prev, parsedRows: buildCompanyBookingParsedRows(prev) } : prev));
  };

  const derivePayerForCompanyContext = (companyId: string | null | undefined, individualId?: string | null) => {
    const company = companyId ? companyCustomers.find((co) => co.id === companyId) : undefined;
    const role = company?.companyRole || 'payer';
    if (role === 'beneficiary') {
      return {
        payerType: 'individual' as const,
        payerCustomerId: individualId || null
      };
    }
    return {
      payerType: 'company' as const,
      payerCustomerId: companyId || null
    };
  };

  const updateCompanyBookingRow = (
    rowId: string,
    patch: Partial<CompanyBookingParticipantDraft>
  ) => {
    setCompanyBookingModal((prev) =>
      prev
        ? {
            ...prev,
            parsedRows: prev.parsedRows.map((item) => {
              if (item.id !== rowId) return item;
              const next = { ...item, ...patch };
              return {
                ...next,
                parseError: participantRowParseError(next),
              };
            })
          }
        : prev
    );
  };

  const confirmReserveVirtualSpots = async () => {
    if (!selectedSession || !reserveVirtualModal) return;
    const count = Number(reserveVirtualModal.countStr || 0);
    if (!Number.isFinite(count) || count <= 0) {
      showError('Enter how many virtual places to reserve.');
      return;
    }
    const selectedCompany = companyCustomers.find((co) => co.id === reserveVirtualModal.companyCustomerId);
    const label = String(selectedCompany?.companyName || currentCustomer?.companyName || '').trim();
    if (!label) {
      showError('Select a company for these reserved places.');
      return;
    }

    const fee = parseEuroInput(reserveVirtualModal.enrollmentCourseFee);
    const amt = parseEuroInput(reserveVirtualModal.amountStr);
    let paymentStatus: 'unpaid' | 'half_paid' | 'paid' = 'unpaid';
    let amountPaid = 0;
    if (reserveVirtualModal.payKind === 'unpaid') {
      paymentStatus = 'unpaid';
      amountPaid = 0;
    } else if (reserveVirtualModal.payKind === 'full') {
      paymentStatus = 'paid';
      amountPaid = fee > 0 ? fee : amt;
      if (amountPaid <= 0) {
        showError('Set a course fee for this session, or enter an amount paid.');
        return;
      }
    } else if (reserveVirtualModal.payKind === 'part') {
      if (amt <= 0) {
        showError('Enter how much has been paid so far.');
        return;
      }
      paymentStatus = 'half_paid';
      const cap = fee > 0 ? fee : amt;
      amountPaid = Math.min(amt, cap);
    } else if (reserveVirtualModal.payKind === 'prepaid') {
      if (amt <= 0) {
        showError('Enter the prepaid amount.');
        return;
      }
      const cap = fee > 0 ? fee : amt;
      amountPaid = Math.min(amt, cap);
      if (fee > 0 && amountPaid >= fee - 0.005) paymentStatus = 'paid';
      else if (amountPaid > 0) paymentStatus = 'half_paid';
      else paymentStatus = 'unpaid';
    }

    if (!reserveVirtualModal.source) {
      showError('Pick how this booking reached us (Source).');
      return;
    }

    try {
      const linkedCompanyId = reserveVirtualModal.companyCustomerId?.trim() || null;
      const perSeatPayments = splitPaymentAcrossSeats(amountPaid, count);
      const reservedRows = await courseSessionsService.reserveVirtualSpots(selectedSession.id, {
        count,
        placeholderLabel: label,
        companyCustomerId: linkedCompanyId,
        ...derivePayerForCompanyContext(linkedCompanyId, null),
        enrollmentCourseFee: fee > 0 ? fee : null,
        paymentStatus: 'unpaid',
        amountPaid: 0,
        bookedByName: reserveVirtualModal.companyCustomerId
          ? (companyCustomers.find((co) => co.id === reserveVirtualModal.companyCustomerId)?.companyName || null)
          : null,
        bookedByCustomerId: reserveVirtualModal.companyCustomerId?.trim() || null,
        source: reserveVirtualModal.source as InboundChannel,
      });
      if (amountPaid > 0) {
        const paymentDate = new Date().toISOString().slice(0, 10);
        for (let i = 0; i < reservedRows.length; i++) {
          const seatPaid = perSeatPayments[i] ?? 0;
          if (seatPaid <= 0) continue;
          await courseSessionsService.addAttendeePayment({
            bookingId: selectedSession.id,
            attendeeId: reservedRows[i].id,
            amount: seatPaid,
            method: reserveVirtualModal.paymentMethod,
            paymentDate,
            stepType: reserveVirtualModal.payKind === 'prepaid' ? 'deposit' : 'on_day'
          });
          await courseSessionsService.syncAttendeePaidFromLedger(reservedRows[i].id);
        }
        await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: selectedSession.id });
      }
      await load();
      setReserveVirtualModal(null);
      showSuccess(`${count} virtual place(s) reserved.`);
    } catch (e: any) {
      showError(e?.message || 'Could not reserve virtual places.');
    }
  };

  const confirmCompanyBooking = async () => {
    if (!companyBookingModal) return;
    if (companyBookingSavingRef.current) return;
    companyBookingSavingRef.current = true;
    setCompanyBookingSaving(true);
    try {
    const session = sessions.find((s) => s.id === companyBookingModal.sessionId);
    if (!session) return showError('Session not found.');
    if ((session.status || 'scheduled') === 'cancelled') {
      return showError('This course session has been cancelled.');
    }
    const company = companyCustomers.find((co) => co.id === companyBookingModal.companyCustomerId);
    if (!company) return showError('Select a company for this booking.');

    const totalSeats = Number(companyBookingModal.totalSeats || 0);
    if (!Number.isFinite(totalSeats) || totalSeats <= 0) {
      return showError('Enter how many seats to book.');
    }

    const parsedFresh =
      companyBookingModal.parsedRows.length > 0
        ? companyBookingModal.parsedRows.map((row) => ({
            ...row,
            parseError: participantRowParseError(row),
          }))
        : buildCompanyBookingParsedRows(companyBookingModal);
    const invalidParticipant = parsedFresh.find((row) => row.parseError && row.action !== 'reserve_seat');
    if (invalidParticipant?.parseError) {
      return showError(invalidParticipant.parseError);
    }
    const namedRowsRaw = parsedFresh.filter((row) => row.action !== 'reserve_seat' && !row.parseError);
    const seenMatchIds = new Set<string>();
    const seenNewKeys = new Set<string>();
    const namedRows: typeof namedRowsRaw = [];
    for (const row of namedRowsRaw) {
      if (row.action === 'match_existing' && row.matchCustomerId) {
        if (seenMatchIds.has(row.matchCustomerId)) continue;
        seenMatchIds.add(row.matchCustomerId);
        namedRows.push(row);
        continue;
      }
      if (row.action === 'create_new') {
        const nk = `${row.firstName.trim().toLowerCase()}|${row.surname.trim().toLowerCase()}|${row.emailAddress.trim().toLowerCase()}|${phoneDigitsOnly(row.contactPhoneNumber)}`;
        if (seenNewKeys.has(nk)) continue;
        seenNewKeys.add(nk);
        namedRows.push(row);
      }
    }
    if (namedRows.length > totalSeats) {
      return showError('Named participants cannot exceed the total seats booked.');
    }
    const missingContact = namedRows.find(
      (row) => row.action === 'create_new' && !row.emailAddress.trim() && !row.contactPhoneNumber.trim()
    );
    if (missingContact) {
      const who = `${missingContact.firstName} ${missingContact.surname}`.trim() || 'Participant';
      return showError(`${who} needs an email or mobile number.`);
    }

    const fee = parseEuroInput(companyBookingModal.enrollmentCourseFee);
    const amt = parseEuroInput(companyBookingModal.amountStr);
    let paymentStatus: 'unpaid' | 'half_paid' | 'paid' = 'unpaid';
    let amountPaid = 0;
    if (companyBookingModal.payKind === 'unpaid') {
      paymentStatus = 'unpaid';
    } else if (companyBookingModal.payKind === 'full') {
      paymentStatus = 'paid';
      amountPaid = fee > 0 ? fee * totalSeats : amt;
      if (amountPaid <= 0) return showError('Set a course fee for this session, or enter an amount paid.');
    } else if (companyBookingModal.payKind === 'part') {
      if (amt <= 0) return showError('Enter how much has been paid so far.');
      paymentStatus = 'half_paid';
      amountPaid = Math.min(amt, fee > 0 ? fee : amt);
    } else if (companyBookingModal.payKind === 'prepaid') {
      if (amt <= 0) return showError('Enter the prepaid amount.');
      amountPaid = Math.min(amt, fee > 0 ? fee : amt);
      if (fee > 0 && amountPaid >= fee - 0.005) paymentStatus = 'paid';
      else if (amountPaid > 0) paymentStatus = 'half_paid';
    }

    const bookedIndividualIds = new Set(
      (attendeesBySession[session.id] || [])
        .filter((a) => isAttendeeOnActiveRoster(a) && a.individualCustomerId)
        .map((a) => a.individualCustomerId as string)
    );

    try {
      const createdOrMatchedIds: string[] = [];
      const batchIds = new Set<string>();
      type ResolvedRow = (typeof namedRows)[number] & { individualCustomerId: string };
      const resolved: ResolvedRow[] = [];
      for (const row of namedRows) {
        let individualCustomerId = row.action === 'match_existing' ? row.matchCustomerId : null;
        if (row.action === 'create_new') {
          let existingMatch = matchParticipantDraftToCustomer(row);
          if (!existingMatch) {
            const q = `${row.firstName} ${row.surname}`.trim();
            if (q) {
              const rows = await supabaseCustomerStorage.getCustomersList(20, 0, q, 'individual');
              upsertCustomersInMemory((rows || []).map((c) => mapCustomerRow(c as any)));
              existingMatch = matchParticipantDraftToCustomer(row);
            }
          }
          if (existingMatch) {
            individualCustomerId = existingMatch.id;
          } else {
          const customerPayload: Customer = {
            id: crypto.randomUUID(),
            customerType: 'individual',
            companyName: company.companyName,
            preferredLanguage: row.preferredLanguage || company.preferredLanguage || 'English',
            firstName: row.firstName.trim(),
            surname: row.surname.trim(),
            contactPhoneNumber: row.contactPhoneNumber.trim() || undefined,
            emailAddress: row.emailAddress.trim() || undefined,
            safePassProcessNumber: row.safePassProcessNumber.trim() || undefined,
            locations: [],
            contacts: [],
            paymentHistory: [],
            generatedCertificates: [],
            numCertsGenerated: 0,
            isPaid: false,
            lastPaymentDate: '',
            currentBatchCertificates: []
          };
          await persistNewCustomer(customerPayload);
          individualCustomerId = customerPayload.id;
          upsertCustomersInMemory([customerPayload]);
          }
        }
        if (!individualCustomerId) continue;
        if (bookedIndividualIds.has(individualCustomerId) || batchIds.has(individualCustomerId)) {
          const who = `${row.firstName} ${row.surname}`.trim() || 'Participant';
          throw new Error(`${who} is already booked on this session.`);
        }
        batchIds.add(individualCustomerId);
        resolved.push({ ...row, individualCustomerId });
      }

      const reservedSeatCount = Math.max(0, totalSeats - namedRows.length);
      const totalSeatCount = resolved.length + reservedSeatCount;
      const deferPaymentsToInvoiceSettlement = companyBookingModal.invoicePaid;
      const perSeatPayments = splitPaymentAcrossSeats(amountPaid, totalSeatCount);
      let seatPaymentIndex = 0;

      for (const row of resolved) {
        const { individualCustomerId } = row;
        const seatPaid = capPaidToFee(perSeatPayments[seatPaymentIndex++] ?? 0, fee);
        const seatStatus = derivePaymentStatusFromPaid(seatPaid, fee);
        await courseSessionsService.addAttendee(session.id, {
          individualCustomerId,
          companyCustomerId: company.id,
          sponsoringCompanyCustomerId: company.id,
          ...derivePayerForCompanyContext(company.id, individualCustomerId),
          paymentStatus: seatStatus,
          amountPaid: seatPaid,
          enrollmentCourseFee: fee > 0 ? fee : null,
          safePassProcessNumber: row.safePassProcessNumber.trim() || null,
          bookedByName: companyBookingModal.bookedByName.trim() || company.companyName || null,
          bookedByCustomerId: company.id,
          source: companyBookingModal.source,
        });
        createdOrMatchedIds.push(individualCustomerId);
        const refreshed = await courseSessionsService.listAttendees(session.id);
        const linked = refreshed
          .filter((a) => a.individualCustomerId === individualCustomerId)
          .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
          .slice(-1)[0];
        if (linked?.id) {
          if (seatPaid > 0 && !deferPaymentsToInvoiceSettlement) {
            await courseSessionsService.addAttendeePayment({
              bookingId: session.id,
              attendeeId: linked.id,
              amount: seatPaid,
              method: companyBookingModal.paymentMethod,
              paymentDate: new Date().toISOString().slice(0, 10),
              stepType: companyBookingModal.payKind === 'prepaid' ? 'deposit' : 'on_day',
              payerNotes: companyBookingModal.paymentReference.trim() || null,
            });
            await courseSessionsService.syncAttendeePaidFromLedger(linked.id);
          }
        }
      }

      if (reservedSeatCount > 0) {
        const reserveDraftRows = parsedFresh.filter((row) => row.action === 'reserve_seat').slice(0, reservedSeatCount);
        const externalBooker = companyBookingModal.bookedByName.trim() || company.companyName || null;
        const reservedIds: Array<{ id: string }> = [];
        for (const draft of reserveDraftRows) {
          const created = await courseSessionsService.reserveVirtualSpots(session.id, {
            count: 1,
            placeholderLabel: company.companyName,
            companyCustomerId: company.id,
            ...derivePayerForCompanyContext(company.id, null),
            enrollmentCourseFee: fee > 0 ? fee : null,
            paymentStatus: 'unpaid',
            amountPaid: 0,
            bookedByName: externalBooker,
            bookedByCustomerId: company.id,
            safePassProcessNumber: draft.safePassProcessNumber.trim() || null,
            source: companyBookingModal.source,
          });
          reservedIds.push(...created);
        }
        const remainingReserved = reservedSeatCount - reserveDraftRows.length;
        if (remainingReserved > 0) {
          const created = await courseSessionsService.reserveVirtualSpots(session.id, {
            count: remainingReserved,
            placeholderLabel: company.companyName,
            companyCustomerId: company.id,
            ...derivePayerForCompanyContext(company.id, null),
            enrollmentCourseFee: fee > 0 ? fee : null,
            paymentStatus: 'unpaid',
            amountPaid: 0,
            bookedByName: externalBooker,
            bookedByCustomerId: company.id,
            source: companyBookingModal.source,
          });
          reservedIds.push(...created);
        }
        const reservedRows = reservedIds;
        if (amountPaid > 0 && !deferPaymentsToInvoiceSettlement) {
          const paymentDate = new Date().toISOString().slice(0, 10);
          for (const row of reservedRows) {
            const seatPaid = capPaidToFee(perSeatPayments[seatPaymentIndex++] ?? 0, fee);
            if (seatPaid <= 0) continue;
            await courseSessionsService.addAttendeePayment({
              bookingId: session.id,
              attendeeId: row.id,
              amount: seatPaid,
              method: companyBookingModal.paymentMethod,
              paymentDate,
              stepType: companyBookingModal.payKind === 'prepaid' ? 'deposit' : 'on_day',
              payerNotes: companyBookingModal.paymentReference.trim() || null,
            });
            await courseSessionsService.syncAttendeePaidFromLedger(row.id);
          }
        }
      }

      const inv = companyBookingModal.invoiceNumber.trim();
      if (inv || companyBookingModal.invoicePaid) {
        const allAttendees = await courseSessionsService.listAttendees(session.id);
        const anchor = allAttendees.find((a) => a.companyCustomerId === company.id);
        if (anchor) {
          await applyCompanyInvoiceToCohort(
            allAttendees,
            anchor,
            companyCustomers,
            getAttendeeCustomer,
            {
              invoiceNumber: inv || null,
              invoicePaid: companyBookingModal.invoicePaid,
            },
            companyBookingModal.invoicePaid === true || companyBookingModal.invoicePaid === false
              ? makeInvoiceSettlement(session.id, {
                  paymentMethod: companyBookingModal.paymentMethod,
                  paymentReference: companyBookingModal.paymentReference.trim() || null,
                })
              : null
          );
        }
      }

      if (amountPaid > 0) {
        await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: session.id });
      }
      await load();
      setCompanyBookingModal(null);
      showSuccess('Company booking added.');
    } catch (e: any) {
      showError(e?.message || 'Could not create company booking.');
    }
    } finally {
      companyBookingSavingRef.current = false;
      setCompanyBookingSaving(false);
    }
  };

  const replaceVirtualWithCustomer = async (attendee: CourseSessionAttendee) => {
    if (!selectedSession || !attendee.isVirtual) return;
    setReplaceVirtualModal({ attendee, query: '' });
  };


  const openBulkVirtualFillModal = (groupLabel: string, attendees: CourseSessionAttendee[]) => {
    const virtualSeatIds = attendees
      .filter((a) => a.isVirtual)
      .sort((a, b) =>
        String(a.reservedCreatedAt || a.createdAt || '').localeCompare(
          String(b.reservedCreatedAt || b.createdAt || '')
        )
      )
      .map((a) => a.id);
    if (virtualSeatIds.length === 0) {
      showError('No virtual seats in this group to fill.');
      return;
    }
    setBulkVirtualFillModal({ groupLabel, attendeeIds: virtualSeatIds, pasted: '' });
  };

  const submitBulkVirtualFill = async () => {
    if (!bulkVirtualFillModal || !selectedSession) return;
    if (bulkVirtualFillSavingRef.current) return;
    bulkVirtualFillSavingRef.current = true;
    setBulkVirtualFillSaving(true);
    try {
      const { attendeeIds, pasted } = bulkVirtualFillModal;
      const lines = participantLinesFromPaste(pasted);
      if (lines.length === 0) {
        showError('Paste at least one full name (one per line).');
        return;
      }
      const pairs = lines.slice(0, attendeeIds.length).map((line, idx) => {
        const { firstName, surname } = splitParticipantName(line);
        return { attendeeId: attendeeIds[idx]!, firstName: firstName.trim(), surname: surname.trim() };
      });
      if (!pairs.every((p) => p.firstName)) {
        showError('Each line needs at least a first name. Use one full name per line.');
        return;
      }
      const allAttendees = selectedAttendees;
      const createdOrMatchedByName = new Map<string, string>();
      let linkedCount = 0;
      let createdCount = 0;
      let filledCount = 0;
      for (const p of pairs) {
        const attendee = allAttendees.find((a) => a.id === p.attendeeId);
        if (!attendee?.isVirtual) continue;

        const companyCustomerId = attendee.companyCustomerId || null;
        const companyName = companyCustomerId
          ? companyCustomers.find((co) => co.id === companyCustomerId)?.companyName || ''
          : '';

        const nameKey = `${p.firstName.trim().toLowerCase()}|${p.surname.trim().toLowerCase()}`;
        let individualCustomerId = createdOrMatchedByName.get(nameKey);

        if (!individualCustomerId) {
          let existingMatch = matchIndividualByNameOnly(p.firstName, p.surname);
          if (!existingMatch) {
            const q = `${p.firstName} ${p.surname}`.trim();
            if (q) {
              const rows = await supabaseCustomerStorage.getCustomersList(20, 0, q, 'individual');
              upsertCustomersInMemory((rows || []).map((c) => mapCustomerRow(c as any)));
              existingMatch = matchIndividualByNameOnly(p.firstName, p.surname);
            }
          }
          if (existingMatch) {
            individualCustomerId = existingMatch.id;
            linkedCount += 1;
          } else {
            const newCust: Customer = {
              id: crypto.randomUUID(),
              customerType: 'individual',
              companyName: companyName,
              firstName: p.firstName,
              surname: p.surname,
              preferredLanguage: selectedSession.language || 'English',
              emailAddress: '',
              contactPhoneNumber: '',
              locations: [],
              contacts: [],
              paymentHistory: [],
              generatedCertificates: [],
              numCertsGenerated: 0,
              isPaid: false,
              lastPaymentDate: '',
              currentBatchCertificates: []
            };
            await persistNewCustomer(newCust);
            individualCustomerId = newCust.id;
            upsertCustomersInMemory([newCust]);
            createdCount += 1;
          }
          createdOrMatchedByName.set(nameKey, individualCustomerId);
        }

        await courseSessionsService.convertVirtualAttendeeToReal({
          attendeeId: p.attendeeId,
          individualCustomerId,
          companyCustomerId,
          sponsoringCompanyCustomerId: companyCustomerId,
          ...derivePayerForCompanyContext(companyCustomerId, individualCustomerId)
        });
        filledCount += 1;
      }
      await load();
      setBulkVirtualFillModal(null);
      if (filledCount === 0) {
        showSuccess('Seats already filled.');
        return;
      }
      const parts: string[] = [];
      if (linkedCount > 0) {
        parts.push(`linked ${linkedCount} existing CRM record${linkedCount === 1 ? '' : 's'}`);
      }
      if (createdCount > 0) {
        parts.push(`created ${createdCount} new CRM record${createdCount === 1 ? '' : 's'}`);
      }
      const summary = parts.length ? parts.join(' and ') : `filled ${filledCount} seat${filledCount === 1 ? '' : 's'}`;
      showSuccess(
        `${summary.charAt(0).toUpperCase() + summary.slice(1)}${
          lines.length > pairs.length ? `. ${lines.length - pairs.length} extra name(s) skipped.` : '.'
        }`
      );
    } catch (e: any) {
      showError(e?.message || 'Could not fill seats from names.');
    } finally {
      bulkVirtualFillSavingRef.current = false;
      setBulkVirtualFillSaving(false);
    }
  };

  const openTransferAttendeeModal = (attendee: CourseSessionAttendee) => {
    setTransferAttendeeModal({ attendee, note: 'Transferred to another course date' });
  };

  const submitTransferAttendee = async () => {
    if (!transferAttendeeModal) return;
    const { attendee, note } = transferAttendeeModal;
    const trimmedNote = note.trim() || 'Removed from session';
    const existing = String(attendee.attendeeNotes || '').trim();
    const stamp = `Removed: ${trimmedNote} (${formatDateUk(new Date().toISOString().slice(0, 10))})`;
    const merged = existing ? `${existing}\n${stamp}` : stamp;
    try {
      await softRevokeSeatCertificates(attendee, 'Moved below roster (cancelled)');
      await courseSessionsService.updateAttendee(attendee.id, {
        status: 'cancelled',
        attendeeNotes: merged
      });
      await load();
      setTransferAttendeeModal(null);
      showSuccess('Moved to removed list.');
    } catch (e: any) {
      showError(e?.message || 'Could not move attendee.');
    }
  };

  const restoreRemovedAttendee = async (attendee: CourseSessionAttendee) => {
    if (bookedCount >= capacityCount) {
      return showError('Session is at capacity ? remove someone or increase capacity first.');
    }
    try {
      if (attendee.individualCustomerId) {
        await assertNotAlreadyBooked(attendee.bookingId, attendee.individualCustomerId, attendee.id);
      }
      await courseSessionsService.updateAttendee(attendee.id, { status: 'booked' });
      await load();
      showSuccess('Attendee restored to roster.');
    } catch (e: any) {
      showError(e?.message || 'Could not restore attendee.');
    }
  };

  const confirmReplaceVirtualWithCustomer = async (customer: Customer) => {
    const attendee = replaceVirtualModal?.attendee;
    if (!attendee) return;
    if (customer.customerType !== 'individual') {
      showError('Only Individual CRM records can be assigned to course participants.');
      return;
    }
    try {
      const placeholderCompanyName = String(attendee.placeholderLabel || '').trim().toLowerCase();
      const matchedPlaceholderCompany = companyCustomers.find(
        (co) => String(co.companyName || '').trim().toLowerCase() === placeholderCompanyName
      );
      const matchedCustomerCompany = companyCustomers.find(
        (co) => String(co.companyName || '').trim().toLowerCase() === String(customer.companyName || '').trim().toLowerCase()
      );
      const companyCustomerId =
        attendee.companyCustomerId ||
        matchedPlaceholderCompany?.id ||
        matchedCustomerCompany?.id ||
        null;
      await courseSessionsService.convertVirtualAttendeeToReal({
        attendeeId: attendee.id,
        individualCustomerId: customer.id,
        companyCustomerId,
        sponsoringCompanyCustomerId: companyCustomerId,
        ...derivePayerForCompanyContext(companyCustomerId, customer.id)
      });
      await load();
      setReplaceVirtualModal(null);
      showSuccess('Virtual place replaced with participant.');
    } catch (e: any) {
      showError(e?.message || 'Could not replace virtual place.');
    }
  };

  const openCopyAttendeeModal = (attendee: CourseSessionAttendee) => {
    const c = getAttendeeCustomer(attendee);
    if (!c) {
      showError('Link a CRM record before copying this attendee.');
      return;
    }
    setCopyAttendeeModal({ attendee, customer: c, targetSessionId: '' });
  };

  const removePaymentLine = async (
    paymentId: string,
    bookingId: string,
    attendeeId: string,
    attendee?: CourseSessionAttendee
  ) => {
    if (!window.confirm('Remove this payment line from the ledger?')) return;
    try {
      await courseSessionsService.deleteAttendeePayment(paymentId, {
        bookingId,
        attendeeLabel: attendee ? getAttendeeLabel(attendee) : null
      });
      setPaymentTimelineByAttendee((prev) => ({
        ...prev,
        [attendeeId]: (prev[attendeeId] ?? []).filter((p) => p.id !== paymentId),
      }));
      await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId });
      await load();
      showSuccess('Payment line removed.');
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not remove payment.');
    }
  };

  const saveEditPaymentLine = async () => {
    if (!editPaymentModal || !selectedSession) return;
    const amount = parseEuroInput(editPaymentModal.amountStr);
    const paymentDate = parseUkDateToIso(editPaymentModal.paymentDateUk);
    if (amount <= 0) {
      showError('Enter a valid amount.');
      return;
    }
    if (!paymentDate) {
      showError('Payment date must be in DD/MM/YYYY format.');
      return;
    }
    try {
      const attendee = (attendeesBySession[selectedSession.id] || []).find((a) => a.id === editPaymentModal.attendeeId);
      await courseSessionsService.updateAttendeePayment(editPaymentModal.paymentId, {
        bookingId: selectedSession.id,
        amount,
        method: editPaymentModal.method,
        paymentDate,
        stepType: editPaymentModal.stepType,
        payerNotes: editPaymentModal.payerNotes.trim() || null,
        attendeeLabel: attendee ? getAttendeeLabel(attendee) : null
      });
      await courseSessionsService.upsertSessionFinancialSnapshot({ bookingId: selectedSession.id });
      await load();
      setEditPaymentModal(null);
      showSuccess('Payment updated.');
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not update payment.');
    }
  };

  const saveSessionExpenses = async (options?: { silent?: boolean }): Promise<boolean> => {
    if (!selectedSession) return false;
    const silent = options?.silent === true;
    try {
      await courseSessionsService.upsertSessionFinancialSnapshot({
        bookingId: selectedSession.id,
        tutorExpense: parseEuroInput(expenseDraft.tutorExpense),
        interpreterExpense: parseEuroInput(expenseDraft.interpreterExpense),
        venueExpense: parseEuroInput(expenseDraft.venueExpense),
        numberCards: Math.round(parseEuroInput(expenseDraft.numberCards)),
        cardFee: parseEuroInput(expenseDraft.cardFee),
        referralFee1: parseEuroInput(expenseDraft.referralFee1),
        referralFee2: parseEuroInput(expenseDraft.referralFee2),
        referralFee3: parseEuroInput(expenseDraft.referralFee3),
        otherExpenses: parseEuroInput(expenseDraft.otherExpenses)
      });
      const freshSnapshot = await courseSessionsService
        .getSessionFinancialSnapshot(selectedSession.id)
        .catch(() => null);
      if (freshSnapshot) setFinancialSnapshot(freshSnapshot);
      if (!silent) {
        await load();
        showSuccess('Session expenses saved.');
      }
      return true;
    } catch (e: any) {
      showError(e?.message || 'Could not save expenses.');
      return false;
    }
  };

  const rescheduleAttendee = async (attendee: CourseSessionAttendee) => {
    setRescheduleAttendeeModal({ attendee, targetSessionId: '' });
  };

  const confirmCopyAttendeeToSession = async () => {
    const modal = copyAttendeeModal;
    if (!modal) return;
    const targetId = modal.targetSessionId.trim();
    if (!targetId) {
      showError('Select a target session.');
      return;
    }
    const c = modal.customer;
    try {
      const sourceAttendee = modal.attendee;
      const sourceSession = sessions.find((s) => s.id === sourceAttendee.bookingId);
      const targetSession = sessions.find((s) => s.id === targetId);
      if (sourceSession && targetSession && sessionsAreLikelyDuplicates(sourceSession, targetSession)) {
        const ref = sessionReferenceCode(targetSession.id, buildSessionReferenceIndex(sessions).get(targetSession.id));
        const updatedLabel = formatDateUk((targetSession.updatedAt || targetSession.createdAt).slice(0, 10));
        const proceed = window.confirm(
          `This is a different session record (${ref}, last edited ${updatedLabel}). Participants booked here will not appear on your current session roster. Continue?`
        );
        if (!proceed) return;
      }
      const linkedCompanyId =
        c.customerType !== 'individual'
          ? c.id
          : String(sourceAttendee.companyCustomerId || sourceAttendee.sponsoringCompanyCustomerId || '').trim() || undefined;
      const sponsorCompanyId =
        String(sourceAttendee.sponsoringCompanyCustomerId || linkedCompanyId || '').trim() || undefined;
      const targetCatalogFee = targetSession
        ? getCourseCatalogDefaultFeeForSession(targetSession.courseName, courseDbFees, targetSession.language)
        : 0;
      const originalBookedBy = resolveBookedByLabel(sourceAttendee);
      const bookedByName = originalBookedBy || null;
      await courseSessionsService.addAttendee(targetId, {
        individualCustomerId: c.customerType === 'individual' ? c.id : undefined,
        companyCustomerId: linkedCompanyId,
        sponsoringCompanyCustomerId: sponsorCompanyId,
        ...derivePayerForCompanyContext(linkedCompanyId, c.customerType === 'individual' ? c.id : null),
        paymentStatus: 'unpaid',
        amountPaid: 0,
        enrollmentCourseFee: targetCatalogFee > 0 ? targetCatalogFee : null,
        safePassProcessNumber: modal.attendee.safePassProcessNumber || null,
        bookedByName,
        bookedByCustomerId: sourceAttendee.bookedByCustomerId || null,
        source: sourceAttendee.source || undefined,
      });
      setCopyAttendeeModal(null);
      await load();
      showSuccess('Attendee copied to the selected session (fee reset; payments not copied).');
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not copy attendee.');
    }
  };

  const confirmRescheduleAttendee = async () => {
    const modal = rescheduleAttendeeModal;
    if (!modal) return;
    const targetId = modal.targetSessionId.trim();
    if (!targetId) {
      showError('Select a target session.');
      return;
    }
    const sourceSession = sessions.find((s) => s.id === modal.attendee.bookingId);
    const targetSession = sessions.find((s) => s.id === targetId);
    if (sourceSession && targetSession && sessionsAreLikelyDuplicates(sourceSession, targetSession)) {
      const ref = sessionReferenceCode(targetSession.id, buildSessionReferenceIndex(sessions).get(targetSession.id));
      const updatedLabel = formatDateUk((targetSession.updatedAt || targetSession.createdAt).slice(0, 10));
      const proceed = window.confirm(
        `This is a different session record (${ref}, last edited ${updatedLabel}). Participants moved here will not appear on your current session roster. Continue?`
      );
      if (!proceed) return;
    }
    try {
      await courseSessionsService.rescheduleAttendee(modal.attendee.id, targetId);
      await load();
      setRescheduleAttendeeModal(null);
      showSuccess('Attendee rescheduled and payment history preserved.');
    } catch (e: any) {
      showError(e?.message || 'Could not reschedule attendee.');
    }
  };

  const selectedSession = selectedSessionId ? sessions.find((s) => s.id === selectedSessionId) : null;

  const helpSurfaceContext = useMemo(() => {
    if (selectedSession) {
      return {
        surface: 'session-roster' as const,
        tags: [sessionMainTab, 'session-roster', 'bookings'],
      };
    }
    if (mode === 'manage') {
      return { surface: 'bookings' as const, tags: ['bookings'] };
    }
    return null;
  }, [selectedSession, sessionMainTab, mode]);
  useRegisterHelpContext(helpSurfaceContext);

  const selectedSessionDayCount = selectedSession ? sessionDayCount(selectedSession.dateBlocks) : 0;
  const selectedIsMultiday = selectedSessionDayCount > 1;
  const companyBookingSession = useMemo(
    () => (companyBookingModal ? sessions.find((s) => s.id === companyBookingModal.sessionId) || null : null),
    [companyBookingModal, sessions]
  );
  const selectedAttendees = useMemo(
    () => (selectedSession ? attendeesBySession[selectedSession.id] || EMPTY_ATTENDEES : EMPTY_ATTENDEES),
    [selectedSession?.id, attendeesBySession]
  );

  useEffect(() => {
    const loadCommunicationPreview = async () => {
      if (!selectedSession) {
        setCommunicationPreviewByAttendee({});
        return;
      }
      const sessionRef = sessionReferenceCode(selectedSession.id).toLowerCase();
      const sessionCtx = `${selectedSession.courseName} (${formatDateBlocksUk(selectedSession.dateBlocks)})`.toLowerCase();
      const attendeeToCustomer = new Map<string, string>();
      selectedAttendees.forEach((attendee) => {
        const cid = getAttendeeBillingCustomerId(attendee);
        if (cid) attendeeToCustomer.set(attendee.id, cid);
      });
      const customerIds = Array.from(new Set(Array.from(attendeeToCustomer.values())));
      if (customerIds.length === 0) {
        setCommunicationPreviewByAttendee({});
        return;
      }

      const { data, error } = await supabase
        .from('client_notes')
        .select('customer_id,note_text,created_at')
        .in('customer_id', customerIds)
        .order('created_at', { ascending: false })
        .limit(800);
      if (error) {
        console.warn('Could not load communication preview notes:', error);
        setCommunicationPreviewByAttendee({});
        return;
      }

      const latestByCustomer: Record<string, { channel: 'email' | 'whatsapp' | 'other'; text: string; createdAt: string }> = {};
      (data || []).forEach((row: any) => {
        const customerId = String(row.customer_id || '');
        const noteText = String(row.note_text || '');
        const lower = noteText.toLowerCase();
        if (!customerId || !lower.includes('communication:')) return;
        if (!communicationNoteBelongsToSession(lower, sessionRef, sessionCtx)) return;
        if (latestByCustomer[customerId]) return;

        const communicationText = noteText.split(/communication:/i).pop()?.trim() || noteText.trim();
        const channel: 'email' | 'whatsapp' | 'other' =
          lower.includes('whatsapp') ? 'whatsapp' : lower.includes('email') ? 'email' : 'other';
        latestByCustomer[customerId] = {
          channel,
          text: communicationText,
          createdAt: String(row.created_at || ''),
        };
      });

      const byAttendee: Record<string, { channel: 'email' | 'whatsapp' | 'other'; text: string; createdAt: string }> = {};
      attendeeToCustomer.forEach((customerId, attendeeId) => {
        if (latestByCustomer[customerId]) byAttendee[attendeeId] = latestByCustomer[customerId];
      });
      setCommunicationPreviewByAttendee((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(byAttendee);
        if (prevKeys.length === nextKeys.length) {
          const same = nextKeys.every((k) => {
            const p = prev[k];
            const n = byAttendee[k];
            return p && n && p.channel === n.channel && p.text === n.text && p.createdAt === n.createdAt;
          });
          if (same) return prev;
        }
        return byAttendee;
      });
    };

    void loadCommunicationPreview();
  }, [selectedSession?.id, selectedAttendees]);

  useEffect(() => {
    const loadRosterEmailIndicators = async () => {
      if (!selectedSession) {
        setRosterEmailByAttendee({});
        return;
      }
      const sessionId = selectedSession.id;
      const { data, error } = await supabase
        .from('email_outbox')
        .select('id,recipient_email,to_email,email_type,subject,status,created_at,sent_at,metadata,related_booking_id,related_customer_id')
        .eq('related_booking_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) {
        console.warn('Could not load roster email indicators:', error);
        // Keep any optimistic local state; do not wipe on transient errors
        return;
      }

      const attendees = attendeesBySession[sessionId] || [];
      const emailToAttendeeIds = new Map<string, string[]>();
      const companyIdToAttendeeIds = new Map<string, string[]>();
      attendees.forEach((attendee) => {
        if (attendee.isVirtual) return;
        const c = getAttendeeCustomer(attendee);
        const person = attendee.individualCustomerId
          ? customerById[attendee.individualCustomerId]
          : c;
        const email = recipientEmailKey(person?.emailAddress || c?.emailAddress);
        if (email) {
          const list = emailToAttendeeIds.get(email) || [];
          list.push(attendee.id);
          emailToAttendeeIds.set(email, list);
        }
        const companyId = attendee.companyCustomerId || attendee.sponsoringCompanyCustomerId || null;
        if (companyId) {
          const list = companyIdToAttendeeIds.get(companyId) || [];
          list.push(attendee.id);
          companyIdToAttendeeIds.set(companyId, list);
        }
      });

      const next: Record<string, RosterEmailIndicators> = {};
      const ensure = (id: string): RosterEmailIndicators => {
        if (!next[id]) next[id] = {
          bookingConf: { ...EMPTY_ROSTER_EMAIL_INDICATORS.bookingConf },
          reminder: { ...EMPTY_ROSTER_EMAIL_INDICATORS.reminder },
          certificate: { ...EMPTY_ROSTER_EMAIL_INDICATORS.certificate },
        };
        return next[id];
      };

      (data || []).forEach((row: any) => {
        const metadata = (row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata))
          ? (row.metadata as Record<string, unknown>)
          : null;
        const kind = classifyRosterEmailKind({
          emailType: row.email_type,
          subject: row.subject,
          templateName: metadata ? String(metadata.templateName || metadata.template_name || '') : '',
          metadata,
        });
        if (!kind) return;
        const status = mapOutboxStatus(row.status);
        if (status === 'not_sent') return;
        const info: RosterEmailKindStatus = {
          status,
          at: String(row.sent_at || row.created_at || '') || null,
          by: pickInitiatorName(metadata),
        };
        const email = recipientEmailKey(row.recipient_email || row.to_email);
        let targetIds: string[] = email ? (emailToAttendeeIds.get(email) || []) : [];
        const emailType = String(row.email_type || '').toLowerCase();
        if (
          targetIds.length === 0 &&
          (emailType === 'company_cohort_contact' || Array.isArray(metadata?.attendeeIds) || metadata?.companyName)
        ) {
          const metaIds = Array.isArray(metadata?.attendeeIds)
            ? (metadata!.attendeeIds as unknown[]).map((x) => String(x || '')).filter(Boolean)
            : [];
          if (metaIds.length > 0) {
            targetIds = metaIds;
          } else {
            const companyId = String(row.related_customer_id || metadata?.companyCustomerId || '').trim();
            if (companyId && companyIdToAttendeeIds.has(companyId)) {
              targetIds = companyIdToAttendeeIds.get(companyId) || [];
            }
          }
        }
        targetIds.forEach((attendeeId) => {
          const slot = ensure(attendeeId);
          if (kind === 'booking_conf') {
            slot.bookingConf = mergeKindStatus(slot.bookingConf, info);
          } else if (kind === 'reminder') {
            slot.reminder = mergeKindStatus(slot.reminder, info);
          } else if (kind === 'certificate') {
            slot.certificate = mergeKindStatus(slot.certificate, info);
          }
        });
      });

      // Also pull certificate outbox rows keyed by metadata.session_id (legacy rows may lack related_booking_id).
      try {
        const { data: certOutbox } = await supabase
          .from('email_outbox')
          .select('id,recipient_email,to_email,email_type,subject,status,created_at,sent_at,metadata,related_booking_id,related_customer_id')
          .contains('metadata', { kind: 'certificate' })
          .order('created_at', { ascending: false })
          .limit(500);
        const rosterAttendeeIds = new Set(
          attendees.filter((a) => !a.isVirtual).map((a) => a.id)
        );
        (certOutbox || []).forEach((row: any) => {
          const metadata = (row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata))
            ? (row.metadata as Record<string, unknown>)
            : null;
          const metaSession = String(metadata?.session_id || row.related_booking_id || '').trim();
          if (metaSession && metaSession !== sessionId) return;
          const status = mapOutboxStatus(row.status);
          if (status === 'not_sent') return;
          const info: RosterEmailKindStatus = {
            status,
            at: String(row.sent_at || row.created_at || '') || null,
            by: pickInitiatorName(metadata),
          };
          let targetIds: string[] = [];
          const metaAttendee = String(metadata?.attendee_id || metadata?.attendeeId || '').trim();
          if (metaAttendee && rosterAttendeeIds.has(metaAttendee)) {
            targetIds = [metaAttendee];
          } else if (metaSession === sessionId) {
            const email = recipientEmailKey(
              row.recipient_email || row.to_email || (metadata ? String(metadata.to || '') : '')
            );
            if (email) targetIds = emailToAttendeeIds.get(email) || [];
          }
          // Skip unscoped legacy rows (no session_id and no attendee_id on this roster).
          targetIds.forEach((attendeeId) => {
            if (!rosterAttendeeIds.has(attendeeId)) return;
            const slot = ensure(attendeeId);
            slot.certificate = mergeKindStatus(slot.certificate, info);
          });
        });
      } catch (certErr) {
        console.warn('Could not load certificate outbox indicators:', certErr);
      }

      // Seed from roster certificate_sent (set only by real send path going forward).
      attendees.forEach((attendee) => {
        if (attendee.isVirtual || !attendee.certificateSent) return;
        const slot = ensure(attendee.id);
        const fromSeat: RosterEmailKindStatus = {
          status: 'sent',
          at: attendee.certificateSentAt || null,
          by: null,
        };
        slot.certificate = mergeKindStatus(slot.certificate, fromSeat);
      });

      setRosterEmailByAttendee((prev) => {
        // Merge: prefer newer loaded rows, but keep optimistic local-only keys when outbox has nothing yet
        const merged: Record<string, RosterEmailIndicators> = { ...prev };
        Object.keys(next).forEach((id) => {
          const loaded = next[id];
          const existing = merged[id];
          if (!existing) {
            merged[id] = loaded;
            return;
          }
          merged[id] = {
            bookingConf: mergeKindStatus(existing.bookingConf, loaded.bookingConf),
            reminder: mergeKindStatus(existing.reminder, loaded.reminder),
            certificate: mergeKindStatus(existing.certificate, loaded.certificate),
          };
        });
        return merged;
      });
    };

    void loadRosterEmailIndicators();
  }, [selectedSession?.id, selectedAttendees, customerById, attendeesBySession]);

  const applyRosterEmailQueued = useCallback(
    (info: {
      kind: RosterEmailKind;
      recipientEmail: string;
      attendeeIds: string[];
      sentAt: string;
      sentBy?: string;
    }, fallbackAttendeeId?: string) => {
      const statusInfo: RosterEmailKindStatus = {
        status: 'sent',
        at: info.sentAt,
        by: info.sentBy || actorName || null,
      };
      let ids = (info.attendeeIds || []).filter(Boolean);
      if (ids.length === 0 && fallbackAttendeeId) ids = [fallbackAttendeeId];
      if (ids.length === 0 && info.recipientEmail && selectedSession) {
        const email = recipientEmailKey(info.recipientEmail);
        (attendeesBySession[selectedSession.id] || []).forEach((attendee) => {
          if (attendee.isVirtual) return;
          const c = getAttendeeCustomer(attendee);
          const person = attendee.individualCustomerId
            ? customerById[attendee.individualCustomerId]
            : c;
          const attEmail = recipientEmailKey(person?.emailAddress || c?.emailAddress);
          if (attEmail && attEmail === email) ids.push(attendee.id);
        });
      }
      if (ids.length === 0) return;
      setRosterEmailByAttendee((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          const cur = next[id] || {
            bookingConf: { ...EMPTY_ROSTER_EMAIL_INDICATORS.bookingConf },
            reminder: { ...EMPTY_ROSTER_EMAIL_INDICATORS.reminder },
            certificate: { ...EMPTY_ROSTER_EMAIL_INDICATORS.certificate },
          };
          next[id] =
            info.kind === 'booking_conf'
              ? { ...cur, bookingConf: mergeKindStatus(cur.bookingConf, statusInfo) }
              : info.kind === 'reminder'
                ? { ...cur, reminder: mergeKindStatus(cur.reminder, statusInfo) }
                : { ...cur, certificate: mergeKindStatus(cur.certificate, statusInfo) };
        });
        return next;
      });
    },
    [actorName, selectedSession?.id, attendeesBySession, customerById]
  );

  const selectedSearch = selectedSession ? (searchBySession[selectedSession.id] || '').trim().toLowerCase() : '';
  const expenseDraftSignature = useMemo(() => JSON.stringify(expenseDraft), [expenseDraft]);
  const detailsDraftSignature = useMemo(
    () => JSON.stringify({ sessionDraft, sessionDateBlocksDraft, postSessionChecklist }),
    [sessionDraft, sessionDateBlocksDraft, postSessionChecklist]
  );
  detailsDraftSignatureRef.current = detailsDraftSignature;

  const liveCrmSearchQuery = (replaceVirtualModal?.query || selectedSearch || '').trim();
  useEffect(() => {
    const q = liveCrmSearchQuery;
    if (q.length < 2) {
      setCrmSearchHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const rows = await supabaseCustomerStorage.getCustomersList(40, 0, q, 'individual');
          if (cancelled) return;
          const mapped = (rows || []).map((c) => mapCustomerRow(c as any));
          setCrmSearchHits(mapped);
          upsertCustomersInMemory(mapped);
        } catch (e) {
          console.error('CRM search failed', e);
          if (!cancelled) setCrmSearchHits([]);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [liveCrmSearchQuery]);

  const companyPickerActive = Boolean(companyBookingModal || reserveVirtualModal || addAttendeeModal);
  useEffect(() => {
    if (!companyPickerActive) return;
    const q = companyPickerQuery.trim();
    if (q.length < 2) {
      setCompanyPickerHits([]);
      setCompanyPickerSearching(false);
      return;
    }
    let cancelled = false;
    setCompanyPickerSearching(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const rows = await supabaseCustomerStorage.getCustomersList(40, 0, q, 'company');
          if (cancelled) return;
          const mapped = (rows || [])
            .map((c) => mapCustomerRow(c as any))
            .filter((c) => c.customerType === 'company' && String(c.companyName || '').trim());
          setCompanyPickerHits(mapped);
          upsertCustomersInMemory(mapped);
        } catch (e) {
          console.error('Company search failed', e);
          if (!cancelled) setCompanyPickerHits([]);
        } finally {
          if (!cancelled) setCompanyPickerSearching(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companyPickerActive, companyPickerQuery]);

  useEffect(() => {
    if (!newSessionPanelOpen) {
      setCoursePickerHits([]);
      setCoursePickerSearching(false);
      return;
    }
    if (coursePickerChosen) {
      setCoursePickerHits([]);
      setCoursePickerSearching(false);
      return;
    }
    const q = newSession.courseName.trim();
    if (q.length < 2) {
      setCoursePickerHits([]);
      setCoursePickerSearching(false);
      return;
    }
    let cancelled = false;
    setCoursePickerSearching(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const names = await searchActiveCourseNamesFromDb(q, 25);
          if (!cancelled) setCoursePickerHits(names);
        } catch (e) {
          console.error('Course search failed', e);
          if (!cancelled) setCoursePickerHits([]);
        } finally {
          if (!cancelled) setCoursePickerSearching(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [newSessionPanelOpen, newSession.courseName, coursePickerChosen]);

  useEffect(() => {
    if (!companyBookingModal) return;
    const names = [
      ...new Set(
        (companyBookingModal.parsedRows || [])
          .filter((row) => row.action !== 'reserve_seat' && row.firstName && row.surname)
          .map((row) => `${row.firstName} ${row.surname}`.trim())
      ),
    ].filter(Boolean);
    if (!names.length) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const extra: Customer[] = [];
        for (const name of names.slice(0, 30)) {
          if (cancelled) return;
          try {
            const rows = await supabaseCustomerStorage.getCustomersList(15, 0, name, 'individual');
            extra.push(...(rows || []).map((c) => mapCustomerRow(c as any)));
          } catch {
            /* keep going */
          }
        }
        if (!cancelled && extra.length) upsertCustomersInMemory(extra);
      })();
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companyBookingModal?.pastedParticipants, companyBookingModal?.sessionId]);

  const selectedSearchResults = !selectedSession || !selectedSearch
    ? []
    : [...crmSearchHits, ...allCustomers]
        .filter((c, idx, arr) => arr.findIndex((x) => x.id === c.id) === idx)
        .filter((c) => {
          if (!isIndividualCustomerRecord(c)) return false;
          const name = `${c.firstName || ''} ${c.surname || ''}`.trim();
          return [name, c.companyName, c.emailAddress, c.contactPhoneNumber].some((v) =>
            (v || '').toLowerCase().includes(selectedSearch)
          );
        })
        .slice(0, 6);
  const replaceVirtualSearchResults = !replaceVirtualModal?.query.trim()
    ? []
    : [...crmSearchHits, ...allCustomers]
        .filter((c, idx, arr) => arr.findIndex((x) => x.id === c.id) === idx)
        .filter((c) => {
          if (!isIndividualCustomerRecord(c)) return false;
          const q = replaceVirtualModal.query.trim().toLowerCase();
          const name = `${c.firstName || ''} ${c.surname || ''}`.trim();
          return [name, c.companyName, c.emailAddress, c.contactPhoneNumber].some((v) =>
            (v || '').toLowerCase().includes(q)
          );
        })
        .slice(0, 8);

  const loadSessionDocuments = async (sessionId: string) => {
    setSessionDocumentsLoading(true);
    try {
      const docs = await courseSessionsService.listSessionDocuments(sessionId);
      setSessionDocuments(docs);
      setSessionDocumentCounts((prev) => ({ ...prev, [sessionId]: docs.length }));
    } catch (e: any) {
      setSessionDocuments([]);
      setSessionDocumentCounts((prev) => ({ ...prev, [sessionId]: 0 }));
      showError(e?.message || 'Could not load session documents.');
    } finally {
      setSessionDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSession) {
      setSessionDocuments([]);
      return;
    }
    void loadSessionDocuments(selectedSession.id);
  }, [selectedSession?.id]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!sessionDocumentViewer) {
        setSessionDocumentPreviewHtml('');
        setSessionDocumentPreviewError('');
        return;
      }
      const lower = sessionDocumentViewer.name.toLowerCase();
      const isPdf = (sessionDocumentViewer.mimeType || '').includes('pdf') || lower.endsWith('.pdf');
      const isDocx = lower.endsWith('.docx');
      const isXlsx = lower.endsWith('.xlsx') || lower.endsWith('.xls');
      if (isPdf) {
        setSessionDocumentPreviewHtml('');
        setSessionDocumentPreviewError('');
        return;
      }
      if (!isDocx && !isXlsx) {
        setSessionDocumentPreviewHtml('');
        setSessionDocumentPreviewError('');
        return;
      }
      setSessionDocumentPreviewLoading(true);
      setSessionDocumentPreviewError('');
      try {
        const response = await fetch(sessionDocumentViewer.signedUrl);
        if (!response.ok) throw new Error('Could not fetch file for preview.');
        const buffer = await response.arrayBuffer();
        let html = '';
        if (isDocx) {
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          html = result.value || '<p>No preview content found.</p>';
        } else {
          const wb = XLSX.read(buffer, { type: 'array' });
          const firstSheet = wb.SheetNames[0];
          if (!firstSheet) throw new Error('Spreadsheet is empty.');
          html = XLSX.utils.sheet_to_html(wb.Sheets[firstSheet], { editable: false });
        }
        if (!cancelled) setSessionDocumentPreviewHtml(html);
      } catch (e: any) {
        if (!cancelled) {
          setSessionDocumentPreviewHtml('');
          setSessionDocumentPreviewError(e?.message || 'Preview failed. Use Open/Download instead.');
        }
      } finally {
        if (!cancelled) setSessionDocumentPreviewLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [sessionDocumentViewer?.path, sessionDocumentViewer?.signedUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedSession) {
        setFinancialSnapshot(null);
        setExpenseDraft({
          tutorExpense: '',
          interpreterExpense: '',
          venueExpense: '',
          numberCards: '',
          cardFee: '',
          referralFee1: '',
          referralFee2: '',
          referralFee3: '',
          otherExpenses: ''
        });
        return;
      }
      const [timeline, snapshot] = await Promise.all([
        courseSessionsService.listPaymentsBySessionIds([selectedSession.id]).catch(() => ({})),
        courseSessionsService.getSessionFinancialSnapshot(selectedSession.id).catch(() => null),
      ]);
      if (!cancelled) {
        const toDraft = (value: unknown) => {
          const n = Number(value ?? 0);
          if (!Number.isFinite(n) || n === 0) return '';
          return String(value);
        };
        setPaymentTimelineByAttendee((prev) => {
          const ids = (attendeesBySession[selectedSession.id] || []).map((a) => a.id);
          return mergeSessionPayments(prev, ids, timeline);
        });
        setFinancialSnapshot(snapshot);
        setExpenseDraft({
          tutorExpense: toDraft(snapshot?.tutorExpense),
          interpreterExpense: toDraft(snapshot?.interpreterExpense),
          venueExpense: toDraft(snapshot?.venueExpense),
          numberCards: toDraft(snapshot?.numberCards),
          cardFee: toDraft(snapshot?.cardFee),
          referralFee1: toDraft(snapshot?.referralFee1),
          referralFee2: toDraft(snapshot?.referralFee2),
          referralFee3: toDraft(snapshot?.referralFee3),
          otherExpenses: toDraft(snapshot?.otherExpenses)
        });
        void courseSessionsService
          .ensureSnapshotExpensesSyncedToLedger(selectedSession.id, snapshot)
          .catch((e) => console.warn('Snapshot expense ledger sync skipped:', e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSession, attendeesBySession]);
  const selectedMoney = useMemo(() => {
    const feeForAttendee = (attendeeId: string) => {
      const attendee = selectedAttendees.find((a) => a.id === attendeeId);
      if (!attendee) return 0;
      const c = getAttendeeCustomer(attendee);
      return getEffectiveFee(attendee, c, selectedSession?.courseName, courseDbFees, selectedSession?.language);
    };
    return computeSessionFinanceOverview({
      attendees: selectedAttendees,
      paymentTimelineByAttendee,
      feeForAttendee,
      collectedProfit: 0,
    });
  }, [selectedAttendees, customerById, paymentTimelineByAttendee, selectedSession?.courseName, courseDbFees]);
  const bookedCount = selectedAttendees.filter((a) => isAttendeeOnActiveRoster(a)).length;
  const capacityCount = Math.max(1, Number(sessionDraft.capacity || selectedSession?.capacity || 1));
  const sessionIsFullyBooked = bookedCount >= capacityCount;
  const sessionIsCancelled = (selectedSession?.status || 'scheduled') === 'cancelled';
  const occupancyPct = Math.min(100, Math.round((bookedCount / capacityCount) * 100));
  const collectionPct =
    selectedMoney.totalFee > 0
      ? Math.min(100, Math.round((selectedMoney.totalCollected / selectedMoney.totalFee) * 100))
      : 0;
  const selectedMethodTotals = selectedMoney.methodTotals;
  const selectedMethodCounts = selectedMoney.methodCounts;
  const groupedSelectedAttendees = useMemo(() => {
    const activeAttendees = selectedAttendees.filter((a) => isAttendeeOnActiveRoster(a));
    return buildRosterDisplayGroups(activeAttendees, getAttendeeCustomer, companyCustomers).map((g) => ({
      groupLabel: g.groupLabel,
      attendees: g.attendees,
      isCompanyGroup: g.isCompanyGroup,
    }));
  }, [selectedAttendees, customerById, companyCustomers]);
  const rosterSpreadsheetRows = useMemo((): RosterSpreadsheetRow[] => {
    if (!selectedSession) return [];
    const sessionLang = String(selectedSession.language || '').trim();
    const activeAttendees = selectedAttendees.filter((a) => isAttendeeOnActiveRoster(a));
    const ordered = flattenRosterDisplayOrder(activeAttendees, getAttendeeCustomer, companyCustomers);
    return ordered.map((a) => {
        const c = getAttendeeCustomer(a);
        const personCustomer = a.individualCustomerId
          ? customerById[a.individualCustomerId]
          : c;
        const fee = getEffectiveFee(a, c, selectedSession.courseName, courseDbFees, selectedSession.language);
        const ledger = paymentTimelineByAttendee[a.id] || [];
        const paid = getAttendeePaidForRoster(a, c, selectedSession.courseName, courseDbFees, ledger);
        const companyName = a.companyCustomerId
          ? companyCustomers.find((co) => co.id === a.companyCustomerId)?.companyName || ''
          : String(c?.companyName || '').trim();
        const cohort = companyCohortAttendees(a, activeAttendees, companyCustomers, getAttendeeCustomer);
        const invoice = resolveAttendeeInvoice(a, c, companyCustomers, cohort);
        const orphaned = Boolean(a.individualCustomerId && !personCustomer && !a.isVirtual);
        return {
          attendeeId: a.id,
          isVirtual: Boolean(a.isVirtual),
          isOrphaned: orphaned,
          name: getAttendeeLabel(a),
          company: companyName,
          sessionLanguage: sessionLang,
          preferredLanguage: String(c?.preferredLanguage || '').trim(),
          status: a.status || 'booked',
          paymentStatus: a.paymentStatus || 'unpaid',
          feeEuro: fee,
          paidEuro: paid,
          balanceEuro: attendeeBalanceEuro(fee, paid),
          processNumber: String(a.safePassProcessNumber || (c as { safePassProcessNumber?: string })?.safePassProcessNumber || ''),
          mhFlag: a.mhFlag ? 'Yes' : 'No',
          invoiceNumber: invoice.number,
          invoicePaid: invoice.paid ? 'Yes' : invoice.number ? 'No' : '',
          email: String(personCustomer?.emailAddress || c?.emailAddress || '').trim(),
          phone: String(
            personCustomer?.contactPhoneNumber ||
              c?.contactPhoneNumber ||
              personCustomer?.mobilePhone ||
              c?.mobilePhone ||
              ''
          ).trim(),
          bookedDate: formatDateUk(a.createdAt),
          bookedBy: resolveBookedByLabel(a),
          bookedByUser: String(a.bookedByUserName || '').trim(),
          certificateSent: a.certificateSent ? 'Yes' : 'No',
          notes: String(a.rosterNote || '').trim().slice(0, ROSTER_NOTE_MAX_LEN)
        };
      });
  }, [selectedSession, selectedAttendees, customerById, companyCustomers, paymentTimelineByAttendee, courseDbFees]);
  const removedSelectedAttendees = useMemo(
    () =>
      selectedAttendees
        .filter((a) => isAttendeeRemoved(a))
        .sort((a, b) =>
          String(a.reservedCreatedAt || a.createdAt || '').localeCompare(
            String(b.reservedCreatedAt || b.createdAt || '')
          )
        ),
    [selectedAttendees]
  );
  const waitingListSelectedAttendees = useMemo(
    () =>
      selectedAttendees
        .filter((a) => isAttendeeWaitingList(a))
        .sort((a, b) =>
          String(a.reservedCreatedAt || a.createdAt || '').localeCompare(
            String(b.reservedCreatedAt || b.createdAt || '')
          )
        ),
    [selectedAttendees]
  );
  const noShowSelectedAttendees = useMemo(
    () =>
      selectedAttendees
        .filter((a) => isAttendeeNoShow(a))
        .sort((a, b) =>
          String(a.reservedCreatedAt || a.createdAt || '').localeCompare(
            String(b.reservedCreatedAt || b.createdAt || '')
          )
        ),
    [selectedAttendees]
  );
  const failedSelectedAttendees = useMemo(
    () =>
      selectedAttendees
        .filter((a) => isAttendeeFailed(a))
        .sort((a, b) =>
          String(a.reservedCreatedAt || a.createdAt || '').localeCompare(
            String(b.reservedCreatedAt || b.createdAt || '')
          )
        ),
    [selectedAttendees]
  );
  const inactiveRosterAttendees = useMemo(
    () =>
      [...waitingListSelectedAttendees, ...removedSelectedAttendees].sort((a, b) =>
        String(a.reservedCreatedAt || a.createdAt || '').localeCompare(String(b.reservedCreatedAt || b.createdAt || ''))
      ),
    [waitingListSelectedAttendees, removedSelectedAttendees]
  );
  const postSessionRows = useMemo(() => {
    const isSafePass = isSafePassCourse(selectedSession?.courseName);
    const activeAttendees = selectedAttendees.filter((a) => isAttendeeOnActiveRoster(a));
    const orderedAttendees = flattenRosterDisplayOrder(
      activeAttendees,
      getAttendeeCustomer,
      companyCustomers
    );
    return orderedAttendees
      .map((attendee) => {
        const customer = getAttendeeCustomer(attendee);
        const processNumber = String(attendee.safePassProcessNumber || (customer as any)?.safePassProcessNumber || '').trim();
        const processStatus = inferProcessStatus(attendee);
        const cardStatus = (attendee.safePassCardStatus || 'not_received') as SafePassCardTrackingStatus;
        const cardNotes = String(attendee.safePassCardNotes || '');
        const paymentStatus = (attendee.paymentStatus || 'unpaid') as 'paid' | 'half_paid' | 'unpaid';
        const cohort = companyCohortAttendees(attendee, activeAttendees, companyCustomers, getAttendeeCustomer);
        const invoice = resolveAttendeeInvoice(attendee, customer, companyCustomers, cohort);
        const attended = attendee.status === 'completed';
        const complianceOk = !isSafePass || processNumber.length > 0;
        const paymentOk = paymentStatus === 'paid';
        const eligible = attended && complianceOk && paymentOk && !attendee.isVirtual;
        const blockers: string[] = [];
        if (attendee.isVirtual) blockers.push('Virtual seat');
        if (!attended) blockers.push('Not marked attended');
        if (!paymentOk) blockers.push(paymentStatus === 'half_paid' ? 'Balance due' : 'Unpaid');
        if (isSafePass && !complianceOk) blockers.push('Missing process #');
        const cardComplete = !isSafePass || cardStatus === 'posted';
        const wrapUpComplete = isSafePass ? eligible && cardComplete : attended && paymentOk && !attendee.isVirtual;
        return {
          attendee,
          customer,
          processNumber,
          processStatus,
          cardStatus,
          cardNotes,
          attended,
          paymentStatus,
          invoiceNumber: invoice.number,
          invoicePaid: invoice.paid,
          complianceOk,
          paymentOk,
          eligible,
          blockers,
          cardComplete,
          wrapUpComplete
        };
      });
  }, [selectedAttendees, customerById, companyCustomers, selectedSession?.courseName]);
  const postSessionBlockedCount = useMemo(
    () => postSessionRows.filter((r) => !r.wrapUpComplete).length,
    [postSessionRows]
  );
  const postSessionReadyCount = useMemo(
    () => postSessionRows.filter((r) => r.wrapUpComplete).length,
    [postSessionRows]
  );
  const getSessionOpsSnapshot = (session: any) => {
    const attendees = (attendeesBySession[session.id] || []).filter(
      (a) => isAttendeeOnActiveRoster(a) && !a.isVirtual
    );
    const unpaid = attendees.filter((a) => {
      const c = getAttendeeCustomer(a);
      const fee = getEffectiveFee(a, c, session.courseName, courseDbFees, session.language);
      const ledger = paymentTimelineByAttendee[a.id];
      const rawPaid = ledger?.length
        ? sumLedgerRawPaid(ledger)
        : capPaidToFee(Number(a.amountPaid || 0), fee);
      return !(fee > 0 && rawPaid >= fee - 0.005);
    }).length;
    const missingCompliance = isSafePassCourse(session.courseName)
      ? attendees.filter((a) => {
          const c = getAttendeeCustomer(a);
          const processNumber = String(a.safePassProcessNumber || (c as any)?.safePassProcessNumber || '').trim();
          return !processNumber;
        }).length
      : 0;
    const docsMissing = Number(sessionDocumentCounts[session.id] || 0) <= 0 ? 1 : 0;
    const checklist = parsePostSessionMeta(session.sessionOtherInfo).checklist;
    const postChecklistMissing = [
      checklist.trainerSubmissionReturned,
      checklist.cardsReceivedFromTrainer,
      checklist.scansArchived,
      checklist.batchReadyToSend
    ].filter((x) => !x).length;
    const postAttendeeWork = isSafePassCourse(session.courseName)
      ? attendees.filter((a) => {
          if (a.isVirtual || a.status === 'cancelled') return false;
          const c = getAttendeeCustomer(a);
          const processNumber = String(a.safePassProcessNumber || (c as any)?.safePassProcessNumber || '').trim();
          const paymentStatus = a.paymentStatus || 'unpaid';
          const attended = a.status === 'completed';
          const cardPosted = (a.safePassCardStatus || 'not_received') === 'posted';
          return attended && paymentStatus === 'paid' && !!processNumber && !cardPosted;
        }).length
      : attendees.filter(
          (a) =>
            !a.isVirtual &&
            a.status !== 'completed' &&
            (a.paymentStatus || 'unpaid') === 'paid'
        ).length;
    const noShows = (attendeesBySession[session.id] || []).filter((a) => a.status === 'no_show').length;
    const waitlist = (attendeesBySession[session.id] || []).filter((a) => isAttendeeWaitingList(a)).length;
    const blockers = unpaid + missingCompliance + docsMissing + postChecklistMissing + postAttendeeWork;
    const health: 'green' | 'amber' | 'red' =
      blockers === 0 ? 'green' : blockers <= 2 ? 'amber' : 'red';
    return { unpaid, missingCompliance, docsMissing, postChecklistMissing, postAttendeeWork, noShows, waitlist, blockers, health };
  };
  const expenseDraftTotal = useMemo(
    () => {
      const numberCards = Math.max(0, Math.round(parseEuroInput(expenseDraft.numberCards)));
      const cardFee = parseEuroInput(expenseDraft.cardFee);
      const cardTotal = numberCards * cardFee;
      return (
        parseEuroInput(expenseDraft.tutorExpense) +
        parseEuroInput(expenseDraft.interpreterExpense) +
        parseEuroInput(expenseDraft.venueExpense) +
        cardTotal +
        parseEuroInput(expenseDraft.referralFee1) +
        parseEuroInput(expenseDraft.referralFee2) +
        parseEuroInput(expenseDraft.referralFee3) +
        parseEuroInput(expenseDraft.otherExpenses)
      );
    },
    [expenseDraft]
  );
  const selectedSnapshotOutlay = useMemo(
    () => {
      if (!financialSnapshot) return 0;
      const cardTotal = Number(financialSnapshot.numberCards || 0) * Number(financialSnapshot.cardFee || 0);
      return (
        Number(financialSnapshot.tutorExpense || 0) +
        Number(financialSnapshot.interpreterExpense || 0) +
        Number(financialSnapshot.venueExpense || 0) +
        cardTotal +
        Number(financialSnapshot.referralFee1 || 0) +
        Number(financialSnapshot.referralFee2 || 0) +
        Number(financialSnapshot.referralFee3 || 0) +
        Number(financialSnapshot.otherExpenses || 0)
      );
    },
    [financialSnapshot]
  );
  // Extra expenses recorded against this session via the Financial module
  // (rows in business_expenses that are NOT mirror copies of the snapshot).
  const [selectedExtraLedgerExpenses, setSelectedExtraLedgerExpenses] = useState<
    Array<{ id: string; amount: number; category: string; description: string | null }>
  >([]);
  useEffect(() => {
    let cancelled = false;
    const sid = selectedSession?.id;
    if (!sid) {
      setSelectedExtraLedgerExpenses([]);
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from('business_expenses')
          .select('id, amount, category, description')
          .eq('session_id', sid);
        if (cancelled) return;
        if (error) {
          setSelectedExtraLedgerExpenses([]);
          return;
        }
        const rows = (data || []).filter((r: any) => {
          const d = String(r?.description || '');
          // Snapshot mirror rows are already represented in the on-session expense form,
          // so they're excluded here to avoid double-counting.
          return !d.startsWith('Session snapshot expense') && !d.startsWith('Migrated from session snapshot');
        }).map((r: any) => ({
          id: String(r.id),
          amount: Number(r.amount || 0),
          category: String(r.category || ''),
          description: r.description ?? null,
        }));
        setSelectedExtraLedgerExpenses(rows);
      } catch {
        if (!cancelled) setSelectedExtraLedgerExpenses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSession?.id, financialSnapshot?.updatedAt]);
  const selectedExtraLedgerTotal = useMemo(
    () => selectedExtraLedgerExpenses.reduce((s, r) => s + r.amount, 0),
    [selectedExtraLedgerExpenses]
  );
  const selectedTotalOutlay = useMemo(
    () => selectedSnapshotOutlay + selectedExtraLedgerTotal,
    [selectedSnapshotOutlay, selectedExtraLedgerTotal]
  );
  const selectedCollectedProfit = useMemo(
    () => selectedMoney.totalCollected - selectedTotalOutlay,
    [selectedMoney.totalCollected, selectedTotalOutlay]
  );
  const selectedProfitTotal = useMemo(
    () => selectedCollectedProfit + selectedMoney.totalOutstanding,
    [selectedCollectedProfit, selectedMoney.totalOutstanding]
  );

  const instructorLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const i of instructors) {
      m[i.id] = `${i.firstName} ${i.lastName}`.trim() || i.email;
    }
    return m;
  }, [instructors]);

  const interpreterLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const i of interpreterOptions) {
      m[i.id] = i.name?.trim() || '';
    }
    return m;
  }, [interpreterOptions]);

  const providerLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of providerOptions) {
      m[p.id] = p.name;
    }
    return m;
  }, [providerOptions]);

  const sessionFilterCourseOptions = useMemo(() => {
    const names = new Set<string>();
    for (const s of sessions) {
      if (s.courseName?.trim()) names.add(s.courseName.trim());
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [sessions]);

  const sessionFilterLanguageOptions = useMemo(() => {
    const langs = new Set<string>(globalCourseLanguageNames);
    for (const s of sessions) {
      if (s.language?.trim()) langs.add(s.language.trim());
    }
    return [...langs].sort((a, b) => a.localeCompare(b));
  }, [sessions, globalCourseLanguageNames]);

  const activeSessionFilterCount = useMemo(
    () => countActiveSessionFilters(sessionFilters, opsMode, opsFilter),
    [sessionFilters, opsMode, opsFilter]
  );

  const sessionFilterContext = useMemo(
    () => ({
      locationById,
      instructorLabelById,
      interpreterLabelById,
      providerLabelById,
    }),
    [locationById, instructorLabelById, interpreterLabelById, providerLabelById]
  );

  const sessionTimeRangeCounts = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayMs = startOfToday.getTime();
    let upcoming = 0;
    let past = 0;
    sessions.forEach((s) => {
      const firstMs = getFirstDateMs(s);
      if (sessionMatchesTimeRange(firstMs, 'upcoming', todayMs)) upcoming += 1;
      if (sessionMatchesTimeRange(firstMs, 'past', todayMs)) past += 1;
    });
    return { upcoming, past, all: sessions.length };
  }, [sessions]);

  const opsBaseSessions = useMemo(() => {
    const filtered = sessions.filter((s) => sessionMatchesListFilters(s, sessionFilters, sessionFilterContext));
    return [...filtered].sort((a, b) => compareSessionsForTimeRange(a, b, sessionFilters.timeRange));
  }, [sessions, sessionFilters, sessionFilterContext]);
  const sortedSessions = useMemo(() => {
    if (!opsMode || opsFilter === 'all') return opsBaseSessions;
    return opsBaseSessions.filter((s) => {
      const snap = getSessionOpsSnapshot(s);
      if (opsFilter === 'unpaid') return snap.unpaid > 0;
      if (opsFilter === 'compliance') return snap.missingCompliance > 0;
      if (opsFilter === 'docs') return snap.docsMissing > 0;
      if (opsFilter === 'post') return snap.postChecklistMissing > 0 || snap.postAttendeeWork > 0;
      if (opsFilter === 'noshow') return snap.noShows > 0;
      if (opsFilter === 'waitlist') return snap.waitlist > 0;
      return true;
    });
  }, [opsBaseSessions, opsMode, opsFilter, attendeesBySession, customerById, sessionDocumentCounts, paymentTimelineByAttendee, courseDbFees]);

  const duplicateSessionGroups = useMemo(() => {
    const rosterCountBySessionId = new Map<string, number>();
    for (const session of sessions) {
      rosterCountBySessionId.set(session.id, getListBookedCount(session.id));
    }
    return findDuplicateSessionGroups(sessions, { rosterCountBySessionId });
  }, [sessions, attendeesBySession, bookedCountBySessionId]);

  const visibleSortedSessions = useMemo(
    () => sortedSessions.slice(0, visibleSessionLimit),
    [sortedSessions, visibleSessionLimit]
  );
  const hasMoreSessions = visibleSortedSessions.length < sortedSessions.length;

  useEffect(() => {
    const observer = sessionCardObserverRef.current;
    if (!observer) return;
    sessionCardRefs.current.forEach((el) => observer.observe(el));
  }, [visibleSortedSessions.length, sessionsListLayout]);

  const todayIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  }, []);

  const sessionsGroupedByDate = useMemo(() => {
    const groups: Array<{ dateIso: string; dateLabel: string; isToday: boolean; sessions: CourseSession[] }> = [];
    let currentKey = '__none__';
    visibleSortedSessions.forEach((s) => {
      const blocks = normalizeDateBlocks((s.dateBlocks || []) as CourseSessionDateBlock[]);
      const dateIso = blocks[0]?.date || '';
      if (dateIso !== currentKey) {
        currentKey = dateIso;
        groups.push({
          dateIso,
          dateLabel: dateIso ? formatDateUk(dateIso) : 'No date set',
          isToday: dateIso === todayIso,
          sessions: [],
        });
      }
      groups[groups.length - 1]!.sessions.push(s);
    });
    return groups;
  }, [visibleSortedSessions, todayIso]);

  const sessionTableRows = useMemo((): SessionListTableRow[] => {
    if (sessionsListLayout !== 'table') return [];
    const refIndexBySessionId = new Map<string, number>();
    [...sortedSessions]
      .sort((a, b) => compareSessionsForTimeRange(a, b, sessionFilters.timeRange))
      .forEach((s, idx) => {
        refIndexBySessionId.set(s.id, idx + 1);
      });

    return sortedSessions.map((s) => {
      const stats = getSessionStats(s.id);
      const ops = getSessionOpsSnapshot(s);
      const blocks = normalizeDateBlocks((s.dateBlocks || []) as CourseSessionDateBlock[]);
      const first = blocks[0];
      const dayCount = sessionDayCount(s.dateBlocks);
      const postMeta = parsePostSessionMeta(s.sessionOtherInfo);
      const humanNotes = String(s.sessionOtherInfo || '').split(POST_SESSION_META_PREFIX)[0].trim();
      const namedBooked = getListBookedCount(s.id);
      const virtualSeats = rosterHydratedRef.current.has(s.id)
        ? stats.booked.filter((a) => a.isVirtual).length
        : 0;
      return {
        sessionId: s.id,
        ref: sessionReferenceCode(s.id, refIndexBySessionId.get(s.id)),
        courseName: s.courseName,
        dates: formatDateBlocksUk(s.dateBlocks),
        firstDateIso: first?.date || '',
        firstDate: first?.date ? formatDateUk(first.date) : '?',
        dayCount,
        startTime: timeInputValue(first?.startTime) || '?',
        endTime: timeInputValue(first?.endTime) || '?',
        language: s.language || '?',
        deliveryMode: String(s.deliveryMode || '').replace(/_/g, ' ') || '?',
        location: locationById[s.locationId || preferredDefaultLocationId || ''] || '?',
        instructor: s.instructorId ? instructorLabelById[s.instructorId] || '?' : 'Unassigned',
        interpreter: s.interpreterId ? interpreterLabelById[s.interpreterId] || '?' : 'Unassigned',
        provider: s.providerId ? providerLabelById[s.providerId] || '?' : '?',
        capacity: Number(s.capacity || 0),
        booked: namedBooked,
        virtualSeats,
        availableSeats: Math.max(0, Number(s.capacity || 0) - namedBooked),
        status: s.status || 'scheduled',
        feeTargetEur: stats.revenueTarget,
        collectedEur: stats.revenueCollected,
        paidCount: stats.paidCount,
        halfPaidCount: stats.halfPaidCount,
        unpaidCount: stats.unpaidCount,
        docsCount: sessionDocumentCounts[s.id] ?? 0,
        documentsPrep: s.documentsPrepDone ? 'Yes' : 'No',
        certificatesPrep: s.certificatesPrepDone ? 'Yes' : 'No',
        opsHealth: ops.health,
        opsBlockers: ops.blockers,
        opsUnpaidAttendees: ops.unpaid,
        missingCompliance: ops.missingCompliance,
        postChecklistMissing: ops.postChecklistMissing,
        notes: humanNotes,
        createdBy: postMeta.createdByName || '?',
        cancellationReason: s.cancellationReason || '?',
        multiday: dayCount > 1 ? 'Yes' : 'No',
      };
    });
  }, [
    sessionsListLayout,
    sortedSessions,
    sessionFilters.timeRange,
    locationById,
    preferredDefaultLocationId,
    instructorLabelById,
    interpreterLabelById,
    providerLabelById,
    sessionDocumentCounts,
    attendeesBySession,
    bookedCountBySessionId,
    courseDbFees,
    paymentTimelineByAttendee,
  ]);
  const opsCounts = useMemo(() => {
    return opsBaseSessions.reduce(
      (acc, s) => {
        const snap = getSessionOpsSnapshot(s);
        if (snap.unpaid > 0) acc.unpaid += 1;
        if (snap.missingCompliance > 0) acc.compliance += 1;
        if (snap.docsMissing > 0) acc.docs += 1;
        if (snap.postChecklistMissing > 0 || snap.postAttendeeWork > 0) acc.post += 1;
        if (snap.noShows > 0) acc.noshow += 1;
        if (snap.waitlist > 0) acc.waitlist += 1;
        return acc;
      },
      { unpaid: 0, compliance: 0, docs: 0, post: 0, noshow: 0, waitlist: 0 }
    );
  }, [opsBaseSessions, attendeesBySession, customerById, sessionDocumentCounts, paymentTimelineByAttendee, courseDbFees]);

  useEffect(() => {
    if (!opsMode && sessionsListLayout !== 'table') return;
    const ids = sessions.map((s) => s.id).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    void (async () => {
      try {
        const maps = await courseSessionsService.listAttendeesBySessionIds(ids);
        if (cancelled) return;
        setAttendeesBySession((prev) => {
          const next = { ...maps };
          for (const id of rosterHydratedRef.current) {
            if (prev[id]) next[id] = prev[id];
          }
          return next;
        });
        const payments = await courseSessionsService.listPaymentsBySessionIds(ids).catch(() => ({}));
        if (!cancelled) {
          setPaymentTimelineByAttendee((prev) => {
            let next = { ...prev };
            for (const [sessionId, attendees] of Object.entries(maps)) {
              const sessionAttendeeIds = attendees.map((a) => a.id);
              const freshForSession: Record<string, AttendeePaymentLedgerRow[]> = {};
              for (const id of sessionAttendeeIds) {
                freshForSession[id] = payments[id] ?? [];
              }
              next = mergeSessionPayments(next, sessionAttendeeIds, freshForSession);
            }
            return next;
          });
        }
      } catch (e) {
        console.error('Failed to load ops/table attendee rows', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opsMode, sessionsListLayout, sessions]);

  useEffect(() => {
    if (!selectedSession) return;
    const postMeta = parsePostSessionMeta(selectedSession.sessionOtherInfo);
    setSessionDraft({
      capacity: Number(selectedSession.capacity || 1),
      locationId: selectedSession.locationId || preferredDefaultLocationId || '',
      instructorId: selectedSession.instructorId || '',
      interpreterId: selectedSession.interpreterId || '',
      providerId: selectedSession.providerId || '',
      notes: selectedSession.notes || '',
      deliveryMode: selectedSession.deliveryMode || 'in_class',
      documentsPrepDone: selectedSession.documentsPrepDone ?? false,
      certificatesPrepDone: selectedSession.certificatesPrepDone ?? false,
      sessionOtherInfo: String(selectedSession.sessionOtherInfo || '').split(POST_SESSION_META_PREFIX)[0].trim(),
      truckType: selectedSession.truckType || '',
      truckMake: selectedSession.truckMake || '',
      truckCapacity: selectedSession.truckCapacity || '',
      truckAttachments: selectedSession.truckAttachments || '',
      interpreterBriefing: selectedSession.interpreterBriefing || '',
      arrivalInstructions: selectedSession.arrivalInstructions || '',
      parkingAccess: selectedSession.parkingAccess || '',
      siteHazardsPpe: selectedSession.siteHazardsPpe || '',
      onSiteContactOverrideName: selectedSession.onSiteContactOverrideName || '',
      onSiteContactOverridePhone: selectedSession.onSiteContactOverridePhone || '',
      onSiteContactOverrideEmail: selectedSession.onSiteContactOverrideEmail || '',
      interpreterReminders: selectedSession.interpreterReminders || ''
    });
    setPostSessionChecklist(postMeta.checklist);
    const blocks = normalizeDateBlocks(selectedSession.dateBlocks || []);
    setSessionDateBlocksDraft(blocks.length ? blocks : [{ date: '', startTime: null, endTime: null }]);
  }, [
    selectedSessionId,
    selectedSession?.updatedAt,
    preferredDefaultLocationId
  ]);

  // Persist preferred default venue when a session has no location_id so Bookings list / Schedule match Logistics.
  useEffect(() => {
    if (!selectedSession?.id) return;
    if (selectedSession.locationId) return;
    if (!preferredDefaultLocationId) return;
    let cancelled = false;
    void (async () => {
      try {
        await courseSessionsService.updateSession(selectedSession.id, {
          locationId: preferredDefaultLocationId,
        });
        if (cancelled) return;
        patchSessionInState(selectedSession.id, { locationId: preferredDefaultLocationId });
      } catch (e) {
        console.warn('Could not persist default session location', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSession?.id, selectedSession?.locationId, preferredDefaultLocationId]);

  useEffect(() => {
    setShowHeaderExistingSearch(false);
  }, [selectedSessionId]);

  useEffect(() => {
    if (sessionMainTab !== 'history' || !selectedSession?.id) return;
    const sessionId = selectedSession.id;
    let cancelled = false;
    setSessionHistoryLoading(true);
    setSessionHistoryError(null);
    void (async () => {
      try {
        const events = await courseSessionsService.getSessionHistory(sessionId);
        if (cancelled) return;
        setSessionHistoryEvents(events);
      } catch (e: unknown) {
        if (cancelled) return;
        setSessionHistoryError((e as Error)?.message || 'Could not load session history.');
        setSessionHistoryEvents([]);
      } finally {
        if (!cancelled) setSessionHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSession?.id, sessionMainTab]);

  useEffect(() => {
    if (!selectedSession?.id) {
      setSessionHistoryEvents([]);
      setSessionHistoryError(null);
    }
  }, [selectedSession?.id]);

  useEffect(() => {
    if (!selectedSession) return;
    if (sessionMainTab === 'expenses') {
      lastAutoExpenseSignatureRef.current = expenseDraftSignature;
    }
    if (sessionMainTab === 'interpreters' || sessionMainTab === 'logistics' || sessionMainTab === 'postSession') {
      lastAutoDetailsSignatureRef.current = detailsDraftSignature;
    }
    setAutoSaveState('idle');
    setAutoSavedAtLabel('');
  }, [selectedSession?.id, sessionMainTab]);

  useEffect(() => {
    if (!selectedSession || sessionMainTab !== 'expenses') return;
    if (expenseDraftSignature === lastAutoExpenseSignatureRef.current) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        setAutoSaveState('saving');
        const ok = await saveSessionExpenses({ silent: true });
        if (ok) {
          lastAutoExpenseSignatureRef.current = expenseDraftSignature;
          setAutoSaveState('saved');
          setAutoSavedAtLabel(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        } else {
          setAutoSaveState('error');
        }
      })();
    }, 700);
    return () => window.clearTimeout(timer);
  }, [selectedSession?.id, sessionMainTab, expenseDraftSignature]);

  useEffect(() => {
    if (!selectedSession) return;
    if (sessionMainTab !== 'interpreters' && sessionMainTab !== 'logistics' && sessionMainTab !== 'postSession') return;
    if (detailsDraftSignature === lastAutoDetailsSignatureRef.current) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        setAutoSaveState('saving');
        const ok = await saveSessionDetails({ silent: true });
        if (ok) {
          lastAutoDetailsSignatureRef.current = detailsDraftSignature;
          setAutoSaveState('saved');
          setAutoSavedAtLabel(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        } else {
          setAutoSaveState('error');
        }
      })();
    }, 700);
    return () => window.clearTimeout(timer);
  }, [selectedSession?.id, sessionMainTab, detailsDraftSignature]);

  const selectedInstructorLabel = useMemo(() => {
    const i = instructors.find((x) => x.id === sessionDraft.instructorId);
    return i ? `${i.firstName} ${i.lastName}`.trim() || i.email : 'Unassigned';
  }, [instructors, sessionDraft.instructorId]);
  const selectedInterpreterLabel = useMemo(() => {
    const i = interpreterOptions.find((x) => x.id === sessionDraft.interpreterId);
    return i?.name?.trim() || 'Unassigned';
  }, [interpreterOptions, sessionDraft.interpreterId]);
  const requiresLiftTruckFieldsForSelectedSession = useMemo(() => {
    if (!selectedSession?.courseName) return false;
    return CERTIFICATE_FIELD_CONFIG.liftTruckFields.requiredFor.includes(selectedSession.courseName);
  }, [selectedSession?.courseName]);

  const isSafepassModal = selectedSession?.courseName === SAFE_PASS_COURSE;

  const saveSessionDetails = async (options?: { silent?: boolean }): Promise<boolean> => {
    if (!selectedSession) return false;
    const silent = options?.silent === true;
    const prev = selectedSession;
    const newCap = Math.max(1, Number(sessionDraft.capacity || 1));
    const newLoc = sessionDraft.locationId || null;
    const newInst = sessionDraft.instructorId || null;
    const newNotes = sessionDraft.notes || null;
    const newDelivery = sessionDraft.deliveryMode || 'in_class';
    const newInterp = sessionDraft.interpreterId || null;
    const newProv = sessionDraft.providerId?.trim() ? sessionDraft.providerId.trim() : null;
    const newDocs = !!sessionDraft.documentsPrepDone;
    const newCerts = !!sessionDraft.certificatesPrepDone;
    const newOtherBase = (sessionDraft.sessionOtherInfo || '').trim() || null;
    const creatorMeta = parsePostSessionMeta(prev.sessionOtherInfo);
    const newOther = composeSessionOtherInfoWithMeta(newOtherBase, postSessionChecklist, {
      createdByName: creatorMeta.createdByName,
      createdById: creatorMeta.createdById
    });
    const newTruckType = (sessionDraft.truckType || '').trim() || null;
    const newTruckMake = (sessionDraft.truckMake || '').trim() || null;
    const newTruckCapacity = (sessionDraft.truckCapacity || '').trim() || null;
    const newTruckAttachments = (sessionDraft.truckAttachments || '').trim() || null;
    const newInterpreterBriefing = (sessionDraft.interpreterBriefing || '').trim() || null;
    const newArrivalInstructions = (sessionDraft.arrivalInstructions || '').trim() || null;
    const newParkingAccess = (sessionDraft.parkingAccess || '').trim() || null;
    const newSiteHazardsPpe = (sessionDraft.siteHazardsPpe || '').trim() || null;
    const newOnSiteContactOverrideName = (sessionDraft.onSiteContactOverrideName || '').trim() || null;
    const newOnSiteContactOverridePhone = (sessionDraft.onSiteContactOverridePhone || '').trim() || null;
    const newOnSiteContactOverrideEmail = (sessionDraft.onSiteContactOverrideEmail || '').trim() || null;
    const newInterpreterReminders = (sessionDraft.interpreterReminders || '').trim() || null;
    const newDateBlocks = normalizeDateBlocks(sessionDateBlocksDraft);
    if (newDateBlocks.length === 0) {
      showError('Add at least one course day with a valid date.');
      return false;
    }

    const changes: string[] = [];
    if (dateBlocksSignature(prev.dateBlocks || []) !== dateBlocksSignature(newDateBlocks)) {
      changes.push(`dates ${formatDateBlocksUk(prev.dateBlocks)} ? ${formatDateBlocksUk(newDateBlocks)}`);
    }
    if (Number(prev.capacity) !== newCap) {
      changes.push(`capacity ${prev.capacity} ? ${newCap}`);
    }
    if ((prev.locationId || '') !== (newLoc || '')) {
      const prevLabel = prev.locationId ? locationById[prev.locationId] || prev.locationId : 'none';
      const newLabel = newLoc ? locationById[newLoc] || newLoc : 'none';
      changes.push(`location "${prevLabel}" ? "${newLabel}"`);
    }
    if ((prev.instructorId || '') !== (newInst || '')) {
      const prevI = instructors.find((x) => x.id === prev.instructorId);
      const newI = instructors.find((x) => x.id === newInst);
      const prevLabel = prevI ? `${prevI.firstName} ${prevI.lastName}`.trim() || prevI.email : 'none';
      const newLabel = newI ? `${newI.firstName} ${newI.lastName}`.trim() || newI.email : 'none';
      changes.push(`instructor "${prevLabel}" ? "${newLabel}"`);
    }
    const notesTrimPrev = (prev.notes || '').trim();
    const notesTrimNew = (newNotes || '').trim();
    if (notesTrimPrev !== notesTrimNew) {
      changes.push('session notes text updated');
    }
    if ((prev.deliveryMode || 'in_class') !== newDelivery) {
      changes.push(`delivery mode ${prev.deliveryMode || 'in_class'} ? ${newDelivery}`);
    }
    if ((prev.interpreterId || '') !== (newInterp || '')) {
      changes.push('interpreter updated');
    }
    if ((prev.providerId || '') !== (newProv || '')) {
      const prevP = providerOptions.find((x) => x.id === prev.providerId);
      const newP = providerOptions.find((x) => x.id === newProv);
      changes.push(
        `provider "${prevP?.name || 'none'}" ? "${newP?.name || 'none'}"`
      );
    }
    if ((prev.documentsPrepDone ?? false) !== newDocs) {
      changes.push(`documents prep ${prev.documentsPrepDone ? 'yes' : 'no'} ? ${newDocs ? 'yes' : 'no'}`);
    }
    if ((prev.certificatesPrepDone ?? false) !== newCerts) {
      changes.push(`certificates prep ${prev.certificatesPrepDone ? 'yes' : 'no'} ? ${newCerts ? 'yes' : 'no'}`);
    }
    const prevMeta = parsePostSessionMeta(prev.sessionOtherInfo);
    const prevOther = composeSessionOtherInfoWithMeta(
      String(prev.sessionOtherInfo || '').split(POST_SESSION_META_PREFIX)[0].trim(),
      prevMeta.checklist,
      { createdByName: prevMeta.createdByName, createdById: prevMeta.createdById }
    );
    const newOtherTrim = (newOther || '').trim();
    if (prevOther !== newOtherTrim) {
      changes.push('session other info updated');
    }
    if ((prev.truckType || '').trim() !== (newTruckType || '')) {
      changes.push('truck type updated');
    }
    if ((prev.truckMake || '').trim() !== (newTruckMake || '')) {
      changes.push('truck make updated');
    }
    if ((prev.truckCapacity || '').trim() !== (newTruckCapacity || '')) {
      changes.push('truck capacity updated');
    }
    if ((prev.truckAttachments || '').trim() !== (newTruckAttachments || '')) {
      changes.push('truck attachments updated');
    }
    if ((prev.interpreterBriefing || '').trim() !== (newInterpreterBriefing || '')) changes.push('interpreter briefing updated');
    if ((prev.arrivalInstructions || '').trim() !== (newArrivalInstructions || '')) changes.push('arrival instructions updated');
    if ((prev.parkingAccess || '').trim() !== (newParkingAccess || '')) changes.push('parking/access updated');
    if ((prev.siteHazardsPpe || '').trim() !== (newSiteHazardsPpe || '')) changes.push('site hazards/PPE updated');
    if ((prev.onSiteContactOverrideName || '').trim() !== (newOnSiteContactOverrideName || '')) changes.push('on-site contact name updated');
    if ((prev.onSiteContactOverridePhone || '').trim() !== (newOnSiteContactOverridePhone || '')) changes.push('on-site contact phone updated');
    if ((prev.onSiteContactOverrideEmail || '').trim() !== (newOnSiteContactOverrideEmail || '')) changes.push('on-site contact email updated');
    if ((prev.interpreterReminders || '').trim() !== (newInterpreterReminders || '')) changes.push('interpreter reminders updated');

    if (changes.length === 0) return true;

    try {
      if (!silent) {
        const ruleCheck = await courseSessionsService.checkSchedulingRules({
          dateBlocks: newDateBlocks,
          instructorId: newInst,
          interpreterId: newInterp,
          excludeSessionId: selectedSession.id
        });
        if (ruleCheck.blockedDay?.severity === 'warn') {
          const proceed = window.confirm(ruleCheck.blockedDay.reason || 'This date is marked as blocked (warning). Continue anyway?');
          if (!proceed) return false;
        }
      }
      await courseSessionsService.updateSession(selectedSession.id, {
        capacity: newCap,
        locationId: newLoc,
        instructorId: newInst,
        interpreterId: newInterp,
        providerId: newProv,
        notes: newNotes,
        dateBlocks: newDateBlocks,
        deliveryMode: newDelivery,
        documentsPrepDone: newDocs,
        certificatesPrepDone: newCerts,
        sessionOtherInfo: newOther,
        truckType: newTruckType,
        truckMake: newTruckMake,
        truckCapacity: newTruckCapacity,
        truckAttachments: newTruckAttachments,
        interpreterBriefing: newInterpreterBriefing,
        arrivalInstructions: newArrivalInstructions,
        parkingAccess: newParkingAccess,
        siteHazardsPpe: newSiteHazardsPpe,
        onSiteContactOverrideName: newOnSiteContactOverrideName,
        onSiteContactOverridePhone: newOnSiteContactOverridePhone,
        onSiteContactOverrideEmail: newOnSiteContactOverrideEmail,
        interpreterReminders: newInterpreterReminders,
        interpreterBriefingUpdatedBy: user?.id || null
      });
      patchSessionInState(selectedSession.id, {
        capacity: newCap,
        locationId: newLoc,
        instructorId: newInst,
        interpreterId: newInterp,
        providerId: newProv,
        notes: newNotes,
        dateBlocks: newDateBlocks,
        deliveryMode: newDelivery,
        documentsPrepDone: newDocs,
        certificatesPrepDone: newCerts,
        sessionOtherInfo: newOther,
        truckType: newTruckType,
        truckMake: newTruckMake,
        truckCapacity: newTruckCapacity,
        truckAttachments: newTruckAttachments,
        interpreterBriefing: newInterpreterBriefing,
        arrivalInstructions: newArrivalInstructions,
        parkingAccess: newParkingAccess,
        siteHazardsPpe: newSiteHazardsPpe,
        onSiteContactOverrideName: newOnSiteContactOverrideName,
        onSiteContactOverridePhone: newOnSiteContactOverridePhone,
        onSiteContactOverrideEmail: newOnSiteContactOverrideEmail,
        interpreterReminders: newInterpreterReminders
      });
      if (!silent && changes.length > 0) {
        const activityText = `Session detail change: ${changes.join('; ')}`;
        const datesSummary = formatDateBlocksUk(newDateBlocks);
        const roster = attendeesBySession[selectedSession.id] || [];
        const customerIds = [
          ...new Set(
            roster
              .filter((a) => isAttendeeOnActiveRoster(a))
              .map((a) => getAttendeeBillingCustomerId(a))
              .filter((id): id is string => id != null && id !== '')
          )
        ];
        await Promise.all(
          customerIds.map((cid) =>
            ClientNotesService.logClientTrainingUpdate(
              cid,
              { courseName: selectedSession.courseName, datesSummary },
              activityText,
              user?.id,
              actorName
            ).catch((err) => console.error('Failed to log client note:', err))
          )
        );
      }
      if (!silent) {
        await load();
        showSuccess('Session details updated.');
      }
      return true;
    } catch (e: any) {
      showError(e?.message || 'Failed to update session details.');
      return false;
    }
  };

  // Flush pending logistics/interpreter/post-session edits when leaving those tabs
  // (the debounced auto-save timer is cleared on tab change).
  useEffect(() => {
    const tab = sessionMainTab;
    return () => {
      if (tab !== 'interpreters' && tab !== 'logistics' && tab !== 'postSession') return;
      if (detailsDraftSignatureRef.current === lastAutoDetailsSignatureRef.current) return;
      void (async () => {
        const ok = await saveSessionDetails({ silent: true });
        if (ok) {
          lastAutoDetailsSignatureRef.current = detailsDraftSignatureRef.current;
        }
      })();
    };
  }, [sessionMainTab]);

  const handleUploadSessionDocument = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const targetSessionId = sessionDocumentsModal?.sessionId || selectedSession?.id || '';
    if (!file || !targetSessionId) return;
    event.target.value = '';
    const maxSizeBytes = 5 * 1024 * 1024;
    const allowedExt = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx']);
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
    if (!allowedExt.has(ext)) {
      showError('Only PDF, Word, and Excel files are supported.');
      return;
    }
    if (file.size > maxSizeBytes) {
      showError('File exceeds 5 MB limit.');
      return;
    }
    try {
      setSessionDocumentUploading(true);
      await courseSessionsService.uploadSessionDocument(targetSessionId, file);
      await loadSessionDocuments(targetSessionId);
      showSuccess('Document uploaded.');
    } catch (e: any) {
      showError(e?.message || 'Could not upload document.');
    } finally {
      setSessionDocumentUploading(false);
    }
  };

  const handleDeleteSessionDocument = async (doc: SessionDocumentAttachment) => {
    const targetSessionId = sessionDocumentsModal?.sessionId || selectedSession?.id || '';
    if (!targetSessionId) return;
    if (!window.confirm(`Delete "${doc.name}" from this session?`)) return;
    try {
      await courseSessionsService.deleteSessionDocument(doc.path);
      await loadSessionDocuments(targetSessionId);
      if (sessionDocumentViewer?.path === doc.path) setSessionDocumentViewer(null);
      showSuccess('Document deleted.');
    } catch (e: any) {
      showError(e?.message || 'Could not delete document.');
    }
  };

  const applyBulkPostChecklist = async (field: keyof PostSessionChecklist, value: boolean) => {
    const sessionIds = Object.keys(selectedOpsSessionIds).filter((id) => selectedOpsSessionIds[id]);
    if (sessionIds.length === 0) return;
    try {
      await Promise.all(
        sessionIds.map(async (id) => {
          const target = sessions.find((s) => s.id === id);
          if (!target) return;
          const current = parsePostSessionMeta(target.sessionOtherInfo);
          const next = { ...current.checklist, [field]: value };
          const sessionOtherInfo = composeSessionOtherInfoWithMeta(
            String(target.sessionOtherInfo || '').split(POST_SESSION_META_PREFIX)[0].trim(),
            next,
            { createdByName: current.createdByName, createdById: current.createdById }
          );
          await courseSessionsService.updateSession(id, { sessionOtherInfo });
        })
      );
      await load();
      showSuccess(`Updated checklist for ${sessionIds.length} session${sessionIds.length === 1 ? '' : 's'}.`);
    } catch (e: any) {
      showError(e?.message || 'Bulk checklist update failed.');
    }
  };

  const openSessionDocumentsModal = async (session: any, openPicker = false) => {
    setSessionDocumentsModal({ sessionId: session.id, courseName: session.courseName });
    await loadSessionDocuments(session.id);
    if (openPicker) {
      setTimeout(() => uploadDocumentInputRef.current?.click(), 0);
    }
  };

  const openQuickRescheduleModal = (session: any) => {
    const blocks = normalizeDateBlocks(session.dateBlocks || []);
    setQuickRescheduleModal({
      sessionId: session.id,
      courseName: session.courseName,
      dateBlocks: blocks.length ? blocks : [{ date: '', startTime: null, endTime: null }]
    });
  };

  const saveQuickReschedule = async () => {
    if (!quickRescheduleModal) return;
    const session = sessions.find((s) => s.id === quickRescheduleModal.sessionId);
    if (!session) {
      showError('Session not found.');
      return;
    }
    const newDateBlocks = normalizeDateBlocks(quickRescheduleModal.dateBlocks);
    if (newDateBlocks.length === 0) {
      showError('Add at least one course day with a valid date.');
      return;
    }
    try {
      const ruleCheck = await courseSessionsService.checkSchedulingRules({
        dateBlocks: newDateBlocks,
        instructorId: session.instructorId || null,
        interpreterId: session.interpreterId || null,
        excludeSessionId: session.id
      });
      if (ruleCheck.blockedDay?.severity === 'warn') {
        const proceed = window.confirm(ruleCheck.blockedDay.reason || 'This date is marked as blocked (warning). Continue anyway?');
        if (!proceed) return;
      }
      await courseSessionsService.updateSession(session.id, { dateBlocks: newDateBlocks });
      await load();
      setQuickRescheduleModal(null);
      showSuccess('Session rescheduled.');
    } catch (e: any) {
      showError(e?.message || 'Could not reschedule session.');
    }
  };

  const openCancelSessionModal = (session: CourseSession) => {
    if ((session.status || 'scheduled') === 'cancelled') return;
    setCancelSessionModal({ session, reason: '' });
  };

  const confirmCancelSession = async () => {
    if (!cancelSessionModal) return;
    const reason = cancelSessionModal.reason.trim();
    if (!reason) {
      showError('Please enter a cancellation reason.');
      return;
    }
    const session = cancelSessionModal.session;
    try {
      await courseSessionsService.cancelSession(session.id, reason);
      if (selectedSessionId === session.id) setSelectedSessionId(null);
      await load();
      setCancelSessionModal(null);
      showSuccess('Course session cancelled.');
    } catch (e: any) {
      showError(e?.message || 'Could not cancel session.');
    }
  };

  const hardDeleteSessionFromCard = async (session: CourseSession) => {
    const attendeeCount = getListBookedCount(session.id);
    const msg =
      attendeeCount > 0
        ? `Permanently delete "${session.courseName}"? This session has ${attendeeCount} active attendee(s). This cannot be undone.`
        : `Permanently delete course session "${session.courseName}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await courseSessionsService.deleteSession(session.id);
      if (selectedSessionId === session.id) setSelectedSessionId(null);
      await load();
      showSuccess('Course session deleted.');
    } catch (e: any) {
      showError(e?.message || 'Could not delete session.');
    }
  };

  const restoreSessionFromCard = async (session: CourseSession) => {
    if ((session.status || 'scheduled') !== 'cancelled') return;
    if (!window.confirm(`Restore "${session.courseName}" back to the active roster?`)) return;
    try {
      await courseSessionsService.restoreSession(session.id);
      await load();
      showSuccess('Course session restored.');
    } catch (e: any) {
      showError(e?.message || 'Could not restore session.');
    }
  };

  const promptSeatOpenedIfApplicable = (sessionId: string, freedLabel?: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;
    const attendees = attendeesBySession[sessionId] || [];
    const waitlistCount = attendees.filter((a) => isAttendeeWaitingList(a)).length;
    const booked = attendees.filter((a) => isAttendeeOnActiveRoster(a)).length;
    const cap = Number(session.capacity || 1);
    if (waitlistCount > 0 && booked < cap) {
      setSeatOpenedModal({ sessionId, waitlistCount, freedLabel });
    }
  };

  const fillFromWaitlist = async (attendeeId?: string) => {
    if (!selectedSession) return;
    const waitlist = waitingListSelectedAttendees;
    const target = attendeeId ? waitlist.find((a) => a.id === attendeeId) : waitlist[0];
    if (!target) return showError('No one on the waiting list.');
    if (bookedCount >= capacityCount) return showError('Session is at capacity.');
    try {
      await courseSessionsService.updateAttendee(target.id, { status: 'booked' });
      await load();
      setSeatOpenedModal(null);
      showSuccess(`${getAttendeeLabel(target)} moved from waiting list to the roster.`);
    } catch (e: any) {
      showError(e?.message || 'Could not fill seat from waiting list.');
    }
  };

  const markAttendeeNoShow = async (attendee: CourseSessionAttendee) => {
    if (attendee.isVirtual) return showError('Virtual seats cannot be marked as no-show.');
    if (!window.confirm(`Mark ${getAttendeeLabel(attendee)} as no-show?`)) return;
    try {
      await softRevokeSeatCertificates(attendee, 'Marked no-show');
      await courseSessionsService.updateAttendee(attendee.id, { status: 'no_show' });
      await load();
      promptSeatOpenedIfApplicable(selectedSession!.id, getAttendeeLabel(attendee));
      showSuccess('Marked as no-show.');
    } catch (e: any) {
      showError(e?.message || 'Could not mark no-show.');
    }
  };

  const markAttendeeFailed = async (attendee: CourseSessionAttendee) => {
    if (attendee.isVirtual) return showError('Virtual seats cannot be marked as failed.');
    if (!window.confirm(`Mark ${getAttendeeLabel(attendee)} as failed (did not pass the course)?`)) return;
    try {
      await softRevokeSeatCertificates(attendee, 'Marked failed');
      await courseSessionsService.updateAttendee(attendee.id, { status: 'failed' });
      await load();
      showSuccess('Marked as failed.');
    } catch (e: any) {
      showError(e?.message || 'Could not mark failed.');
    }
  };

  const restoreFromNoShow = async (attendee: CourseSessionAttendee) => {
    if (bookedCount >= capacityCount) {
      return showError('Session is at capacity ? remove someone or increase capacity first.');
    }
    try {
      if (attendee.individualCustomerId) {
        await assertNotAlreadyBooked(attendee.bookingId, attendee.individualCustomerId, attendee.id);
      }
      await courseSessionsService.updateAttendee(attendee.id, { status: 'booked' });
      await load();
      showSuccess(`${getAttendeeLabel(attendee)} restored to roster.`);
    } catch (e: any) {
      showError(e?.message || 'Could not restore to roster.');
    }
  };

  // Certificate-sent (Cert column) is derived from the real send path / email_outbox.
  // Send Certificate is a separate permission flag (send_certificate) on the roster.

  const exportCompanyInvoiceSnapshot = () => {
    if (!selectedSession) return;
    const nonVirtual = selectedAttendees.filter((a) => !a.isVirtual);
    const allAttendees = flattenRosterDisplayOrder(nonVirtual, getAttendeeCustomer, companyCustomers);
    if (allAttendees.length === 0) return showError('No participants on this session.');

    const buildParticipantRow = (a: CourseSessionAttendee): FinancialSummaryParticipantRow => {
      const c = getAttendeeCustomer(a);
      const label = getAttendeeLabel(a);
      const company =
        companyCustomers.find((co) => co.id === (a.companyCustomerId || a.sponsoringCompanyCustomerId))?.companyName ||
        c?.companyName ||
        'Individual';
      const fee = getEffectiveFee(a, c, selectedSession.courseName, courseDbFees, selectedSession.language);
      const ledger = paymentTimelineByAttendee[a.id] || [];
      const paid = ledger.length ? sumLedgerRawPaid(ledger) : Math.max(0, Number(a.amountPaid || 0));
      const activeCohort = selectedAttendees.filter((row) => isAttendeeOnActiveRoster(row));
      const cohort = companyCohortAttendees(a, activeCohort, companyCustomers, getAttendeeCustomer);
      const invoice = resolveAttendeeInvoice(a, c, companyCustomers, cohort);
      return {
        attendeeName: label,
        companyName: String(company).trim(),
        status: attendeeStatusLabel(a.status),
        feeEuro: fee,
        paidEuro: paid,
        balanceEuro: Math.max(0, fee - paid),
        paymentStatus: a.paymentStatus || 'unpaid',
        invoiceNumber: invoice.number || '',
        invoicePaid: invoice.paid ? 'Yes' : invoice.number ? 'No' : '',
      };
    };

    const participantGroups: FinancialSummaryParticipantGroup[] = [
      {
        label: 'Active roster',
        rows: allAttendees.filter(isAttendeeOnActiveRoster).map(buildParticipantRow),
      },
      {
        label: 'No-shows',
        rows: allAttendees.filter(isAttendeeNoShow).map(buildParticipantRow),
      },
      {
        label: 'Failed',
        rows: allAttendees.filter(isAttendeeFailed).map(buildParticipantRow),
      },
      {
        label: 'Waiting list',
        rows: allAttendees.filter(isAttendeeWaitingList).map(buildParticipantRow),
      },
      {
        label: 'Removed / transferred',
        rows: allAttendees.filter(isAttendeeRemoved).map(buildParticipantRow),
      },
    ].filter((group) => group.rows.length > 0);

    const paymentRows: FinancialSummaryPaymentRow[] = [];
    for (const a of allAttendees) {
      const c = getAttendeeCustomer(a);
      const label = getAttendeeLabel(a);
      const company =
        companyCustomers.find((co) => co.id === (a.companyCustomerId || a.sponsoringCompanyCustomerId))?.companyName ||
        c?.companyName ||
        'Individual';
      const status = attendeeStatusLabel(a.status);
      for (const p of paymentTimelineByAttendee[a.id] || []) {
        paymentRows.push({
          participant: label,
          company: String(company).trim(),
          status,
          date: p.paymentDate ? formatDateUk(p.paymentDate) : '',
          method: p.method,
          amount: Number(p.amount || 0),
          stepType: p.stepType || 'other',
          notes: String(p.payerNotes || ''),
        });
      }
    }

    setFinancialSummaryModal({
      session: selectedSession,
      datesLabel: formatDateBlocksUk(selectedSession.dateBlocks),
      locationLabel: locationById[selectedSession.locationId || ''] || 'No location',
      generatedAt: new Date().toLocaleString('en-GB'),
      overview: selectedMoney,
      collectedProfit: selectedCollectedProfit,
      profitTotal: selectedProfitTotal,
      totalOutlay: selectedTotalOutlay,
      financialSnapshot,
      participantGroups,
      paymentRows,
    });
  };

  const moveAttendeeToWaitingList = async (attendee: CourseSessionAttendee) => {
    const existing = String(attendee.attendeeNotes || '').trim();
    const stamp = `Waiting list (${formatDateUk(new Date().toISOString().slice(0, 10))})`;
    const merged = existing ? `${existing}\n${stamp}` : stamp;
    const label = getAttendeeLabel(attendee);
    try {
      await courseSessionsService.updateAttendee(attendee.id, {
        status: 'waiting_list',
        attendeeNotes: merged
      });
      await load();
      promptSeatOpenedIfApplicable(selectedSession!.id, label);
      showSuccess('Moved to waiting list.');
    } catch (e: any) {
      showError(e?.message || 'Could not move to waiting list.');
    }
  };

  const buildSessionCardMenuItems = (s: CourseSession, isSessionCancelled: boolean): OverflowMenuItem[] => [
    {
      icon: Upload,
      label: 'Upload docs',
      onClick: () => void openSessionDocumentsModal(s, true),
    },
    {
      icon: Eye,
      label: 'View docs',
      onClick: () => void openSessionDocumentsModal(s),
    },
    {
      icon: CalendarDays,
      label: 'Reschedule',
      onClick: () => openQuickRescheduleModal(s),
      disabled: isSessionCancelled,
    },
    ...(isSessionCancelled
      ? [
          {
            icon: RotateCcw,
            label: 'Restore session',
            onClick: () => void restoreSessionFromCard(s),
          },
        ]
      : [
          {
            icon: Trash2,
            label: 'Cancel session',
            onClick: () => openCancelSessionModal(s),
            destructive: true,
          },
        ]),
    {
      icon: Trash2,
      label: 'Delete session',
      onClick: () => void hardDeleteSessionFromCard(s),
      destructive: true,
    },
    ...(currentCustomer
      ? [
          {
            icon: UserPlus,
            label: 'Assign profile',
            onClick: () => assignCurrent(s.id),
            disabled: isSessionCancelled,
          },
        ]
      : []),
  ];

  const buildManageSessionMenuItems = (): OverflowMenuItem[] => {
    if (!selectedSession) return [];
    const isCancelled = (selectedSession.status || 'scheduled') === 'cancelled';
    const tabItems = SESSION_MAIN_TABS.map((tab) => ({
      icon: tab.icon,
      label: tab.label,
      onClick: () => setSessionMainTab(tab.id),
    }));
    const financeItem: OverflowMenuItem = {
      icon: Wallet,
      label: rosterSnapshotOpen ? 'Hide finance snapshot' : 'Show finance snapshot',
      onClick: () => setRosterSnapshotOpen((v) => !v),
    };
    const uploadDocsItem: OverflowMenuItem = {
      icon: Upload,
      label: 'Upload docs',
      onClick: () => void openSessionDocumentsModal(selectedSession, true),
    };
    const viewDocsItem: OverflowMenuItem = {
      icon: Eye,
      label: 'View docs',
      onClick: () => void openSessionDocumentsModal(selectedSession),
    };
    const rescheduleItem: OverflowMenuItem = {
      icon: CalendarDays,
      label: 'Reschedule',
      onClick: () => openQuickRescheduleModal(selectedSession),
      disabled: isCancelled,
    };
    const cancelOrRestoreItems: OverflowMenuItem[] = isCancelled
      ? [
          {
            icon: RotateCcw,
            label: 'Restore session',
            onClick: () => void restoreSessionFromCard(selectedSession),
          },
        ]
      : [
          {
            icon: Trash2,
            label: 'Cancel session',
            onClick: () => openCancelSessionModal(selectedSession),
            destructive: true,
          },
        ];
    const deleteItem: OverflowMenuItem = {
      icon: Trash2,
      label: 'Delete session',
      onClick: () => void hardDeleteSessionFromCard(selectedSession),
      destructive: true,
    };
    const safePassBlocksCertificates = isSafePassCourse(selectedSession.courseName);
    const generateCertsItem: OverflowMenuItem = {
      icon: FileDown,
      label: safePassBlocksCertificates
        ? 'Generate certificates (disabled for Safe Pass)'
        : isGeneratingSessionCerts
          ? 'Generating certs?'
          : 'Generate certificates',
      onClick: () => void generateSessionCertificatesZip(),
      disabled:
        safePassBlocksCertificates ||
        isGeneratingSessionCerts ||
        !selectedAttendees.some(
          (a) =>
            isAttendeeOnActiveRoster(a) &&
            isAttendeeCertificateEligible(a, {
              courseName: selectedSession.courseName,
              processNumber: a.safePassProcessNumber || undefined,
              cardStatus: a.safePassCardStatus,
            })
        ),
    };
    const companyInvoiceItem: OverflowMenuItem = {
      icon: FileDown,
      label: 'Course Financial Summary',
      onClick: () => exportCompanyInvoiceSnapshot(),
    };
    const waitlistItem: OverflowMenuItem[] =
      waitingListSelectedAttendees.length > 0
        ? [
            {
              icon: Users,
              label: `Fill from waitlist (${waitingListSelectedAttendees.length})`,
              onClick: () => void fillFromWaitlist(),
            },
          ]
        : [];
    return [
      financeItem,
      ...tabItems,
      uploadDocsItem,
      viewDocsItem,
      rescheduleItem,
      ...cancelOrRestoreItems,
      deleteItem,
      generateCertsItem,
      companyInvoiceItem,
      ...waitlistItem,
    ];
  };
  const sanitizeFilePart = (raw: string): string =>
    raw
      .replace(/[<>:"/\\|?*]+/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'certificate';

  const getAttendeeCertificateName = (a: CourseSessionAttendee): string => {
    return getAttendeeLabel(a);
  };

  const openSession = (sessionId: string, tab: SessionMainTab = 'roster') => {
    setSelectedSessionId(sessionId);
    setSessionMainTab(tab);
    setShowHeaderExistingSearch(false);
    void loadSessionRoster(sessionId);
  };

  const closeSession = () => {
    setSelectedSessionId(null);
    setSessionMainTab('roster');
  };

  const markAttendeeAttendance = async (attendee: CourseSessionAttendee, attended: boolean) => {
    const paymentStatus = attendee.paymentStatus || 'unpaid';
    if (attended) {
      if (attendee.isVirtual) {
        showError('Reserved/virtual seats cannot be marked attended.');
        return;
      }
      if (paymentStatus !== 'paid') {
        const proceed = window.confirm(
          'Participant has not fully paid for the course. Are you sure you want to mark them as Attended?'
        );
        if (!proceed) return;
      }
    }
    try {
      await courseSessionsService.updateAttendee(attendee.id, {
        status: attended ? 'completed' : 'booked'
      });
      await load();
    } catch (e: any) {
      showError(e?.message || 'Could not update attendance.');
    }
  };

  const updateAttendeeRosterNote = async (attendee: CourseSessionAttendee, value: string | null) => {
    const normalized = (value || '').trim().slice(0, ROSTER_NOTE_MAX_LEN) || null;
    const prev = String(attendee.rosterNote || '').trim() || null;
    if (prev === normalized) return;
    try {
      await courseSessionsService.updateAttendee(attendee.id, { rosterNote: normalized });
      await load();
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not update note.');
    }
  };

  const updateAttendeeRosterFlags = async (
    attendee: CourseSessionAttendee,
    patch: {
      sendCertificate?: boolean;
      rosterAttended?: boolean;
      onlineCpl?: boolean;
      mhFlag?: boolean;
    }
  ) => {
    if (attendee.isVirtual && (patch.sendCertificate || patch.rosterAttended)) {
      showError('Reserved/virtual seats cannot be marked attended or allowed for certificates.');
      return;
    }
    try {
      await courseSessionsService.updateAttendee(attendee.id, patch);
      await load();
    } catch (e: unknown) {
      showError((e as Error)?.message || 'Could not update roster flag.');
    }
  };

  const updateAttendeeSafePassCard = async (
    attendee: CourseSessionAttendee,
    patch: {
      cardStatus?: SafePassCardTrackingStatus;
      cardNotes?: string;
      cardStatusDate?: string;
    }
  ) => {
    const customer = getAttendeeCustomer(attendee);
    const processNumber = String(attendee.safePassProcessNumber || (customer as any)?.safePassProcessNumber || '').trim();
    const paymentStatus = attendee.paymentStatus || 'unpaid';
    const attended = attendee.status === 'completed';
    const complianceOk = !isSafePassCourse(selectedSession?.courseName) || processNumber.length > 0;
    const paymentOk = paymentStatus === 'paid';
    const nextStatus = patch.cardStatus ?? ((attendee.safePassCardStatus || 'not_received') as SafePassCardTrackingStatus);
    const nextNotes = patch.cardNotes !== undefined ? patch.cardNotes : String(attendee.safePassCardNotes || '');

    if (nextStatus === 'posted') {
      if (!attended) {
        showError('Mark attendance before setting card status to Posted.');
        return;
      }
      if (!paymentOk) {
        showError('Participant must be fully paid before posting the card.');
        return;
      }
      if (!complianceOk) {
        showError('Process number is required before posting the card.');
        return;
      }
    }

    try {
      await courseSessionsService.updateAttendee(attendee.id, {
        safePassCardStatus: nextStatus,
        safePassCardNotes: nextNotes.trim() || null,
        safePassCardStatusDate: patch.cardStatusDate || todayIsoDate(),
      });
      await load();
    } catch (e: any) {
      showError(e?.message || 'Could not update Safe Pass card status.');
    }
  };

  const openProcessNumbersModule = (tab: 'process-numbers' | 'safe-pass-cards' = 'safe-pass-cards') => {
    if (!selectedSessionId) return;
    if (onViewChange) {
      onViewChange('process-numbers', { activeTab: tab, sessionId: selectedSessionId });
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'process-numbers');
    window.history.replaceState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
    showSuccess('Open Process Numbers / Safe Pass Cards from the sidebar for this session.');
  };

  const generateSessionCertificatesZip = async () => {
    if (!selectedSession) return;
    if (isSafePassCourse(selectedSession.courseName)) {
      showError(
        'Safe Pass courses do not issue RapidCert standard certificates (SOLAS plastic cards only). Generate certificates stays disabled for these sessions.'
      );
      return;
    }
    const activeAttendees = selectedAttendees.filter((a) => isAttendeeOnActiveRoster(a));
    if (activeAttendees.length === 0) {
      showError('No active attendees in this session.');
      return;
    }

    const ineligible = activeAttendees
      .map((attendee) => ({
        attendee,
        reasons: getCertificateIneligibilityReasons(attendee, {
          courseName: selectedSession.courseName,
          processNumber: attendee.safePassProcessNumber || undefined,
          cardStatus: attendee.safePassCardStatus,
        }),
      }))
      .filter((entry) => entry.reasons.length > 0);

    const eligibleAttendees = activeAttendees.filter(
      (attendee) =>
        isAttendeeCertificateEligible(attendee, {
          courseName: selectedSession.courseName,
          processNumber: attendee.safePassProcessNumber || undefined,
          cardStatus: attendee.safePassCardStatus,
        })
    );

    if (eligibleAttendees.length === 0) {
      const summary = ineligible
        .slice(0, 5)
        .map((entry) => `${getAttendeeLabel(entry.attendee)}: ${entry.reasons.join(', ')}`)
        .join('; ');
      showError(
        `No attendees are eligible for certificates. ${summary}${ineligible.length > 5 ? '?' : ''}`
      );
      return;
    }

    const blocks = normalizeDateBlocks(selectedSession.dateBlocks || []);
    const courseDate = blocks[0]?.date;
    if (!courseDate) {
      showError('Session has no valid date. Add at least one date first.');
      return;
    }
    const courseDates = blocks.map((b) => b.date).filter(Boolean);

    const instructorName =
      selectedInstructorLabel && selectedInstructorLabel !== 'Unassigned' ? selectedInstructorLabel : undefined;

    try {
      setIsGeneratingSessionCerts(true);
      const zip = new JSZip();
      const customersToPersist = new Map<string, Customer>();
      const sessionRefIndex = buildSessionReferenceIndex(sessions);
      const sessionRef =
        sessionReferenceCode(selectedSession.id, sessionRefIndex.get(selectedSession.id)) || null;
      for (const attendee of eligibleAttendees) {
        const customerId = attendee.individualCustomerId;
        if (!customerId) {
          throw new Error('Certificate generation requires an individual participant on every roster seat.');
        }
        let customer = customerById[customerId];
        if (!customer) {
          customer = await supabaseCustomerStorage.getCustomerById(customerId);
        }
        if (!customer) {
          throw new Error(`CRM record not found for ${getAttendeeLabel(attendee)}.`);
        }
        const attendeeName = await resolveAttendeeLabelForCertificate(attendee);
        const provisionalId = provisionalSessionCertificateId(selectedSession.id, attendee.id);
        let customerForUpdate = customersToPersist.get(customerId);
        if (!customerForUpdate) {
          customerForUpdate = customer;
          customersToPersist.set(customerId, customer);
        }
        const numberIdentity = {
          currentId: provisionalId,
          sessionId: selectedSession.id,
          attendeeId: attendee.id,
          attendeeName,
          courseName: selectedSession.courseName,
          courseDate,
          customerId,
        };
        const existingVin = await findExistingCertificateNumber(
          numberIdentity,
          customerForUpdate.generatedCertificates
        );
        const cert: ProcessedCertificate = {
          id: existingVin || provisionalId,
          attendeeName,
          companyName: customer?.companyName || attendeeName,
          courseName: selectedSession.courseName,
          courseDate,
          courseDates: courseDates.length > 1 ? courseDates : undefined,
          instructorName,
          truckType: (selectedSession.truckType || '').trim() || undefined,
          truckMake: (selectedSession.truckMake || '').trim() || undefined,
          truckCapacity: (selectedSession.truckCapacity || '').trim() || undefined,
          truckAttachments: (selectedSession.truckAttachments || '').trim() || undefined,
          fileName: buildDefaultCertificateFileName(selectedSession.courseName, attendeeName, courseDate),
          bookingId: selectedSession.id,
          attendeeId: attendee.id,
        };
        const blob = await generateCertificatePDF(cert);
        zip.file(cert.fileName, blob);

        const dateForExpiry = cert.courseDates?.length
          ? cert.courseDates[cert.courseDates.length - 1]
          : cert.courseDate;
        const bookingCompanyName =
          linkedCompanyDisplayForSeat(attendee, customer, companyCustomers) || null;
        const generatedCert: GeneratedCertificate = {
          id: cert.id,
          attendeeName: cert.attendeeName,
          courseName: cert.courseName,
          courseDate: cert.courseDate,
          courseDates: cert.courseDates && cert.courseDates.length > 0 ? cert.courseDates : undefined,
          expiryDate: calculateExpiryDate(dateForExpiry, cert.courseName),
          fileName: cert.fileName,
          generatedAt: new Date().toISOString(),
          status: 'active',
          instructorName: cert.instructorName,
          location_id: selectedSession.locationId || undefined,
          sessionId: selectedSession.id,
          attendeeId: attendee.id,
          bookingCompanyName: bookingCompanyName || null,
          generatedBy: actorName || null,
          generatedByUserId: user?.id || null,
          sessionReference: sessionRef,
          courseLanguage: selectedSession.language || null,
          truckType: cert.truckType,
          truckMake: cert.truckMake,
          truckCapacity: cert.truckCapacity,
          truckAttachments: cert.truckAttachments
        };

        const existingCertificates = customerForUpdate.generatedCertificates || [];
        const existingCertIndex = findMatchingGeneratedCertificateIndex(existingCertificates, {
          ...numberIdentity,
          currentId: generatedCert.id,
        });
        const nextCertificates = [...existingCertificates];
        if (existingCertIndex >= 0) {
          const prevCert = existingCertificates[existingCertIndex];
          nextCertificates[existingCertIndex] = {
            ...prevCert,
            ...generatedCert,
            generatedAt: prevCert.generatedAt || generatedCert.generatedAt,
            id: generatedCert.id,
            // Keep prior send / fail metadata across regenerations of the same seat.
            emailSentAt: prevCert.emailSentAt,
            emailSentBy: prevCert.emailSentBy,
            emailSentByUserId: prevCert.emailSentByUserId,
            emailSentTo: prevCert.emailSentTo,
            emailSendStatus: prevCert.emailSendStatus,
            emailSendError: prevCert.emailSendError,
            emailBrevoMessageId: prevCert.emailBrevoMessageId,
          };
        } else {
          nextCertificates.push(generatedCert);
        }

        customersToPersist.set(customerId, {
          ...customerForUpdate,
          numCertsGenerated: nextCertificates.length,
          generatedCertificates: nextCertificates
        });

        try {
          await upsertIssuedCertificateFromGenerated({
            cert: generatedCert,
            customerId,
            issuedBy: customerForUpdate.companyName || generatedCert.attendeeName,
          });
        } catch (registryErr) {
          console.error('issued_certificates upsert failed', registryErr);
        }
      }

      for (const updatedCustomer of customersToPersist.values()) {
        await supabaseCustomerStorage.updateCustomer(updatedCustomer);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const stamp = formatDateUkForFileName(new Date().toISOString().slice(0, 10));
      saveAs(zipBlob, `session-certs-${sanitizeFilePart(selectedSession.courseName)}-${stamp}.zip`);
      void courseSessionsService.logSessionActivity({
        sessionId: selectedSession.id,
        actorUserId: user?.id || null,
        actorName: actorName || null,
        eventType: 'certificates_generated',
        summary: `Generated ${eligibleAttendees.length} certificate(s)`,
        details: {
          count: eligibleAttendees.length,
          skippedCount: ineligible.length,
          failedCount: 0,
        },
      });
      if (ineligible.length > 0) {
        void courseSessionsService.logSessionActivity({
          sessionId: selectedSession.id,
          actorUserId: user?.id || null,
          actorName: actorName || null,
          eventType: 'certificates_generation_failed',
          summary: `Skipped ${ineligible.length} ineligible attendee(s) during certificate generation`,
          details: {
            failedCount: ineligible.length,
            generatedCount: eligibleAttendees.length,
          },
        });
      }
      showSuccess(
        ineligible.length > 0
          ? `Generated ${eligibleAttendees.length} certificate(s). Skipped ${ineligible.length} ineligible attendee(s).`
          : `Generated ${eligibleAttendees.length} certificate(s). ZIP is ready to print.`
      );
    } catch (e: any) {
      showError(e?.message || 'Failed to generate session certificates.');
    } finally {
      setIsGeneratingSessionCerts(false);
    }
  };

  return (
    <div className="course-sessions-panel space-y-4">
      <input
        ref={uploadDocumentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx"
        className="hidden"
        onChange={handleUploadSessionDocument}
      />
      {mode === 'manage' && (
        <div ref={bookingsStickyRef} className="sticky top-[calc(3.75rem+env(safe-area-inset-top))] z-20 space-y-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm lg:top-0 dark:border-slate-600 dark:bg-slate-800/95 dark:shadow-black/20">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search sessions?"
                className={`w-full ${nativeControl} pl-9`}
                value={sessionFilters.query}
                onChange={(e) => setSessionFilters((f) => ({ ...f, query: e.target.value }))}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${
                  newSessionPanelOpen
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setNewSessionPanelOpen((v) => !v)}
              >
                <Plus className="h-4 w-4" />
                New session
              </button>
              <button
                type="button"
                className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${
                  sessionFiltersPanelOpen || activeSessionFilterCount > 0
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setSessionFiltersPanelOpen((v) => !v)}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeSessionFilterCount > 0 ? (
                  <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {activeSessionFilterCount}
                  </span>
                ) : null}
              </button>
              <div className="hidden md:flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="Sessions list layout">
                <button
                  type="button"
                  onClick={() => setSessionsListLayout('table')}
                  title="Table view"
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
                    sessionsListLayout === 'table' ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Table2 className="h-3.5 w-3.5" />
                  Table
                </button>
                <button
                  type="button"
                  onClick={() => setSessionsListLayout('cards')}
                  title="Card view"
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
                    sessionsListLayout === 'cards' ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  Cards
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div
              className="flex w-full flex-nowrap gap-0.5 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-0.5 [-webkit-overflow-scrolling:touch] sm:w-auto sm:flex-wrap"
              role="tablist"
              aria-label="Session date range"
            >
              {(
                [
                  { id: 'upcoming' as const, label: 'Upcoming', hint: 'Today and later, soonest first' },
                  { id: 'past' as const, label: 'Past', hint: 'Before today, oldest dates first' },
                  { id: 'all' as const, label: 'All', hint: 'Every session, calendar order' },
                ] as const
              ).map((tab) => {
                const active = sessionFilters.timeRange === tab.id;
                const count = sessionTimeRangeCounts[tab.id];
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    title={tab.hint}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                      active ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-700 hover:bg-white'
                    }`}
                    onClick={() => setSessionFilters((f) => ({ ...f, timeRange: tab.id }))}
                  >
                    {tab.label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <BookingsCollapsibleSection
          title="New session"
          open={newSessionPanelOpen}
          onToggle={() => setNewSessionPanelOpen((v) => !v)}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-3">
              <label className={createRowLabel}>Course</label>
              {(() => {
                const typed = newSession.courseName.trim();
                const showHits =
                  newSessionPanelOpen && typed.length >= 2 && !coursePickerChosen;
                return (
                  <div className="relative space-y-1">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        className={`w-full ${nativeControl} pl-8`}
                        value={newSession.courseName}
                        onChange={(e) => {
                          setCoursePickerChosen(false);
                          setNewSession((p) => ({ ...p, courseName: e.target.value }));
                        }}
                        placeholder="Search courses?"
                        autoComplete="off"
                      />
                    </div>
                    {typed.length > 0 && typed.length < 2 ? (
                      <p className="text-[10px] text-slate-500">Type at least 2 characters to search.</p>
                    ) : null}
                    {showHits ? (
                      <div className="absolute z-20 mt-0.5 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                        {coursePickerSearching ? (
                          <p className="px-3 py-2 text-xs text-slate-500">Searching?</p>
                        ) : coursePickerHits.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-slate-500">No courses match that search.</p>
                        ) : (
                          coursePickerHits.map((courseName) => (
                            <button
                              key={courseName}
                              type="button"
                              className="block w-full truncate border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
                              onClick={() => {
                                setNewSession((p) => ({ ...p, courseName }));
                                setCoursePickerChosen(true);
                                setCoursePickerHits([]);
                              }}
                            >
                              {courseName}
                            </button>
                          ))
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
            <div className="lg:col-span-2">
              <label className={createRowLabel}>Course day</label>
              <input
                type="date"
                lang="en-GB"
                className={`w-full ${nativeControl}`}
                value={newSession.firstDate}
                onChange={(e) => setNewSession((p) => ({ ...p, firstDate: e.target.value }))}
              />
              {newSession.firstDate ? (
                <p className="mt-0.5 text-[10px] text-gray-500">{formatDateUk(newSession.firstDate)}</p>
              ) : null}
            </div>
            <div className="lg:col-span-1">
              <label className={createRowLabel}>Language</label>
              <select
                className={`w-full ${nativeControl}`}
                disabled={sessionLanguageOptions.length === 0}
                value={newSession.language}
                onChange={(e) => setNewSession((p) => ({ ...p, language: e.target.value }))}
              >
                {sessionLanguageOptions.length === 0 ? (
                  <option value="">No languages configured</option>
                ) : (
                  sessionLanguageOptions.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="lg:col-span-1">
              <label className={createRowLabel}>Delivery mode</label>
              <select
                className={`w-full ${nativeControl}`}
                value={newSession.deliveryMode}
                onChange={(e) => setNewSession((p) => ({ ...p, deliveryMode: e.target.value }))}
              >
                <option value="in_class">In class</option>
                <option value="online">Online</option>
                <option value="blended">Blended</option>
                <option value="on_site">On site</option>
              </select>
            </div>
            <div className="lg:col-span-1">
              <label className={createRowLabel}>Capacity</label>
              <input
                type="number"
                min={1}
                title="Capacity"
                className={`w-full ${nativeControl} placeholder:text-gray-400`}
                value={newSession.capacity}
                onChange={(e) => setNewSession((p) => ({ ...p, capacity: Number(e.target.value) }))}
              />
            </div>
            <div className="lg:col-span-2">
              <label className={createRowLabel}>Location</label>
              <select
                className={`w-full ${nativeControl}`}
                value={newSession.locationId}
                onChange={(e) => setNewSession((p) => ({ ...p, locationId: e.target.value }))}
              >
                <option value="">Select location</option>
                {locationOptions.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className={createRowLabel}>Instructor</label>
              <select
                className={`w-full ${nativeControl}`}
                value={newSession.instructorId}
                onChange={(e) => setNewSession((p) => ({ ...p, instructorId: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {`${i.firstName} ${i.lastName}`.trim() || i.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className={createRowLabel}>Interpreter</label>
              <select
                className={`w-full ${nativeControl}`}
                value={newSession.interpreterId}
                onChange={(e) => setNewSession((p) => ({ ...p, interpreterId: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {interpreterOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className={createRowLabel}>Provider</label>
              <select
                className={`w-full ${nativeControl}`}
                value={newSession.providerId}
                onChange={(e) => setNewSession((p) => ({ ...p, providerId: e.target.value }))}
              >
                <option value="">?</option>
                {providerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-1">
              <label className={fieldLabel}>Start</label>
              <input
                type="time"
                title="Start time"
                className={`w-full ${nativeControl}`}
                value={newSession.startTime}
                onChange={(e) => setNewSession((p) => ({ ...p, startTime: e.target.value }))}
              />
            </div>
            <div className="lg:col-span-1">
              <label className={fieldLabel}>End</label>
              <input
                type="time"
                title="End time"
                className={`w-full ${nativeControl}`}
                value={newSession.endTime}
                onChange={(e) => setNewSession((p) => ({ ...p, endTime: e.target.value }))}
              />
            </div>
            <button type="button" onClick={createSession} className={`lg:col-span-2 ${btnPrimary} w-full`}>
              Create
            </button>
          </div>
          <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Additional course days</p>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                onClick={() =>
                  setExtraSessionDays((p) => [...p, { date: '', startTime: newSession.startTime, endTime: newSession.endTime }])
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Add day
              </button>
            </div>
            {extraSessionDays.length > 0 ? (
              <div className="mt-2 space-y-2">
                {extraSessionDays.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2 sm:grid-cols-12 sm:items-end">
                    <div className="sm:col-span-3">
                      <label className={fieldLabel}>Date</label>
                      <input
                        type="date"
                        className={nativeControl}
                        value={row.date}
                        onChange={(e) =>
                          setExtraSessionDays((p) => {
                            const next = [...p];
                            next[idx] = { ...next[idx]!, date: e.target.value };
                            return next;
                          })
                        }
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className={fieldLabel}>Start</label>
                      <input
                        type="time"
                        className={nativeControl}
                        value={row.startTime}
                        onChange={(e) =>
                          setExtraSessionDays((p) => {
                            const next = [...p];
                            next[idx] = { ...next[idx]!, startTime: e.target.value };
                            return next;
                          })
                        }
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className={fieldLabel}>End</label>
                      <input
                        type="time"
                        className={nativeControl}
                        value={row.endTime}
                        onChange={(e) =>
                          setExtraSessionDays((p) => {
                            const next = [...p];
                            next[idx] = { ...next[idx]!, endTime: e.target.value };
                            return next;
                          })
                        }
                      />
                    </div>
                    <div className="flex items-end sm:col-span-3">
                      <button
                        type="button"
                        className="w-full rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50"
                        aria-label="Remove day"
                        onClick={() => setExtraSessionDays((p) => p.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                One course can run across several days. Optional per-day times; leave blank to reuse the first day?s start and end.
              </p>
            )}
          </div>
        </BookingsCollapsibleSection>
        <BookingsCollapsibleSection
          title="Advanced filters"
          open={sessionFiltersPanelOpen}
          onToggle={() => setSessionFiltersPanelOpen((v) => !v)}
          badge={
            <>
              <span className="text-xs font-normal normal-case tracking-normal text-gray-500">
                Course, location, language, instructor?
              </span>
              {activeSessionFilterCount > 0 ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-emerald-800">
                  {activeSessionFilterCount} active
                </span>
              ) : null}
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <div className="min-w-0">
                <label className={fieldLabel}>Course</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.courseName} onChange={(e) => setSessionFilters((f) => ({ ...f, courseName: e.target.value }))}>
                  <option value="">Any course</option>
                  {sessionFilterCourseOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0 max-w-xs">
                <label className={fieldLabel}>Location</label>
                <select
                  className={`w-full max-w-xs ${nativeControl}`}
                  value={sessionFilters.locationId}
                  onChange={(e) => setSessionFilters((f) => ({ ...f, locationId: e.target.value }))}
                  title={
                    sessionFilters.locationId
                      ? locationById[sessionFilters.locationId] || undefined
                      : undefined
                  }
                >
                  <option value="">Any location</option>
                  {locationOptions.map((l) => {
                    const short =
                      locationFilterShortLabel(
                        l.shortLabel ||
                          String(locationDetailsById[l.id]?.name || '').trim() ||
                          l.label
                      );
                    return (
                      <option key={l.id} value={l.id} title={l.label}>
                        {short}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Language</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.language} onChange={(e) => setSessionFilters((f) => ({ ...f, language: e.target.value }))}>
                  <option value="">Any language</option>
                  {sessionFilterLanguageOptions.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Delivery mode</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.deliveryMode} onChange={(e) => setSessionFilters((f) => ({ ...f, deliveryMode: e.target.value }))}>
                  <option value="">Any mode</option>
                  <option value="in_class">In class</option>
                  <option value="online">Online</option>
                  <option value="blended">Blended</option>
                  <option value="on_site">On site</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Instructor</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.instructorId} onChange={(e) => setSessionFilters((f) => ({ ...f, instructorId: e.target.value }))}>
                  <option value="">Any instructor</option>
                  <option value="__unassigned__">Unassigned</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}>{`${i.firstName} ${i.lastName}`.trim() || i.email}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Interpreter</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.interpreterId} onChange={(e) => setSessionFilters((f) => ({ ...f, interpreterId: e.target.value }))}>
                  <option value="">Any interpreter</option>
                  <option value="__unassigned__">Unassigned</option>
                  {interpreterOptions.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Provider</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.providerId} onChange={(e) => setSessionFilters((f) => ({ ...f, providerId: e.target.value }))}>
                  <option value="">Any provider</option>
                  <option value="__none__">None</option>
                  {providerOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>Status</label>
                <select className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.status} onChange={(e) => setSessionFilters((f) => ({ ...f, status: e.target.value as SessionListFilters['status'] }))}>
                  <option value="">Any status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>First day from</label>
                <input type="date" lang="en-GB" className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.dateFrom} onChange={(e) => setSessionFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
              </div>
              <div className="min-w-0">
                <label className={fieldLabel}>First day to</label>
                <input type="date" lang="en-GB" className={`w-full max-w-full ${nativeControl}`} value={sessionFilters.dateTo} onChange={(e) => setSessionFilters((f) => ({ ...f, dateTo: e.target.value }))} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={() => { setSessionFilters(EMPTY_SESSION_FILTERS); setOpsFilter('all'); }}>
                <Filter className="h-4 w-4" />
                Clear filters
              </button>
              <button type="button" onClick={() => { setOpsMode((v) => !v); if (opsMode) setOpsFilter('all'); }} className={`inline-flex items-center rounded-lg border px-3 py-2 text-sm font-medium ${opsMode ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-700'}`}>
                Ops mode {opsMode ? 'On' : 'Off'}
              </button>
            </div>
          {opsMode && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setOpsFilter('all')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'}`}>All</button>
              <button type="button" onClick={() => setOpsFilter('unpaid')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'unpaid' ? 'bg-rose-700 text-white' : 'bg-rose-100 text-rose-700'}`}>Unpaid ({opsCounts.unpaid})</button>
              <button type="button" onClick={() => setOpsFilter('compliance')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'compliance' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-700'}`}>Compliance ({opsCounts.compliance})</button>
              <button type="button" onClick={() => setOpsFilter('docs')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'docs' ? 'bg-emerald-700 text-white' : 'bg-emerald-100 text-emerald-700'}`}>No docs ({opsCounts.docs})</button>
              <button type="button" onClick={() => setOpsFilter('post')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'post' ? 'bg-indigo-700 text-white' : 'bg-indigo-100 text-indigo-700'}`}>Post-session ({opsCounts.post})</button>
              <button type="button" onClick={() => setOpsFilter('waitlist')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'waitlist' ? 'bg-sky-700 text-white' : 'bg-sky-100 text-sky-700'}`}>Waitlist ({opsCounts.waitlist})</button>
              <button type="button" onClick={() => setOpsFilter('noshow')} className={`rounded-full px-3 py-1 text-xs font-semibold ${opsFilter === 'noshow' ? 'bg-orange-700 text-white' : 'bg-orange-100 text-orange-700'}`}>No-shows ({opsCounts.noshow})</button>
            </div>
          )}
          {opsMode && Object.keys(selectedOpsSessionIds).some((id) => selectedOpsSessionIds[id]) && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2">
              <span className="text-xs font-semibold text-indigo-900">Bulk checklist:</span>
              <button type="button" onClick={() => void applyBulkPostChecklist('trainerSubmissionReturned', true)} className="rounded bg-white px-2 py-1 text-xs text-indigo-700">Trainer returned</button>
              <button type="button" onClick={() => void applyBulkPostChecklist('cardsReceivedFromTrainer', true)} className="rounded bg-white px-2 py-1 text-xs text-indigo-700">Cards received</button>
              <button type="button" onClick={() => void applyBulkPostChecklist('scansArchived', true)} className="rounded bg-white px-2 py-1 text-xs text-indigo-700">Scans archived</button>
              <button type="button" onClick={() => void applyBulkPostChecklist('batchReadyToSend', true)} className="rounded bg-white px-2 py-1 text-xs text-indigo-700">Batch ready</button>
            </div>
          )}
          </div>
        </BookingsCollapsibleSection>
        </div>
      )}

      {duplicateSessionGroups.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Possible orphan session records</p>
          <p className="mt-1 text-amber-900">
            {duplicateSessionGroups.length} slot{duplicateSessionGroups.length === 1 ? '' : 's'} have an extra
            scheduled session with the same course, language, date, location, and times as another record, but one copy
            has no roster. Cancel empty ghost sessions so participants are not copied to the wrong record.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-900">
            {duplicateSessionGroups.slice(0, 5).map((group) => {
              const sample = group[0]!;
              const lang = String(sample.language || '').trim();
              const label = lang ? `${sample.courseName} ${lang}` : sample.courseName;
              const locationLabel = locationById[sample.locationId || ''] || 'No location';
              const refs = sessionReferenceCodesForGroup(group, sessions);
              return (
                <li key={group.map((s) => s.id).join('-')}>
                  {label} ? {formatDateBlocksUk(sample.dateBlocks)} � {locationLabel} ({group.length} records: {refs})
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {sessionsListLayout === 'table' ? (
        <SessionsTableView
          rows={sessionTableRows}
          selectedSessionId={selectedSessionId}
          onSelectSession={(id) => {
            openSession(id);
            requestAnimationFrame(() => {
              sessionCardRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }}
          onRestoreSession={(id) => {
            const session = sessions.find((s) => s.id === id);
            if (session) void restoreSessionFromCard(session);
          }}
          onDeleteSession={(id) => {
            const session = sessions.find((s) => s.id === id);
            if (session) void hardDeleteSessionFromCard(session);
          }}
        />
      ) : sortedSessions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-10 text-center">
          <p className="text-sm font-semibold text-gray-900">
            No {sessionFilters.timeRange === 'past' ? 'past' : sessionFilters.timeRange === 'upcoming' ? 'upcoming' : ''} sessions match your filters
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {sessionFilters.timeRange === 'past'
              ? 'Past sessions are in calendar order (oldest at top). Scroll down for more recent dates like 17?18 May.'
              : sessionFilters.timeRange === 'upcoming'
                ? 'Try All dates or Past, or clear filters.'
                : 'Try adjusting search or filters.'}
          </p>
          {(activeSessionFilterCount > 0 || sessionFilters.timeRange !== 'upcoming') && (
            <button
              type="button"
              className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setSessionFilters(EMPTY_SESSION_FILTERS)}
            >
              Reset to upcoming
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
        {sessionsGroupedByDate.map((group) => (
          <section key={group.dateIso || `group-${group.dateLabel}`}>
            <div
              className={`sticky z-10 mb-3 flex items-center gap-2 rounded-lg px-3 py-2.5 shadow-sm ${
                group.isToday
                  ? 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-50 dark:ring-emerald-700'
                  : 'bg-slate-200/90 text-slate-900 ring-1 ring-slate-300 dark:bg-slate-700 dark:text-slate-50 dark:ring-slate-500'
              }`}
              style={{ top: bookingsStickyHeight }}
            >
              <CalendarDays className={`h-4 w-4 ${group.isToday ? 'text-emerald-700 dark:text-emerald-200' : 'text-slate-600 dark:text-slate-200'}`} />
              <p className="text-base font-semibold tracking-tight">
                {group.dateLabel}
                {group.isToday ? ' � Today' : ''}
                <span className="ml-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                  {group.sessions.length} session{group.sessions.length === 1 ? '' : 's'}
                </span>
              </p>
            </div>
            <div className="space-y-3">
            {group.sessions.map((s) => {
        const listBooked = getListBookedCount(s.id);
        const ops = opsMode ? getSessionOpsSnapshot(s) : null;
        const cardSessionFull = listBooked >= Math.max(1, Number(s.capacity || 1));
        const sortedDisplay = normalizeDateBlocks((s.dateBlocks || []) as CourseSessionDateBlock[]);
        const firstRaw = sortedDisplay[0]?.date;
        const firstMs = getFirstDateMs(s);
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const isPastSession = firstMs !== null && firstMs < startOfToday.getTime();
        const { monthLabel, dayLabel } = calendarLabelsFromIso(firstRaw);
        const dayCount = sessionDayCount(s.dateBlocks);
        const isMultiday = dayCount > 1;
        const isSessionCancelled = (s.status || 'scheduled') === 'cancelled';
        const deliveryLabel = formatDeliveryModeLabel(s.deliveryMode);
        const catalogFee = getCourseCatalogDefaultFeeForSession(s.courseName, courseDbFees, s.language);
        const trainerLabel = s.instructorId ? instructorLabelById[s.instructorId] || '?' : 'No trainer';
        const interpreterLabel = s.interpreterId ? interpreterLabelById[s.interpreterId] || '?' : 'No interpreter';
        const courseAccent = sessionCourseAccent(s.courseName);
        const sessionNotesLine = String(s.notes || '').trim();
        const cancelReasonLine =
          isSessionCancelled && s.cancellationReason ? String(s.cancellationReason).trim() : '';
        return (
          <article
            key={s.id}
            ref={(el) => registerSessionCardRef(s.id, el)}
            className={`flex h-[5.75rem] overflow-hidden rounded-xl border bg-white shadow-sm transition hover:shadow-md dark:bg-slate-800 dark:shadow-black/25 dark:hover:bg-slate-700 ${
              isSessionCancelled
                ? 'border-red-300 opacity-90 dark:border-red-600'
                : 'border-slate-300 hover:border-slate-400 dark:border-slate-500 dark:hover:border-slate-400'
            }`}
          >
            <span
              className={`w-1.5 shrink-0 self-stretch ${isSessionCancelled ? 'bg-red-500' : courseAccent.bar}`}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => openSession(s.id)}
              className="flex h-full min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left"
              title="Open session workspace"
            >
              {isMultiday ? (
                <div className="flex shrink-0 items-center gap-1.5" title={formatDateBlocksUk(s.dateBlocks)}>
                  {sortedDisplay.slice(0, 2).map((block) => {
                    const { monthLabel: ml, dayLabel: dl } = calendarLabelsFromIso(block.date);
                    return (
                      <div
                        key={block.date}
                        className={`${sessionDatePill} shrink-0 ${
                          isSessionCancelled ? 'border-red-300 dark:border-red-600' : 'border-slate-200'
                        }`}
                      >
                        <span className={sessionDatePillMonth}>{ml}</span>
                        <span className={sessionDatePillDay}>{dl}</span>
                      </div>
                    );
                  })}
                  {dayCount > 2 ? (
                    <span className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-bold text-slate-700">
                      +{dayCount - 2}
                    </span>
                  ) : null}
                </div>
              ) : (
                <div
                  className={`${sessionDatePill} shrink-0 ${
                    isSessionCancelled ? 'border-red-300 dark:border-red-600' : 'border-slate-200'
                  }`}
                >
                  <span className={sessionDatePillMonth}>{monthLabel}</span>
                  <span className={sessionDatePillDay}>{dayLabel}</span>
                </div>
              )}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-0.5 overflow-hidden">
                <div className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
                  <h3
                    className={`min-w-0 shrink truncate text-[15px] font-semibold leading-tight ${
                      isSessionCancelled ? 'text-red-800 line-through' : courseAccent.name
                    }`}
                  >
                    {s.courseName}
                  </h3>
                  {(s.language || '').trim() ? (
                    <span className="shrink-0 rounded bg-white px-1.5 py-px text-[11px] font-semibold text-slate-800 ring-1 ring-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-500">
                      {s.language}
                    </span>
                  ) : null}
                  {deliveryLabel ? (
                    <span className="shrink-0 rounded bg-indigo-50 px-1.5 py-px text-[11px] font-semibold text-indigo-900 ring-1 ring-indigo-200 dark:bg-indigo-950 dark:text-indigo-100 dark:ring-indigo-700">
                      {deliveryLabel}
                    </span>
                  ) : null}
                  <span
                    className={`shrink-0 rounded px-1.5 py-px text-[11px] font-semibold tabular-nums ${
                      cardSessionFull
                        ? 'bg-red-50 text-red-800 ring-1 ring-red-200 dark:bg-red-950 dark:text-red-100 dark:ring-red-800'
                        : 'bg-slate-100 text-slate-800 ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-500'
                    }`}
                  >
                    {listBooked}/{s.capacity}
                  </span>
                  {isSessionCancelled ? (
                    <span className="shrink-0 rounded bg-red-100 px-1.5 py-px text-[10px] font-bold text-red-700">Cancelled</span>
                  ) : null}
                  {isPastSession && sessionFilters.timeRange === 'all' ? (
                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-500">Past</span>
                  ) : null}
                  {ops ? (
                    <span
                      className={`shrink-0 rounded px-1.5 py-px text-[10px] font-semibold ${
                        ops.health === 'green'
                          ? 'bg-emerald-100 text-emerald-800'
                          : ops.health === 'amber'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-rose-100 text-rose-700'
                      }`}
                    >
                      {ops.health}
                    </span>
                  ) : null}
                  {ops && ops.waitlist > 0 ? (
                    <span className="shrink-0 rounded bg-sky-100 px-1.5 py-px text-[10px] font-medium text-sky-800">
                      WL {ops.waitlist}
                    </span>
                  ) : null}
                  {ops && ops.noShows > 0 ? (
                    <span className="shrink-0 rounded bg-orange-100 px-1.5 py-px text-[10px] font-medium text-orange-800">
                      NS {ops.noShows}
                    </span>
                  ) : null}
                </div>
                <p
                  className="truncate text-[13px] font-medium leading-tight text-slate-800 dark:text-slate-100"
                  title={locationById[s.locationId || preferredDefaultLocationId || ''] || 'No location'}
                >
                  {String(locationDetailsById[s.locationId || preferredDefaultLocationId || '']?.name || '').trim() ||
                    (locationById[s.locationId || preferredDefaultLocationId || ''] || 'No location').split(' - ')[0]}
                  <span className="mx-1.5 text-slate-300 dark:text-slate-500">�</span>
                  <span className="font-normal text-slate-700 dark:text-slate-200">
                    {trainerLabel}
                    <span className="mx-1 text-slate-300 dark:text-slate-500">/</span>
                    {interpreterLabel}
                  </span>
                </p>
                <p
                  className={`h-4 truncate text-xs leading-4 ${
                    cancelReasonLine
                      ? 'italic text-red-700 dark:text-red-300'
                      : sessionNotesLine
                        ? 'text-amber-800 dark:text-amber-200'
                        : 'text-transparent'
                  }`}
                  title={cancelReasonLine || sessionNotesLine || undefined}
                >
                  {cancelReasonLine
                    ? `Reason: ${cancelReasonLine}`
                    : sessionNotesLine
                      ? `Notes: ${sessionNotesLine}`
                      : '\u00a0'}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end justify-center">
                <span className="rounded-md bg-slate-50 px-2 py-1 text-sm font-semibold tabular-nums text-slate-900 ring-1 ring-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:ring-slate-500">
                  ?{catalogFee != null ? catalogFee.toFixed(0) : '?'}
                </span>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1 border-l border-slate-100 px-2">
              {opsMode ? (
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                  checked={!!selectedOpsSessionIds[s.id]}
                  onChange={(e) =>
                    setSelectedOpsSessionIds((prev) => ({ ...prev, [s.id]: e.target.checked }))
                  }
                  title="Select session for bulk ops"
                />
              ) : null}
              {isSessionCancelled ? (
                <button
                  type="button"
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
                  onClick={() => void restoreSessionFromCard(s)}
                  title="Restore cancelled session"
                >
                  Restore
                </button>
              ) : null}
              {ops && (ops.postAttendeeWork > 0 || ops.postChecklistMissing > 0) ? (
                <button
                  type="button"
                  className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-900 hover:bg-amber-100"
                  onClick={() => openSession(s.id, 'postSession')}
                  title="Post-session wrap-up"
                >
                  Post ({ops.postAttendeeWork + ops.postChecklistMissing})
                </button>
              ) : null}
              <ActionsOverflowMenu
                align="right"
                menuTitle="Session actions"
                items={buildSessionCardMenuItems(s, isSessionCancelled)}
              />
            </div>
          </article>
        );
      })}
            </div>
          </section>
        ))}
        </div>
      )}

      {sessionsListLayout === 'cards' && hasMoreSessions && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
            onClick={() => setVisibleSessionLimit((n) => n + 25)}
          >
            Show more sessions ({sortedSessions.length - visibleSortedSessions.length} remaining)
          </button>
        </div>
      )}

      {companyBookingModal && (
        <div className="fixed inset-0 z-[58] flex items-center justify-center bg-black/50 p-3">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-gray-900">Add company booking</p>
                <p className="mt-1 text-base font-semibold text-gray-900">
                  {(companyBookingSession?.courseName || 'Course')} � {formatDateBlocksUk(companyBookingSession?.dateBlocks)} �{' '}
                  {String(companyBookingSession?.language || 'Language not set')}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {String(companyBookingSession?.deliveryMode || '').replace(/_/g, ' ') || 'Delivery mode not set'}
                  {companyBookingSession?.locationId
                    ? ` � ${locationById[companyBookingSession.locationId] || 'Location not set'}`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => {
                  resetCompanyPicker();
                  setCompanyBookingModal(null);
                }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Booking details</p>
                <div>
                  <label className={fieldLabel}>Company</label>
                  {(() => {
                    const selectedCompany = companyBookingModal.companyCustomerId
                      ? customerById[companyBookingModal.companyCustomerId] ||
                        companyCustomers.find((co) => co.id === companyBookingModal.companyCustomerId) ||
                        companyPickerHits.find((co) => co.id === companyBookingModal.companyCustomerId) ||
                        (companyPickerQuery.trim()
                          ? ({
                              id: companyBookingModal.companyCustomerId,
                              companyName: companyPickerQuery.trim(),
                              customerType: 'company' as const,
                              locations: [],
                              contacts: [],
                              paymentHistory: [],
                              generatedCertificates: [],
                              currentBatchCertificates: [],
                              numCertsGenerated: 0,
                              isPaid: false,
                              lastPaymentDate: '',
                            } as Customer)
                          : null)
                      : null;
                    if (selectedCompany) {
                      return (
                        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                            {selectedCompany.companyName}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 text-xs font-semibold text-slate-600 hover:text-slate-900"
                            onClick={clearCompanyBookingSelection}
                          >
                            Change
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-1.5">
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${fieldIn} pl-8`}
                            placeholder="Search companies by name?"
                            value={companyPickerQuery}
                            onChange={(e) => setCompanyPickerQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        {companyPickerSearching ? (
                          <p className="text-xs text-slate-500">Searching?</p>
                        ) : companyPickerQuery.trim().length < 2 ? (
                          <p className="text-xs text-slate-500">Type at least 2 characters to search.</p>
                        ) : companyPickerHits.length === 0 ? (
                          <p className="text-xs text-slate-500">No companies match that search.</p>
                        ) : (
                          <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                            {companyPickerHits.map((co) => (
                              <button
                                key={co.id}
                                type="button"
                                className="block w-full truncate border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
                                onClick={() => selectCompanyForBooking(co)}
                              >
                                {co.companyName}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label className={fieldLabel}>
                    Source <span className="text-red-500">*</span>
                    <span className="ml-1 font-normal text-[11px] text-gray-500">(applies to every row in this list)</span>
                  </label>
                  <select
                    className={fieldIn}
                    value={companyBookingModal.source}
                    onChange={(e) =>
                      setCompanyBookingModal((prev) =>
                        prev ? { ...prev, source: e.target.value as InboundChannel } : prev
                      )
                    }
                  >
                    {INBOUND_CHANNEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={fieldLabel}>Booked by</label>
                  <input
                    className={fieldIn}
                    placeholder="Person or company who placed this booking"
                    value={companyBookingModal.bookedByName}
                    onChange={(e) =>
                      setCompanyBookingModal((prev) =>
                        prev ? { ...prev, bookedByName: e.target.value } : prev
                      )
                    }
                  />
                  <p className="mt-1 text-[11px] text-gray-500">
                    External booker ? who arranged seats (may differ from attendees).
                  </p>
                </div>
                <div>
                  <label className={fieldLabel}>Total seats</label>
                  <input
                    className={fieldIn}
                    value={companyBookingModal.totalSeats}
                    inputMode="numeric"
                    onChange={(e) => setCompanyBookingModal((prev) => (prev ? { ...prev, totalSeats: e.target.value.replace(/[^\d]/g, '') } : prev))}
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className={fieldLabel}>Individual Course Price</label>
                  <div className="flex items-center gap-2">
                    <input
                      className={`${moneyIn} ${companyBookingModal.feeLocked ? 'bg-gray-100' : ''}`}
                      value={companyBookingModal.enrollmentCourseFee}
                      onChange={(e) => setCompanyBookingModal((prev) => (prev ? { ...prev, enrollmentCourseFee: e.target.value } : prev))}
                      disabled={companyBookingModal.feeLocked}
                      placeholder="0.00"
                    />
                    {companyBookingModal.feeLocked ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        onClick={() => {
                          const feeLang = resolveCatalogFeeLanguage(companyBookingSession?.language);
                          const courseDefault = getCourseCatalogDefaultFee(
                            companyBookingSession?.courseName,
                            courseDbFees,
                            feeLang
                          );
                          const langNote = feeLang ? ` for a session delivered in ${feeLang}` : '';
                          const msg =
                            courseDefault > 0
                              ? `The catalog fee${langNote} is ?${courseDefault.toFixed(2)}. Change this session's fee?`
                              : 'Change the fee for this session?';
                          if (window.confirm(msg)) {
                            setCompanyBookingModal((prev) => (prev ? { ...prev, feeLocked: false } : prev));
                          }
                        }}
                      >
                        Change price
                      </button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <label className={fieldLabel}>Payment status</label>
                  <div className="space-y-1.5">
                    {[
                      ['unpaid', 'Not paid yet'],
                      ['prepaid', 'Prepaid (enter amount below)'],
                      ['part', 'Part paid (enter amount collected)'],
                      ['full', 'Paid in full']
                    ].map(([v, label]) => (
                      <label key={v} className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700">
                        <input
                          type="radio"
                          name="company-booking-paykind"
                          checked={companyBookingModal.payKind === v}
                          onChange={() => setCompanyBookingModal((prev) => (prev ? { ...prev, payKind: v as AddPayKind } : prev))}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                {(companyBookingModal.payKind === 'prepaid' || companyBookingModal.payKind === 'part') && (
                  <div>
                    <label className={fieldLabel}>{companyBookingModal.payKind === 'prepaid' ? 'Prepaid amount EUR' : 'Amount paid so far EUR'}</label>
                    <input
                      className={moneyIn}
                      value={companyBookingModal.amountStr}
                      onChange={(e) => setCompanyBookingModal((prev) => (prev ? { ...prev, amountStr: e.target.value } : prev))}
                      placeholder="0.00"
                    />
                  </div>
                )}
                {companyBookingModal.payKind !== 'unpaid' && (
                  <div>
                    <label className={fieldLabel}>Payment method</label>
                    <PaymentMethodSelect
                      className={fieldIn}
                      value={companyBookingModal.paymentMethod}
                      onChange={(paymentMethod) =>
                        setCompanyBookingModal((prev) => (prev ? { ...prev, paymentMethod } : prev))
                      }
                    />
                  </div>
                )}
                <div>
                  <label className={fieldLabel}>Invoice number (company)</label>
                  <input
                    className={fieldIn}
                    value={companyBookingModal.invoiceNumber}
                    onChange={(e) =>
                      setCompanyBookingModal((prev) => (prev ? { ...prev, invoiceNumber: e.target.value } : prev))
                    }
                    placeholder="e.g. INV-2026-0142"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">
                    Applies to every seat in this company booking. Marking paid records the remaining fee on each seat.
                  </p>
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={companyBookingModal.invoicePaid}
                    onChange={(e) => {
                      const company = companyCustomers.find((co) => co.id === companyBookingModal.companyCustomerId);
                      requestCompanyInvoicePaidToggle(e.target.checked, {
                        target: 'companyBooking',
                        sessionId: companyBookingModal.sessionId,
                        companyLabel: company?.companyName,
                        seatCount: Math.max(1, Number(companyBookingModal.totalSeats || 0)),
                      });
                    }}
                  />
                  Invoice paid (all seats in group)
                </label>
                {(companyBookingModal.payKind !== 'unpaid' || companyBookingModal.invoicePaid) && (
                  <div>
                    <label className={fieldLabel}>Payment reference / note</label>
                    <input
                      className={fieldIn}
                      value={companyBookingModal.paymentReference}
                      onChange={(e) =>
                        setCompanyBookingModal((prev) =>
                          prev ? { ...prev, paymentReference: e.target.value } : prev
                        )
                      }
                      placeholder="e.g. Bank transfer ref 33264"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Shown on each seat&apos;s payment line when recording payment or settling the company invoice.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Paste participants</p>
                  <button
                    type="button"
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                    onClick={parseCompanyBookingParticipants}
                  >
                    Parse list
                  </button>
                </div>
                <textarea
                  className={`${fieldIn} min-h-[180px]`}
                  value={companyBookingModal.pastedParticipants}
                  onChange={(e) =>
                    setCompanyBookingModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            pastedParticipants: e.target.value,
                            parsedRows: buildCompanyBookingParsedRows({ ...prev, pastedParticipants: e.target.value })
                          }
                        : prev
                    )
                  }
                  placeholder={'John Smith, john@example.com, 0871234567\nMaria Doyle\nPeter Walsh, peter@example.com'}
                />
                <p className="text-xs text-gray-500">
                  Paste one participant per line. It auto-parses as you type. CRM auto-match needs the same first name, surname, and email or phone ? a shared company email alone is not enough.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Participants preview</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Named participants: {companyBookingModal.parsedRows.filter((row) => row.action !== 'reserve_seat').length} � Reserved seats to create:{' '}
                    {Math.max(0, Number(companyBookingModal.totalSeats || 0) - companyBookingModal.parsedRows.filter((row) => row.action !== 'reserve_seat').length)}
                  </p>
                </div>
              </div>

              {companyBookingModal.parsedRows.length > 0 ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Participant</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Contact</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Extra</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">CRM match</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {companyBookingModal.parsedRows.map((row) => {
                        const match = row.matchCustomerId ? individualCustomers.find((c) => c.id === row.matchCustomerId) : null;
                        const matchLabel = match
                          ? `${match.firstName || ''} ${match.surname || ''}`.trim() || match.companyName
                          : 'No exact match';
                        return (
                          <tr key={row.id}>
                            <td className="px-3 py-2 align-top">
                              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                <input
                                  className={fieldIn}
                                  value={row.firstName}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { firstName: e.target.value })}
                                  placeholder="First name"
                                />
                                <input
                                  className={fieldIn}
                                  value={row.surname}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { surname: e.target.value })}
                                  placeholder="Surname"
                                />
                              </div>
                              {row.parseError ? <div className="mt-1 text-xs text-amber-700">{row.parseError}</div> : null}
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="grid grid-cols-1 gap-2">
                                <input
                                  className={fieldIn}
                                  value={row.emailAddress}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { emailAddress: e.target.value })}
                                  placeholder="Email (or mobile below)"
                                />
                                <input
                                  className={fieldIn}
                                  value={row.contactPhoneNumber}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { contactPhoneNumber: e.target.value })}
                                  placeholder="Mobile (or email above)"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="grid grid-cols-1 gap-2">
                                <select
                                  className={fieldIn}
                                  value={row.preferredLanguage}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { preferredLanguage: e.target.value })}
                                >
                                  {sessionLanguageOptions.map((lang) => (
                                    <option key={lang} value={lang}>
                                      {lang}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  className={fieldIn}
                                  value={row.safePassProcessNumber}
                                  onChange={(e) => updateCompanyBookingRow(row.id, { safePassProcessNumber: e.target.value })}
                                  placeholder="Safe Pass process number"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <select
                                className={fieldIn}
                                value={row.matchCustomerId || ''}
                                onChange={(e) =>
                                  updateCompanyBookingRow(row.id, {
                                    matchCustomerId: e.target.value || null,
                                    action: e.target.value ? 'match_existing' : row.action === 'match_existing' ? 'create_new' : row.action
                                  })
                                }
                              >
                                <option value="">{matchLabel}</option>
                                {companyBookingMatchCandidates(row).map((c) => {
                                  const label = `${c.firstName || ''} ${c.surname || ''}`.trim() || c.companyName;
                                  return (
                                    <option key={c.id} value={c.id}>
                                      {label}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <select
                                className={fieldIn}
                                value={row.action}
                                onChange={(e) => {
                                  const action = e.target.value as CompanyBookingParticipantDraft['action'];
                                  updateCompanyBookingRow(row.id, {
                                    action,
                                    matchCustomerId: action === 'create_new' ? null : row.matchCustomerId,
                                  });
                                }}
                              >
                                <option value="match_existing">Match existing CRM person</option>
                                <option value="create_new">Create new CRM person</option>
                                <option value="reserve_seat">Leave as reserved seat</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  Paste participants and click `Parse list` to preview them. If you only know the seat count, leave the list empty and the remainder will be created as reserved seats.
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                disabled={companyBookingSaving}
                onClick={() => {
                  resetCompanyPicker();
                  setCompanyBookingModal(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                disabled={companyBookingSaving}
                onClick={() => void confirmCompanyBooking()}
              >
                {companyBookingSaving ? 'Creating?' : 'Create booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelSessionModal && (
        <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-gray-900">Cancel session</p>
                <p className="text-sm text-gray-600">{cancelSessionModal.session.courseName}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setCancelSessionModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              The session will stay visible on the calendar in red. Please provide a reason for cancellation.
            </p>
            <label className={`${fieldLabel} mt-3`}>Cancellation reason</label>
            <textarea
              rows={3}
              className={fieldIn}
              value={cancelSessionModal.reason}
              onChange={(e) =>
                setCancelSessionModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))
              }
              placeholder="e.g. Trainer unavailable, low enrolment?"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setCancelSessionModal(null)}>
                Back
              </button>
              <button type="button" className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700" onClick={() => void confirmCancelSession()}>
                Cancel session
              </button>
            </div>
          </div>
        </div>
      )}

      {quickRescheduleModal && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-gray-900">Reschedule session</p>
                <p className="text-sm text-gray-600">{quickRescheduleModal.courseName}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setQuickRescheduleModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {quickRescheduleModal.dateBlocks.map((row, idx) => (
                <div key={idx} className="grid grid-cols-1 gap-2 sm:grid-cols-12 sm:items-end">
                  <div className="sm:col-span-5">
                    <label className={fieldLabel}>Date</label>
                    <input
                      type="date"
                      className={fieldIn}
                      value={row.date}
                      onChange={(e) =>
                        setQuickRescheduleModal((prev) => {
                          if (!prev) return prev;
                          const next = [...prev.dateBlocks];
                          next[idx] = { ...next[idx]!, date: e.target.value };
                          return { ...prev, dateBlocks: next };
                        })
                      }
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className={fieldLabel}>Start</label>
                    <input
                      type="time"
                      className={fieldIn}
                      value={timeInputValue(row.startTime)}
                      onChange={(e) =>
                        setQuickRescheduleModal((prev) => {
                          if (!prev) return prev;
                          const next = [...prev.dateBlocks];
                          next[idx] = { ...next[idx]!, startTime: e.target.value || null };
                          return { ...prev, dateBlocks: next };
                        })
                      }
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className={fieldLabel}>End</label>
                    <input
                      type="time"
                      className={fieldIn}
                      value={timeInputValue(row.endTime)}
                      onChange={(e) =>
                        setQuickRescheduleModal((prev) => {
                          if (!prev) return prev;
                          const next = [...prev.dateBlocks];
                          next[idx] = { ...next[idx]!, endTime: e.target.value || null };
                          return { ...prev, dateBlocks: next };
                        })
                      }
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <button
                      type="button"
                      className="w-full rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={quickRescheduleModal.dateBlocks.length <= 1}
                      onClick={() =>
                        setQuickRescheduleModal((prev) =>
                          prev
                            ? { ...prev, dateBlocks: prev.dateBlocks.filter((_, i) => i !== idx) }
                            : prev
                        )
                      }
                      aria-label="Remove date"
                    >
                      <Trash2 className="mx-auto h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() =>
                  setQuickRescheduleModal((prev) =>
                    prev
                      ? { ...prev, dateBlocks: [...prev.dateBlocks, { date: '', startTime: null, endTime: null }] }
                      : prev
                  )
                }
              >
                <Plus className="h-4 w-4" />
                Add day
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setQuickRescheduleModal(null)}>
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={() => void saveQuickReschedule()}>
                Save dates
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 dark:bg-black/70 sm:p-4">
          <div className="flex max-h-[96vh] w-full max-w-[98vw] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-slate-500 dark:bg-slate-900 dark:shadow-black/50">
            <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-slate-600 dark:bg-slate-900">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700 dark:text-slate-200">
                  <span className="inline-flex items-center gap-1 font-medium">
                    <CalendarDays className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                    {formatDateBlocksUk(selectedSession.dateBlocks)}
                  </span>
                  <span className="text-gray-300">�</span>
                  <span className="font-semibold text-gray-900 dark:text-slate-50">{selectedSession.courseName}</span>
                  {(selectedSession.language || '').trim() ? (
                    <>
                      <span className="text-gray-300">�</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-700 dark:bg-slate-700 dark:text-slate-100">
                        {selectedSession.language}
                      </span>
                    </>
                  ) : null}
                  {formatDeliveryModeLabel(selectedSession.deliveryMode) ? (
                    <>
                      <span className="text-gray-300">�</span>
                      <span className="text-xs font-medium text-indigo-800">{formatDeliveryModeLabel(selectedSession.deliveryMode)}</span>
                    </>
                  ) : null}
                  <span className="text-gray-300">�</span>
                  <span className={`font-semibold ${sessionIsFullyBooked ? 'text-red-600' : 'text-emerald-700'}`}>
                    {bookedCount}/{capacityCount} booked
                  </span>
                  {selectedIsMultiday ? (
                    <>
                      <span className="text-gray-300">�</span>
                      <span className="text-xs font-bold text-indigo-900">{selectedSessionDayCount} days</span>
                    </>
                  ) : null}
                  {(selectedSession.status || 'scheduled') === 'cancelled' ? (
                    <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">Cancelled</span>
                  ) : null}
                </div>
                {mode === 'manage' && !showHeaderExistingSearch ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      disabled={sessionIsFullyBooked || sessionIsCancelled}
                      onClick={() => {
                        if (rejectIfCourseCancelled(selectedSession.id)) return;
                        if (rejectIfCourseFullyBooked(selectedSession.id)) return;
                        setShowHeaderExistingSearch(true);
                      }}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-blue-800 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Search className="h-3.5 w-3.5" aria-hidden />
                      Add existing client
                    </button>
                    <button
                      type="button"
                      disabled={sessionIsFullyBooked || sessionIsCancelled}
                      onClick={() => {
                        if (rejectIfCourseCancelled(selectedSession.id)) return;
                        if (rejectIfCourseFullyBooked(selectedSession.id)) return;
                        setCreateCrmForSessionOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-blue-300 bg-blue-600 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UserPlus className="h-3.5 w-3.5" aria-hidden />
                      Create new client
                    </button>
                    <button
                      type="button"
                      disabled={sessionIsFullyBooked || sessionIsCancelled}
                      onClick={() => openCompanyBookingModal(selectedSession.id)}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] font-semibold text-violet-900 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Users className="h-3.5 w-3.5" aria-hidden />
                      Company booking
                    </button>
                  </div>
                ) : null}
                <div className="flex shrink-0 items-center gap-2">
                  {mode === 'manage' ? (
                    <ActionsOverflowMenu
                      align="right"
                      label="Manage"
                      menuTitle="Manage session"
                      items={buildManageSessionMenuItems()}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="shrink-0 rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition hover:bg-gray-100"
                    onClick={closeSession}
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {(selectedSession.status || 'scheduled') === 'cancelled' && selectedSession.cancellationReason ? (
                <p className="mt-1 text-xs italic text-red-700">Reason: {selectedSession.cancellationReason}</p>
              ) : null}
              {mode === 'manage' ? (
                <div className="mt-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Course notes</label>
                  <textarea
                    rows={2}
                    className={`${fieldIn} mt-1 text-sm`}
                    placeholder="Special requirements or notes for this course?"
                    value={sessionDraft.notes}
                    onChange={(e) => setSessionDraft((p) => ({ ...p, notes: e.target.value }))}
                    onBlur={() => {
                      if ((selectedSession.notes || '') !== (sessionDraft.notes || '')) {
                        void saveSessionDetails({ silent: true });
                      }
                    }}
                  />
                </div>
              ) : String(selectedSession.notes || '').trim() ? (
                <p className="mt-1 text-xs text-amber-900 dark:text-amber-100">
                  Notes: {String(selectedSession.notes || '').trim()}
                </p>
              ) : null}
              {mode === 'manage' && showHeaderExistingSearch && (
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    className={`${fieldIn} flex-1 text-sm`}
                    placeholder="Search CRM records?"
                    value={searchBySession[selectedSession.id] || ''}
                    onChange={(e) => setSearchBySession((p) => ({ ...p, [selectedSession.id]: e.target.value }))}
                  />
                  <button
                    type="button"
                    disabled={!(searchBySession[selectedSession.id] || '').trim()}
                    onClick={() => addSelected(selectedSession.id)}
                    className={`shrink-0 ${btnPrimary} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    Add match
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHeaderExistingSearch(false)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {mode === 'manage' && showHeaderExistingSearch && selectedSearchResults.length > 0 && (
                <div className="mt-2 grid w-full max-w-2xl grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {selectedSearchResults.map((c) => {
                    const displayName = individualCustomerDisplayName(c);
                    const { languageLine, contactLine } = individualCustomerSearchSubtitle(c);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => openAddAttendeeModal(selectedSession.id, c)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left transition hover:border-gray-300 hover:bg-gray-50"
                      >
                        <div className="text-sm font-semibold text-gray-900">{languageLine || displayName}</div>
                        <div className="mt-0.5 text-xs text-gray-500">{contactLine}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={`relative flex min-h-0 flex-1 flex-col overflow-hidden ${sessionMainTab === 'spreadsheet' ? '' : 'lg:flex-row'}`}>
              {sessionMainTab === 'roster' && rosterSnapshotOpen && (
              <aside className="flex max-h-[50vh] shrink-0 flex-col overflow-y-auto border-b border-gray-200 bg-gray-50 p-4 text-gray-700 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-200 sm:h-auto sm:max-h-none lg:max-h-[calc(96vh-7rem)] lg:w-[17rem] xl:w-[19rem] lg:border-b-0 lg:border-r lg:border-gray-200 dark:lg:border-slate-600">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Session snapshot</p>
                {selectedIsMultiday ? (
                  <p className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold leading-snug text-indigo-950">
                    Multiday course ? this session runs on {selectedSessionDayCount} separate days (one booking).
                  </p>
                ) : null}
                <ul className="mt-3 space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                      <Users className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Capacity</p>
                      <p className="font-semibold text-gray-900">
                        <span className={sessionIsFullyBooked ? 'text-red-600' : undefined}>
                          {bookedCount} / {sessionDraft.capacity} booked
                        </span>
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Location</p>
                      <p className="font-medium leading-snug text-gray-800">{locationById[sessionDraft.locationId] || 'Not set'}</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                      <GraduationCap className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Instructor</p>
                      <p className="font-medium leading-snug text-gray-800">{selectedInstructorLabel}</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                      <Languages className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Interpreter</p>
                      <p className="font-medium leading-snug text-gray-800">{selectedInterpreterLabel}</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-5 border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-gray-600" />
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Finance overview</p>
                  </div>
                  <dl className="mt-3 space-y-2 text-xs">
                    <div className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-2">
                      <dt className="text-gray-500">Booked seats</dt>
                      <dd className="text-right font-semibold text-gray-900">
                        {bookedCount}/{capacityCount}{' '}
                        <span className="font-semibold text-gray-500">({occupancyPct}%)</span>
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <dt className="text-gray-500">Payment mix</dt>
                      <dd className="text-right font-semibold leading-tight text-gray-800">
                        <span className="text-emerald-700">{selectedMoney.paidCount} paid</span>
                        <span className="text-gray-300"> � </span>
                        <span className="text-amber-700">{selectedMoney.halfPaidCount} part</span>
                        <span className="text-gray-300"> � </span>
                        <span className="text-red-600">{selectedMoney.unpaidCount} not</span>
                        <p className="text-[10px] font-normal text-gray-500">Active roster only</p>
                      </dd>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <div className="flex h-full">
                        <div className="bg-emerald-500" style={{ width: `${selectedMoney.feeTotalSeatCount ? (selectedMoney.paidCount / selectedMoney.feeTotalSeatCount) * 100 : 0}%` }} />
                        <div className="bg-amber-500" style={{ width: `${selectedMoney.feeTotalSeatCount ? (selectedMoney.halfPaidCount / selectedMoney.feeTotalSeatCount) * 100 : 0}%` }} />
                        <div className="bg-red-500" style={{ width: `${selectedMoney.feeTotalSeatCount ? (selectedMoney.unpaidCount / selectedMoney.feeTotalSeatCount) * 100 : 0}%` }} />
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 pt-1">
                      <dt className="text-gray-500">Total fees</dt>
                      <dd className="text-right">
                        <span className="font-semibold text-gray-900">?{selectedMoney.totalFee.toFixed(2)}</span>
                        <span className="ml-1 text-[10px] text-gray-500">collected {collectionPct}%</span>
                        <p className="text-[10px] font-normal text-gray-500">Sum of fees (active roster only)</p>
                      </dd>
                    </div>
                    <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-white p-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Collected</p>
                        <p className="text-lg font-bold text-emerald-700">?{selectedMoney.totalCollected.toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500">Sum of all payment lines (incl. removed)</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500" title="Deposits recorded before course day">Prepaid</p>
                        <p className="text-lg font-bold text-gray-900">?{selectedMoney.totalPrepaid.toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500">Deposit &amp; other payments</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500" title="On-day payments">On-site</p>
                        <p className="text-lg font-bold text-gray-900">?{selectedMoney.totalDirectCollected.toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500">On-day payments</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">Outstanding</p>
                        <p className="text-lg font-bold text-red-700">?{selectedMoney.totalOutstanding.toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500">Balance (active roster only)</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600">Overpayment</p>
                        <p className="text-lg font-bold text-violet-700">?{selectedMoney.totalOverpayment.toFixed(2)}</p>
                        <p className="text-[10px] text-gray-500">Paid above fee (active roster only)</p>
                      </div>
                    </div>
                    {financialSnapshot && (
                      <div className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-3">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Collected profit</p>
                          <p className={`text-lg font-bold ${selectedCollectedProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            ?{selectedCollectedProfit.toFixed(2)}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            Collected minus total outlay
                            {selectedExtraLedgerTotal > 0
                              ? ` (incl. ?${selectedExtraLedgerTotal.toFixed(2)} extra from Financials)`
                              : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Profit total</p>
                          <p className={`text-lg font-bold ${selectedProfitTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            ?{selectedProfitTotal.toFixed(2)}
                          </p>
                          <p className="text-[10px] text-gray-500">Collected profit plus outstanding</p>
                        </div>
                      </div>
                    )}
                    {financialSnapshot && (
                      <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Costs & payment methods</p>
                        <div className="mt-2 grid grid-cols-2 gap-1.5 text-[10px]">
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Tutor cost</p><p className="font-semibold text-gray-800">EUR {Number(financialSnapshot.tutorExpense || 0).toFixed(2)}</p></div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Interpreter cost</p><p className="font-semibold text-gray-800">EUR {Number(financialSnapshot.interpreterExpense || 0).toFixed(2)}</p></div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Venue cost</p><p className="font-semibold text-gray-800">EUR {Number(financialSnapshot.venueExpense || 0).toFixed(2)}</p></div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Cards</p><p className="font-semibold text-gray-800">{Number(financialSnapshot.numberCards || 0)} / EUR {Number(financialSnapshot.cardFee || 0).toFixed(2)}</p></div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Referral fees</p><p className="font-semibold text-gray-800">EUR {(Number(financialSnapshot.referralFee1 || 0) + Number(financialSnapshot.referralFee2 || 0) + Number(financialSnapshot.referralFee3 || 0)).toFixed(2)}</p></div>
                          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Other expenses</p><p className="font-semibold text-gray-800">EUR {Number(financialSnapshot.otherExpenses || 0).toFixed(2)}</p></div>
                        </div>
                        <div className="mt-2 rounded-md bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                          <span className="font-semibold">Total outlay:</span>{' '}
                          <span className="font-bold">
                            EUR {selectedTotalOutlay.toFixed(2)}
                          </span>
                        </div>
                        <div className="mt-2">
                          <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-gray-500">By method</p>
                          <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Cash ({selectedMethodCounts.cash})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.cash || 0).toFixed(2)}</p></div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Bank Transfer ({selectedMethodCounts.bank})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.bank || 0).toFixed(2)}</p></div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Stripe ({selectedMethodCounts.stripe})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.stripe || 0).toFixed(2)}</p></div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Revolut ({selectedMethodCounts.revolut})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.revolut || 0).toFixed(2)}</p></div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">SumUp ({selectedMethodCounts.sumup})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.sumup || 0).toFixed(2)}</p></div>
                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"><p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">PayPal ({selectedMethodCounts.paypal})</p><p className="font-semibold text-gray-800">{Number(selectedMethodTotals.paypal || 0).toFixed(2)}</p></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </dl>
                </div>

                {mode === 'manage' && (
                  <div className="mt-5 border-t border-gray-200 pt-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">At a glance</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
                      Use the tabs above to switch sections. Finance summary stays here while you work on the roster.
                    </p>
                  </div>
                )}
              </aside>
              )}

              <div className={`min-h-0 flex-1 ${sessionMainTab === 'spreadsheet' ? 'flex flex-col overflow-hidden bg-gray-50' : 'overflow-y-auto bg-gray-50'}`}>
                <div className={sessionMainTab === 'spreadsheet' ? 'flex min-h-0 flex-1 flex-col p-3 sm:p-4' : sessionMainTab === 'roster' ? 'flex min-h-0 flex-1 flex-col space-y-3 p-4' : 'space-y-4 p-5'}>
                  {sessionMainTab === 'expenses' && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Tutor fee</label><input className={fieldIn} value={expenseDraft.tutorExpense} onChange={(e) => setExpenseDraft((p) => ({ ...p, tutorExpense: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Interpreter fee</label><input className={fieldIn} value={expenseDraft.interpreterExpense} onChange={(e) => setExpenseDraft((p) => ({ ...p, interpreterExpense: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Venue fee</label><input className={fieldIn} value={expenseDraft.venueExpense} onChange={(e) => setExpenseDraft((p) => ({ ...p, venueExpense: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Number cards</label><input className={fieldIn} value={expenseDraft.numberCards} onChange={(e) => setExpenseDraft((p) => ({ ...p, numberCards: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Card fee</label><input className={fieldIn} value={expenseDraft.cardFee} onChange={(e) => setExpenseDraft((p) => ({ ...p, cardFee: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Referral fee 1</label><input className={fieldIn} value={expenseDraft.referralFee1} onChange={(e) => setExpenseDraft((p) => ({ ...p, referralFee1: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Referral fee 2</label><input className={fieldIn} value={expenseDraft.referralFee2} onChange={(e) => setExpenseDraft((p) => ({ ...p, referralFee2: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Referral fee 3</label><input className={fieldIn} value={expenseDraft.referralFee3} onChange={(e) => setExpenseDraft((p) => ({ ...p, referralFee3: e.target.value }))} /></div>
                            <div className="rounded-md border border-gray-200 bg-white p-2"><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">Other expenses</label><input className={fieldIn} value={expenseDraft.otherExpenses} onChange={(e) => setExpenseDraft((p) => ({ ...p, otherExpenses: e.target.value }))} /></div>
                          </div>
                          <div className="rounded-md bg-emerald-50 px-2 py-1.5 text-sm">
                            <span className="font-semibold text-emerald-800">Total cost</span>
                            <span className="float-right font-bold text-emerald-800">EUR {expenseDraftTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      )}

                      {sessionMainTab === 'logistics' && (
                        <div className="space-y-3">
                          <div>
                            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                              <label className="mb-0 block text-xs font-medium text-gray-600">Course days</label>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                                onClick={() =>
                                  setSessionDateBlocksDraft((p) => {
                                    const last = p[p.length - 1];
                                    const t0 = timeInputValue(last?.startTime);
                                    const t1 = timeInputValue(last?.endTime);
                                    return [...p, { date: '', startTime: t0 || null, endTime: t1 || null }];
                                  })
                                }
                              >
                                <Plus className="h-3 w-3" />
                                Add day
                              </button>
                            </div>
                            <div className="space-y-2">
                              {sessionDateBlocksDraft.map((row, idx) => (
                                <div key={idx} className="rounded-lg border border-gray-200 bg-white p-2">
                                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-12 sm:items-end">
                                    <div className="sm:col-span-4">
                                      <label className={fieldLabel}>Date</label>
                                      <input
                                        type="date"
                                        className={fieldIn}
                                        value={row.date}
                                        onChange={(e) =>
                                          setSessionDateBlocksDraft((p) => {
                                            const next = [...p];
                                            next[idx] = { ...next[idx]!, date: e.target.value };
                                            return next;
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="sm:col-span-3">
                                      <label className={fieldLabel}>Start</label>
                                      <input
                                        type="time"
                                        className={fieldIn}
                                        value={timeInputValue(row.startTime)}
                                        onChange={(e) =>
                                          setSessionDateBlocksDraft((p) => {
                                            const next = [...p];
                                            next[idx] = { ...next[idx]!, startTime: e.target.value || null };
                                            return next;
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="sm:col-span-3">
                                      <label className={fieldLabel}>End</label>
                                      <input
                                        type="time"
                                        className={fieldIn}
                                        value={timeInputValue(row.endTime)}
                                        onChange={(e) =>
                                          setSessionDateBlocksDraft((p) => {
                                            const next = [...p];
                                            next[idx] = { ...next[idx]!, endTime: e.target.value || null };
                                            return next;
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="flex items-end sm:col-span-2">
                                      <button
                                        type="button"
                                        disabled={sessionDateBlocksDraft.length <= 1}
                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                                        aria-label="Remove day"
                                        onClick={() =>
                                          setSessionDateBlocksDraft((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)))
                                        }
                                      >
                                        <Trash2 className="mx-auto h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="mt-1 text-[11px] text-gray-500">Multiple days are stored in order by date. Duplicate calendar days are ignored.</p>
                          </div>
                          <div>
                            <label className={fieldLabel}>Location</label>
                            <select className={fieldIn} value={sessionDraft.locationId} onChange={(e) => setSessionDraft((p) => ({ ...p, locationId: e.target.value }))}>
                              <option value="">No location</option>
                              {locationOptions.map((l) => (
                                <option key={l.id} value={l.id}>
                                  {l.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={fieldLabel}>Instructor</label>
                            <select className={fieldIn} value={sessionDraft.instructorId} onChange={(e) => setSessionDraft((p) => ({ ...p, instructorId: e.target.value }))}>
                              <option value="">Unassigned</option>
                              {instructors.map((i) => (
                                <option key={i.id} value={i.id}>
                                  {`${i.firstName} ${i.lastName}`.trim() || i.email}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={fieldLabel}>Capacity</label>
                            <input type="number" min={1} className={fieldIn} value={sessionDraft.capacity} onChange={(e) => setSessionDraft((p) => ({ ...p, capacity: Number(e.target.value || 1) }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>Delivery mode</label>
                            <select className={fieldIn} value={sessionDraft.deliveryMode} onChange={(e) => setSessionDraft((p) => ({ ...p, deliveryMode: e.target.value }))}>
                              <option value="in_class">In class</option>
                              <option value="online">Online</option>
                              <option value="blended">Blended</option>
                              <option value="on_site">On site</option>
                            </select>
                          </div>
                          <div>
                            <label className={fieldLabel}>Interpreter</label>
                            <select className={fieldIn} value={sessionDraft.interpreterId} onChange={(e) => setSessionDraft((p) => ({ ...p, interpreterId: e.target.value }))}>
                              <option value="">None</option>
                              {interpreterOptions.map((i) => (
                                <option key={i.id} value={i.id}>
                                  {i.name}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-[11px] text-gray-500">Manage the list under Admin or Super Admin ? Interpreters.</p>
                          </div>
                          <div>
                            <label className={fieldLabel}>Provider</label>
                            <select className={fieldIn} value={sessionDraft.providerId} onChange={(e) => setSessionDraft((p) => ({ ...p, providerId: e.target.value }))}>
                              <option value="">?</option>
                              {providerOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-[11px] text-gray-500">Managed under Admin ? Providers (active entries only).</p>
                          </div>
                          <div>
                            <label className={fieldLabel}>Course notes</label>
                            <textarea rows={2} className={fieldIn} value={sessionDraft.notes} onChange={(e) => setSessionDraft((p) => ({ ...p, notes: e.target.value }))} />
                          </div>
                          {requiresLiftTruckFieldsForSelectedSession && (
                            <>
                              <div className="col-span-full rounded-lg border border-cyan-100 bg-cyan-50/60 p-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-800">Lift truck certificate fields (session-wide)</p>
                                <p className="mt-1 text-[11px] text-cyan-900/80">These values are used for all certificates generated from this session.</p>
                              </div>
                              <div>
                                <label className={fieldLabel}>Truck type</label>
                                <input className={fieldIn} placeholder="e.g. Counterbalance" value={sessionDraft.truckType} onChange={(e) => setSessionDraft((p) => ({ ...p, truckType: e.target.value }))} />
                              </div>
                              <div>
                                <label className={fieldLabel}>Truck make</label>
                                <input className={fieldIn} placeholder="e.g. Toyota" value={sessionDraft.truckMake} onChange={(e) => setSessionDraft((p) => ({ ...p, truckMake: e.target.value }))} />
                              </div>
                              <div>
                                <label className={fieldLabel}>Truck capacity</label>
                                <input className={fieldIn} placeholder="e.g. 2.5T" value={sessionDraft.truckCapacity} onChange={(e) => setSessionDraft((p) => ({ ...p, truckCapacity: e.target.value }))} />
                              </div>
                              <div>
                                <label className={fieldLabel}>Truck attachments</label>
                                <input className={fieldIn} placeholder="e.g. Side shift" value={sessionDraft.truckAttachments} onChange={(e) => setSessionDraft((p) => ({ ...p, truckAttachments: e.target.value }))} />
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {sessionMainTab === 'interpreters' && (
                        <div className="space-y-3">
                          <p className="text-xs text-slate-600">These details are shown read-only in interpreter portal session details.</p>
                          <div>
                            <label className={fieldLabel}>Interpreter briefing</label>
                            <textarea rows={3} className={fieldIn} value={sessionDraft.interpreterBriefing} onChange={(e) => setSessionDraft((p) => ({ ...p, interpreterBriefing: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>Arrival instructions</label>
                            <textarea rows={2} className={fieldIn} value={sessionDraft.arrivalInstructions} onChange={(e) => setSessionDraft((p) => ({ ...p, arrivalInstructions: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>Parking / access</label>
                            <textarea rows={2} className={fieldIn} value={sessionDraft.parkingAccess} onChange={(e) => setSessionDraft((p) => ({ ...p, parkingAccess: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>Site hazards / PPE</label>
                            <textarea rows={2} className={fieldIn} value={sessionDraft.siteHazardsPpe} onChange={(e) => setSessionDraft((p) => ({ ...p, siteHazardsPpe: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>Interpreter reminders</label>
                            <textarea rows={2} className={fieldIn} value={sessionDraft.interpreterReminders} onChange={(e) => setSessionDraft((p) => ({ ...p, interpreterReminders: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>On-site contact (override name)</label>
                            <input className={fieldIn} value={sessionDraft.onSiteContactOverrideName} onChange={(e) => setSessionDraft((p) => ({ ...p, onSiteContactOverrideName: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>On-site contact (override phone)</label>
                            <input className={fieldIn} value={sessionDraft.onSiteContactOverridePhone} onChange={(e) => setSessionDraft((p) => ({ ...p, onSiteContactOverridePhone: e.target.value }))} />
                          </div>
                          <div>
                            <label className={fieldLabel}>On-site contact (override email)</label>
                            <input className={fieldIn} value={sessionDraft.onSiteContactOverrideEmail} onChange={(e) => setSessionDraft((p) => ({ ...p, onSiteContactOverrideEmail: e.target.value }))} />
                          </div>
                        </div>
                      )}
                      {sessionMainTab === 'postSession' && (
                        <div className="space-y-3">
                          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                            {isSafePassCourse(selectedSession?.courseName) ? (
                              <>
                                After the course: mark attendance, complete the session checklist, then update each
                                participant&apos;s Safe Pass card status. Process numbers and full card tracking also
                                live in{' '}
                                <button
                                  type="button"
                                  className="font-semibold underline"
                                  onClick={() => openProcessNumbersModule('safe-pass-cards')}
                                >
                                  Process Numbers / Safe Pass Cards
                                </button>
                                .
                              </>
                            ) : (
                              <>Mark attendance once each participant has attended and paid. Use the checklist for trainer paperwork and archiving.</>
                            )}
                          </div>
                          {isSafePassCourse(selectedSession?.courseName) && (
                            <button
                              type="button"
                              onClick={() => openProcessNumbersModule('process-numbers')}
                              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 transition hover:bg-blue-100"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              Open Process Numbers for this session
                            </button>
                          )}
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                            <p className="text-xs font-semibold text-slate-800">Session checklist</p>
                            <div className="mt-2 space-y-1.5 text-xs">
                              <label className="flex items-center gap-2 text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={postSessionChecklist.trainerSubmissionReturned}
                                  onChange={(e) =>
                                    setPostSessionChecklist((p) => ({ ...p, trainerSubmissionReturned: e.target.checked }))
                                  }
                                />
                                Trainer submission returned
                              </label>
                              <label className="flex items-center gap-2 text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={postSessionChecklist.cardsReceivedFromTrainer}
                                  onChange={(e) =>
                                    setPostSessionChecklist((p) => ({ ...p, cardsReceivedFromTrainer: e.target.checked }))
                                  }
                                />
                                Cards received from trainer
                              </label>
                              <label className="flex items-center gap-2 text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={postSessionChecklist.scansArchived}
                                  onChange={(e) =>
                                    setPostSessionChecklist((p) => ({ ...p, scansArchived: e.target.checked }))
                                  }
                                />
                                Scans archived (cards + envelopes)
                              </label>
                              <label className="flex items-center gap-2 text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={postSessionChecklist.batchReadyToSend}
                                  onChange={(e) =>
                                    setPostSessionChecklist((p) => ({ ...p, batchReadyToSend: e.target.checked }))
                                  }
                                />
                                Batch ready for distribution
                              </label>
                            </div>
                          </div>
                          <div className="rounded-md border border-slate-200 bg-white p-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-slate-800">Wrap-up progress</span>
                              <span className="text-slate-600">
                                Done {postSessionReadyCount}/{postSessionRows.length} � Pending {postSessionBlockedCount}
                              </span>
                            </div>
                          </div>
                          <div className="max-h-[46vh] space-y-2 overflow-y-auto">
                            {postSessionRows.map((row) => (
                              <div
                                key={row.attendee.id}
                                className={`rounded-md border bg-white p-2 text-xs ${
                                  row.wrapUpComplete
                                    ? 'border-emerald-200'
                                    : row.blockers.length
                                    ? 'border-rose-200 bg-rose-50/40'
                                    : 'border-slate-200'
                                }`}
                              >
                                <div className="mb-1 flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900">{getAttendeeLabel(row.attendee)}</p>
                                    {row.blockers.length > 0 && (
                                      <p className="mt-0.5 text-[10px] font-medium text-rose-700">
                                        {row.blockers.join(' � ')}
                                      </p>
                                    )}
                                  </div>
                                  <span
                                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                      row.wrapUpComplete
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : 'bg-rose-100 text-rose-700'
                                    }`}
                                  >
                                    {row.wrapUpComplete ? 'Complete' : 'Pending'}
                                  </span>
                                </div>
                                <div className="mb-2 grid grid-cols-2 gap-1 text-[11px] text-slate-700">
                                  <label className="flex items-center gap-1.5">
                                    <input
                                      type="checkbox"
                                      checked={row.attended}
                                      onChange={(e) => {
                                        void markAttendeeAttendance(row.attendee, e.target.checked);
                                      }}
                                    />
                                    Attended
                                  </label>
                                  <span
                                    className="inline-flex items-center gap-1.5"
                                    title={buildRosterEmailTitle(
                                      'Certificate',
                                      rosterEmailByAttendee[row.attendee.id]?.certificate ||
                                        EMPTY_ROSTER_EMAIL_INDICATORS.certificate,
                                      formatDateTimeUk
                                    )}
                                  >
                                    <RosterEmailMark
                                      status={
                                        (rosterEmailByAttendee[row.attendee.id]?.certificate ||
                                          EMPTY_ROSTER_EMAIL_INDICATORS.certificate).status
                                      }
                                      title={buildRosterEmailTitle(
                                        'Certificate',
                                        rosterEmailByAttendee[row.attendee.id]?.certificate ||
                                          EMPTY_ROSTER_EMAIL_INDICATORS.certificate,
                                        formatDateTimeUk
                                      )}
                                    />
                                    <span className="text-[11px] font-medium text-slate-700">Cert</span>
                                  </span>
                                  <span>
                                    Payment:{' '}
                                    {row.paymentStatus === 'paid'
                                      ? 'Fully paid'
                                      : row.paymentStatus === 'half_paid'
                                      ? 'Deposit / balance due'
                                      : 'Unpaid'}
                                    {row.invoiceNumber ? ` � Inv. ${row.invoiceNumber}${row.invoicePaid ? ' paid' : ' unpaid'}` : ''}
                                  </span>
                                  {isSafePassCourse(selectedSession?.courseName) ? (
                                    <>
                                      <span className={isUrgentProcessStatus(row.processStatus) ? 'font-semibold text-red-700' : ''}>
                                        Process: {getProcessStatusLabel(row.processStatus)}
                                        {row.processNumber ? ` � ${row.processNumber}` : ''}
                                      </span>
                                      <span>Card: {getCardStatusLabel(row.cardStatus)}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span>Compliance: n/a</span>
                                      <span>{row.attendee.isVirtual ? 'Virtual seat' : 'Named attendee'}</span>
                                    </>
                                  )}
                                </div>
                                {isSafePassCourse(selectedSession?.courseName) && !row.attendee.isVirtual && (
                                  <div className="grid grid-cols-2 gap-1.5">
                                    <select
                                      className={fieldIn}
                                      value={row.cardStatus}
                                      onChange={(e) =>
                                        void updateAttendeeSafePassCard(row.attendee, {
                                          cardStatus: e.target.value as SafePassCardTrackingStatus,
                                        })
                                      }
                                    >
                                      {CARD_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="date"
                                      className={fieldIn}
                                      value={String(row.attendee.safePassCardStatusDate || '').slice(0, 10)}
                                      onChange={(e) =>
                                        void updateAttendeeSafePassCard(row.attendee, {
                                          cardStatusDate: e.target.value || todayIsoDate(),
                                        })
                                      }
                                    />
                                    <input
                                      className={`col-span-2 ${fieldIn}`}
                                      placeholder="Card notes (e.g. as per envelope, posted to home address)"
                                      value={row.cardNotes}
                                      onChange={(e) =>
                                        void updateAttendeeSafePassCard(row.attendee, {
                                          cardNotes: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                            {postSessionRows.length === 0 && (
                              <p className="text-xs text-slate-500">No active attendees in this session.</p>
                            )}
                          </div>
                        </div>
                      )}

                      {sessionMainTab === 'history' && (
                        <div className="space-y-3">
                          <div className="rounded-md border border-violet-200 bg-violet-50 p-2 text-xs text-violet-900">
                            Full audit trail for this session ? who created it, added attendees, and recorded payments. Loaded only when you open this panel.
                          </div>
                          {sessionHistoryLoading ? (
                            <p className="text-sm text-slate-500">Loading session history?</p>
                          ) : sessionHistoryError ? (
                            <p className="text-sm text-red-600">{sessionHistoryError}</p>
                          ) : sessionHistoryEvents.length === 0 ? (
                            <p className="text-sm text-slate-500">No history recorded for this session yet.</p>
                          ) : (
                            <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
                              {sessionHistoryEvents.map((event) => {
                                const detailEntries = visibleHistoryDetailEntries(event.details);
                                return (
                                  <div
                                    key={event.id}
                                    className="rounded-md border border-slate-200 bg-white p-2.5 text-xs shadow-sm"
                                  >
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span
                                            className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${historyCategoryStyles(event.category)}`}
                                          >
                                            {formatHistoryActionLabel(event.action)}
                                          </span>
                                          {event.actorName ? (
                                            <span className="text-[11px] font-semibold text-slate-700">
                                              {event.actorName}
                                            </span>
                                          ) : (
                                            <span className="text-[11px] italic text-slate-400">System</span>
                                          )}
                                          <span className="text-[10px] text-slate-500">{formatHistoryAt(event.at)}</span>
                                        </div>
                                        <p className="mt-1 font-medium text-slate-900">{event.summary}</p>
                                      </div>
                                    </div>
                                    {detailEntries.length > 0 && (
                                      <dl className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
                                        {detailEntries.map(([key, value]) => (
                                          <div key={`${event.id}-${key}`} className="grid grid-cols-[minmax(0,7rem)_1fr] gap-x-2">
                                            <dt className="truncate font-medium capitalize text-slate-500">
                                              {key.replace(/([A-Z])/g, ' $1').trim()}
                                            </dt>
                                            <dd className="text-slate-800">{String(value)}</dd>
                                          </div>
                                        ))}
                                      </dl>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                  {sessionMainTab === 'spreadsheet' && selectedSession ? (
                    <SessionRosterSpreadsheet
                      key={selectedSession.id}
                      session={selectedSession}
                      rows={rosterSpreadsheetRows}
                      showMh={isSafePassCourse(selectedSession.courseName)}
                      onBack={() => setSessionMainTab('roster')}
                      canEdit={mode === 'manage'}
                      financeSummary={{
                        booked: bookedCount,
                        capacity: capacityCount,
                        collected: selectedMoney.totalCollected,
                        outstanding: selectedMoney.totalOutstanding
                      }}
                      actions={{
                        onUpdateNotes: (attendeeId, notes) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a) void updateAttendeeRosterNote(a, notes || null);
                        },
                        onUpdateProcessNumber: (attendeeId, processNumber) => {
                          void (async () => {
                            try {
                              const value = processNumber.trim() || null;
                              await courseSessionsService.updateAttendee(attendeeId, {
                                safePassProcessNumber: value,
                                ...(value
                                  ? {
                                      safePassProcessStatus: 'received' as const,
                                      safePassProcessStatusDate: new Date().toISOString().slice(0, 10),
                                    }
                                  : {}),
                              });
                              await load();
                            } catch (e: unknown) {
                              showError((e as Error)?.message || 'Could not update process number.');
                            }
                          })();
                        },
                        onUpdateStatus: (attendeeId, status) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (!a) return;
                          if (status === 'completed') void markAttendeeAttendance(a, true);
                          else if (status === 'booked' && a.status === 'completed') void markAttendeeAttendance(a, false);
                          else if (status === 'no_show') void markAttendeeNoShow(a);
                          else if (status === 'failed') void markAttendeeFailed(a);
                          else if (status === 'cancelled') void removeAttendeeFromBooking(a.id);
                          else {
                            void (async () => {
                              try {
                                await courseSessionsService.updateAttendee(attendeeId, {
                                  status: status as CourseSessionAttendee['status'],
                                });
                                await load();
                              } catch (e: unknown) {
                                showError((e as Error)?.message || 'Could not update status.');
                              }
                            })();
                          }
                        },
                        onUpdateFee: (attendeeId, feeEuro) => {
                          void (async () => {
                            try {
                              await courseSessionsService.updateAttendee(attendeeId, {
                                enrollmentCourseFee: feeEuro > 0 ? feeEuro : null,
                              });
                              await load();
                            } catch (e: unknown) {
                              showError((e as Error)?.message || 'Could not update course price.');
                            }
                          })();
                        },
                        onEditAttendee: (attendeeId) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a && selectedSession) openEditAttendeeModal(a, selectedSession.id);
                        },
                        onRecordPayment: (attendeeId) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a && selectedSession) {
                            openEditAttendeeModal(a, selectedSession.id, { recordPayment: true });
                          }
                        },
                        onMarkAttended: (attendeeId, attended) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a) void markAttendeeAttendance(a, attended);
                        },
                        onMarkNoShow: (attendeeId) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a) void markAttendeeNoShow(a);
                        },
                        onMarkFailed: (attendeeId) => {
                          const a = selectedAttendees.find((x) => x.id === attendeeId);
                          if (a) void markAttendeeFailed(a);
                        },
                        onRemove: (attendeeId) => {
                          void removeAttendeeFromBooking(attendeeId);
                        },
                      }}
                    />
                  ) : null}

                  {sessionMainTab === 'roster' && (
                    <>
                  {isSafepassModal && (
                    <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                      <div className="text-xs text-amber-950">
                        <p className="font-semibold">Safe Pass session</p>
                        <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-amber-900/90">
                          <li>Use the roster below for attendance and card status</li>
                          <li>Use <button type="button" className="font-semibold underline" onClick={() => setSessionMainTab('postSession')}>Post-session</button> for the trainer checklist</li>
                          <li>Use <button type="button" className="font-semibold underline" onClick={() => openProcessNumbersModule('process-numbers')}>Process Numbers</button> for bulk process-number work</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white p-2 shadow-sm dark:border-slate-500 dark:bg-slate-800 dark:shadow-black/20">
                    <div className="mb-1 flex flex-wrap items-end justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-600 dark:text-slate-300" aria-hidden />
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-50">
                            Participants ({bookedCount})
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-slate-300">
                            {bookedCount}/{capacityCount} booked � ?{selectedMoney.totalOutstanding.toFixed(0)} outstanding
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                      {groupedSelectedAttendees.map((group) => {
                        const groupInvoice = resolveGroupInvoice(
                          group.groupLabel,
                          group.attendees,
                          companyCustomers,
                          getAttendeeCustomer
                        );
                        const groupOutstanding = selectedSession
                          ? cohortSeatsOutstandingEuro(
                              group.attendees,
                              selectedSession.courseName,
                              selectedSession.language,
                              courseDbFees,
                              paymentTimelineByAttendee,
                              getAttendeeCustomer
                            )
                          : 0;
                        const groupAnchor = group.attendees[0];
                        const groupCompanyId =
                          group.attendees.map((x) => x.companyCustomerId).find((id): id is string => Boolean(id)) ||
                          companyCustomers.find((co) => String(co.companyName || '').trim() === group.groupLabel.trim())?.id ||
                          null;
                        const groupCompanyCustomer =
                          (groupCompanyId ? companyCustomers.find((co) => co.id === groupCompanyId) : undefined) ||
                          companyCustomers.find((co) => String(co.companyName || '').trim() === group.groupLabel.trim());
                        return (
                        <div key={group.groupLabel} className="mb-4 last:mb-0">
                          <div className="mb-1.5 flex items-center justify-between gap-2 rounded-md bg-slate-100 px-2.5 py-2 dark:bg-slate-700">
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                              {group.groupLabel} ({group.attendees.length})
                            </span>
                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                              {group.isCompanyGroup && group.attendees.length > 0 && groupAnchor ? (
                                <button
                                  type="button"
                                  className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-800 hover:bg-blue-100"
                                  onClick={() => {
                                    const participantNames = group.attendees
                                      .filter((a) => !a.isVirtual)
                                      .map((a) => getAttendeeLabel(a))
                                      .filter(Boolean);
                                    setContactModal({
                                      attendee: groupAnchor,
                                      customer: groupCompanyCustomer || getAttendeeCustomer(groupAnchor),
                                      initialTab: 'email',
                                      companyCohort: {
                                        companyName: group.groupLabel,
                                        participantNames,
                                        seatCount: group.attendees.length,
                                        companyCustomer: groupCompanyCustomer,
                                        attendeeIds: group.attendees.filter((x) => !x.isVirtual).map((x) => x.id),
                                      },
                                    });
                                  }}
                                >
                                  Send company email
                                </button>
                              ) : null}
                              <RosterGroupInvoiceLabel
                                number={groupInvoice.number}
                                invoiceFlagPaid={groupInvoice.paid}
                                seatsOutstanding={groupOutstanding}
                                onManageInvoice={
                                  mode === 'manage' && selectedSession && groupAnchor && group.isCompanyGroup
                                    ? () =>
                                        openCompanyGroupInvoiceModal(
                                          selectedSession.id,
                                          groupAnchor,
                                          group.groupLabel,
                                          group.attendees.length,
                                          groupInvoice,
                                          groupOutstanding
                                        )
                                    : undefined
                                }
                                onRecordPayments={
                                  mode === 'manage' &&
                                  selectedSession &&
                                  groupAnchor &&
                                  groupInvoice.paid &&
                                  groupOutstanding > 0.005
                                    ? () =>
                                        void settleCompanyGroupSeatPayments(
                                          selectedSession.id,
                                          groupAnchor,
                                          groupInvoice.number
                                        )
                                    : undefined
                                }
                              />
                              {mode === 'manage' && group.attendees.some((x) => x.isVirtual) && (
                                <button
                                  type="button"
                                  className="rounded border border-violet-200 bg-white px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-50"
                                  onClick={() => openBulkVirtualFillModal(group.groupLabel, group.attendees)}
                                  title="Paste full names (one per line) to fill the virtual seats in this group"
                                >
                                  Fill seats from names
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="overflow-x-auto rounded-md">
                          <table className="w-full min-w-[36rem] border-separate border-spacing-y-2 text-left">
                            <thead>
                              <tr className="bg-slate-200 dark:bg-slate-700">
                                <th className="rounded-l-md px-2 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                                  Participant
                                </th>
                                <th
                                  className="w-12 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                  title="Attended (manual tick)"
                                >
                                  Attend.
                                </th>
                                {isSafePassCourse(selectedSession?.courseName) ? (
                                  <th
                                    className="w-10 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                    title="Manual Handling reminder for interpreter"
                                  >
                                    MH
                                  </th>
                                ) : (
                                  <th
                                    className="w-14 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                    title="Online iHasco / CPL completed"
                                  >
                                    Online CPL.
                                  </th>
                                )}
                                <th
                                  className="w-11 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                  title="Booking confirmation"
                                >
                                  Conf
                                </th>
                                <th
                                  className="w-11 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                  title="Course reminder"
                                >
                                  Rem
                                </th>
                                <th
                                  className="w-11 px-1 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                                  title="Certificate email sent"
                                >
                                  Cert
                                </th>
                                <th className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100">
                                  Fee / Paid / Bal
                                </th>
                                <th className="w-14 rounded-r-md px-1 py-1" aria-label="Actions" />
                              </tr>
                            </thead>
                            <tbody>
                          {group.attendees.map((a, rowIndex) => {
                        const c = getAttendeeCustomer(a);
                        const fee = getEffectiveFee(a, c, selectedSession?.courseName, courseDbFees, selectedSession?.language);
                        const paid = getAttendeePaidForRoster(
                          a,
                          c,
                          selectedSession?.courseName,
                          courseDbFees,
                          paymentTimelineByAttendee[a.id] || []
                        );
                        const outstanding = attendeeBalanceEuro(fee, paid);
                        const isOrphaned =
                          Boolean(selectedSession && rosterHydratedBySession[selectedSession.id]) &&
                          !a.isVirtual &&
                          !c &&
                          Boolean(a.individualCustomerId);
                        const label = getAttendeeLabel(a);
                        const crmStatus = (a.paymentStatus || 'unpaid') as 'paid' | 'half_paid' | 'unpaid';
                        const customerId = getAttendeeCustomerId(a);
                        const canOpenCrm = Boolean(customerId && onNavigateToClient) && !isOrphaned;
                        const linkedCompanyTarget = linkedCompanyDisplayForSeat(a, c, companyCustomers);
                        const groupCompanyId =
                          group.attendees.map((x) => x.companyCustomerId).find((id): id is string => Boolean(id)) ||
                          companyCustomers.find((co) => String(co.companyName || '').trim() === group.groupLabel.trim())?.id ||
                          null;
                        const companyCohort =
                          groupCompanyId != null
                            ? group.attendees.filter((x) => {
                                if (x.companyCustomerId === groupCompanyId) return true;
                                if (x.isVirtual) {
                                  const ph = String(x.placeholderLabel || '').trim().toLowerCase();
                                  const gl = group.groupLabel.trim().toLowerCase();
                                  if (ph && ph === gl) return true;
                                }
                                return false;
                              })
                            : [];
                        const seatIndexLabel = companySeatBadgeLabel(a, companyCohort);
                        const personCustomer = a.individualCustomerId
                          ? customerById[a.individualCustomerId]
                          : c;
                        const emailDisplay = String(
                          personCustomer?.emailAddress || c?.emailAddress || ''
                        ).trim();
                        const phoneDisplay = String(
                          personCustomer?.contactPhoneNumber || c?.contactPhoneNumber || ''
                        ).trim();
                        const bookedByLabel = resolveBookedByLabel(a);
                        const safePassProcessNumberDisplay = String(
                          a.safePassProcessNumber ||
                            (c as { safePassProcessNumber?: string })?.safePassProcessNumber ||
                            ''
                        ).trim();
                        const safePassProcessStatusLabel = getProcessStatusLabel(inferProcessStatus(a));
                        const attendeeMenuItems: OverflowMenuItem[] = [
                          ...(mode === 'manage' && c
                            ? [
                                {
                                  icon: Pencil,
                                  label: 'Edit fee',
                                  onClick: () => openEditAttendeeModal(a, selectedSession!.id, { recordPayment: true }),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && c
                            ? [
                                {
                                  icon: Copy,
                                  label: 'Copy to another course',
                                  onClick: () => openCopyAttendeeModal(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && a.isVirtual
                            ? [
                                {
                                  icon: UserPlus,
                                  label: 'Assign seat',
                                  onClick: () => replaceVirtualWithCustomer(a),
                                },
                              ]
                            : []),
                          ...(!a.isVirtual || c
                            ? [
                                {
                                  icon: Mail,
                                  label: 'Send email',
                                  onClick: () => setContactModal({ attendee: a, customer: c, initialTab: 'email' }),
                                },
                                {
                                  icon: MessageCircle,
                                  label: 'WhatsApp',
                                  onClick: () => setContactModal({ attendee: a, customer: c, initialTab: 'whatsapp' }),
                                },
                              ]
                            : []),
                          ...(mode === 'manage'
                            ? [
                                {
                                  icon: CalendarDays,
                                  label: 'Move to another date',
                                  onClick: () => void rescheduleAttendee(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && a.isVirtual
                            ? [
                                {
                                  icon: Users,
                                  label: 'Fill seat from names',
                                  onClick: () => void replaceVirtualWithCustomer(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && isOrphaned
                            ? [
                                {
                                  icon: RotateCcw,
                                  label: 'Restore as virtual seat',
                                  onClick: () => void restoreOrphanedAsVirtual(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && !a.isVirtual && !isOrphaned && (a.status === 'booked' || a.status === 'completed')
                            ? [
                                {
                                  icon: ArrowDownToLine,
                                  label: 'Mark no-show',
                                  onClick: () => void markAttendeeNoShow(a),
                                },
                                {
                                  icon: ArrowDownToLine,
                                  label: 'Mark failed',
                                  onClick: () => void markAttendeeFailed(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && !a.isVirtual && isAttendeeFailed(a)
                            ? [
                                {
                                  icon: RotateCcw,
                                  label: 'Clear failed status',
                                  onClick: () => void restoreFromNoShow(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && !a.isVirtual && !isOrphaned
                            ? [
                                {
                                  icon: ArrowDownToLine,
                                  label: 'Move to waiting list',
                                  onClick: () => void moveAttendeeToWaitingList(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage' && !a.isVirtual && !isOrphaned
                            ? [
                                {
                                  icon: ArrowDownToLine,
                                  label: 'Remove from session',
                                  onClick: () => openTransferAttendeeModal(a),
                                },
                              ]
                            : []),
                          ...(mode === 'manage'
                            ? [
                                {
                                  icon: Trash2,
                                  label:
                                    !a.isVirtual && linkedCompanyTarget && !isOrphaned
                                      ? 'Remove & reserve company seat'
                                      : 'Delete attendee',
                                  onClick: () => void removeAttendeeFromBooking(a.id),
                                  destructive: true,
                                },
                              ]
                            : []),
                        ];
                        const balance = outstanding;
                        const ledger = paymentTimelineByAttendee[a.id] || [];
                        const showLinkedCompany =
                          !a.isVirtual &&
                          Boolean(linkedCompanyTarget) &&
                          linkedCompanyTarget.trim().toLowerCase() !== label.trim().toLowerCase();
                        const virtualCompanyLabel = String(a.placeholderLabel || group.groupLabel || '').trim();
                        const showVirtualCompany =
                          virtualCompanyLabel &&
                          virtualCompanyLabel.toLowerCase() !== group.groupLabel.trim().toLowerCase();
                        const confStatus = rosterEmailByAttendee[a.id]?.bookingConf || EMPTY_ROSTER_EMAIL_INDICATORS.bookingConf;
                        const remStatus = rosterEmailByAttendee[a.id]?.reminder || EMPTY_ROSTER_EMAIL_INDICATORS.reminder;
                        const certStatus = rosterEmailByAttendee[a.id]?.certificate || EMPTY_ROSTER_EMAIL_INDICATORS.certificate;
                        const seatTone = rosterSeatTone(rowIndex);
                        return (
                          <Fragment key={a.id}>
                          <tr className={`h-[4.25rem] align-middle ${seatTone.shell}`}>
                              <td className={`min-w-0 border-l-2 border-t-2 ${seatTone.edge} px-2 py-1.5 ${a.isVirtual ? `rounded-l-lg border-b-2` : 'rounded-tl-lg'}`}>
                                  <button
                                    type="button"
                                    disabled={!canOpenCrm}
                                    title={canOpenCrm ? 'Open client in CRM' : 'No CRM profile linked'}
                                    className={`flex h-[3.75rem] w-full flex-col justify-center gap-0.5 overflow-hidden rounded px-0.5 text-left transition ${
                                      canOpenCrm ? 'hover:bg-white/70 dark:hover:bg-slate-700/50' : 'cursor-default'
                                    }`}
                                    onClick={() => {
                                      if (canOpenCrm && customerId) onNavigateToClient!(customerId);
                                    }}
                                  >
                                    <div className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
                                      {seatIndexLabel ? (
                                        <span
                                          className="shrink-0 text-[11px] font-bold leading-none text-sky-800 dark:text-sky-200"
                                          title="Seat position in this company booking"
                                        >
                                          {seatIndexLabel}
                                        </span>
                                      ) : null}
                                      {a.isVirtual ? (
                                        <>
                                          <span className="shrink-0 text-sm font-semibold text-violet-800 dark:text-violet-200">Virtual seat</span>
                                          {showVirtualCompany ? (
                                            <span className="min-w-0 truncate text-xs text-slate-600 dark:text-slate-300" title={virtualCompanyLabel}>
                                              � {virtualCompanyLabel}
                                            </span>
                                          ) : null}
                                        </>
                                      ) : (
                                        <>
                                          <span className={`min-w-0 truncate text-sm font-semibold ${isOrphaned ? 'text-red-700 dark:text-red-300' : 'text-slate-950 dark:text-slate-50'}`} title={label}>
                                            {label}
                                          </span>
                                          {showLinkedCompany ? (
                                            <span className="min-w-0 truncate text-xs font-semibold text-slate-700 dark:text-slate-200" title={linkedCompanyTarget}>
                                              � {linkedCompanyTarget}
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                      <span className="shrink-0">
                                        <PaymentStatusPill status={crmStatus} />
                                      </span>
                                      {isOrphaned && (
                                        <span className="inline-flex shrink-0 items-center rounded-full bg-red-100 px-1.5 py-px text-[9px] font-bold text-red-700">
                                          CRM deleted
                                        </span>
                                      )}
                                      {communicationPreviewByAttendee[a.id] ? (
                                        <span className="group relative inline-flex shrink-0" onClick={(e) => e.stopPropagation()}>
                                          <span
                                            className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
                                              communicationPreviewByAttendee[a.id].channel === 'email'
                                                ? 'border-blue-200 bg-blue-50 text-blue-700'
                                                : communicationPreviewByAttendee[a.id].channel === 'whatsapp'
                                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                                : 'border-slate-200 bg-slate-50 text-slate-700'
                                            }`}
                                          >
                                            {communicationPreviewByAttendee[a.id].channel === 'email' ? (
                                              <Mail className="h-2.5 w-2.5" />
                                            ) : (
                                              <MessageCircle className="h-2.5 w-2.5" />
                                            )}
                                          </span>
                                          <span className="pointer-events-none absolute left-0 top-5 z-30 hidden max-w-[28rem] rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-700 shadow-lg group-hover:block dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                                            {communicationPreviewByAttendee[a.id].text}
                                          </span>
                                        </span>
                                      ) : null}
                                    </div>
                                  {!a.isVirtual ? (
                                    <div
                                      className="flex h-4 min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-xs leading-4 text-slate-700 dark:text-slate-200"
                                      title="Select email or phone with the mouse to copy"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {emailDisplay ? (
                                        <span className="inline-flex min-w-0 max-w-[14rem] items-center gap-0.5" title={emailDisplay}>
                                          <Mail className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
                                          <span className="cursor-text select-text truncate">{emailDisplay}</span>
                                        </span>
                                      ) : (
                                        <span className="text-slate-400">No email</span>
                                      )}
                                      {bookedByLabel ? (
                                        <span className="min-w-0 max-w-[10rem] truncate cursor-text select-text" title="Booked by (Add to session)">
                                          / {bookedByLabel}
                                        </span>
                                      ) : null}
                                      {phoneDisplay ? (
                                        <span className="shrink-0 cursor-text select-text tabular-nums">{phoneDisplay}</span>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div className="h-4" aria-hidden />
                                  )}
                                  {!a.isVirtual ? (
                                    <div className="flex h-4 min-w-0 items-center overflow-hidden whitespace-nowrap text-xs leading-4 text-slate-800 dark:text-slate-100">
                                      <span className="truncate font-medium" title="Date booked and the CRM user who added this seat">
                                        Booked: {formatDateUk(a.createdAt)}
                                        {a.bookedByUserName ? ` / ${a.bookedByUserName}` : ''}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="h-4" aria-hidden />
                                  )}
                                  </button>
                              </td>
                              <td className={`border-t-2 ${seatTone.edge} px-1 text-center align-middle ${a.isVirtual ? 'border-b-2' : ''}`}>
                                {mode === 'manage' && !isOrphaned ? (
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300"
                                    checked={Boolean(a.rosterAttended)}
                                    disabled={Boolean(a.isVirtual)}
                                    title={a.isVirtual ? 'Reserved seats cannot be marked attended' : 'Attended'}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      void updateAttendeeRosterFlags(a, { rosterAttended: e.target.checked });
                                    }}
                                  />
                                ) : (
                                  <span className="text-xs text-slate-400">{a.rosterAttended ? '?' : '?'}</span>
                                )}
                              </td>
                              <td className={`border-t-2 ${seatTone.edge} px-1 text-center align-middle ${a.isVirtual ? 'border-b-2' : ''}`}>
                                {isSafePassCourse(selectedSession?.courseName) ? (
                                  mode === 'manage' && !isOrphaned && !a.isVirtual ? (
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-gray-300"
                                      checked={Boolean(a.mhFlag)}
                                      title="Manual Handling reminder"
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        void updateAttendeeRosterFlags(a, { mhFlag: e.target.checked });
                                      }}
                                    />
                                  ) : (
                                    <span className="text-xs text-slate-400">{a.mhFlag ? '?' : '?'}</span>
                                  )
                                ) : mode === 'manage' && !isOrphaned && !a.isVirtual ? (
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300"
                                    checked={Boolean(a.onlineCpl)}
                                    title="Online CPL completed"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      void updateAttendeeRosterFlags(a, { onlineCpl: e.target.checked });
                                    }}
                                  />
                                ) : (
                                  <span className="text-xs text-slate-400">{a.onlineCpl ? '?' : '?'}</span>
                                )}
                              </td>
                              <td className={`border-t-2 ${seatTone.edge} px-1 text-center align-middle ${a.isVirtual ? 'border-b-2' : ''}`}>
                                {!a.isVirtual ? (
                                  <RosterEmailMark
                                    status={confStatus.status}
                                    title={buildRosterEmailTitle('Booking Confirmation', confStatus, formatDateTimeUk)}
                                  />
                                ) : (
                                  <span className="text-xs text-slate-300">?</span>
                                )}
                              </td>
                              <td className={`border-t-2 ${seatTone.edge} px-1 text-center align-middle ${a.isVirtual ? 'border-b-2' : ''}`}>
                                {!a.isVirtual ? (
                                  <RosterEmailMark
                                    status={remStatus.status}
                                    title={buildRosterEmailTitle('Course Reminder', remStatus, formatDateTimeUk)}
                                  />
                                ) : (
                                  <span className="text-xs text-slate-300">?</span>
                                )}
                              </td>
                              <td className={`border-t-2 ${seatTone.edge} px-1 text-center align-middle ${a.isVirtual ? 'border-b-2' : ''}`}>
                                {!a.isVirtual ? (
                                  <RosterEmailMark
                                    status={certStatus.status}
                                    title={buildRosterEmailTitle('Certificate', certStatus, formatDateTimeUk)}
                                  />
                                ) : (
                                  <span className="text-xs text-slate-300">?</span>
                                )}
                              </td>
                              <td className={`whitespace-nowrap border-t-2 ${seatTone.edge} px-2 text-right align-middle text-xs font-semibold leading-tight text-slate-700 dark:text-slate-200 ${a.isVirtual ? 'border-b-2' : ''}`}>
                                  <p className="whitespace-nowrap tabular-nums">
                                    Fee ?{fee.toFixed(2)} � Paid ?{paid.toFixed(2)} �{' '}
                                    <span
                                      className={
                                        balance > 0.005
                                          ? 'text-red-600'
                                          : balance < -0.005
                                            ? 'text-emerald-600'
                                            : 'text-slate-700 dark:text-slate-200'
                                      }
                                    >
                                      Bal ?{balance.toFixed(2)}
                                    </span>
                                  </p>
                              </td>
                              <td className={`border-r-2 border-t-2 ${seatTone.edge} px-1 align-middle ${a.isVirtual ? 'rounded-r-lg border-b-2' : 'rounded-tr-lg'}`}>
                                <div className="flex shrink-0 items-center justify-end gap-1">
                                {mode === 'manage' && !a.isVirtual && !isOrphaned && selectedSession ? (
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                    title="Add payment"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditAttendeeModal(a, selectedSession.id, { recordPayment: true });
                                    }}
                                  >
                                    <Banknote className="h-3.5 w-3.5" aria-hidden />
                                  </button>
                                ) : null}
                                <ActionsOverflowMenu
                                  items={attendeeMenuItems}
                                  align="right"
                                  menuTitle="Attendee actions"
                                />
                                </div>
                              </td>
                            </tr>
                            {!a.isVirtual ? (
                              <tr className={`align-top ${seatTone.shell}`}>
                                <td
                                  colSpan={8}
                                  className={`rounded-b-lg border-b-2 border-l-2 border-r-2 ${seatTone.edge} px-2 py-1.5`}
                                >
                              <div className="flex min-w-0 flex-wrap items-center gap-3">
                                <details className="min-w-0 shrink-0 open:min-w-[min(36rem,90vw)]">
                                  <summary className="cursor-pointer text-xs font-semibold text-slate-700 underline-offset-2 hover:underline dark:text-slate-200">
                                    Payment history{ledger.length > 0 ? ` (${ledger.length})` : ''}
                                  </summary>
                                  {ledger.length > 0 ? (
                                    <div className="mt-1.5 max-h-40 space-y-1 overflow-auto rounded-md border border-slate-300 bg-white p-2 shadow-md dark:border-slate-500 dark:bg-slate-900">
                                    {ledger.map((p) => (
                                      <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-700 dark:text-slate-200">
                                        <span>
                                          {formatDateUk(p.paymentDate)} ? {paymentMethodLabel(p.method)} ? EUR {Number(p.amount || 0).toFixed(2)} ({paymentStepTypeLabel(p.stepType)})
                                          {p.payerNotes ? (
                                            <span className="ml-1 text-gray-500" title={p.payerNotes}>
                                              ? {p.payerNotes}
                                            </span>
                                          ) : null}
                                          {p.recordedByName ? (
                                            <span className="ml-1 font-medium text-gray-500">
                                              � by {p.recordedByName}
                                            </span>
                                          ) : null}
                                        </span>
                                        {mode === 'manage' && selectedSession ? (
                                          <span className="flex shrink-0 gap-2">
                                            <button
                                              type="button"
                                              className="font-medium text-blue-700 hover:text-blue-800"
                                              onClick={() => {
                                                setEditPaymentModal({
                                                  paymentId: p.id,
                                                  attendeeId: a.id,
                                                  bookingId: selectedSession.id,
                                                  amountStr: Number(p.amount || 0).toFixed(2),
                                                  paymentDateUk: formatDateUk(p.paymentDate),
                                                  method: (p.method || 'cash') as AttendeePaymentMethod,
                                                  stepType: (p.stepType || 'other') as AttendeePaymentStepType,
                                                  payerNotes: String(p.payerNotes || ''),
                                                });
                                              }}
                                            >
                                              Edit
                                            </button>
                                            <button
                                              type="button"
                                              className="font-medium text-red-700 hover:text-red-800"
                                              onClick={() => void removePaymentLine(p.id, selectedSession.id, a.id, a)}
                                            >
                                              Delete
                                            </button>
                                          </span>
                                        ) : null}
                                      </div>
                                    ))}
                                    </div>
                                  ) : (
                                    <div className="mt-1.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-500 shadow-md dark:border-slate-500 dark:bg-slate-900">
                                      No ledger payments yet.
                                    </div>
                                  )}
                                </details>
                                {mode === 'manage' && !isOrphaned ? (
                                  <label
                                    className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-slate-800 dark:text-slate-100"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <CheckCircle2 className={`h-3.5 w-3.5 ${a.sendCertificate ? 'text-emerald-600' : 'text-gray-400'}`} aria-hidden />
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 rounded border-gray-300"
                                      checked={Boolean(a.sendCertificate)}
                                      disabled={Boolean(a.isVirtual)}
                                      onChange={(e) => {
                                        void updateAttendeeRosterFlags(a, { sendCertificate: e.target.checked });
                                      }}
                                    />
                                    Send Certificate
                                  </label>
                                ) : a.sendCertificate ? (
                                  <span className="shrink-0 text-xs font-semibold text-emerald-700">Send Certificate</span>
                                ) : null}

                                {isSafePassCourse(selectedSession?.courseName) ? (
                                  <span
                                    className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-700 dark:text-slate-200"
                                    title="Display only. Status comes from Process Numbers."
                                  >
                                    <span className="font-semibold">Process #</span>
                                    <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-50">
                                      {safePassProcessNumberDisplay || '?'}
                                    </span>
                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium dark:bg-slate-600">
                                      {safePassProcessStatusLabel}
                                    </span>
                                  </span>
                                ) : null}

                                <span
                                  className="inline-flex min-w-0 shrink-0 items-center gap-1"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {mode === 'manage' && !isOrphaned ? (
                                    <>
                                      <span className="shrink-0 text-xs font-medium text-slate-600 dark:text-slate-300">Note:</span>
                                      <input
                                        type="text"
                                        key={`${a.id}-roster-note-${String(a.rosterNote || '')}`}
                                        maxLength={ROSTER_NOTE_MAX_LEN}
                                        className={ROSTER_NOTE_INPUT_CLASS}
                                        placeholder="Note"
                                        defaultValue={String(a.rosterNote || '')}
                                        onBlur={(e) => {
                                          const next = e.target.value.trim().slice(0, ROSTER_NOTE_MAX_LEN);
                                          const prev = String(a.rosterNote || '').trim();
                                          if (next !== prev) {
                                            void updateAttendeeRosterNote(a, next || null);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.currentTarget.blur();
                                          }
                                        }}
                                      />
                                    </>
                                  ) : a.rosterNote ? (
                                    <span className="max-w-[28rem] truncate text-xs text-slate-600" title={String(a.rosterNote)}>
                                      Note: {a.rosterNote}
                                    </span>
                                  ) : null}
                                </span>
                              </div>
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                          })}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      );})}
                      {selectedSession && rosterLoadingBySession[selectedSession.id] && selectedAttendees.length === 0 ? (
                        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-12 text-center text-sm font-medium text-gray-500">
                          Loading participants?
                        </div>
                      ) : selectedAttendees.length === 0 ? (
                        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-12 text-center text-sm font-medium text-gray-500">
                          No attendees yet ? search above to add someone.
                        </div>
                      ) : null}
                      {noShowSelectedAttendees.length > 0 && (
                        <div className="mt-4 space-y-1.5 rounded-lg border border-orange-300/80 bg-orange-50/50 p-2">
                          <div className="sticky top-0 z-[1] rounded-md border border-orange-300 bg-orange-50/95 px-2 py-1">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-orange-900">
                              NO-SHOWS ({noShowSelectedAttendees.length})
                            </span>
                          </div>
                          {noShowSelectedAttendees.map((a) => {
                            const c = getAttendeeCustomer(a);
                            const label = getAttendeeLabel(a);
                            const fee = getEffectiveFee(a, c, selectedSession?.courseName, courseDbFees, selectedSession?.language);
                            const ledger = paymentTimelineByAttendee[a.id] || [];
                            const paid = getAttendeePaidForRoster(a, c, selectedSession?.courseName, courseDbFees, ledger);
                            const balance = attendeeBalanceEuro(fee, paid);
                            const bookedByLabel = resolveBookedByLabel(a);
                            const extraNoteLines = attendeeNotesForDisplay(a.attendeeNotes);
                            const personCustomer = a.individualCustomerId
                              ? customerById[a.individualCustomerId]
                              : c;
                            const emailDisplay = String(
                              personCustomer?.emailAddress || c?.emailAddress || ''
                            ).trim();
                            const phoneDisplay = String(
                              personCustomer?.contactPhoneNumber || c?.contactPhoneNumber || ''
                            ).trim();
                            return (
                              <div
                                key={a.id}
                                className="rounded-lg border border-orange-200 bg-white px-2 py-1 dark:border-orange-800 dark:bg-slate-800"
                              >
                                <div className="flex flex-wrap items-start justify-between gap-1.5">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-gray-900 line-through decoration-orange-400 dark:text-slate-100">
                                      {label}
                                    </p>
                                    {!a.isVirtual && (emailDisplay || phoneDisplay || bookedByLabel) ? (
                                      <div
                                        className="flex flex-wrap items-baseline gap-x-2 gap-y-0 text-[11px] leading-tight text-slate-600 dark:text-slate-300"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {emailDisplay ? (
                                          <span className="inline-flex min-w-0 max-w-full items-center gap-0.5" title={emailDisplay}>
                                            <Mail className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
                                            <span className="cursor-text select-text truncate">{emailDisplay}</span>
                                          </span>
                                        ) : null}
                                        {phoneDisplay ? (
                                          <span className="cursor-text select-text break-all">{phoneDisplay}</span>
                                        ) : null}
                                        {bookedByLabel ? (
                                          <span className="cursor-text select-text" title="Booked by (Add to session)">
                                            / {bookedByLabel}
                                          </span>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {!a.isVirtual ? (
                                      <div className="flex flex-wrap items-center gap-1 text-[10px] leading-tight text-slate-600 dark:text-slate-300">
                                        <span className="font-semibold text-slate-800 dark:text-slate-100" title="Date booked and the CRM user who added this seat">
                                          Booked: {formatDateUk(a.createdAt)}
                                          {a.bookedByUserName ? ` / ${a.bookedByUserName}` : ''}
                                        </span>
                                        {isSafePassCourse(selectedSession?.courseName) ? (
                                          <span
                                            className="inline-flex items-center gap-0.5"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <span className="text-slate-500">#</span>
                                            <span
                                              className="max-w-[40vw] truncate text-slate-600 dark:text-slate-300"
                                              title="Process number (edit in Process Numbers menu)"
                                            >
                                              {String(
                                                a.safePassProcessNumber ||
                                                  (c as { safePassProcessNumber?: string })?.safePassProcessNumber ||
                                                  ''
                                              ).trim() || '?'}
                                            </span>
                                          </span>
                                        ) : null}
                                        <span
                                          className="inline-flex items-center gap-0.5"
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {mode === 'manage' ? (
                                            <>
                                              <span className="text-slate-500">Note:</span>
                                              <input
                                                type="text"
                                                key={`${a.id}-noshow-roster-note-${String(a.rosterNote || '')}`}
                                                maxLength={ROSTER_NOTE_MAX_LEN}
                                                className={ROSTER_NOTE_INPUT_CLASS}
                                                placeholder="Note"
                                                defaultValue={String(a.rosterNote || '')}
                                                onBlur={(e) => {
                                                  const next = e.target.value.trim().slice(0, ROSTER_NOTE_MAX_LEN);
                                                  const prev = String(a.rosterNote || '').trim();
                                                  if (next !== prev) {
                                                    void updateAttendeeRosterNote(a, next || null);
                                                  }
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') e.currentTarget.blur();
                                                }}
                                              />
                                            </>
                                          ) : a.rosterNote ? (
                                            <span className="max-w-[45vw] truncate text-slate-500">Note: {a.rosterNote}</span>
                                          ) : null}
                                        </span>
                                      </div>
                                    ) : null}
                                    <p className="mt-0.5 text-[10px] text-orange-800 dark:text-orange-200">Did not attend</p>
                                    {extraNoteLines.length > 0 ? (
                                      <ul className="mt-1 space-y-0.5 text-[11px] text-orange-900 dark:text-orange-200">
                                        {extraNoteLines.map((line, idx) => (
                                          <li key={idx}>? {line}</li>
                                        ))}
                                      </ul>
                                    ) : null}
                                  </div>
                                  <div className="shrink-0 text-right text-[10px] leading-tight font-semibold text-gray-600 dark:text-slate-300">
                                    <p>Fee ?{fee.toFixed(2)}</p>
                                    <p>Paid ?{paid.toFixed(2)}</p>
                                    <p className={balance < -0.005 ? 'text-emerald-600' : balance > 0.005 ? 'text-red-600' : 'text-gray-600 dark:text-slate-300'}>
                                      Bal ?{balance.toFixed(2)}
                                    </p>
                                  </div>
                                  {mode === 'manage' ? (
                                    <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100"
                                        onClick={() => void restoreFromNoShow(a)}
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Restore to roster
                                      </button>
                                      <button
                                        type="button"
                                        title="Reschedule to another session"
                                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-800 transition hover:bg-slate-100"
                                        onClick={() => void rescheduleAttendee(a)}
                                      >
                                        <CalendarDays className="h-3.5 w-3.5" />
                                        Reschedule
                                      </button>
                                      {c ? (
                                        <button
                                          type="button"
                                          title="Copy to another session"
                                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 transition hover:bg-gray-50"
                                          onClick={() => openCopyAttendeeModal(a)}
                                        >
                                          <Copy className="h-3.5 w-3.5" />
                                          Copy
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        title="Permanently delete"
                                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-800 transition hover:bg-red-100"
                                        onClick={() => void removeAttendeeFromBooking(a.id)}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Delete
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                                {!a.isVirtual ? (
                                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-0.5 dark:border-slate-600">
                                    <details className="min-w-0">
                                      <summary className="cursor-pointer text-[10px] font-medium text-slate-500">
                                        Payment history{ledger.length > 0 ? ` (${ledger.length})` : ''}
                                      </summary>
                                      {ledger.length > 0 ? (
                                        <div className="mt-1 space-y-1 pb-1">
                                          {ledger.map((p) => (
                                            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-slate-300">
                                              <span>
                                                {formatDateUk(p.paymentDate)} ? {paymentMethodLabel(p.method)} ? EUR {Number(p.amount || 0).toFixed(2)} ({paymentStepTypeLabel(p.stepType)})
                                                {p.payerNotes ? (
                                                  <span className="ml-1 text-gray-500" title={p.payerNotes}>
                                                    ? {p.payerNotes}
                                                  </span>
                                                ) : null}
                                                {p.recordedByName ? (
                                                  <span className="ml-1 font-medium text-gray-500">� by {p.recordedByName}</span>
                                                ) : null}
                                              </span>
                                              {mode === 'manage' && selectedSession ? (
                                                <span className="flex shrink-0 gap-2">
                                                  <button
                                                    type="button"
                                                    className="font-medium text-blue-700 hover:text-blue-800"
                                                    onClick={() => {
                                                      setEditPaymentModal({
                                                        paymentId: p.id,
                                                        attendeeId: a.id,
                                                        bookingId: selectedSession.id,
                                                        amountStr: Number(p.amount || 0).toFixed(2),
                                                        paymentDateUk: formatDateUk(p.paymentDate),
                                                        method: (p.method || 'cash') as AttendeePaymentMethod,
                                                        stepType: (p.stepType || 'other') as AttendeePaymentStepType,
                                                        payerNotes: String(p.payerNotes || ''),
                                                      });
                                                    }}
                                                  >
                                                    Edit
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="font-medium text-red-700 hover:text-red-800"
                                                    onClick={() => void removePaymentLine(p.id, selectedSession.id, a.id, a)}
                                                  >
                                                    Delete
                                                  </button>
                                                </span>
                                              ) : null}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="mt-1 text-[11px] text-slate-400">No ledger payments yet.</div>
                                      )}
                                    </details>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {failedSelectedAttendees.length > 0 && (
                        <div className="mt-4 space-y-1.5 rounded-lg border border-rose-300/80 bg-rose-50/50 p-2">
                          <div className="sticky top-0 z-[1] rounded-md border border-rose-300 bg-rose-50/95 px-2 py-1">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-rose-900">
                              FAILED ({failedSelectedAttendees.length})
                            </span>
                          </div>
                          {failedSelectedAttendees.map((a) => {
                            const c = getAttendeeCustomer(a);
                            const label = getAttendeeLabel(a);
                            const bookedByLabel = resolveBookedByLabel(a);
                            return (
                              <div
                                key={a.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 bg-white px-2 py-1.5"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900">{label}</p>
                                  <p className="text-[10px] text-rose-800">
                                    Did not pass
                                    {bookedByLabel ? ` � Booked by ${bookedByLabel}` : ''}
                                  </p>
                                </div>
                                {mode === 'manage' ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100"
                                    onClick={() => void restoreFromNoShow(a)}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Clear failed
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {inactiveRosterAttendees.length > 0 && (
                        <div className="mt-4 space-y-1.5 rounded-lg border border-orange-200/80 bg-orange-50/40 p-2">
                          <div className="sticky top-0 z-[1] flex flex-wrap items-center justify-between gap-2 rounded-md border border-orange-200 bg-orange-50/95 px-2 py-1">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-orange-800">
                              REMOVED / TRANSFERRED / WAITING LIST ({inactiveRosterAttendees.length})
                            </span>
                            {mode === 'manage' && waitingListSelectedAttendees.length > 0 && bookedCount < capacityCount ? (
                              <button
                                type="button"
                                className="rounded-md border border-sky-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-sky-800 hover:bg-sky-100"
                                onClick={() => void fillFromWaitlist()}
                              >
                                Fill next seat
                              </button>
                            ) : null}
                          </div>
                          {inactiveRosterAttendees.map((a) => {
                            const c = getAttendeeCustomer(a);
                            const label = getAttendeeLabel(a);
                            const fee = getEffectiveFee(a, c, selectedSession?.courseName, courseDbFees, selectedSession?.language);
                            const ledger = paymentTimelineByAttendee[a.id] || [];
                            const paid = getAttendeePaidForRoster(a, c, selectedSession?.courseName, courseDbFees, ledger);
                            const balance = attendeeBalanceEuro(fee, paid);
                            const bookedByLabel = resolveBookedByLabel(a);
                            const waitingListAddedDate = parseWaitingListAddedDate(a.attendeeNotes);
                            const extraNoteLines = attendeeNotesForDisplay(a.attendeeNotes);
                            const isWaiting = isAttendeeWaitingList(a);
                            const isNoShow = isAttendeeNoShow(a);
                            const personCustomer = a.individualCustomerId
                              ? customerById[a.individualCustomerId]
                              : c;
                            const emailDisplay = String(
                              personCustomer?.emailAddress || c?.emailAddress || ''
                            ).trim();
                            const phoneDisplay = String(
                              personCustomer?.contactPhoneNumber || c?.contactPhoneNumber || ''
                            ).trim();
                            return (
                              <div key={a.id} className="rounded-lg border border-orange-200 bg-white px-2 py-1 dark:border-orange-800 dark:bg-slate-800">
                                <div className="flex flex-wrap items-start justify-between gap-1.5">
                                  <div className="min-w-0 flex-1">
                                    <p
                                      className={`text-xs font-semibold ${
                                        isWaiting
                                          ? 'text-amber-950 dark:text-amber-100'
                                          : 'text-gray-900 line-through decoration-orange-400 dark:text-slate-100'
                                      }`}
                                    >
                                      {label}
                                    </p>
                                    {!a.isVirtual && (emailDisplay || phoneDisplay || bookedByLabel) ? (
                                      <div
                                        className="flex flex-wrap items-baseline gap-x-2 gap-y-0 text-[11px] leading-tight text-slate-600 dark:text-slate-300"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {emailDisplay ? (
                                          <span className="inline-flex min-w-0 max-w-full items-center gap-0.5" title={emailDisplay}>
                                            <Mail className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
                                            <span className="cursor-text select-text truncate">{emailDisplay}</span>
                                          </span>
                                        ) : null}
                                        {phoneDisplay ? (
                                          <span className="cursor-text select-text break-all">{phoneDisplay}</span>
                                        ) : null}
                                        {bookedByLabel ? (
                                          <span className="cursor-text select-text" title="Booked by (Add to session)">
                                            / {bookedByLabel}
                                          </span>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {!a.isVirtual ? (
                                      <div className="flex flex-wrap items-center gap-1 text-[10px] leading-tight text-slate-600 dark:text-slate-300">
                                        <span className="font-semibold text-slate-800 dark:text-slate-100" title="Date booked and the CRM user who added this seat">
                                          Booked: {formatDateUk(a.createdAt)}
                                          {a.bookedByUserName ? ` / ${a.bookedByUserName}` : ''}
                                        </span>
                                        {isSafePassCourse(selectedSession?.courseName) ? (
                                          <span
                                            className="inline-flex items-center gap-0.5"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <span className="text-slate-500">#</span>
                                            <span
                                              className="max-w-[40vw] truncate text-slate-600 dark:text-slate-300"
                                              title="Process number (edit in Process Numbers menu)"
                                            >
                                              {String(
                                                a.safePassProcessNumber ||
                                                  (c as { safePassProcessNumber?: string })?.safePassProcessNumber ||
                                                  ''
                                              ).trim() || '?'}
                                            </span>
                                          </span>
                                        ) : null}
                                        <span
                                          className="inline-flex items-center gap-0.5"
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {mode === 'manage' ? (
                                            <>
                                              <span className="text-slate-500">Note:</span>
                                              <input
                                                type="text"
                                                key={`${a.id}-inactive-roster-note-${String(a.rosterNote || '')}`}
                                                maxLength={ROSTER_NOTE_MAX_LEN}
                                                className={ROSTER_NOTE_INPUT_CLASS}
                                                placeholder="Note"
                                                defaultValue={String(a.rosterNote || '')}
                                                onBlur={(e) => {
                                                  const next = e.target.value.trim().slice(0, ROSTER_NOTE_MAX_LEN);
                                                  const prev = String(a.rosterNote || '').trim();
                                                  if (next !== prev) {
                                                    void updateAttendeeRosterNote(a, next || null);
                                                  }
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') e.currentTarget.blur();
                                                }}
                                              />
                                            </>
                                          ) : a.rosterNote ? (
                                            <span className="max-w-[45vw] truncate text-slate-500">Note: {a.rosterNote}</span>
                                          ) : null}
                                        </span>
                                      </div>
                                    ) : null}
                                    {isWaiting ? (
                                      <p className="mt-0.5 text-[10px] text-sky-800 dark:text-sky-200">
                                        Waiting list � Added {waitingListAddedDate || formatDateUk(a.createdAt)}
                                      </p>
                                    ) : null}
                                    {isNoShow ? (
                                      <p className="mt-0.5 text-[10px] text-orange-800 dark:text-orange-200">Did not attend</p>
                                    ) : null}
                                    {extraNoteLines.length > 0 ? (
                                      <ul className="mt-1 space-y-0.5 text-[11px] text-orange-900 dark:text-orange-200">
                                        {extraNoteLines.map((line, idx) => (
                                          <li key={idx}>? {line}</li>
                                        ))}
                                      </ul>
                                    ) : null}
                                  </div>
                                  <div className="shrink-0 text-right text-[10px] leading-tight font-semibold text-gray-600 dark:text-slate-300">
                                    <p>Fee ?{fee.toFixed(2)}</p>
                                    <p>Paid ?{paid.toFixed(2)}</p>
                                    <p className={balance < -0.005 ? 'text-emerald-600' : balance > 0.005 ? 'text-red-600' : 'text-gray-600 dark:text-slate-300'}>
                                      Bal ?{balance.toFixed(2)}
                                    </p>
                                  </div>
                                  {mode === 'manage' && (
                                    <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">
                                      {isWaiting && bookedCount < capacityCount ? (
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100"
                                          onClick={() => void fillFromWaitlist(a.id)}
                                        >
                                          <RotateCcw className="h-3.5 w-3.5" />
                                          Move to roster
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          title="Restore to roster"
                                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100"
                                          onClick={() => void restoreRemovedAttendee(a)}
                                        >
                                          <RotateCcw className="h-3.5 w-3.5" />
                                          Restore
                                        </button>
                                      )}
                                      {!isWaiting ? (
                                        <>
                                          <button
                                            type="button"
                                            title="Reschedule to another session"
                                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-800 transition hover:bg-slate-100"
                                            onClick={() => void rescheduleAttendee(a)}
                                          >
                                            <CalendarDays className="h-3.5 w-3.5" />
                                            Reschedule
                                          </button>
                                          {c ? (
                                            <button
                                              type="button"
                                              title="Copy to another session"
                                              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 transition hover:bg-gray-50"
                                              onClick={() => openCopyAttendeeModal(a)}
                                            >
                                              <Copy className="h-3.5 w-3.5" />
                                              Copy
                                            </button>
                                          ) : null}
                                        </>
                                      ) : null}
                                      <button
                                        type="button"
                                        title="Permanently delete"
                                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-800 transition hover:bg-red-100"
                                        onClick={() => void removeAttendeeFromBooking(a.id)}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {!a.isVirtual ? (
                                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-0.5 dark:border-slate-600">
                                    <details className="min-w-0">
                                      <summary className="cursor-pointer text-[10px] font-medium text-slate-500">
                                        Payment history{ledger.length > 0 ? ` (${ledger.length})` : ''}
                                      </summary>
                                      {ledger.length > 0 ? (
                                        <div className="mt-1 space-y-1 pb-1">
                                          {ledger.map((p) => (
                                            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600 dark:text-slate-300">
                                              <span>
                                                {formatDateUk(p.paymentDate)} ? {paymentMethodLabel(p.method)} ? EUR {Number(p.amount || 0).toFixed(2)} ({paymentStepTypeLabel(p.stepType)})
                                                {p.payerNotes ? (
                                                  <span className="ml-1 text-gray-500" title={p.payerNotes}>
                                                    ? {p.payerNotes}
                                                  </span>
                                                ) : null}
                                                {p.recordedByName ? (
                                                  <span className="ml-1 font-medium text-gray-500">� by {p.recordedByName}</span>
                                                ) : null}
                                              </span>
                                              {mode === 'manage' && selectedSession ? (
                                                <span className="flex shrink-0 gap-2">
                                                  <button
                                                    type="button"
                                                    className="font-medium text-blue-700 hover:text-blue-800"
                                                    onClick={() => {
                                                      setEditPaymentModal({
                                                        paymentId: p.id,
                                                        attendeeId: a.id,
                                                        bookingId: selectedSession.id,
                                                        amountStr: Number(p.amount || 0).toFixed(2),
                                                        paymentDateUk: formatDateUk(p.paymentDate),
                                                        method: (p.method || 'cash') as AttendeePaymentMethod,
                                                        stepType: (p.stepType || 'other') as AttendeePaymentStepType,
                                                        payerNotes: String(p.payerNotes || ''),
                                                      });
                                                    }}
                                                  >
                                                    Edit
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="font-medium text-red-700 hover:text-red-800"
                                                    onClick={() => void removePaymentLine(p.id, selectedSession.id, a.id, a)}
                                                  >
                                                    Delete
                                                  </button>
                                                </span>
                                              ) : null}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="mt-1 text-[11px] text-slate-400">No ledger payments yet.</div>
                                      )}
                                    </details>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                    </>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {contactModal && (
        <AttendeeContactModal
          attendeeName={getAttendeeLabel(contactModal.attendee)}
          customer={contactModal.customer}
          session={sessions.find((s) => s.id === contactModal.attendee.bookingId)}
          sessionCourseName={
            sessions.find((s) => s.id === (contactModal.attendee.bookingId))?.courseName || 'Training Session'
          }
          sessionDates={
            (() => {
              const s = sessions.find((s) => s.id === contactModal.attendee.bookingId);
              return s ? formatDateBlocksUk(s.dateBlocks) : '?';
            })()
          }
          sessionLocation={
            (() => {
              const s = sessions.find((s) => s.id === contactModal.attendee.bookingId);
              const locId = s?.locationId || preferredDefaultLocationId;
              return locId ? locationById[locId] : undefined;
            })()
          }
          sessionLocationDetails={
            (() => {
              const s = sessions.find((s) => s.id === contactModal.attendee.bookingId);
              const locId = s?.locationId || preferredDefaultLocationId;
              return locId ? locationDetailsById[locId] : undefined;
            })()
          }
          sessionReferenceSequence={
            (() => {
              const s = sessions.find((x) => x.id === contactModal.attendee.bookingId);
              if (!s) return undefined;
              return buildSessionReferenceIndex(sessions).get(s.id);
            })()
          }
          sessionLocationMapsUrl={
            (() => {
              const s = sessions.find((s) => s.id === contactModal.attendee.bookingId);
              const locId = s?.locationId || preferredDefaultLocationId;
              if (!locId) return undefined;
              return locationMapsUrl[locId] || (locationDetailsById[locId]
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    String(locationDetailsById[locId].eircodePostcode || locationDetailsById[locId].postcode || locationById[locId] || '').trim()
                  )}`
                : undefined);
            })()
          }
          sessionDeliveryMode={
            sessions.find((s) => s.id === contactModal.attendee.bookingId)?.deliveryMode
          }
          attendeeCount={
            (() => {
              const s = sessions.find((s) => s.id === contactModal.attendee.bookingId);
              return s ? (attendeesBySession[s.id] || []).filter((a) => a.status !== 'cancelled').length : undefined;
            })()
          }
          enrollmentCourseFee={contactModal.attendee.enrollmentCourseFee}
          amountPaid={contactModal.attendee.amountPaid}
          paymentStatus={contactModal.attendee.paymentStatus}
          actorUserId={user?.id}
          actorName={actorName}
          initialTab={contactModal.initialTab}
          companyCohort={contactModal.companyCohort}
          onRosterEmailQueued={(info) =>
            applyRosterEmailQueued(info, contactModal.attendee.id)
          }
          onClose={() => setContactModal(null)}
        />
      )}

      {seatOpenedModal && (
        <div className="fixed inset-0 z-[66] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900">Seat available</p>
            <p className="mt-1 text-sm text-gray-600">
              {seatOpenedModal.freedLabel
                ? `${seatOpenedModal.freedLabel} freed a seat on this session.`
                : 'A seat opened on this session.'}
              {seatOpenedModal.waitlistCount > 0
                ? ` ${seatOpenedModal.waitlistCount} person${seatOpenedModal.waitlistCount === 1 ? '' : 's'} on the waiting list.`
                : ''}
            </p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => setSeatOpenedModal(null)}
              >
                Leave open
              </button>
              {seatOpenedModal.waitlistCount > 0 ? (
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={() => void fillFromWaitlist()}
                >
                  Fill from waitlist
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {createCrmForSessionOpen && selectedSessionId && (
        <AddEditCustomerModal
          headingTitle="Add CRM record"
          submitButtonLabel="Create record and add to the session"
          onClose={() => setCreateCrmForSessionOpen(false)}
          onSave={async (cust) => {
            try {
              await beginCreateIndividualWithDedup(cust, 'session');
            } catch (e: any) {
              showError(e?.message || 'Could not create record.');
              throw e;
            }
          }}
        />
      )}

      {createCrmForVirtualSeatOpen && replaceVirtualModal && (
        <AddEditCustomerModal
          headingTitle="Add CRM record"
          submitButtonLabel="Create record and add to virtual seat"
          defaultCustomerType="individual"
          initialCompanyName={String(currentCustomer?.companyName || '').trim()}
          onClose={() => setCreateCrmForVirtualSeatOpen(false)}
          onSave={async (cust) => {
            try {
              await beginCreateIndividualWithDedup(cust, 'virtual');
            } catch (e: any) {
              showError(e?.message || 'Could not create record.');
              throw e;
            }
          }}
        />
      )}

      {duplicateCreatePrompt && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-amber-200 bg-white p-5 shadow-xl">
            <p className="text-lg font-semibold text-gray-900">Existing client found</p>
            <p className="mt-2 text-sm text-gray-600">
              A CRM record already matches this name and contact details. Reuse it instead of creating a duplicate?
            </p>
            {duplicateCreatePrompt.matches.length === 1 ? (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {individualCustomerMatchLabel(duplicateCreatePrompt.matches[0]!)}
              </p>
            ) : (
              <div className="mt-3">
                <label className={fieldLabel}>Matching records</label>
                <select
                  className={fieldIn}
                  value={duplicateCreatePrompt.selectedMatchId}
                  onChange={(e) =>
                    setDuplicateCreatePrompt((prev) =>
                      prev ? { ...prev, selectedMatchId: e.target.value } : prev
                    )
                  }
                >
                  {duplicateCreatePrompt.matches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {individualCustomerMatchLabel(m)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => void resolveDuplicateCreatePrompt('cancel')}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50"
                onClick={() => void resolveDuplicateCreatePrompt('create_anyway')}
              >
                Create anyway
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={() => void resolveDuplicateCreatePrompt('use_existing')}
              >
                Use existing
              </button>
            </div>
          </div>
        </div>
      )}

      {reserveVirtualModal && selectedSession && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900">Reserve virtual places</p>
                <p className="text-sm text-gray-600">{selectedSession.courseName}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => {
                  resetCompanyPicker();
                  setReserveVirtualModal(null);
                }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className={fieldLabel}>Company</label>
                {(() => {
                  const selectedCompany =
                    companyCustomers.find((co) => co.id === reserveVirtualModal.companyCustomerId) ||
                    companyPickerHits.find((co) => co.id === reserveVirtualModal.companyCustomerId) ||
                    null;
                  if (selectedCompany) {
                    return (
                      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                          {selectedCompany.companyName}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 text-xs font-semibold text-slate-600 hover:text-slate-900"
                          onClick={() => {
                            setReserveVirtualModal((prev) => (prev ? { ...prev, companyCustomerId: '' } : prev));
                            resetCompanyPicker();
                          }}
                        >
                          Change
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-1.5">
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                          className={`${fieldIn} pl-8`}
                          placeholder="Search companies by name?"
                          value={companyPickerQuery}
                          onChange={(e) => setCompanyPickerQuery(e.target.value)}
                        />
                      </div>
                      {companyPickerSearching ? (
                        <p className="text-xs text-slate-500">Searching?</p>
                      ) : companyPickerQuery.trim().length < 2 ? (
                        <p className="text-xs text-slate-500">Type at least 2 characters to search.</p>
                      ) : companyPickerHits.length === 0 ? (
                        <p className="text-xs text-slate-500">No companies match that search.</p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                          {companyPickerHits.map((co) => (
                            <button
                              key={co.id}
                              type="button"
                              className="block w-full truncate border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
                              onClick={() => {
                                upsertCustomersInMemory([co]);
                                setReserveVirtualModal((prev) =>
                                  prev ? { ...prev, companyCustomerId: co.id } : prev
                                );
                                setCompanyPickerQuery(String(co.companyName || ''));
                                setCompanyPickerHits([]);
                              }}
                            >
                              {co.companyName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className={fieldLabel}>How many virtual places to reserve?</label>
                <input
                  className={fieldIn}
                  value={reserveVirtualModal.countStr}
                  onChange={(e) => setReserveVirtualModal((prev) => (prev ? { ...prev, countStr: e.target.value.replace(/[^\d]/g, '') } : prev))}
                  inputMode="numeric"
                  placeholder="1"
                />
              </div>
              <div>
                <label className={fieldLabel}>
                  Source <span className="text-red-500">*</span>
                  <span className="ml-1 font-normal text-[11px] text-gray-500">(how did this booking reach us?)</span>
                </label>
                <select
                  className={fieldIn}
                  value={reserveVirtualModal.source}
                  onChange={(e) =>
                    setReserveVirtualModal((prev) =>
                      prev ? { ...prev, source: e.target.value as InboundChannel | '' } : prev
                    )
                  }
                >
                  <option value="">Select a source?</option>
                  {INBOUND_CHANNEL_OPTIONS.filter((o) => o.value !== 'bulk_import').map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabel}>Individual Course Price</label>
                <div className="flex items-center gap-2">
                  <input
                    className={`${moneyIn} ${virtualFeeLocked ? 'bg-gray-100' : ''}`}
                    value={reserveVirtualModal.enrollmentCourseFee}
                    onChange={(e) => setReserveVirtualModal((prev) => (prev ? { ...prev, enrollmentCourseFee: e.target.value } : prev))}
                    disabled={virtualFeeLocked}
                    placeholder="0.00"
                  />
                  {virtualFeeLocked ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        const sessionRow = sessions.find((s) => s.id === reserveVirtualModal?.sessionId);
                        const feeLang = resolveCatalogFeeLanguage(sessionRow?.language);
                        const courseDefault = getCourseCatalogDefaultFee(
                          sessionRow?.courseName,
                          courseDbFees,
                          feeLang
                        );
                        const langNote = feeLang ? ` for a session delivered in ${feeLang}` : '';
                        const msg =
                          courseDefault > 0
                            ? `The catalog fee${langNote} is ?${courseDefault.toFixed(2)}. Change this session's fee?`
                            : 'Change the fee for this session?';
                        if (window.confirm(msg)) setVirtualFeeLocked(false);
                      }}
                    >
                      Change price
                    </button>
                  ) : null}
                </div>
              </div>
              <div>
                <label className={fieldLabel}>Payment status</label>
                <div className="space-y-1.5">
                  {[
                    ['unpaid', 'Not paid yet'],
                    ['prepaid', 'Prepaid (enter amount below)'],
                    ['part', 'Part paid (enter amount collected)'],
                    ['full', 'Paid in full']
                  ].map(([v, label]) => (
                    <label key={v} className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="reserve-paykind"
                        checked={reserveVirtualModal.payKind === v}
                        onChange={() => setReserveVirtualModal((prev) => (prev ? { ...prev, payKind: v as AddPayKind } : prev))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              {(reserveVirtualModal.payKind === 'prepaid' || reserveVirtualModal.payKind === 'part') && (
                <div>
                  <label className={fieldLabel}>{reserveVirtualModal.payKind === 'prepaid' ? 'Prepaid amount EUR' : 'Amount paid so far EUR'}</label>
                  <input
                    className={moneyIn}
                    value={reserveVirtualModal.amountStr}
                    onChange={(e) => setReserveVirtualModal((prev) => (prev ? { ...prev, amountStr: e.target.value } : prev))}
                    placeholder="0.00"
                  />
                </div>
              )}
              {reserveVirtualModal.payKind !== 'unpaid' && (
                <div>
                  <label className={fieldLabel}>Payment method</label>
                  <PaymentMethodSelect
                    className={fieldIn}
                    value={reserveVirtualModal.paymentMethod}
                    onChange={(paymentMethod) =>
                      setReserveVirtualModal((prev) => (prev ? { ...prev, paymentMethod } : prev))
                    }
                  />
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setReserveVirtualModal(null)}>
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={() => void confirmReserveVirtualSpots()}>
                Add to booking
              </button>
            </div>
          </div>
        </div>
      )}

      {addAttendeeModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3">
          <div
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-gray-200 bg-white p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-attendee-title"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p id="add-attendee-title" className="text-lg font-semibold text-gray-900">
                  {addAttendeeModal.editingAttendee ? 'Edit session enrollment' : 'Add to session'}
                </p>
                <p className="mt-0.5 text-sm text-gray-600">
                  {sessions.find((x) => x.id === addAttendeeModal.sessionId)?.courseName || 'Course'}
                </p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {addAttendeeModal.customer.customerType === 'individual'
                    ? `${addAttendeeModal.customer.firstName || ''} ${addAttendeeModal.customer.surname || ''}`.trim() ||
                      addAttendeeModal.customer.companyName
                    : addAttendeeModal.customer.companyName}
                </p>
                {(addAttendeeModal.customer.preferredLanguage || '').trim() ? (
                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-800">
                    <Languages className="h-3.5 w-3.5" />
                    Prefers {addAttendeeModal.customer.preferredLanguage}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => {
                  setAddAttendeeModal(null);
                  setSessionFeeLocked(false);
                  setAddAssignedCompanyCustomerId('');
                }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-xs text-gray-600">
              Catalog fee uses the <span className="font-medium">language this session is delivered in</span> (not attendee or company
              preference). Set the session fee and how much is already paid.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className={fieldLabel}>Booked by</label>
                <input
                  className={fieldIn}
                  placeholder="Person or company who placed this booking"
                  value={addBookedByName}
                  onChange={(e) => setAddBookedByName(e.target.value)}
                />
              </div>
              <div>
                <label className={fieldLabel}>Individual Course Price</label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className={`${moneyIn} sm:min-w-0 sm:flex-1`}
                    placeholder="e.g. 150.00"
                    value={addSessionFee}
                    disabled={sessionFeeLocked}
                    onChange={(e) => setAddSessionFee(e.target.value)}
                  />
                  {sessionFeeLocked ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900 transition hover:bg-blue-100"
                      onClick={() => {
                        const sessionRow = sessions.find((s) => s.id === addAttendeeModal.sessionId);
                        const feeLang = resolveCatalogFeeLanguage(sessionRow?.language);
                        const courseDefault = getCourseCatalogDefaultFee(
                          sessionRow?.courseName,
                          courseDbFees,
                          feeLang
                        );
                        const langNote = feeLang ? ` for a session delivered in ${feeLang}` : '';
                        const msg =
                          courseDefault > 0
                            ? `The catalog fee${langNote} is ?${courseDefault.toFixed(2)}. Change this session's fee? The roster will show any discount versus the catalog price.`
                            : 'Change the fee for this session?';
                        if (window.confirm(msg)) setSessionFeeLocked(false);
                      }}
                    >
                      Change price
                    </button>
                  ) : null}
                </div>
                {(() => {
                  const sessionRow = sessions.find((s) => s.id === addAttendeeModal.sessionId);
                  const deliveryLang = (sessionRow?.language || '').trim();
                  if (!deliveryLang) {
                    return (
                      <p className="mt-1 text-[11px] text-amber-800">
                        This session has no delivery language ? using the course default fee. Edit the session and set Language to
                        match a catalog language fee (e.g. Arabic ?300).
                      </p>
                    );
                  }
                  const catalog = getCourseCatalogDefaultFeeForSession(
                    sessionRow?.courseName,
                    courseDbFees,
                    deliveryLang
                  );
                  if (catalog <= 0) return null;
                  const langSpecific = hasLanguageSpecificCatalogFee(sessionRow?.courseName, courseDbFees, deliveryLang);
                  return (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Session delivered in <span className="font-medium">{deliveryLang}</span>
                      {langSpecific
                        ? ` ? catalog fee ?${catalog.toFixed(2)}`
                        : ` ? no separate catalog price for ${deliveryLang}; using course default ?${catalog.toFixed(2)}`}
                    </p>
                  );
                })()}
              </div>
              {!addAttendeeModal.editingAttendee && (
                <div>
                  <label className={fieldLabel}>
                    Source <span className="text-red-500">*</span>
                    <span className="ml-1 font-normal text-[11px] text-gray-500">(how did this booking reach us?)</span>
                  </label>
                  <select
                    className={fieldIn}
                    value={addAttendeeModalSource}
                    onChange={(e) => setAddAttendeeModalSource(e.target.value as InboundChannel | '')}
                  >
                    <option value="">Select a source?</option>
                    {INBOUND_CHANNEL_OPTIONS.filter((o) => o.value !== 'bulk_import').map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <p className="mb-2 text-xs font-medium text-gray-700">Payment status</p>
                {addAttendeeModal.editingAttendee ? (
                  <p className="mb-2 text-[11px] text-slate-500">
                    To correct a mistaken paid-in-full: change the course fee above and save with
                    &ldquo;Keep existing payments&rdquo; ? the paid amount updates to match. Or choose
                    &ldquo;Not paid yet&rdquo; to clear all payments. Use prepaid/part only to record an
                    additional payment.
                  </p>
                ) : null}
                <div className="grid gap-2">
                  {(
                    [
                      ...(addAttendeeModal.editingAttendee
                        ? ([['keep', 'Keep existing payments / correct fee']] as const)
                        : []),
                      ['unpaid', 'Not paid yet'],
                      ['prepaid', 'Prepaid (enter amount below)'],
                      ['part', 'Part paid (enter amount collected)'],
                      ['full', 'Paid in full']
                    ] as const
                  ).map(([v, label]) => (
                    <label key={v} className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm has-[:checked]:border-emerald-400 has-[:checked]:bg-emerald-50/60">
                      <input
                        type="radio"
                        name={`session-pay-${addAttendeeModal.editingAttendee?.id || 'new'}`}
                        className="text-emerald-600"
                        checked={addPayKind === v}
                        onChange={() => setAddPayKind(v)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              {(addPayKind === 'prepaid' || addPayKind === 'part') && (
                <div>
                  <label className={fieldLabel}>
                    {addAttendeeModal.editingAttendee &&
                    (paymentTimelineByAttendee[addAttendeeModal.editingAttendee.id] || []).length > 0
                      ? addPayKind === 'prepaid'
                        ? 'Additional prepaid amount ?'
                        : 'Additional payment ?'
                      : addPayKind === 'prepaid'
                        ? 'Prepaid amount ?'
                        : 'Amount paid so far ?'}
                  </label>
                  {addAttendeeModal.editingAttendee &&
                    (paymentTimelineByAttendee[addAttendeeModal.editingAttendee.id] || []).length > 0 && (
                      <p className="mb-1 text-xs text-gray-500">
                        Already collected ?
                        {getExistingAttendeePaid(
                          addAttendeeModal.editingAttendee,
                          paymentTimelineByAttendee[addAttendeeModal.editingAttendee.id] || [],
                          parseEuroInput(addSessionFee)
                        ).toFixed(2)}
                        {parseEuroInput(addSessionFee) > 0
                          ? ` of ?${parseEuroInput(addSessionFee).toFixed(2)}`
                          : ''}
                      </p>
                    )}
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className={moneyIn}
                    placeholder="0.00"
                    value={addAmountStr}
                    onChange={(e) => setAddAmountStr(e.target.value)}
                  />
                </div>
              )}
              {addPayKind !== 'unpaid' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div>
                    <label className={fieldLabel}>Payment method</label>
                    <PaymentMethodSelect
                      className={fieldIn}
                      value={addPaymentMethod}
                      onChange={setAddPaymentMethod}
                    />
                  </div>
                  <div>
                    <label className={fieldLabel}>Payment date (DD/MM/YYYY)</label>
                    <input
                      type="text"
                      className={fieldIn}
                      placeholder="DD/MM/YYYY"
                      value={addPaymentDateUk}
                      onChange={(e) => setAddPaymentDateUk(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={fieldLabel}>Step type</label>
                    <select
                      className={fieldIn}
                      value={addPaymentStepType}
                      onChange={(e) => setAddPaymentStepType(e.target.value as AttendeePaymentStepType)}
                    >
                      <option value="deposit">Deposit</option>
                      <option value="on_day">On-day</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              )}
              {addPayKind !== 'unpaid' && (
                <div>
                  <label className={fieldLabel}>Payment note</label>
                  <textarea
                    className={fieldIn}
                    rows={2}
                    placeholder='e.g. ?190 received on 12/05 from John Smith'
                    value={addPaymentNote}
                    onChange={(e) => setAddPaymentNote(e.target.value)}
                  />
                  {addPayKind === 'keep' ? (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Used when correcting a paid-in-full fee ? a new Payment history line is recorded for the
                      updated amount.
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Optional accounting note ? who paid, which transfer, which courses it covers.
                    </p>
                  )}
                </div>
              )}
              {(addAttendeeModal.editingAttendee?.companyCustomerId ||
                addAssignedCompanyCustomerId ||
                addAttendeeModal.customer.customerType !== 'individual') && (
                <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-900">Company invoice</p>
                  <p className="text-[11px] text-amber-800/90">
                    One invoice per company group. When marked paid, remaining balances are recorded for every seat in
                    that group.
                  </p>
                  <div>
                    <label className={fieldLabel}>Invoice number</label>
                    <input
                      className={fieldIn}
                      value={addInvoiceNumber}
                      onChange={(e) => setAddInvoiceNumber(e.target.value)}
                      placeholder="e.g. INV-2026-0142"
                    />
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      checked={addInvoicePaid}
                      onChange={(e) => {
                        if (!addAttendeeModal) return;
                        const sessionId = addAttendeeModal.sessionId;
                        const companyId =
                          addAttendeeModal.editingAttendee?.companyCustomerId ||
                          addAttendeeModal.editingAttendee?.sponsoringCompanyCustomerId ||
                          addAssignedCompanyCustomerId ||
                          (addAttendeeModal.customer.customerType !== 'individual' ? addAttendeeModal.customer.id : '');
                        const sessionAttendees = attendeesBySession[sessionId] || [];
                        const anchor =
                          addAttendeeModal.editingAttendee ||
                          sessionAttendees.find(
                            (a) =>
                              a.companyCustomerId === companyId || a.sponsoringCompanyCustomerId === companyId
                          );
                        requestCompanyInvoicePaidToggle(e.target.checked, {
                          target: 'addAttendee',
                          sessionId,
                          anchorAttendee: anchor,
                          companyLabel: companyCustomers.find((co) => co.id === companyId)?.companyName,
                          seatCount: anchor ? undefined : 1,
                        });
                      }}
                    />
                    Invoice paid (all seats in group)
                  </label>
                </div>
              )}
              {addAttendeeModal.customer.customerType === 'individual' && (
                <div>
                  <label className={fieldLabel}>Assign to company (optional)</label>
                  {(() => {
                    const selectedCompany =
                      companyCustomers.find((co) => co.id === addAssignedCompanyCustomerId) ||
                      companyPickerHits.find((co) => co.id === addAssignedCompanyCustomerId) ||
                      null;
                    if (selectedCompany) {
                      return (
                        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                            {selectedCompany.companyName}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 text-xs font-semibold text-slate-600 hover:text-slate-900"
                            onClick={() => {
                              setAddAssignedCompanyCustomerId('');
                              resetCompanyPicker();
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-1.5">
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${fieldIn} pl-8`}
                            placeholder="Search companies by name?"
                            value={companyPickerQuery}
                            onChange={(e) => setCompanyPickerQuery(e.target.value)}
                          />
                        </div>
                        {companyPickerSearching ? (
                          <p className="text-xs text-slate-500">Searching?</p>
                        ) : companyPickerQuery.trim().length >= 2 && companyPickerHits.length === 0 ? (
                          <p className="text-xs text-slate-500">No companies match that search.</p>
                        ) : companyPickerHits.length > 0 ? (
                          <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                            {companyPickerHits.map((co) => (
                              <button
                                key={co.id}
                                type="button"
                                className="block w-full truncate border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
                                onClick={() => {
                                  upsertCustomersInMemory([co]);
                                  setAddAssignedCompanyCustomerId(co.id);
                                  setCompanyPickerQuery(String(co.companyName || ''));
                                  setCompanyPickerHits([]);
                                }}
                              >
                                {co.companyName}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500">Leave blank for Individuals / Unassigned, or search a company.</p>
                        )}
                      </div>
                    );
                  })()}
                  <p className="mt-1 text-[11px] text-gray-500">
                    Use this when the attendee should appear inside a company roster block.
                  </p>
                </div>
              )}
              {isSafePassCourse(sessions.find((x) => x.id === addAttendeeModal.sessionId)?.courseName) && (
                <div>
                  <label className={fieldLabel}>Safe Pass process number</label>
                  <input
                    type="text"
                    className={fieldIn}
                    placeholder="Enter process number"
                    value={addSafePassProcessNumber}
                    onChange={(e) => setAddSafePassProcessNumber(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setAddAttendeeModal(null);
                  setSessionFeeLocked(false);
                  setAddAssignedCompanyCustomerId('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                disabled={addAttendeeSaving}
                onClick={() => void confirmAddAttendee()}
              >
                {addAttendeeSaving
                  ? addAttendeeModal.editingAttendee
                    ? 'Saving?'
                    : 'Adding?'
                  : addAttendeeModal.editingAttendee
                    ? 'Save changes'
                    : 'Add to booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {replaceVirtualModal && !createCrmForVirtualSeatOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900">Replace virtual seat</p>
                <p className="text-sm text-gray-600">Search CRM and pick the participant to assign.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                  onClick={() => setCreateCrmForVirtualSeatOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add participant
                </button>
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                  onClick={() => {
                    setCreateCrmForVirtualSeatOpen(false);
                    setReplaceVirtualModal(null);
                  }}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="mt-4">
              <label className={fieldLabel}>Search CRM</label>
              <input
                type="text"
                className={fieldIn}
                placeholder="Name, company, email, phone"
                value={replaceVirtualModal.query}
                onChange={(e) => setReplaceVirtualModal((prev) => (prev ? { ...prev, query: e.target.value } : prev))}
                autoFocus
              />
            </div>
            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
              {replaceVirtualSearchResults.map((c) => {
                const name = `${c.firstName || ''} ${c.surname || ''}`.trim() || c.companyName || 'Customer';
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:bg-gray-50"
                    onClick={() => void confirmReplaceVirtualWithCustomer(c)}
                  >
                    <div className="font-medium text-gray-900">{name}</div>
                    <div className="text-xs text-gray-500">
                      {[c.companyName, c.emailAddress, c.contactPhoneNumber].filter(Boolean).join(' � ') || 'No contact details'}
                    </div>
                  </button>
                );
              })}
              {replaceVirtualModal.query.trim() && replaceVirtualSearchResults.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  No Individual CRM match found.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {copyAttendeeModal && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-base font-semibold text-gray-900">Copy to another course</p>
                <p className="text-sm text-gray-600">Adds this participant to another session with the target course fee. Payments are not copied ? use Move to another date to keep payment history.</p>
              </div>
              <button type="button" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setCopyAttendeeModal(null)} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <label className={fieldLabel}>Target session</label>
              <select className={fieldIn} value={copyAttendeeModal.targetSessionId} onChange={(e) => setCopyAttendeeModal((prev) => (prev ? { ...prev, targetSessionId: e.target.value } : prev))}>
                <option value="">Select target session</option>
                {sessions
                  .filter(
                    (s) =>
                      s.id !== copyAttendeeModal.attendee.bookingId && (s.status || 'scheduled') !== 'cancelled'
                  )
                  .sort(compareSessionsByNextDate)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {formatSessionTargetLabel(s)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setCopyAttendeeModal(null)}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={() => void confirmCopyAttendeeToSession()}>Copy to course</button>
            </div>
          </div>
        </div>
      )}

      {companyGroupInvoiceModal && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-slate-600 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-slate-50">Company invoice</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
                  <span className="font-semibold">{companyGroupInvoiceModal.companyLabel}</span>
                  {' � '}
                  {companyGroupInvoiceModal.seatCount} seat
                  {companyGroupInvoiceModal.seatCount === 1 ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                onClick={() => setCompanyGroupInvoiceModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className={fieldLabel}>Invoice number</label>
                <input
                  className={fieldIn}
                  value={companyGroupInvoiceModal.invoiceNumber}
                  onChange={(e) =>
                    setCompanyGroupInvoiceModal((prev) =>
                      prev ? { ...prev, invoiceNumber: e.target.value } : prev
                    )
                  }
                  placeholder="e.g. INV-2026-0142"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
                  One invoice for every seat in this company group on this session.
                </p>
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2.5 text-sm text-gray-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  checked={companyGroupInvoiceModal.invoicePaid}
                  onChange={(e) => {
                    const sessionAttendees =
                      attendeesBySession[companyGroupInvoiceModal.sessionId] || [];
                    const anchor = sessionAttendees.find(
                      (a) => a.id === companyGroupInvoiceModal.anchorAttendeeId
                    );
                    requestCompanyInvoicePaidToggle(e.target.checked, {
                      target: 'companyGroupInvoice',
                      sessionId: companyGroupInvoiceModal.sessionId,
                      anchorAttendee: anchor,
                      companyLabel: companyGroupInvoiceModal.companyLabel,
                      seatCount: companyGroupInvoiceModal.seatCount,
                      invoiceNumber: companyGroupInvoiceModal.invoiceNumber,
                    });
                  }}
                />
                Invoice paid ? record remaining fee on all seats
              </label>
              {companyGroupInvoiceModal.invoicePaid ? (
                <div>
                  <label className={fieldLabel}>Payment method</label>
                  <PaymentMethodSelect
                    className={fieldIn}
                    value={companyGroupInvoiceModal.paymentMethod}
                    onChange={(paymentMethod) =>
                      setCompanyGroupInvoiceModal((prev) => (prev ? { ...prev, paymentMethod } : prev))
                    }
                  />
                </div>
              ) : null}
              {companyGroupInvoiceModal.invoicePaid &&
              companyGroupInvoiceModal.seatsOutstandingEuro > 0.005 ? (
                <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 dark:border-orange-800 dark:bg-orange-950/40">
                  <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                    ?{companyGroupInvoiceModal.seatsOutstandingEuro.toFixed(2)} still owed across these seats
                  </p>
                  <p className="mt-1 text-xs text-orange-800 dark:text-orange-200">
                    Invoice is marked paid but seat payment lines were not recorded yet.
                  </p>
                  <button
                    type="button"
                    className="mt-2 rounded border border-orange-300 bg-white px-3 py-1.5 text-xs font-semibold text-orange-800 hover:bg-orange-100 dark:border-orange-600 dark:bg-slate-800 dark:text-orange-100"
                    onClick={() => {
                      const sessionAttendees =
                        attendeesBySession[companyGroupInvoiceModal.sessionId] || [];
                      const anchor = sessionAttendees.find(
                        (a) => a.id === companyGroupInvoiceModal.anchorAttendeeId
                      );
                      if (anchor) {
                        void settleCompanyGroupSeatPayments(
                          companyGroupInvoiceModal.sessionId,
                          anchor,
                          companyGroupInvoiceModal.invoiceNumber,
                          { paymentMethod: companyGroupInvoiceModal.paymentMethod }
                        ).then(() => setCompanyGroupInvoiceModal(null));
                      }
                    }}
                  >
                    Record seat payments now
                  </button>
                </div>
              ) : null}
              {!companyGroupInvoiceModal.invoicePaid ? (
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Unchecking paid removes auto-recorded company invoice settlement lines from every seat in
                  this group.
                </p>
              ) : null}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                onClick={() => setCompanyGroupInvoiceModal(null)}
              >
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={() => void saveCompanyGroupInvoice()}>
                Save invoice number
              </button>
            </div>
          </div>
        </div>
      )}

      {companyInvoicePaidConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-slate-600 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-slate-50">Mark company invoice paid?</p>
                <p className="mt-2 text-sm text-gray-700 dark:text-slate-200">
                  Mark the company invoice as paid and record the remaining fee on all{' '}
                  <span className="font-semibold">{companyInvoicePaidConfirm.seatCount}</span> seat
                  {companyInvoicePaidConfirm.seatCount === 1 ? '' : 's'} in{' '}
                  <span className="font-semibold">{companyInvoicePaidConfirm.companyLabel}</span> for{' '}
                  <span className="font-semibold">{companyInvoicePaidConfirm.sessionLabel}</span>?
                </p>
                {(companyInvoicePaidConfirm.seatsOutstandingEuro ?? 0) > 0.005 ? (
                  <p className="mt-2 text-sm font-semibold text-orange-800 dark:text-orange-200">
                    ?{companyInvoicePaidConfirm.seatsOutstandingEuro!.toFixed(2)} outstanding across these seats will
                    be recorded as paid.
                  </p>
                ) : null}
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  Each seat gets a payment line for whatever balance is still owed. Seat badges update to Paid when fully
                  covered.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                onClick={() => setCompanyInvoicePaidConfirm(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {(companyInvoicePaidConfirm.seatsOutstandingEuro ?? 0) > 0.005 ? (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={fieldLabel}>Payment method</label>
                    <PaymentMethodSelect
                      className={fieldIn}
                      value={invoiceSettlementMethod}
                      onChange={setInvoiceSettlementMethod}
                    />
                  </div>
                  <div>
                    <label className={fieldLabel}>Payment date (DD/MM/YYYY)</label>
                    <input
                      className={fieldIn}
                      placeholder="DD/MM/YYYY"
                      value={invoiceSettlementDateUk}
                      onChange={(e) => setInvoiceSettlementDateUk(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className={fieldLabel}>Payment reference / note</label>
                  <input
                    className={fieldIn}
                    value={invoiceSettlementReference}
                    onChange={(e) => setInvoiceSettlementReference(e.target.value)}
                    placeholder="e.g. Bank transfer ref 33264"
                  />
                </div>
              </div>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                onClick={() => setCompanyInvoicePaidConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={() => void confirmCompanyInvoicePaidApply()}
              >
                Mark paid & record seats
              </button>
            </div>
          </div>
        </div>
      )}

      {editPaymentModal && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <p className="text-lg font-semibold text-gray-900">Edit payment line</p>
              <button type="button" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditPaymentModal(null)} aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <div><label className={fieldLabel}>Amount EUR</label><input className={moneyIn} value={editPaymentModal.amountStr} onChange={(e) => setEditPaymentModal((prev) => (prev ? { ...prev, amountStr: e.target.value } : prev))} /></div>
              <div><label className={fieldLabel}>Payment date (DD/MM/YYYY)</label><input className={fieldIn} value={editPaymentModal.paymentDateUk} onChange={(e) => setEditPaymentModal((prev) => (prev ? { ...prev, paymentDateUk: e.target.value } : prev))} /></div>
              <div><label className={fieldLabel}>Method</label><PaymentMethodSelect className={fieldIn} value={editPaymentModal.method} onChange={(method) => setEditPaymentModal((prev) => (prev ? { ...prev, method } : prev))} /></div>
              <div>
                <label className={fieldLabel}>Step type</label>
                <select
                  className={fieldIn}
                  value={editPaymentModal.stepType}
                  onChange={(e) =>
                    setEditPaymentModal((prev) =>
                      prev ? { ...prev, stepType: e.target.value as AttendeePaymentStepType } : prev
                    )
                  }
                >
                  <option value="deposit">Deposit</option>
                  <option value="on_day">On-day</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className={fieldLabel}>Payment note</label>
                <textarea
                  className={fieldIn}
                  rows={2}
                  value={editPaymentModal.payerNotes}
                  onChange={(e) => setEditPaymentModal((prev) => (prev ? { ...prev, payerNotes: e.target.value } : prev))}
                  placeholder="Accounting note for this payment line"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setEditPaymentModal(null)}>Cancel</button>
              <button type="button" className={btnPrimary} onClick={() => void saveEditPaymentLine()}>Save payment</button>
            </div>
          </div>
        </div>
      )}
      {rescheduleAttendeeModal && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900">Reschedule attendee</p>
                <p className="text-sm text-gray-600">Move this attendee to another session.</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setRescheduleAttendeeModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <label className={fieldLabel}>Target session</label>
              <select
                className={fieldIn}
                value={rescheduleAttendeeModal.targetSessionId}
                onChange={(e) => setRescheduleAttendeeModal((prev) => (prev ? { ...prev, targetSessionId: e.target.value } : prev))}
              >
                <option value="">Select target session</option>
                {sessions
                  .filter(
                    (s) =>
                      s.id !== rescheduleAttendeeModal.attendee.bookingId &&
                      (s.status || 'scheduled') !== 'cancelled'
                  )
                  .sort(compareSessionsByNextDate)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {formatSessionTargetLabel(s)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setRescheduleAttendeeModal(null)}>
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={() => void confirmRescheduleAttendee()}>
                Move attendee
              </button>
            </div>
          </div>
        </div>
      )}


      {bulkVirtualFillModal && (
        <div className="fixed inset-0 z-[76] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900">Fill seats from names</p>
                <p className="text-sm text-gray-600">
                  Paste full names (one per line) for{' '}
                  <span className="font-semibold">{bulkVirtualFillModal.groupLabel}</span>. The first{' '}
                  {bulkVirtualFillModal.attendeeIds.length} name(s) will fill the oldest virtual seat(s) in this group.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => setBulkVirtualFillModal(null)}
                disabled={bulkVirtualFillSaving}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <label className={fieldLabel}>
                Names ({bulkVirtualFillModal.attendeeIds.length} virtual seat
                {bulkVirtualFillModal.attendeeIds.length === 1 ? '' : 's'} available)
              </label>
              <textarea
                className={`${fieldIn} min-h-[180px]`}
                value={bulkVirtualFillModal.pasted}
                onChange={(e) =>
                  setBulkVirtualFillModal((prev) => (prev ? { ...prev, pasted: e.target.value } : prev))
                }
                placeholder={'John Smith\nMaria Garcia\nIon Popescu'}
                disabled={bulkVirtualFillSaving}
                autoFocus
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => setBulkVirtualFillModal(null)}
                disabled={bulkVirtualFillSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={btnPrimary}
                disabled={bulkVirtualFillSaving}
                onClick={() => void submitBulkVirtualFill()}
              >
                {bulkVirtualFillSaving ? 'Filling?' : 'Fill seats'}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferAttendeeModal && (
        <div className="fixed inset-0 z-[76] flex items-center justify-center bg-black/50 p-3">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-900">Move to removed list</p>
                <p className="text-sm text-gray-600">
                  Keeps the participant on this session below the active roster (with their Process # and a note).
                  Use the Reschedule action if you want to move them onto a different session date.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setTransferAttendeeModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <label className={fieldLabel}>Reason / note</label>
              <input
                type="text"
                className={fieldIn}
                value={transferAttendeeModal.note}
                onChange={(e) =>
                  setTransferAttendeeModal((prev) => (prev ? { ...prev, note: e.target.value } : prev))
                }
                placeholder="e.g. Transferred to Safe Pass Romanian 14/05/2026"
                autoFocus
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => setTransferAttendeeModal(null)}
              >
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={() => void submitTransferAttendee()}>
                Move below list
              </button>
            </div>
          </div>
        </div>
      )}

      {financialSummaryModal && (
        <CourseFinancialSummaryModal
          data={financialSummaryModal}
          canDownload={canDownloadFinancialSummary}
          onClose={() => setFinancialSummaryModal(null)}
        />
      )}

      {sessionDocumentViewer && (
        <div className="fixed inset-0 z-[78] flex items-center justify-center bg-black/60 p-3">
          <div className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{sessionDocumentViewer.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(sessionDocumentViewer.size)}</p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={sessionDocumentViewer.signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  Download
                </a>
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                  onClick={() => setSessionDocumentViewer(null)}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {(sessionDocumentViewer.mimeType || '').includes('pdf') || sessionDocumentViewer.name.toLowerCase().endsWith('.pdf') ? (
              <iframe
                title={sessionDocumentViewer.name}
                src={sessionDocumentViewer.signedUrl}
                className="h-full w-full"
              />
            ) : sessionDocumentViewer.name.toLowerCase().endsWith('.doc') ||
                sessionDocumentViewer.name.toLowerCase().endsWith('.docx') ||
                sessionDocumentViewer.name.toLowerCase().endsWith('.xls') ||
                sessionDocumentViewer.name.toLowerCase().endsWith('.xlsx') ? (
              <div className="flex h-full flex-col">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-600">
                  Inline preview runs locally in the app. If parsing fails, use Download or open in a new tab.
                </div>
                {sessionDocumentPreviewLoading ? (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-gray-600">
                    Preparing preview...
                  </div>
                ) : sessionDocumentPreviewError ? (
                  <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-600">
                    {sessionDocumentPreviewError}
                  </div>
                ) : (
                  <div className="h-full overflow-auto bg-white p-4">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sessionDocumentPreviewHtml || '<p>No preview content found.</p>' }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-600">
                Inline preview is not available for this file type. Use Open/Download to view it in your default app.
              </div>
            )}
          </div>
        </div>
      )}

      {sessionDocumentsModal && (
        <div className="fixed inset-0 z-[77] flex items-center justify-center bg-black/55 p-3">
          <div className="w-full max-w-3xl rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-semibold text-gray-900">Session documents</p>
                <p className="text-sm text-gray-600">{sessionDocumentsModal.courseName}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setSessionDocumentsModal(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">PDF, Word, Excel up to 5 MB.</p>
              <button
                type="button"
                onClick={() => uploadDocumentInputRef.current?.click()}
                disabled={sessionDocumentUploading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Upload className="h-4 w-4" />
                {sessionDocumentUploading ? 'Uploading?' : 'Upload'}
              </button>
            </div>
            <div className="mt-3 space-y-2 max-h-[52vh] overflow-y-auto pr-1">
              {sessionDocumentsLoading ? (
                <p className="text-sm text-gray-500">Loading documents...</p>
              ) : sessionDocuments.length === 0 ? (
                <p className="text-sm text-gray-500">No documents uploaded for this session yet.</p>
              ) : (
                sessionDocuments.map((doc) => {
                  const isPdf = (doc.mimeType || '').includes('pdf') || doc.name.toLowerCase().endsWith('.pdf');
                  return (
                    <div key={doc.path} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900" title={doc.name}>{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(doc.size)}
                          {doc.updatedAt ? ` � ${new Date(doc.updatedAt).toLocaleString('en-GB')}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                          onClick={() => setSessionDocumentViewer(doc)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          {isPdf ? 'View' : 'Open'}
                        </button>
                        <a
                          href={doc.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                        >
                          <FileDown className="h-3.5 w-3.5" />
                          Download
                        </a>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800 hover:bg-red-100"
                          onClick={() => void handleDeleteSessionDocument(doc)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}